/**
 * App-wide i18n. Locale codes match backend and config/locales.js.
 * Main app UI (sidebar dropdown): en, es, ar only; default from VITE_DEFAULT_LOCALE.
 * Public templates and other features: en, es, zh, fil, de, fr, uk, ru, ja, nl, ar.
 * Load from JSON or CMS later if needed.
 */

import { createI18n } from 'vue-i18n'

const messages = {
  en: {
    app: {
      name: 'RMS',
      dashboard: 'Dashboard',
      login: 'Sign in',
      register: 'Create an account',
      logout: 'Sign out',
      profile: 'Profile & Settings',
      profileTab: 'Profile',
      restaurants: 'Businesses',
      menu: 'Catalog',
      menuItems: 'Catalog',
      feedbacks: 'Feedbacks',
      featureRequest: 'Feature request',
      users: 'Users',
      ownerFeedbacks: 'Owner feedbacks',
      inquiries: 'Inquiries',
      languages: 'Languages & description',
      default: 'Default',
      addLanguage: 'Add language',
      defaultLanguage: 'Default language',
      languagesHint: 'Add or remove languages. The default language is used when no language is selected on the public site.',
      description: 'Description',
      save: 'Save',
      cancel: 'Cancel',
      cropApply: 'Apply',
      cropTitle: 'Crop image',
      cropDescription: 'Adjust the crop area, then apply.',
      close: 'Close',
      loading: 'Loading…',
      noContent: 'No content yet.',
      remove: 'Remove',
      add: 'Add',
      translate: 'Translate from default',
      addItem: 'Add item',
      editItem: 'Edit item',
      createItem: 'Add item',
      languageLabel: 'Language',
      languageLabelTooltip: 'This language is only for the admin app interface, not for your public pages or catalogs.',
      menuItemsSubtitle: 'Add and edit items. You can add them to catalog categories inside each business.',
      catalogNavInfoTooltip: 'About Catalog',
      catalogNavInfoModalTitle: 'About Catalog',
      catalogNavInfoModalDescription: 'How Catalog in the sidebar relates to businesses and catalog items.',
      catalogNavInfoModalP1: 'Everything here is about your catalog items—your shared library of products, services, bundles, and variants.',
      catalogNavInfoModalP2: 'Item images are managed here: open a catalog item to upload or change photos (including variant images where applicable).',
      catalogNavInfoModalP3: 'Add catalog items from the Businesses page: open a business, then use its Catalog tab to create items and organize them into categories for your public site.',
      catalogNavInfoGoToBusinesses: 'Open Businesses',
      catalogNotAssignedBadge: 'Not on any menu',
      catalogAssignedToPrefix: 'Assigned to:',
      catalogAssignedMoreCount: '+{count}',
      catalogListResultSummary: 'Showing {shown} of {total} items',
      catalogListNoMatches: 'No items match your search or filters.',
      catalogListLoadMore: 'Load more',
      catalogFilterAllTypes: 'All',
      ariaCatalogTypeFilter: 'Filter by catalog item type',
      catalogVariantsFromPrice: 'From {price}',
      addFirstMenuItem: 'Add your first item',
      menuItemsEmptyHint: 'Create products, services, or listings. Add them to categories inside each business\'s Catalog tab.',
      deleteMenuItem: 'Delete item',
      deleteMenuItemConfirm: 'This cannot be undone. The item will be removed from all businesses that use it.',
      menuItemFormSubtitleEdit: 'Name and description per language. At least the default language is required.',
      menuItemFormSubtitleEditSingle: 'Update the name and optional description.',
      menuItemFormSubtitleCreate: 'Add the name and optional description. You can add other languages after creating the item.',
      menuItemFormSubtitleCreateSingle: 'Add the name and optional description for your item.',
      menuItemCatalogPerBusinessHint:
        'You are editing the shared catalog item. If this item is customized per business—price, description, availability, or visibility on the public menu—open that business in the dashboard and edit it under the Menu tab so the correct location is updated.',
      catalogSummaryTypePrefix: 'Type:',
      catalogComboEntriesHint: 'Entries (edit below to change):',
      catalogComboPricePrefix: 'Combo price:',
      catalogOptionGroupsHeading: 'Option groups:',
      catalogVariantsHeadingPrice: 'Variants (price):',
      catalogVariantsHeadingPriceImage: 'Variants (price, image):',
      itemTypeSimple: 'Simple',
      itemTypeCombo: 'Combo',
      itemTypeWithVariants: 'With variants',
      catalogItemTypeSimpleHint: 'Single item with one price',
      catalogItemTypeComboHint: 'Bundle of other catalog items',
      catalogItemTypeVariantsHint: 'Options (e.g. size, type) with a price per combination',
      ariaMenuItemTypeRadiogroup: 'Catalog item type',
      catalogTypeSectionTitle: 'Type',
      priceFieldLabel: 'Price',
      priceOptionalLabel: 'Price (optional)',
      comboPriceOptionalLabel: 'Combo price (optional)',
      placeholderPriceExample: 'e.g. 10.00',
      placeholderComboPriceExample: 'e.g. 12.00',
      nameFieldLabel: 'Name',
      descriptionOptionalLabel: 'Description (optional)',
      shortDescriptionPlaceholder: 'Short description',
      catalogAddPageTitle: 'Add catalog item',
      catalogAddPageSubtitle: 'Choose a type, then add the name and type-specific details.',
      assignCatalogPageTitle: 'Add item to a business',
      assignCatalogPageSubtitle:
        'Choose a business and category. The item stays in your catalog; this places it on that business’s public menu.',
      assignCatalogSheetEyebrow: 'Catalog item',
      assignCatalogSuccessEyebrow: 'All set',
      assignCatalogItemLabel: 'Item',
      assignCatalogChooseBusiness: 'Business',
      assignCatalogChooseBusinessPlaceholder: '— Select business —',
      assignCatalogFirstMenuHint: 'Categories are taken from this business’s first menu (by order).',
      assignCatalogCategoryModeLabel: 'Category',
      assignCatalogCategoryModeExisting: 'Existing category',
      assignCatalogCategoryModeNew: 'New category',
      assignCatalogSelectCategory: 'Category',
      assignCatalogSelectCategoryPlaceholder: '— Select category —',
      assignCatalogNewCategoryName: 'New category name',
      assignCatalogNewCategoryPlaceholder: 'e.g. Starters',
      assignCatalogAssignButton: 'Add to menu',
      assignCatalogSkip: 'Skip for now',
      assignCatalogNoBusinesses: 'You don’t have a business yet. Create one under Businesses, then come back here.',
      assignCatalogGoToBusinesses: 'Open Businesses',
      assignCatalogNoMenus: 'This business has no menu yet. Open its Catalog tab and add a menu first.',
      assignCatalogOpenCatalogTab: 'Open Catalog tab',
      assignCatalogNoCategoriesHint: 'No categories yet—use “New category” above or add categories from the business Catalog tab.',
      assignCatalogErrRestaurant: 'Select a business.',
      assignCatalogErrCategory: 'Select a category.',
      assignCatalogErrNewCategoryName: 'Enter a name for the new category.',
      assignCatalogNotFound: 'This catalog item was not found.',
      assignCatalogNotStandalone: 'This item is not a catalog-only item. Edit it from the business menu instead.',
      assignToBusiness: 'Add to business',
      assignCatalogSuccessTitle: 'Added to your menu',
      assignCatalogSuccessBody: 'Create another catalog item or open the category where you placed this one.',
      assignCatalogCreateAnotherItem: 'Create another item',
      assignCatalogGoToCategory: 'Go to assigned category',
      comboEntriesTitle: 'Combo entries',
      addComboEntry: 'Add entry',
      entryNumber: 'Entry {n}',
      ariaRemoveComboEntry: 'Remove entry',
      selectCatalogItemPlaceholder: '— Select item —',
      selectVariantPlaceholder: '— Select variant —',
      variantFieldLabel: 'Variant',
      quantityFieldLabel: 'Quantity',
      modifierOptionalLabel: 'Modifier (optional)',
      placeholderModifierExample: 'e.g. No ice',
      optionGroupsTitle: 'Option groups',
      addOptionGroup: 'Add group',
      removeOptionGroupAria: 'Remove group',
      catalogGroupNameLabel: 'Group name',
      catalogGroupNamePlaceholder: 'e.g. Size',
      catalogGroupNameLabelWithHint: 'Group name (e.g. {example})',
      exampleSize: 'Size',
      exampleType: 'Type',
      optionValuesOnePerLine: 'Values (one per line or comma-separated)',
      optionValuesCommaSeparated: 'Values (comma-separated)',
      variantSizesPlaceholder: 'Small, Medium, Large',
      combinationColumn: 'Combination',
      priceColumn: 'Price',
      variantPricesTitle: 'Variant prices',
      ariaSelectMenuItemForCombo: 'Select catalog item',
      ariaSelectVariant: 'Select variant',
      placeholderVariantPrice: '0.00',
      creatingItem: 'Creating…',
      createItemSubmit: 'Create item',
      menuItemDeletedToast: 'Item deleted.',
      menuItemCreatedToast: 'Item created.',
      menuItemUpdatedToast: 'Item updated.',
      restaurantCreatedToast: 'Business created.',
      restaurantUpdatedToast: 'Business updated.',
      selectLanguageForItemAria: 'Select language for name and description',
      translateFromDefaultAria: 'Translate from default language',
      descriptionInLanguagePlaceholder: 'Description in {lang}',
      restoreFromCatalogTitle: 'Restore the original name, description and price from the catalog',
      saveChangesConfirmTitle: 'Save changes?',
      menuItemSaveSensitiveDescription: 'Confirm saving changes that remove variants, combo entries, or update text.',
      menuItemSaveSensitiveBody: 'You’ve removed variants or combo entries, or updated the name or description. Save these changes?',
      overridePriceLabel: 'Override price (leave empty to use base: {base})',
      sameAsBasePlaceholder: 'Same as base',
      noCustomFieldsToReset: 'No custom name or price to reset',
      resettingShort: 'Resetting…',
      resetToOriginal: 'Reset to original',
      menuItemImageSectionTitle: 'Image',
      menuItemImageHint:
        'Optional. JPEG, PNG, GIF or WebP, max 2MB. Crop any shape you like; stored up to 512px on the longest side (aspect ratio kept).',
      menuItemBaseImageTitle: 'Main product image',
      menuItemBaseImageHintVariants: 'Shown on your public menu for this product (the menu card). Variant images below are optional for each option.',
      ariaRemoveItemImage: 'Remove image',
      beautifyWithAi: 'Beautify with AI',
      variantPhotoLabel: 'Photo',
      variantGalleryLabel: 'Gallery',
      variantChangeShort: 'Change',
      variantAddShort: 'Add',
      ariaPriceForVariant: 'Price for {label}',
      genericVariant: 'variant',
      validateNameRequired: 'Name is required.',
      validateDefaultNameRequired: 'Name is required for the default language.',
      validatePriceNonNegative: 'Price must be 0 or greater.',
      validateComboPriceNonNegative: 'Combo price must be 0 or greater.',
      validateComboEntriesMin: 'Add at least one combo entry.',
      validateSelectMenuItem: 'Select a catalog item.',
      validateSelectVariant: 'Select a variant for this item.',
      validateQuantityMin: 'Quantity must be at least 1.',
      validateOptionGroupsMin: 'Add at least one option group with values.',
      validateVariantPricesAll: 'Set a price for every variant.',
      validateModifierNameRequired: 'Enter a name for this modifier in at least one language.',
      validateModifierGroupNameRequired: 'Enter a name for this modifier group.',
      validateModifierPriceDelta: 'Modifier price must be 0 or greater.',
      quickOrderNameMaxLength: 'Name must be at most 100 characters.',
      quickOrderNotesMaxLength: 'Notes must be at most 500 characters.',
      menuItemModifiersSectionTitle: 'Modifiers (optional)',
      menuItemModifiersHint:
        'Optional groups (e.g. Upgrades, Sides). Name each group, choose whether customers pick one option or several, then add options with names and extra prices. Drag handles reorder groups and options.',
      menuItemModifiersHintCreate:
        'Optional groups. Use the same language as the item name above. Name each group, choose one-option (radio) or multi-select, then add options and prices.',
      addModifier: 'Add modifier',
      addModifierGroup: 'Add group',
      addModifierOption: 'Add option',
      removeModifierGroupAria: 'Remove modifier group',
      modifierGroupNameLabel: 'Group name',
      modifierGroupSelectionTypeLabel: 'How customers choose',
      modifierSelectionSingle: 'One option only (radio)',
      modifierSelectionMultiple: 'Any combination (checkboxes)',
      modifierGroupOptionsHeading: 'Options',
      modifierOptionNameLabel: 'Option name',
      catalogModifierGroupSingleHint: 'Customers will pick at most one option from this group on your public site.',
      removeModifierAria: 'Remove modifier',
      modifierPriceDeltaLabel: 'Extra price',
      modifierActiveLabel: 'Active (shown in catalog)',
      modifierNameInLocale: 'Name ({locale})',
      catalogModifierVisibilityTitle: 'Modifiers for this item',
      catalogModifierVisibilityHint:
        'Uncheck to hide a modifier for this item only. It stays in your catalog.',
      catalogModifierShowOnMenu: 'Show on public site',
      catalogVariantVisibilityTitle: 'Variants for this item',
      catalogVariantVisibilityHint:
        'Hide a variant combination from your public site, or keep it visible but mark it not available for ordering. Catalog definitions are unchanged.',
      catalogVariantShowOnMenu: 'Show on public site',
      catalogVariantAvailableForOrder: 'Available for ordering',
      fixErrorsBelow: 'Please fix the errors below.',
      checkFieldsMarkedRed: 'Check each field marked in red.',
      alsoFixErrorsInFormBelow: 'Also fix the errors in the form below.',
      phoneAllowedCharsOnly: 'Use only numbers and + - ( ) spaces.',
      phoneMinDigits: 'Enter a valid phone number (at least 6 digits).',
      restaurantNameRequired: 'Restaurant name is required.',
      slugRequired: 'Subdomain / URL slug is required.',
      slugFormatInvalid: 'Slug must be lowercase letters, numbers and hyphens only (e.g. my-restaurant).',
      slugReserved: 'This slug is reserved (e.g. dev server) and cannot be used. Please choose another.',
      customDomainInvalid: 'Enter a valid hostname (e.g. yourrestaurant.com).',
      smsRequiredForQuickOrdering: 'SMS number is required for quick ordering.',
      enterValidEmail: 'Please enter a valid email address.',
      enterValidUrl: 'Please enter a valid URL.',
      enterValidYear: 'Please enter a valid year (e.g. 1995).',
      fixScheduleForDays: 'Please fix the schedule for {days}.',
      somethingWentWrongTryAgain: 'Something went wrong. Please try again.',
      copyToClipboardFailed: 'Could not copy to clipboard.',
      generateQrFailed: 'Failed to generate QR code.',
      nameProducesReservedSlug: 'This name produces a reserved slug. Try adding a word or number.',
      restoredToOriginalToast: 'Restored to original.',
      imageRemovedToast: 'Image removed.',
      imageUpdatedToast: 'Image updated.',
      uploadImageFirstToast: 'Upload an image first.',
      imageBeautifiedFallback: 'Image beautified.',
      fillDefaultLanguageFirstToast: 'Fill in name or description in the default language first.',
      translationNotAvailableToast: 'Translation not available for this language. Original text shown — you can edit it.',
      translationAppliedToast: 'Translation applied. Review and save.',
      catalogComboEntryUnknownItem: 'Item {id}…',
      menuItemNamePlaceholder: 'e.g. Margherita Pizza',
      catalogItemFieldLabel: 'Catalog item',
      edit: 'Edit',
      delete: 'Delete',
      deleting: 'Deleting…',
      dashboardSubtitle: 'Manage and monitor your business in one place.',
      dashboardSuperadminSubtitle: 'System overview.',
      dashboardWelcome: 'Welcome,',
      dashboardSignedIn: 'Signed in as {email}. Use Profile & Settings to update your account or password.',
      dashboardSuperadminHint: 'Use Users or Businesses to manage the system.',
      dashboardReservationsToday: 'Today',
      dashboardReservationsTomorrow: 'Tomorrow',
      dashboardReservationsDayAfter: 'Day after',
      totalOrders: 'Total Orders',
      revenue: 'Revenue',
      paidUsers: 'Paid users',
      locations: 'Businesses',
      manageRestaurants: 'Manage businesses',
      yourRestaurants: 'Your businesses',
      restaurantsListSubtitle: 'Manage catalog and details for each business.',
      restaurantsListSubtitleEmbed: 'Switch to another business or add a new one.',
      restaurantsPaginationPageOf: 'Page {current} of {total}',
      restaurantsCountLabel: '{count} {count, plural, one {business} other {businesses}}',
      addRestaurant: 'Add business',
      addFirstLocation: 'Add your first business',
      addFirstLocationHint: 'Create a business to get your catalog online, share your address and hours, and let customers find you.',
      firstTimeLink: 'First time? Create your first business',
      createRestaurant: 'Create business',
      restaurant: 'Business',
      welcomeTitle: 'Create your first business',
      welcomeSubtitle: 'Get your catalog online, share your address and hours, and let customers find you in minutes.',
      welcomeCta: 'Create my first business',
      wizardStepOf: 'Step {current} of {total}',
      wizardBusinessTypeStepHint: 'Choose your business type first. We will pick the best starting template automatically.',
      wizardStep1Title: 'Essential details',
      wizardNameLabel: 'Business name',
      wizardTaglineOptional: 'Tagline (optional)',
      wizardSlugPreview: 'Your web address',
      wizardTemplateLabel: 'Public page template',
      wizardTemplateHint: 'Choose the layout for your public page.',
      wizardTemplateAutoSelectedHint: 'Template selected automatically based on your business type',
      wizardDefaultLanguageLabel: 'Default language for your public site',
      wizardDefaultLanguageHint:
        'We suggest Spanish if your device looks like it\'s in Spain (timezone or Spain locale); otherwise English. You can change this anytime in restaurant settings.',
      wizardDefaultLanguageInvalid: 'Choose English or Spanish.',
      contactAndLinks: 'Contact & links',
      contactQuickOrderLabel: 'Contact for quick ordering',
      smsNumberLabel: 'SMS number (required)',
      smsNumberPlaceholder: 'e.g. +1234567890 (used for quick ordering)',
      whatsappOptionalLabel: 'WhatsApp (optional)',
      whatsappOptionalPlaceholder: 'e.g. +1234567890',
      viberOptionalLabel: 'Viber (optional)',
      viberOptionalPlaceholder: 'e.g. +1234567890',
      contactQuickOrderNote: 'These numbers will be used for quick ordering on your public page.',
      contactWhatsAppViberSuggested: 'WhatsApp and Viber are optional but recommended so customers can send their orders.',
      restaurantEditPhoneLabel: 'SMS / business phone',
      restaurantEditPhoneHint:
        'This value is shown on your public site and used for Quick Order SMS when that feature is on. Add WhatsApp, Viber, or extra numbers under Contact & links.',
      wizardNext: 'Next',
      wizardBack: 'Back',
      wizardSkipForNow: 'Skip for now',
      wizardDoLater: "I'll do this later",
      wizardStep2Title: 'Location & contact (optional)',
      wizardYoureAllSet: "You're all set",
      wizardSublineLive: 'Your public page is live. Share the link or QR so customers can find you.',
      wizardCopyAddress: 'Copy address',
      wizardCopied: 'Copied!',
      wizardSaveQr: 'Save QR code',
      wizardScanHint: 'Customers scan this to open your page',
      wizardGoToMyRestaurant: 'Go to my page',
      wizardBackToDashboard: 'Back to dashboard',
      restaurantNotFound: 'Business not found.',
      locationOrItemNotFound: 'Business or item not found.',
      feedbacksSubtitle: 'View and moderate customer feedback for each business.',
      feedbacksTotal: 'Feedbacks (total)',
      feedbacksApproved: 'Approved',
      feedbacksRejected: 'Rejected',
      feedbacksCreateFirst: 'Create a business first to receive and manage feedbacks.',
      feedbacksListSubtitle: 'Approve feedback to show it on your public page. Reject or delete to hide or remove it.',
      feedbacksListEmpty: 'No feedbacks yet. They will appear here when customers submit reviews.',
      feedbacksLoadError: 'Failed to load feedbacks.',
      retry: 'Retry',
      redirectingToFeedbacks: 'Redirecting to feedbacks…',
      viewModerateFeedbacks: 'View & moderate feedbacks',
      viewAll: 'View all',
      featureRequestSubtitle: 'Send a feature request or feedback to the team. We review all submissions.',
      sendFeedbackHeading: 'Send feedback',
      myFeatureRequests: 'My feature requests',
      yourRecentSubmissions: 'Your recent submissions (newest first).',
      noSubmissionsYet: 'No submissions yet. Use the form above to send your first feature request.',
      sending: 'Sending…',
      messageLabel: 'Message',
      messagePlaceholder: 'Describe your feature request or feedback…',
      messageRequired: 'Message is required.',
      messageMaxLength: 'Message must be at most {max} characters.',
      titleOptional: 'Title (optional)',
      titlePlaceholder: 'Short summary',
      titleMaxLength: 'Title must be at most {max} characters.',
      restaurantOptional: 'Business (optional)',
      noneOption: '— None —',
      maxCharactersHint: 'Max 65,535 characters.',
      reviewed: 'Reviewed',
      pending: 'Pending',
      emailAddress: 'Email address',
      emailPlaceholder: "you{'@'}example.com",
      passwordPlaceholder: '••••••••',
      fullName: 'Full name',
      fullNamePlaceholder: 'Jane Smith',
      repeatPassword: 'Repeat password',
      passwordHintPlaceholder: 'At least 8 characters',
      restaurantName: 'Business name',
      restaurantNamePlaceholder: "e.g. Mama Fina's, Joe's Salon, Tech Store",
      taglineOptional: 'Tagline (optional)',
      taglinePlaceholder: 'e.g. Fresh ingredients, bold flavors',
      addressPlaceholder: 'Street, city, state',
      descriptionPlaceholder: 'Short description for your public site',
      emailOptional: 'Email (optional)',
      contactEmailPlaceholder: "contact{'@'}example.com",
      websiteOptional: 'Website (optional)',
      websitePlaceholder: 'https://example.com',
      googleMapsLinkOptional: 'Google Maps link (optional)',
      googleMapsLinkPlaceholder: 'https://www.google.com/maps?q=... or paste a Google Maps link',
      googleMapsLinkHint: 'Paste a Google Maps link. We read coordinates from the link when possible to show the same location on your public page map (OpenStreetMap). If we cannot read coordinates, visitors see a button that opens your saved link.',
      yearEstablishedOptional: 'Year established (optional)',
      yearPlaceholder: 'e.g. 1995',
      categoryName: 'Category name',
      categoryNamePlaceholder: 'e.g. Best Sellers, Popular',
      filterByNamePlaceholder: 'Filter by name…',
      shortDescription: 'Short description',
      shortDescriptionPlaceholder: 'Short description for this category',
      menuNamePlaceholder: 'e.g. Lunch, Services, Products',
      saveChanges: 'Save changes',
      createRestaurantBtn: 'Create business',
      editRestaurant: 'Edit business',
      addNewRestaurant: 'Add new business',
      saving: 'Saving…',
      signIn: 'Sign in',
      signingIn: 'Signing in…',
      createAccount: 'Create account',
      creatingAccount: 'Creating account…',
      resendLink: 'Resend link',
      resendIn: 'Resend in {seconds}s',
      sendingResend: 'Sending…',
      resetPassword: 'Reset password',
      resetting: 'Resetting…',
      saveProfile: 'Save profile',
      savingProfile: 'Saving profile…',
      currentPassword: 'Current password',
      newPassword: 'New password',
      updatePassword: 'Update password',
      updatingPassword: 'Updating password…',
      ownerOnlyMessage: 'This message is only shown to you the owner.',
      ownerNotice: 'Owner notice',
      adminLinkUnavailable: 'Admin link unavailable. Sign in from the main app.',
      feedbackSubmitted: 'Feedback submitted.',
      password: 'Password',
      confirmPassword: 'Confirm password',
      forgotPassword: 'Forgot password?',
      rememberMe: 'Remember me for 30 days',
      continue: 'Continue',
      back: 'Back',
      minimumCharacters: 'Minimum 8 characters',
      passwordsDontMatch: "Passwords don't match",
      accessDashboard: 'Access your dashboard',
      startManaging: 'Start managing your business for free',
      orContinueWith: 'Or continue with',
      orSignUpWithEmail: 'Or sign up with email',
      continueWithGoogle: 'Continue with Google',
      continueWithGoogleConfigure: 'Continue with Google (configure client ID)',
      continueWithFacebook: 'Continue with Facebook',
      continueWithFacebookConfigure: 'Continue with Facebook (configure in .env)',
      continueWithInstagram: 'Continue with Instagram',
      signInFailedTryAgain: 'Sign in failed. Please try again.',
      googleSignInCancelledOrFailed: 'Google sign-in was cancelled or did not finish. Please try again or use email and password.',
      googleSignInGenericFailure: 'Google sign-in did not finish. Please try again or use email and password.',
      googleSignInBackendFailed: 'We could not complete sign-in with Google. Please try again or use email and password.',
      googleSignInScriptLoadFailed: 'Google sign-in could not load (network or blocker). Check your connection, allow accounts.google.com, and refresh the page.',
      googleSignInNotReady: 'Google sign-in is still loading. Wait a moment and try again.',
      googleSignInInitFailed: 'Google sign-in could not start on this page. Refresh and try again.',
      googleSignInCredentialError: 'Google sign-in error: {detail}',
      googleSignInNoSession: 'No Google session was found, or sign-in was blocked. Sign in to Google in this browser, allow third-party cookies for this site if prompted, or use email and password below.',
      googleSignInSuppressedByUser: 'Google sign-in was dismissed recently. Try again in a little while, or use email and password.',
      googleSignInUnregisteredOrigin: 'This site’s URL is not allowed for Google sign-in. The owner must add this origin in Google Cloud Console (OAuth client).',
      googleSignInSecureHttpRequired: 'Google sign-in requires HTTPS. Open the site over a secure connection.',
      googleSignInBrowserNotSupported: 'This browser does not support Google sign-in here. Try another browser or use email and password.',
      googleSignInInvalidClient: 'Google sign-in is misconfigured (invalid client). Contact support.',
      googleSignInMissingClientId: 'Google sign-in is misconfigured (missing client ID). Contact support.',
      googleSignInUnknownReason: 'Google sign-in could not be shown. Try again or use email and password.',
      googleSignInSkippedUserCancel: 'Google sign-in was closed before completing. Try again or use email and password.',
      googleSignInSkippedTapOutside: 'Google sign-in was dismissed. Try again or use email and password.',
      googleSignInSkippedIssuingFailed: 'Google could not issue a sign-in token. Try again or use email and password.',
      googleSignInDismissedCancel: 'Google sign-in was cancelled. Try again or use email and password.',
      googleSignInPromptNoResponse:
        'Google sign-in did not open. A pop-up blocker or browser extension may be blocking it. Allow pop-ups for this site, refresh the page, or sign in with email and password below.',
      googleSignInHintShort:
        'A Google sign-in window may open. Allow pop-ups for this site if nothing appears. You can also sign in with email below.',
      step2Of2: 'Step 2 of 2',
      alreadyHaveAccount: 'Already have an account?',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      agreeTermsIntro: 'I agree to the',
      agreeTermsRequired: 'Please agree to the Terms of Service and Privacy Policy.',
      and: 'and',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      legalContent: 'Terms & Privacy',
      legalContentSubtitle: 'Edit Terms of Service and Privacy Policy. Shown in a modal when users click the links on the register page.',
      plans: 'Plans',
      superadminPlansSubtitle: 'Manage subscription plans and features (Starter, Growth, Pro). Used by the public pricing page.',
      localeEn: 'English',
      localeEs: 'Spanish',
      localeAr: 'Arabic',
      legalHtmlHint: 'HTML supported (e.g. <p>, <strong>, <ul>, <li>, <a>).',
      termsPlaceholder: 'Enter Terms of Service content…',
      privacyPlaceholder: 'Enter Privacy Policy content…',
      errorLoading: 'Failed to load.',
      errorSaving: 'Failed to save.',
      legalContentUpdated: 'Legal content updated.',
      siteSettings: 'Site settings',
      siteSettingsSubtitle: 'Global options for public quick orders (pickup fee percent).',
      pickupAdditionalCostPercent: 'Pickup additional cost (%)',
      pickupAdditionalCostHint: 'Added to the cart subtotal when the customer chooses pickup. Totals are always computed on the server.',
      siteSettingsUpdated: 'Site settings saved.',
      saveSiteSettings: 'Save settings',
      siteSettingsLoadFailed: 'Could not load site settings.',
      siteSettingsSaveFailed: 'Could not save site settings.',
      pickupPercentInvalid: 'Enter a percent between 0 and 100.',
      basicInformation: 'Basic information',
      businessTimezone: 'Business time zone',
      businessTimezoneHint: 'Used for analytics periods and timestamps on quick orders and emails. Defaults to your browser’s zone when you create a business; change it if your venue is elsewhere.',
      businessTimezoneClear: 'Not set (server uses UTC)',
      businessTimezoneUpdated: 'Time zone updated.',
      settingsStoreBasicsSaved: 'Store basics saved.',
      businessTypeLabel: 'Business type',
      businessTypeRestaurants: 'Restaurant',
      businessTypeEcommerce: 'E-commerce',
      businessTypeServices: 'Service',
      businessTypeRequired: 'Please select a business type.',
      selectBusinessTypePlaceholder: 'Select business type',
      settingsPageSubtitle: 'Business type, language, currency, and description by language.',
      settingsGroupBasicsTitle: 'Store basics',
      settingsGroupBasicsHint: 'Business type and time zone used for analytics and order timestamps.',
      settingsGroupPricingTitle: 'Pricing & appearance',
      settingsGroupPricingHint: 'Currency on your public site and optional accent color.',
      settingsGroupContentTitle: 'Languages & descriptions',
      settingsGroupContentHint: 'Languages visitors can choose and the description shown for each.',
      settingsGroupLayoutTitle: 'Public page layout',
      settingsGroupLayoutHint: 'Pick how your public site is structured and styled.',
      settingsGroupVisitorTitle: 'Site & ordering',
      settingsGroupVisitorHint: 'Footer credits and how guests place quick orders.',
      settingsBusinessTypeHint: 'Controls section labels on your public site (e.g. Menu, Products, Services).',
      currencySectionTitle: 'Currency',
      currencySectionHint: 'Currency used for prices on your menu and public site.',
      currencyUpdated: 'Currency updated.',
      primaryColorSectionTitle: 'Primary color',
      primaryColorSectionHint: 'Accent color used on your public restaurant page (buttons, links, highlights). Leave empty to use the default.',
      primaryColorPickAria: 'Pick primary color',
      hexLabel: 'Hex',
      defaultLanguageUpdated: 'Default language updated.',
      setAsDefault: 'Set as default',
      adding: 'Adding…',
      showAllLanguagesTotal: 'Show all languages ({total} total)',
      showLess: 'Show less',
      descriptionByLanguageTitle: 'Description by language',
      descriptionByLanguageHint: 'Optional description shown per language on the public site.',
      editDescriptionForLanguageAria: 'Edit description for language',
      descriptionPlaceholderForLanguage: 'Description ({locale})',
      translatingDescriptionAria: 'Translating description from default language',
      publicPageTemplateTitle: 'Public page template',
      publicPageTemplateHint: 'Choose the layout for your public restaurant page. This affects how your menu and info are displayed to visitors.',
      removeLanguageTitle: 'Remove language?',
      removeLanguageBody: '{locale} will be removed. Description for this language will be deleted.',
      languageAdded: '{locale} added.',
      languageRemoved: 'Language removed.',
      descriptionSaved: 'Description saved.',
      chooseValidTemplate: 'Please choose a valid template.',
      footerCreditsHidden: 'Footer credits hidden.',
      footerCreditsVisible: 'Footer credits visible on your public site.',
      footerCreditsPaidOnly: 'Footer credits can only be hidden on a paid plan.',
      quickOrderEnabledToast: 'Quick order is on.',
      quickOrderDisabledToast: 'Quick order is off.',
      quickOrderViaAppEnabledToast: 'Order via mobile app is on.',
      quickOrderViaAppDisabledToast: 'Order via mobile app is off.',
      defaultDescriptionEmpty: 'Default description is empty. Add a description for the default language first.',
      translationFallbackInfo: 'Translation not available for this language. Original text shown - you can edit it.',
      businessTypeUpdated: 'Business type updated.',
      formSubtitleEdit: 'Update details and media.',
      formSubtitleCreate: 'Fill in the essentials to list your business. You can set opening hours and contacts after creating it.',
      slugHint: 'Your web address will be created automatically from this name.',
      slugLabel: 'Web address',
      slugPlaceholder: 'e.g. my-business',
      slugHintEdit: 'Changing the slug only changes the subdomain URL. QR codes and existing links use the business ID and will keep working. Subdomain: {subdomain}',
      customDomainLabel: 'Custom domain (VIP)',
      customDomainPlaceholder: 'e.g. yourbusiness.com',
      customDomainHint: 'When set, QR scans and your public link use this domain. Your site on this domain can call our API (CORS is allowed). Leave blank to use the subdomain.',
      customDomainVipOnly: 'Available on the VIP plan only. Upgrade to set a custom domain.',
      descriptionHint: 'Shown on your public page. Add more languages in Settings.',
      addressOptional: 'Address (optional)',
      descriptionOptional: 'Description (optional)',
      profileSubtitle: 'Update your account details and password.',
      profileSection: 'Profile',
      authHeroTitle: 'Manage your business.',
      authHeroTitleHighlight: 'Simple and free.',
      authHeroSubtitle: 'Catalogs, QR codes, and opening hours in one place. For owners who care about clarity.',
      authHeroFooter: '© QRestoo. Free for business owners.',
      changePassword: 'Change password',
      socialSignInPasswordInfo:
        'This account uses social sign-in (Google, Facebook, or Instagram). Password change isn’t shown here. To add a password for email login, use Forgot password on the login page.',
      name: 'QRestoo',
      forgotCheckEmail: 'Check your email for a reset link.',
      forgotIntro: 'Enter your email and we\'ll send you a link to reset your password.',
      sendResetLink: 'Send reset link',
      checkInbox: 'Check your inbox',
      sentResetLinkTo: 'We sent a reset link to {email}. The link expires in 1 hour.',
      didntGetEmail: 'Didn\'t get the email?',
      tryAgain: 'Try again',
      dontHaveAccount: 'Don\'t have an account?',
      createOne: 'Create one',
      somethingWentWrong: 'Something went wrong. Please try again.',
      // Aria and layout
      ariaMainNav: 'Main navigation',
      ariaAppLanguage: 'App language',
      ariaOpenMenu: 'Open menu',
      ariaScrollUp: 'Scroll up',
      ariaScrollDown: 'Scroll down',
      // Superadmin owner feedbacks
      ownerFeedbacksSubtitle: 'Feature requests and feedback from restaurant owners. Toggle status when reviewed.',
      superadminInquiriesSubtitle: 'VIP and landing page contact form submissions. Newest first.',
      ownerFeedbacksEmpty: 'No owner feedbacks yet.',
      markPending: 'Mark pending',
      markReviewed: 'Mark reviewed',
      feedbacksUpdateForbidden: "You don't have permission to update feedbacks.",
      feedbackNotFound: 'Feedback not found.',
      feedbackUpdated: 'Feedback updated.',
      feedbackApproved: 'Feedback approved.',
      feedbackRejected: 'Feedback rejected.',
      failedToUpdate: 'Failed to update.',
      feedbackDeleted: 'Feedback deleted.',
      failedToDelete: 'Failed to delete.',
      deleteFeedbackConfirm: 'Delete this feedback? This cannot be undone.',
      // Superadmin users
      superadminUsersSubtitle: 'Manage user accounts: paid status and active/deactivate.',
      paginationPrevious: 'Previous',
      paginationNext: 'Next',
      paginationPageOf: 'Page {current} of {last}',
      paginationPerPage: 'Per page',
      paginationShowing: 'Showing {from}–{to} of {total}',
      noUsersFound: 'No users found.',
      search: 'Search',
      searchUsersPlaceholder: 'Search by name or email…',
      noUsersMatchSearch: 'No users match your search.',
      paid: 'Paid',
      free: 'Free',
      active: 'Active',
      deactivated: 'Deactivated',
      superadmin: 'Superadmin',
      markPaid: 'Mark paid',
      removePaid: 'Remove paid',
      plan: 'Plan',
      planStarter: 'Free',
      planGrowth: 'Growth',
      planPro: 'Pro',
      planVip: 'VIP',
      makeUserFree: 'Make Free',
      makeUserGrowth: 'Make Growth',
      makeUserPro: 'Make Pro',
      userPlanUpdated: 'Plan set to {plan}.',
      userIdentifierMissing: 'User identifier missing. Please refresh the page.',
      activate: 'Activate',
      deactivate: 'Deactivate',
      ariaDeactivateUser: 'Deactivate user',
      ariaActivateUser: 'Activate user',
      vip: 'VIP',
      setVip: 'Set VIP',
      removeVip: 'Remove VIP',
      ariaSetVip: 'Allow mobile app login (Set VIP)',
      ariaRemoveVip: 'Disallow mobile app login (Remove VIP)',
      ariaVipBadge: 'VIP: mobile app login enabled',
      superadminVerifyEmail: 'Verify',
      ariaSuperadminVerifyEmail: 'Mark user email as verified',
      superadminEmailVerifiedToast: 'Email marked as verified.',
      emailUnverified: 'Unverified',
      pendingEmailChange: 'Pending email change',
      you: '(you)',
      usersForbidden: "You don't have permission to view users.",
      // Superadmin restaurants (businesses)
      superadminRestaurantsSubtitle: 'View-only list of all businesses in the system.',
      noRestaurantsFound: 'No businesses found.',
      searchRestaurantsPlaceholder: 'Search by name or slug…',
      noRestaurantsMatchSearch: 'No businesses match your search.',
      restaurantsForbidden: "You don't have permission to view restaurants.",
      // Business manage
      availability: 'Availability',
      availabilitySubtitle: 'When you\'re open. Change times below and tap Save to update.',
      deleteRestaurant: 'Delete business',
      logoAndBanner: 'Logo & banner',
      logoBannerDescription: 'Change or update your logo and banner images.',
      uploadImagesHint: 'Upload new images. JPEG, PNG, GIF, WebP or HEIC (iPhone). Logo max 2MB, banner max 4MB.',
      logo: 'Logo',
      banner: 'Banner',
      takePhoto: 'Take photo',
      chooseFromGallery: 'Choose from gallery',
      changeLogo: 'Change logo',
      changeBanner: 'Change banner',
      removeLogo: 'Remove logo',
      removeBanner: 'Remove banner',
      confirmRemoveLogoTitle: 'Remove logo?',
      confirmRemoveLogoMessage: 'Your public page will show without a logo until you upload a new one.',
      confirmRemoveBannerTitle: 'Remove banner?',
      confirmRemoveBannerMessage: 'Your public page will show without a cover image until you upload a new one.',
      logoRemovedToast: 'Logo removed.',
      bannerRemovedToast: 'Banner removed.',
      ariaRemoveLogoImage: 'Remove logo',
      ariaRemoveBannerImage: 'Remove banner',
      done: 'Done',
      deleteRestaurantConfirm: 'Delete business?',
      cannotBeUndone: 'This cannot be undone.',
      typeToConfirm: 'Type {slug} to confirm',
      ariaUploadingLogo: 'Uploading logo',
      ariaUploadingBanner: 'Uploading banner',
      ariaUploadLogo: 'Upload logo',
      ariaUploadBanner: 'Upload banner',
      ariaUploadLogoFromGallery: 'Upload logo from gallery',
      ariaUploadBannerFromGallery: 'Upload banner from gallery',
      ariaTakePhotoLogo: 'Take photo for logo',
      ariaTakePhotoBanner: 'Take photo for banner',
      settings: 'Settings',
      themeTab: 'Theme',
      themeTabSubtitle: 'Site primary color and public page template.',
      // Quick-add item wizard
      quickAddItem: 'Add item',
      quickAddItemAria: 'Add menu item',
      quickAddStepOf: 'Step {step} of {total}',
      quickAddStep0Title: 'Item <em>type</em>',
      quickAddStep1Title: 'Pick a <em>category</em>',
      quickAddStep2Title: 'Item <em>details</em>',
      quickAddStep3Title: 'Review &amp; <em>save</em>',
      quickAddWhatKind: 'What kind of item?',
      quickAddTypeSimpleSub: 'One price',
      quickAddTypeVariantsSub: 'Sizes, options…',
      quickAddTypeCombSub: 'Bundle',
      quickAddTypeSimpleHint: 'A single product or dish with one fixed price.',
      quickAddTypeVariantsHint: 'One item offered in multiple options — each with its own price (e.g. Small / Large).',
      quickAddTypeComboHint: 'A bundle of items sold together (e.g. Meal Deal).',
      quickAddNoCats: 'No categories yet',
      quickAddNoCatsSub: 'Create your first category below to group your items.',
      quickAddOrCreate: 'or create new',
      quickAddFirstCatName: 'Name your first category',
      quickAddNewCat: 'New category',
      quickAddCatPlaceholder: 'e.g. Mains, Drinks, Services…',
      quickAddCreate: 'Create',
      quickAddCatWillCreate: 'Will create: {name}',
      quickAddItem1: 'item',
      quickAddItemN: 'items',
      quickAddNameLabel: 'Item name',
      quickAddNamePlaceholder: 'e.g. Margherita Pizza',
      quickAddPriceOptional: 'Price (optional)',
      quickAddComboPriceOptional: 'Combo price (optional)',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: 'Description (optional)',
      quickAddDescPlaceholder: 'Shown on item card…',
      quickAddVariantsLabel: 'Variants',
      quickAddVariantName: 'Option name',
      quickAddVariantNamePlaceholder: 'e.g. Large',
      quickAddVariantPrice: 'Price',
      quickAddRemoveVariant: 'Remove variant',
      quickAddAddVariant: 'Add variant',
      quickAddComboItems: 'Items in combo',
      quickAddPickItem: 'Pick an item…',
      quickAddAdd: 'Add',
      quickAddRemoveComboItem: 'Remove item',
      quickAddNoItemsForCombo: 'No items yet — add some simple or variant items first.',
      quickAddReviewItem: 'Item',
      quickAddReviewName: 'Name',
      quickAddReviewType: 'Type',
      quickAddReviewPrice: 'Price',
      quickAddReviewIncludes: 'Includes',
      quickAddReviewCategory: 'Category',
      quickAddReviewCategoryLabel: 'Category',
      quickAddNoPrice: 'No price set',
      quickAddNoCategory: 'None',
      quickAddNew: 'new',
      quickAddPublishNow: 'Publish immediately',
      quickAddPublishNowSub: 'Goes live on your site right away',
      quickAddAddAnother: 'Add another after saving',
      quickAddAddAnotherSub: 'Keep this flow open',
      quickAddSaveItem: 'Save item',
      quickAddReview: 'Review',
      quickAddContinue: 'Continue',
      quickAddBack: 'Back',
      quickAddSaved: 'Item saved.',
      quickAddSavedSub: '{name} added to {category}.',
      quickAddAnotherItem: '+ Add another item',
      quickAddDone: 'Done',
      quickAddBusinessContext: 'Managing your menu — changes apply to your live catalog.',
      quickAddPhotoSection: 'Item photo',
      quickAddPhotoHint: 'Optional. Shown on your public menu.',
      quickAddPhotoHintCrop: 'Optional. Shown on your public menu. Crop the image after you choose it.',
      quickAddPhotoRemove: 'Remove photo',
      quickAddPhotoUpload: 'Upload photo',
      quickAddPhotoReplace: 'Replace photo',
      quickAddPhotoPreviewAlt: 'Item photo preview',
      quickAddModifiersHint: 'Optional add-ons (e.g. extra toppings). Customers can pick these when ordering.',
      quickAddReviewPhoto: 'Photo',
      quickAddReviewPhotoYes: 'Attached',
      quickAddReviewPhotoNone: 'None',
      quickAddReviewModifiers: 'Add-ons',
      quickAddReviewModifiersNone: 'None',
      quickAddPhotoUploadFailed: 'The item was saved, but the photo could not be uploaded. Add it from Menu items in the dashboard.',
      quickAddIntentLabel: 'How do you want to add?',
      quickAddIntentCreate: 'Create new item',
      quickAddIntentCreateSub: 'Add something new to your catalog and this menu.',
      quickAddIntentAssign: 'Assign catalog item',
      quickAddIntentAssignSub: 'Place an item you already created in Menu items into a category.',
      quickAddIntentAssignHint: 'Pick a category next, then choose a catalog item. Nothing new is created in your catalog.',
      quickAddStep0TitleIntent: 'How do you want to <em>add</em>?',
      quickAddStep2TitleAssign: 'Pick a <em>catalog</em> item',
      quickAddPickCatalogItem: 'Catalog item',
      quickAddReviewAssignFromCatalog: 'From catalog',
      quickAddAssignSearchPlaceholder: 'Search by name…',
      quickAddAssignEmpty: 'No catalog items available to add here. Create items under Menu items first, or they may already be in this category.',
      quickAddAssignNoCategories: 'Use the form below to name your first category, tap Create, then continue.',
      quickAddAssignCategoryHint: 'Pick an existing category or create a new one — same as when you add a brand-new item.',
      quickAddNoMenuForCategory: 'This business has no menu yet. Add a menu in the dashboard, then try again.',
      catalogTab: 'Catalog',
      primaryColorLabel: 'Primary color',
      primaryColorHint: 'Accent color used on your public page (buttons, links, highlights). Leave empty to use the default.',
      ariaPickPrimaryColor: 'Pick primary color',
      clear: 'Clear',
      primaryColorUpdated: 'Primary color updated.',
      primaryColorCleared: 'Primary color cleared.',
      primaryColorInvalid: 'Enter a valid hex color (e.g. #e85a2b or #fff).',
      themeTemplateHint: 'Choose the layout for your public page. This affects how your menu and info are displayed to visitors.',
      templateInvalid: 'Please choose a valid template.',
      templateUpdated: 'Template updated.',
      seeWebsite: 'See website',
      goToSite: 'Go to site',
      ariaManageSections: 'Business management sections',
      qrScanHint: 'Scan to visit your page',
      qrSaveButton: 'Save QR code',
      qrSaveTooltip: 'Save or print for your business',
      // Business detail
      contactAndDetails: 'Contact & details',
      address: 'Address',
      email: 'Email',
      social: 'Social',
      // Business content
      addLanguageEllipsis: 'Add language…',
      addLanguageHint: 'Add at least one language above to manage descriptions.',
      editDescriptionFor: 'Edit description for:',
      ariaSelectTemplate: 'Select template',
      templateBestFor: 'Best for: {type}',
      templateBusinessType_restaurants: 'Restaurant',
      templateBusinessType_services: 'Service',
      templateBusinessType_ecommerce: 'E-commerce',
      templateBusinessType_general: 'General',
      footerCreditsSectionTitle: 'Footer credits on public site',
      footerCreditsSectionHint: 'Control whether the "Site created via QRestoo" and DLR credits block is shown at the bottom of your public page.',
      hideFooterCreditsLabel: 'Hide footer credits on my public site',
      showCurrencySectionTitle: 'Show currency on prices',
      showCurrencySectionHint: 'When on, prices on your public site show the currency (e.g. €10.00). When off, only the number is shown (e.g. 10.00).',
      showCurrencyLabel: 'Show currency next to prices',
      showCurrencySavedOn: 'Currency will show next to prices on your public site.',
      showCurrencySavedOff: 'Prices will show numbers only on your public site.',
      orderingModeSectionTitle: 'Ordering Mode',
      orderingModeSectionDescription:
        'Turn on Quick order so guests can add items to a cart on your public site. On Pro and VIP you can also allow checkout through the QRestoo mobile app.',
      orderingModeQuickOrderLabel: 'Quick order',
      orderingModeMobileAppLabel: 'Order via mobile app',
      orderingModeOptionOff: 'Disabled — no cart on your public site',
      orderingModeOptionQuick: 'Quick order — guests use WhatsApp, Viber, or SMS when you have a contact number',
      orderingModeOptionMessaging:
        'Quick order via message — guests use WhatsApp, Viber, or SMS when you have a contact number',
      orderingModeOptionApp: 'Quick order via mobile app — guests check out in the QRestoo app (pickup)',
      ordersReceived: 'Orders received',
      // Catalog & categories
      menusAndCategories: 'Catalog & categories',
      createFirstMenuHint: 'Create your first catalog to add categories.',
      createCategoryFirstHint: 'Create a category first to add items to this catalog.',
      menuItemsSettingsHint: 'Add items to categories from each category\'s page. Item translations use this business\'s Settings.',
      createCategoryAboveHint: 'Create a category above to add items.',
      renameMenu: 'Rename catalog',
      selectMenu: 'Select catalog',
      editCategory: 'Edit category',
      removeCategory: 'Remove category',
      addMenu: 'Add catalog',
      addCategory: 'Add category',
      categoryAvailability: 'Category availability',
      removeCategoryImage: 'Remove category image',
      addMenuOrCategory: 'Add catalog or category',
      editIn: 'Edit in',
      changeImage: 'Change image',
      uploadImage: 'Upload image',
      translating: 'Translating…',
      removing: 'Removing…',
      mainMenuDefault: 'Main catalog',
      category: 'Category',
      categoriesHeading: 'Categories',
      manageItemsInCategory: 'Manage items',
      categoryRowNoItems: 'No items in this category yet.',
      moreCategoryActions: 'More actions',
      // Item catalog list
      menuManagement: 'Catalog management',
      menuManagementSubtitle: 'Catalogs, categories and items. Drag to reorder.',
      menuTabIntro: 'Create categories below, then add items from catalog management.',
      menuTabContextHint: 'In this tab you only create categories and assign items to them. Create, edit, and delete catalog items in Catalog management.',
      noCategoriesYet: 'No categories yet. Add one with the button below or the + button.',
      noMenuItemsYet: 'No items yet. Use the + button below to add one.',
      categoryNameRequired: 'Category name is required.',
      categoryUpdated: 'Category updated.',
      categoryCreated: 'Category created.',
      categoryModalDescEdit: 'Update the category name and description for each language.',
      categoryModalDescAdd: 'Create a new category (e.g. Starters, Mains). Add a name for each language you use.',
      selectLanguageToEditAria: 'Select language to edit',
      localeDefaultSuffix: ' (Default)',
      categoryFieldNameInLanguage: 'Name ({lang})',
      categoryFieldDescriptionInLanguageOptional: 'Description ({lang}) — optional',
      translatingCategoryFromDefaultAria: 'Translating category from default language',
      translateCategoryFromDefaultAria: 'Translate category from default language',
      searchLabel: 'Search',
      categoryItemsPageFallbackTitle: 'Category items',
      categoryItemsSubtitle: 'Add and reorder items in this category. Drag to change order.',
      categoryEmptyTitle: 'Add items to this category',
      categoryEmptyHint: 'Add existing items from your catalog to show them in this category. You can reorder them after adding.',
      addItemToCategoryButton: 'Add item',
      categoryItemCount: '{count} items',
      dragToReorderAria: 'Drag to reorder',
      setAvailabilityForItemAria: 'Set availability times for this item',
      markAvailableOnPublicMenuShort: 'Mark available on public catalog',
      markNotAvailableOnPublicMenuShort: 'Mark not available on public catalog',
      showOnPublicCatalogAria: 'Show on public catalog',
      hideOnPublicCatalogAria: 'Hide from public catalog',
      assignTagsToItemAria: 'Assign tags to this item',
      editCatalogItemAria: 'Edit item',
      removeFromCategoryTitle: 'Remove from category',
      removeFromCategoryListAria: 'Remove this item from this category',
      addItemToCategoryModalTitle: 'Add item to category',
      addItemToCategoryModalDesc: 'Add or remove items in this category. Items come from your catalog. Search to filter.',
      addAllVariantsHint: 'Adds all variants to this category',
      inMenuAsLabel: 'Shown as: {name}',
      removeFromMenuButton: 'Remove from menu',
      addToCategoryAria: 'Add {name} to category',
      removeFromCategoryNamedAria: 'Remove {name} from category',
      loadingCatalogItems: 'Loading items…',
      noItemsMatchSearch: 'No items match your search, or you have no simple, combo, or variant items in your catalog yet.',
      done: 'Done',
      addItemWithVariantsTitle: 'Add item with variants',
      addItemWithVariantsBody: 'This item has multiple variants. Adding it will add all variants to this category.',
      addAllVariantsButton: 'Add all variants',
      assignTagsModalTitlePlain: 'Assign tags',
      assignTagsToItemTitle: 'Assign tags to {name}',
      assignTagsModalDescription: 'Choose tags to show on this item on your public page.',
      noTagsAvailable: 'No tags available.',
      tagClassificationGeneral: 'General',
      tagClassificationRestaurant: 'Restaurant',
      tagClassificationService: 'Service',
      tagClassificationEcommerce: 'E-commerce',
      tagAssignedTooltip: '{name} (assigned)',
      tagFallbackLabel: 'Tag',
      untitledTag: 'Untitled',
      removeCategoryConfirmLong: 'Remove “{name}”? Items in this category will become uncategorized. This cannot be undone. If you are unsure, hide the category on the public menu instead.',
      removingCategory: 'Removing…',
      removeCategoryButton: 'Remove category',
      categoriesTotalCount: '{count} total',
      hideCategoryOnPublicTitle: 'Hide category on public menu',
      showCategoryOnPublicTitle: 'Show category on public menu',
      hideCategoryOnPublicAria: 'Hide category on public menu',
      showCategoryOnPublicAria: 'Show category on public menu',
      menuTabInstructionAria: 'Catalog tab hint',
      menuNameLabel: 'Catalog name',
      menuNamePlaceholderShort: 'e.g. Lunch, Services, Shop',
      menuDescriptionPlaceholderShort: 'Short description for this catalog',
      addMenuModalTitle: 'Add catalog',
      addMenuModalDescription: 'Create another catalog for this business. Add a name and optional description per language.',
      editMenuModalTitle: 'Edit catalog',
      editMenuModalDescription: 'Change the name and description of this catalog for each language.',
      createMenuButton: 'Create catalog',
      creatingMenu: 'Creating…',
      menuCreatedToast: 'Catalog created.',
      menuUpdatedToast: 'Catalog updated.',
      menuHiddenPublicToast: 'Catalog hidden from public site.',
      menuVisiblePublicToast: 'Catalog visible on public site.',
      categoryRemovedToast: 'Category removed.',
      defaultCategoryNameEmptyToast: 'Default category name is empty. Add a name for the default language first.',
      translationFailedShort: 'Translation failed.',
      translationAppliedClickSaveToast: 'Translation applied. Click Save to store.',
      validateAtLeastOneName: 'At least one name is required.',
      validateNameMaxLengthInLocale: 'Name ({lang}) must be at most 255 characters.',
      itemAddedToCategoryToast: 'Item added to category.',
      variantsAddedToCategoryToast: '{count} variants added to category.',
      itemAlreadyInCategoryToast: 'This item is already in this category.',
      itemAlreadyInCategoryAsWholeToast: 'This item is already in this category (added as a whole).',
      allVariantsAlreadyInCategoryToast: 'All variants are already in this category.',
      removedFromCategoryToast: 'Removed from category.',
      itemMarkedAvailableToast: 'Item marked available.',
      itemMarkedNotAvailableToast: 'Item marked not available.',
      itemVisibleOnPublicToast: 'Item visible on public menu.',
      itemHiddenFromPublicToast: 'Item hidden from public menu.',
      categoryHiddenPublicToast: 'Category hidden from public menu.',
      categoryVisiblePublicToast: 'Category visible on public menu.',
      orderUpdatedToast: 'Order updated.',
      availabilityUpdatedToast: 'Availability updated.',
      tagsUpdatedToast: 'Tags updated.',
      fabAddCategoryAria: 'Add category',
      fabCloseAria: 'Close',
      unnamedMenu: 'Unnamed catalog',
      menuFallbackName: 'Catalog',
      // Category items
      addMenuItemsToCategory: 'Add items to this category',
      noMenuItemsOrMatch: 'No items, or none match the search.',
      assignTags: 'Assign tags',
      itemAvailability: 'Item availability',
      dragToReorder: 'Drag to reorder',
      setAvailabilityTimes: 'Set availability times',
      available: 'Available',
      notAvailable: 'Not available',
      markNotAvailable: 'Mark not available',
      markAvailableOnMenu: 'Mark available on public catalog',
      visibleOnMenu: 'Visible on catalog',
      hiddenFromMenu: 'Hidden from catalog',
      addMenuItemToCategory: 'Add item to category',
      menuItemTags: 'Item tags',
      createRestaurantFirstForMenus: 'Create a business first to manage catalog and items.',
      // Feedbacks list (buttons/aria)
      ratingOutOf5: 'Rating: {n} out of 5',
      anonymous: 'Anonymous',
      approve: 'Approve',
      reject: 'Reject',
      ariaApproveFeedback: 'Approve feedback',
      ariaRejectFeedback: 'Reject feedback',
      ariaDeleteFeedback: 'Delete feedback',
      // Reset password
      resetPasswordSuccess: 'Your password has been reset. You can sign in.',
      resetPasswordEnterNew: 'Enter your new password below.',
      // Contact editors
      type: 'Type',
      labelOptional: 'Label (optional)',
      placeholderUrl: 'https://...',
      placeholderPhone: 'e.g. +1 234 567 8900',
      placeholderContactLabel: 'e.g. Reservations, Kitchen',
      showOnPublicPage: 'Show on public page',
      hideOnPublicPage: 'Hide on public page',
      ariaEditContact: 'Edit contact',
      ariaDeleteContact: 'Delete contact',
      noContactsYet: 'No contacts or links yet. Add one so customers can reach you.',
      deleteContactConfirm: 'Delete this contact?',
      addContact: 'Add contact',
      ariaPagination: 'Pagination',
      // Availability
      weeklyScheduleTitle: 'Weekly schedule',
      itemAvailabilityTitle: 'Item availability',
      itemAvailabilityInfo: 'Set when this item is available. Add multiple slots for split availability (e.g. lunch and dinner).',
      restaurantAvailabilityInfo: "Operating hours affect your online ordering availability and how customers see when you're open.",
      itemAvailabilityHint: 'Set open and close times for each day. Add multiple slots for split hours.',
      restaurantAvailabilityHint: "Set your restaurant's open and close times for each day. Add multiple slots for split hours (e.g. lunch and dinner).",
      openForBusinessAria: '{day} open for business',
      openingHourAria: 'Opening hour',
      openingMinuteAria: 'Opening minute',
      closingHourAria: 'Closing hour',
      closingMinuteAria: 'Closing minute',
      addAnotherTimeSlot: 'Add another time slot',
      closed: 'Closed',
      to: 'to',
      ariaOpeningTime: 'Opening time',
      ariaClosingTime: 'Closing time',
      removeTimeSlot: 'Remove time slot',
      open24_7: 'Open 24/7',
      open24_7ConfirmTitle: 'Open 24/7',
      open24_7ConfirmMessage: 'Enabling Open 24/7 will clear your current schedule. Your business will show as always open. Continue?',
      confirm: 'Confirm',
      // Item form
      menuItemLabel: 'Item',
      addMenuItem: 'Add item',
      ariaRemoveImage: 'Remove image',
      ariaRemoveVariantImage: 'Remove variant image',
      ariaRemoveEntry: 'Remove entry',
      ariaRemoveGroup: 'Remove group',
      // Help legend FAB
      ariaShowHelp: 'Show help: what do the buttons mean?',
      help: 'Help',
      ariaButtonLegend: 'Button legend',
      helpLegendDescription:
        'Meaning of the icons you see on menu and category rows in your catalog editor.',
      legendDragHandle: 'Drag handle',
      legendDragDesc: 'Drag to reorder',
      legendSchedule: 'Schedule',
      legendScheduleDesc: 'Set opening hours',
      legendAvailability: 'Availability',
      legendAvailabilityDesc: 'When this item is available',
      legendVisibility: 'Visibility',
      legendVisibilityDesc: 'Show or hide on public menu',
      legendTags: 'Tags',
      legendTagsDesc: 'Assign tags to items',
      // Paid feature modal
      paidFeature: 'Paid feature',
      paidFeatureHeadline: 'Unlock Pro',
      paidFeatureSubhead: 'This and more are included in Pro.',
      paidFeaturePricingHint: 'Cancel anytime.',
      paidFeaturePricingLine: 'See Billing for current pricing. Cancel anytime.',
      paidFeatureWhatIncluded: "What's included in Pro:",
      upgradeToUse: 'Upgrade to use this feature.',
      paidFeatureAiDescription: 'Beautify your catalog images with AI is available on a paid plan. Upgrade to unlock AI-powered image enhancement.',
      paidFeatureAnnouncementDescription: 'Show announcements on your public site for up to 4 days. Upgrade to unlock this and other Pro features.',
      paidFeatureBenefitsIntro: 'Benefits of upgrading:',
      paidFeatureBenefitAnnouncements: 'Announcements on your public site',
      paidFeatureBenefitTableReservation: 'Booking & reservations (with SMS)',
      paidFeatureBenefitMailingList: 'Mailing list',
      paidFeatureBenefitNoAds: 'Can remove QRestoo branding',
      paidFeatureBenefitMultipleRestaurants: 'Multiple businesses',
      // Announcement (admin)
      analytics: 'Analytics',
      analyticsSubtitle: 'Track QR scans, menu views, popular items, and more.',
      analyticsCreateFirst: 'Create a business first to view analytics.',
      analyticsQrScans: 'QR scans',
      analyticsMenuViews: 'Menu views',
      analyticsItemClicks: 'Item clicks',
      analyticsViews: 'Views',
      analyticsReservations: 'Reservations',
      analyticsReviews: 'Reviews',
      analyticsRating: 'Rating',
      analyticsPopularItems: 'Popular items',
      analyticsPeakHours: 'Peak hours',
      analyticsReservationTrends: 'Reservation trends',
      analyticsMonthlyOverview: 'Monthly overview',
      analyticsRevenue: 'Revenue',
      analyticsMultiLocation: 'Multiple businesses',
      analyticsCustomerInsights: 'Customer insights',
      analyticsExportCsv: 'Export CSV',
      analyticsExportPdf: 'Export PDF',
      analyticsPeriodWeek: 'Week',
      analyticsPeriodMonth: 'Month',
      analyticsPeriodAll: 'All time',
      analyticsNoData: 'No data yet',
      analyticsSelectRestaurant: 'Select business',
      analyticsSubtotal: 'Subtotal',
      announcement: 'Announcement',
      announcementSubtitle: 'Show a message on your public site for up to 4 days. Paid plans only.',
      announcementSelectRestaurant: 'Select a business',
      announcementLanguage: 'Language',
      announcementMessage: 'Message',
      announcementMessagePlaceholder: 'e.g. Closed for a private event this Saturday.',
      announcementImage: 'Image (optional)',
      announcementUploadImage: 'Upload image',
      announcementRemoveImage: 'Remove image',
      announcementImageHelp: 'Upload an image to show with your announcement on the public site. JPEG, PNG, GIF, WebP or HEIC (iPhone), max 2MB.',
      announcementImageTypeInvalid: 'Please choose a JPEG, PNG, GIF, WebP or HEIC (iPhone) image.',
      announcementImageSizeInvalid: 'Image must be under {max}MB.',
      announcementImageUpdated: 'Image updated.',
      announcementImageRemoved: 'Image removed.',
      uploading: 'Uploading…',
      announcementActive: 'Show on public site',
      announcementActiveHelp: 'When enabled, the message appears on your public site until it is disabled.',
      announcementSave: 'Save',
      announcementSaved: 'Announcement saved.',
      vipSiteFieldsNav: 'Site fields',
      vipSiteFieldsLabel: 'VIP',
      vipSiteFieldsTitle: 'Custom site fields',
      vipSiteFieldsSubtitle: 'Add keys your public site or integrations can read (text, HTML, or image per field). Text and HTML use each language installed on the selected business.',
      vipSiteFieldsValueForLanguage: 'Value ({label})',
      vipSiteFieldsNoLanguages: 'Add at least one language to this business in restaurant settings before using text or HTML fields.',
      vipSiteFieldsHtmlPlaceholder: 'Write formatted content for this language…',
      vipSiteFieldsSelectBusiness: 'Select a business',
      vipSiteFieldsExisting: 'Your fields',
      vipSiteFieldsAddField: 'Add field',
      vipSiteFieldsEditField: 'Edit field',
      vipSiteFieldsKey: 'Field key',
      vipSiteFieldsKeyPlaceholder: 'e.g. promo_banner',
      vipSiteFieldsKeyHelp: 'Lowercase letters, numbers, underscore. Must start with a letter.',
      vipSiteFieldsKeyRequired: 'Enter a field key.',
      vipSiteFieldsKeyInvalid: 'Use lowercase start letter, then letters, numbers, or underscores (max 64).',
      vipSiteFieldsType: 'Type',
      vipSiteFieldsTypeRequired: 'Choose a type.',
      vipSiteFieldsTypeText: 'Plain text',
      vipSiteFieldsTypeHtml: 'HTML',
      vipSiteFieldsTypeImage: 'Image',
      vipSiteFieldsImageUrl: 'Image URL (optional)',
      vipSiteFieldsImageUrlPlaceholder: 'https://…',
      vipSiteFieldsImageUrlInvalid: 'Enter a valid URL.',
      vipSiteFieldsUploadImage: 'Upload image',
      vipSiteFieldsUploadImageHelp: 'Save the field first, then edit it to upload an image file (replaces URL). JPEG, PNG, GIF, or WebP.',
      vipSiteFieldsNotVip: 'This feature is available on VIP only.',
      vipSiteFieldsLoadFailed: 'Could not load custom fields.',
      vipSiteFieldsSaved: 'Field updated.',
      vipSiteFieldsCreated: 'Field created.',
      vipSiteFieldsDeleted: 'Field removed.',
      vipSiteFieldsSaveFailed: 'Could not save field.',
      vipSiteFieldsDeleteFailed: 'Could not delete field.',
      vipSiteFieldsImageUpdated: 'Image updated.',
      vipSiteFieldsImageUploadFailed: 'Image upload failed.',
      siteContentNav: 'VIP Page',
      vipOrdersNav: 'Orders',
      vipOrdersEyebrow: 'Orders',
      vipOrdersTitle: 'Orders',
      vipOrdersSubtitle: 'Quick orders placed on your public site or via the app (latest 50 per business). Open a row or tap Details for the full breakdown.',
      vipOrdersSelectBusiness: 'Select a business',
      vipOrdersCreateFirst: 'Create a business first to view orders.',
      vipOrdersEmpty: 'No orders yet for this business.',
      vipOrdersLoadFailed: 'Could not load orders.',
      vipOrdersColWhen: 'When',
      vipOrdersColCustomer: 'Customer',
      vipOrdersColStatus: 'Status',
      vipOrdersColTotal: 'Total',
      vipOrdersColActions: 'Actions',
      vipOrdersViewDetails: 'Details',
      vipOrdersDetailTitle: 'Order details',
      vipOrdersFulfillment: 'Fulfillment',
      vipOrdersFulfillmentPickup: 'Pickup',
      vipOrdersFulfillmentDineIn: 'Dine-in',
      vipOrdersEmail: 'Email',
      vipOrdersPhone: 'Phone',
      vipOrdersPhoneVerified: 'Phone verified',
      vipOrdersLineItems: 'Items',
      vipOrdersSubtotal: 'Subtotal',
      vipOrdersPickupFee: 'Pickup fee ({percent}%)',
      vipOrdersTotal: 'Total',
      vipOrdersNotes: 'Notes',
      vipOrdersActionAccept: 'Accept order',
      vipOrdersActionReject: 'Decline',
      vipOrdersActionMarkReady: 'Mark as ready',
      vipOrdersActionPickedUp: 'Mark as picked up',
      vipOrdersActionServed: 'Mark as served',
      vipOrdersStatusUpdateFailed: 'Could not update order status.',
      vipOrdersRejectModalTitle: 'Decline order',
      vipOrdersRejectHint:
        'For pickup orders with a customer phone number, they may receive this reason by SMS when messaging is configured on the server.',
      vipOrdersRejectReasonLabel: 'Reason for declining',
      vipOrdersRejectCancel: 'Cancel',
      vipOrdersRejectConfirm: 'Decline order',
      vipOrdersRejectReasonRequired: 'Please enter a reason for declining.',
      vipOrdersRejectReasonMinLength: 'Enter at least 3 characters.',
      vipOrdersRejectionReason: 'Decline reason',
      vipOrdersStatus_pending: 'Pending',
      vipOrdersStatus_received: 'Received',
      vipOrdersStatus_ready: 'Ready',
      vipOrdersStatus_picked_up: 'Picked up',
      vipOrdersStatus_rejected: 'Rejected',
      vipOrdersStatus_cancelled: 'Cancelled',
      paidFeatureOwnerOrdersDescription:
        'Pro and VIP can review quick orders in the dashboard: amounts, items, customer contact, and notes.',
      paidFeatureBenefitOwnerOrders: 'Orders inbox (Pro / VIP)',
      siteContentLabel: 'VIP',
      siteContentTitle: 'VIP Page',
      siteContentSubtitle:
        'Custom fields (text, HTML, or image), a photo gallery, and unique QR codes for your public site. Pick a business, then use the tabs. Text and gallery copy use each language installed on that business.',
      siteContentUniqueQrHint:
        'Each code opens your live public address (custom domain or subdomain) with ?unique_id=… so you can label tables, campaigns, or staff.',
      siteContentUniqueQrLabel: 'Unique ID',
      siteContentUniqueQrPlaceholder: 'e.g. 1',
      siteContentUniqueQrNoPublicUrl:
        'Set this business’s public address first (subdomain/slug or VIP custom domain in business settings).',
      siteContentUniqueQrEmptyHint: 'Type an ID above to preview the QR code and caption.',
      siteContentUniqueQrHideAppUrl: 'Hide QRestoo branding',
      siteContentUniqueQrDownload: 'Download QR',
      siteContentSelectBusiness: 'Select a business',
      siteContentPickBusinessHint: 'Select a business above.',
      siteContentTabCustomFields: 'Custom fields',
      siteContentTabGallery: 'Gallery',
      siteContentTabUniqueQr: 'Unique QR',
      siteContentGalleryAdd: 'Add gallery image',
      siteContentGalleryNoLanguages:
        'Add at least one language to this business in restaurant settings before editing gallery text.',
      siteContentGalleryUntitled: 'Untitled',
      siteContentGalleryVisible: 'Shown on public site',
      siteContentGalleryHidden: 'Hidden on public site',
      siteContentGalleryShowOnSite: 'Show on site',
      siteContentGalleryTitleForLang: 'Title ({label})',
      siteContentGalleryDescriptionForLang: 'Description ({label})',
      siteContentGalleryTitlePlaceholder: 'Short title',
      siteContentGalleryDescriptionPlaceholder: 'Optional description',
      siteContentGalleryImageUrl: 'Image URL (optional)',
      siteContentGalleryExternalImageUrl: 'External image link',
      siteContentGalleryExternalImageUrlHelp:
        'Optional link to an image on another site. If you upload a file below, that file is shown instead — you do not need to paste the upload URL here.',
      siteContentGalleryImagePreview: 'Preview (as on your site)',
      siteContentGalleryUploadImage: 'Upload image',
      siteContentGalleryMoveUp: 'Move up',
      siteContentGalleryMoveDown: 'Move down',
      siteContentGalleryDragOrderHint:
        'Drag the handle on the left to change the order shown on your public site. You can also use Move up / Move down.',
      siteContentGalleryDragHandleAria: 'Drag to reorder gallery images on your site',
      siteContentGalleryLoadFailed: 'Could not load gallery.',
      siteContentGallerySaveFailed: 'Could not save gallery item.',
      siteContentGalleryReorderFailed: 'Could not reorder.',
      siteContentGalleryDeleteFailed: 'Could not delete item.',
      siteContentGallerySaved: 'Gallery item updated.',
      siteContentGalleryItemCreated: 'Gallery item added.',
      siteContentGalleryItemDeleted: 'Gallery item removed.',
      siteContentGalleryOrderSaved: 'Order saved.',
      siteContentGalleryImageUpdated: 'Image updated.',
      siteContentGalleryImageFailed: 'Image upload failed.',
      siteContentGalleryOptional: ' (optional)',
      siteContentGalleryBatchDropTitle: 'Drop images here or click to browse',
      siteContentGalleryBatchDropHint:
        'Up to 30 images (JPEG, PNG, GIF, WebP, HEIC). Add titles and descriptions later in each row.',
      siteContentGalleryBatchNoImages: 'No supported images in your selection.',
      siteContentGalleryBatchTooMany: 'Only the first {n} images will be uploaded.',
      siteContentGalleryBatchSuccess: '{n} image(s) added to the gallery.',
      siteContentGalleryBatchPartial: '{added} added; {failed} could not be uploaded.',
      siteContentGalleryBatchAllFailed: 'No images could be added.',
      paidFeatureBenefitSiteContent: 'VIP Page: custom fields, gallery & unique QR (VIP)',
      paidFeatureSiteContentDescription:
        'VIP includes custom site fields for integrations, a multi-image gallery with titles, descriptions, and show/hide per image, and unique QR codes for your public URL with ?unique_id=…',
      announcementTranslateNoDefault: 'Add a message in the default language first, then use Translate from default.',
      announcementTranslated: 'Message translated from default language.',
      reservations: 'Reservations',
      reservationsNav: 'Table reservations',
      reservationsSubtitle: 'Manage table reservations and set the phone number to receive new reservation SMS.',
      reservationsCreateFirst: 'Create a business first to use table reservations.',
      reservationsSelectRestaurant: 'Select business',
      reservationsPendingCount: '{n} pending',
      reservationsRestaurantFilter: 'Business',
      reservationsDateFrom: 'From date',
      reservationsDateTo: 'To date',
      reservationsApplyFilter: 'Apply',
      reservationsTableDate: 'Date',
      reservationsTableTime: 'Time',
      reservationsTablePax: 'Guests',
      reservationsTableCustomer: 'Customer',
      reservationsTableMobile: 'Phone',
      reservationsTableStatus: 'Status',
      reservationsApprove: 'Approve',
      reservationsReject: 'Reject',
      reservationsRemove: 'Remove',
      reservationsCancel: 'Cancel reservation',
      reservationApproveConfirmTitle: 'Approve reservation?',
      reservationApproveConfirmMessage: 'The guest will be notified that their reservation is confirmed.',
      reservationRejectConfirmTitle: 'Reject reservation?',
      reservationRejectConfirmMessage: 'The guest will be notified that their reservation was not accepted.',
      reservationsEdit: 'Edit date & time',
      reservationsEditDateTime: 'Change booking date and time',
      reservationsApproved: 'Reservation approved.',
      reservationsRejected: 'Reservation rejected.',
      reservationsCancelled: 'Reservation cancelled.',
      reservationsRemoved: 'Reservation removed.',
      reservationUpdated: 'Reservation updated.',
      reservationsPendingBadge: '{n} pending reservations',
      reservationSmsToPlaceholder: 'e.g. +15551234567',
      reservationSmsToInvalid: 'Please enter a valid phone number with country code (e.g. +1 555 123 4567).',
      phoneMustStartWithPlus: 'Phone number must start with + (e.g. +1234567890).',
      actions: 'Actions',
      approve: 'Approve',
      reject: 'Reject',
      edit: 'Edit',
      reservationsPax: '{count} guests',
      addReservation: 'Add reservation',
      addReservationForDate: 'Add reservation for {date}',
      addReservationPaxInvalid: 'Please enter a number between 1 and 99.',
      reservationCreated: 'Reservation created.',
      reservationsOnDay: 'Reservations on {date}',
      reservationsNoReservationsMonth: 'No reservations this month.',
      reservationsListEmpty: 'No reservations yet.',
      reservationsLoadError: 'Failed to load reservations.',
      reservationsForbidden: 'Table reservations require a paid plan.',
      reservationSmsToLabel: 'SMS notification number',
      reservationSmsToHint: 'Include country code (e.g. +1 555 123 4567). You will receive new reservation and reminder SMS at this number.',
      reservationSmsToSaved: 'SMS number saved.',
      reservationsHowItWorksTitle: 'How table reservations work',
      reservationsHowItWorksIntro: 'Guests can request a table from your restaurant’s public page. You approve, reject, or edit bookings from here.',
      reservationsHowItWorksPhoneRequired: 'A valid phone number is required for each reservation so you can send confirmations and reminders by SMS.',
      reservationsHowItWorksSteps: 'Set the SMS notification number for each business to receive new reservation alerts. Approve or reject requests; guests receive an SMS. You can also edit the date, time, or number of guests, and cancel—guests are notified by SMS.',
      reservationsHowItWorksTooltip: 'How reservations work',
      reservationStatus_pending: 'Pending',
      reservationStatus_approved: 'Approved',
      reservationStatus_rejected: 'Rejected',
      reservationStatus_cancelled: 'Cancelled',
      reservationDetailTitle: 'Reservation details',
      reservationDetailCreatedAt: 'Requested at',
      reservationDetailReminderSent: 'Reminder sent at',
      reminderSent: 'Reminder sent',
      guest: 'guest',
      guests: 'guests',
      reservationsCalendarPrev: 'Previous month',
      reservationsCalendarNext: 'Next month',
      reservationsCount: 'reservations',
      reservationOne: 'reservation',
      weekdaySun: 'Sun',
      weekdayMon: 'Mon',
      weekdayTue: 'Tue',
      weekdayWed: 'Wed',
      weekdayThu: 'Thu',
      weekdayFri: 'Fri',
      weekdaySat: 'Sat',
      daySunday: 'Sunday',
      dayMonday: 'Monday',
      dayTuesday: 'Tuesday',
      dayWednesday: 'Wednesday',
      dayThursday: 'Thursday',
      dayFriday: 'Friday',
      daySaturday: 'Saturday',
      ariaApproveReservation: 'Approve reservation',
      ariaRejectReservation: 'Reject reservation',
      reservationApproved: 'Reservation approved.',
      reservationRejected: 'Reservation rejected.',
      smsTemplates: 'SMS templates',
      smsTemplatesSubtitle: 'Manage SMS templates for reservation and other notifications. Placeholders: {customer_name}, {date}, {time}, {restaurant_name}, {pax}.',
      smsTemplatesAdd: 'Add template',
      smsTemplatesEdit: 'Edit template',
      smsTemplatesEmpty: 'No SMS templates yet.',
      smsTemplatesDeleteConfirm: 'Delete template "{key}"? This cannot be undone.',
      smsTemplatesFormSubtitle: 'Key is unique and used by the system. Body can include placeholders.',
      smsTemplatesKey: 'Key',
      smsTemplatesKeyReadonly: 'Key cannot be changed when editing.',
      smsTemplatesName: 'Name',
      smsTemplatesBody: 'Body',
      smsTemplatesBodyPlaceholders: 'Placeholders: {customer_name}, {date}, {time}, {restaurant_name}, {pax}',
      smsTemplatesBodyPlaceholder: 'e.g. Hi {customer_name}, your table for {pax} on {date} at {time} is confirmed. — {restaurant_name}',
      smsTemplatesKeyRequired: 'Key is required.',
      smsTemplatesNameRequired: 'Name is required.',
      smsTemplatesBodyRequired: 'Body is required.',
      // Superadmin: menu item tags CRUD
      superadminMenuItemTagsSubtitle: 'Manage default menu item tags (e.g. Spicy, Vegan) shown to all owners.',
      superadminItemTagsSearchPlaceholder: 'Search by tag text…',
      superadminItemTagsAdd: 'Add tag',
      superadminItemTagsEdit: 'Edit tag',
      superadminItemTagsEmpty: 'No item tags yet.',
      superadminItemTagsNoMatch: 'No tags match your search.',
      superadminItemTagsForbidden: "You don't have permission to manage item tags.",
      superadminItemTagText: 'Label',
      superadminItemTagTextPlaceholder: 'e.g. Spicy, Vegan',
      superadminItemTagTextRequired: 'Label is required.',
      superadminItemTagTextMax: 'Label must be 100 characters or less.',
      superadminItemTagColor: 'Color (hex)',
      superadminItemTagColorMax: 'Color must be 50 characters or less.',
      superadminItemTagIcon: 'Icon (Material icon name)',
      superadminItemTagIconPlaceholder: 'e.g. local_fire_department, star',
      superadminItemTagIconMax: 'Icon must be 100 characters or less.',
      superadminItemTagClassification: 'Classification',
      superadminItemTagCreated: 'Tag created.',
      superadminItemTagUpdated: 'Tag updated.',
      superadminItemTagDeleted: 'Tag deleted.',
      superadminItemTagDeleteConfirm: 'Delete tag "{text}"? Menu items using this tag will have it removed.',
      mailingList: 'Mailing list',
      mailingListSubtitle: 'View subscribers and send emails to your mailing list. Paid plans only.',
      mailingListCreateFirst: 'Create a business first to use the mailing list.',
      mailingListSelectRestaurant: 'Select a business',
      mailingListSubscribers: 'Subscribers',
      mailingListNoSubscribers: 'No subscribers yet.',
      mailingListSubscribedAt: 'Subscribed',
      mailingListSelectAll: 'Select all on this page',
      mailingListSelectSubscriber: 'Select subscriber',
      mailingListSelectOneSubscriber: 'Select at least one subscriber to send to.',
      mailingListRemoveSelected: 'Remove selected',
      mailingListRemoveConfirmTitle: 'Remove subscribers?',
      mailingListRemoveConfirmMessage: 'Remove {count} subscriber(s) from the list? They will no longer receive emails. This cannot be undone.',
      mailingListRemoveConfirmButton: 'Remove',
      mailingListRemovedSuccess: 'Subscriber(s) removed.',
      mailingListPageInfo: 'Showing {from}–{to} of {total}',
      language: 'Language',
      mailingListSendEmail: 'Send email',
      mailingListSubject: 'Subject',
      mailingListSubjectPlaceholder: 'e.g. News and special offers',
      mailingListBody: 'Body (HTML)',
      mailingListBodyPlaceholder: 'Write your message…',
      mailingListSendButton: 'Send email',
      mailingListSubjectRequired: 'Subject is required.',
      mailingListBodyRequired: 'Body is required.',
      mailingListBodyMaxCharacters: 'Body must be at most {max} characters.',
      mailingListSentSuccess: 'Email sent to {count} subscribers.',
      mailingListPaidOnly: 'This feature requires a paid plan.',
      paidFeatureMailingListDescription: 'Collect subscriber emails and send newsletters from your restaurant\'s public page. Upgrade to unlock the mailing list.',
      paidFeatureVipSiteFieldsDescription: 'Define custom text, HTML, or image fields for your public site (VIP). Contact us to upgrade to VIP.',
      paidFeatureBenefitVipSiteFields: 'Custom site fields (VIP integrations)',
      maybeLater: 'Maybe later',
      upgrade: 'Upgrade',
      // Billing / subscription
      billingTitle: 'Billing & upgrade',
      billingSubtitle: 'Subscribe to Pro to unlock more features.',
      billingCurrentPlan: 'Current plan',
      growthPlan: 'Growth plan',
      growthPlanBenefits: 'Up to 3 businesses, feedback, announcements. Cancel anytime.',
      proPlan: 'Pro plan',
      proPlanBenefits: 'Unlimited businesses, mailing list, and all Growth features. Cancel anytime.',
      proPlanPrice: '—',
      subscribeToPlan: 'Subscribe',
      subscriptionSection: 'Subscription',
      upgradeToPro: 'Upgrade to Pro',
      upgradeToGrowth: 'Upgrade to Growth',
      upgradeToVip: 'Upgrade to VIP',
      manageSubscription: 'Manage subscription',
      cancelSubscription: 'Cancel subscription',
      planUntilDate: '{plan} until {date}',
      billingNotAvailable: 'Billing is not available right now. Please try again later.',
      noBillingAccount: 'No billing account found. Subscribe first.',
      freePlanLabel: "You're on the Free plan.",
      subscriptionSuccessToast: 'Subscription active. Welcome to {plan}!',
      subscriptionPending: 'Your subscription is being processed. Please refresh in a moment.',
      subscriptionRefreshHint: 'If your plan doesn\'t update shortly, try refreshing the page.',
      // Toasts
      ariaNotifications: 'Notifications',
      dismiss: 'Dismiss',
    },
    verify: {
      checkYourEmail: 'Check your email',
      sentToEmail: 'We sent a verification link to {email}. Click it to continue.',
      oneStepAway: 'You\'re one step away from managing your menu.',
      checkSpam: "Check your spam folder. Didn't get it?",
      backToSignIn: 'Back to sign in',
      goToSignIn: 'Go to sign in',
      signOut: 'Sign out',
      resend: 'Resend',
      sending: 'Sending…',
      resendSuccessDefault: 'Verification email sent. Please check your inbox.',
      resendSuccessGuestDefault: 'If that email is registered and unverified, we sent a new link.',
      emailRequired: 'Please enter your email address.',
      emailInvalid: 'Please enter a valid email address.',
      verifying: 'Verifying your email…',
      verifyingNew: 'Verifying your new email…',
      emailVerified: 'Email verified',
      emailUpdated: 'Email updated',
      verificationFailed: 'Verification failed',
      signIn: 'Sign in',
      goToDashboard: 'Go to dashboard',
      verifySuccessDefault: 'You can sign in.',
      verifyNewSuccessDefault: 'Your email has been updated and verified.',
      invalidLinkDefault: 'Invalid or missing verification link. Please use the link from your email.',
      invalidExpiredDefault: 'Invalid or expired link. Please request a new verification email.',
      invalidExpiredNewDefault: 'Invalid or expired link. Please try changing your email again from Profile & Settings.',
    },
    errors: {
      forbidden: "You don't have permission to do this.",
      validation: 'Please check your input.',
      pageNotFound: 'Page not found',
      pageNotFoundDescription: 'The page you’re looking for doesn’t exist or has been moved.',
    },
    landing: {
      nav: {
        features: 'Features',
        solutions: 'How It Works',
        pricing: 'Pricing',
        resources: 'Resources',
        getStarted: 'Create Free Account',
        goToApp: 'Go to App',
      },
      hero: {
        badge: 'Business tech, made simple',
        titleBefore: 'Your business, ',
        titleHighlight: 'online in 10 minutes.',
        subtitle: 'Get your business online today — Item Catalog, Hours, Booking and more. Free to start. No technical skills needed.',
        trustedBy: 'Free forever. No credit card required.',
      },
      stats: {
        activeRestaurants: 'Active Restaurants',
        ordersProcessed: 'Orders Processed',
        globalReach: 'Global Reach',
        countries: 'Countries',
      },
      features: {
        eyebrow: 'WHAT YOU GET',
        title: 'Everything your business needs online',
        subtitle: 'From your catalog to booking and more — all in one place, set up in minutes.',
        menu: { title: 'Catalog', desc: 'Organize products, services, or menu items in categories, set prices and descriptions, and reorder with drag-and-drop.' },
        qr: { title: 'QR Code', desc: 'Your business gets a stable link and QR code that redirects customers to your public page.' },
        website: { title: 'Business Website', desc: 'A dedicated subdomain and public page for your business with catalog, hours, contact info, and booking.' },
        multilingual: { title: 'Multilingual Support', desc: 'Localize currency and language so your catalog works for every customer.' },
        feedback: { title: 'Feedback Management', desc: 'Customers leave ratings and comments; you approve and display them on your public site.' },
        booking: { title: 'Booking', desc: 'Let customers book appointments, tables, or services directly from your public page.' },
      },
      pricing: {
        eyebrow: 'PRICING',
        title: 'Start free. Grow when you\'re ready.',
        subtitle: 'No contracts. No hidden fees. Cancel anytime.',
        startFree: 'Get Started Free',
        getGrowth: 'Start Growing',
        getPro: 'Go Pro',
        contactUs: 'Let\'s Talk',
        vipValueLine: 'Tailored for your business. Nothing held back.',
        vipMysteryDesc: 'For businesses that want the full experience. Ordering, white label, dedicated support and more. Let\'s talk about what you need.',
        vipFeatureExtra: '+ More exclusive features revealed on request ✨',
        vipSmallText: 'We\'ll get back to you within 24 hours.',
        contactSales: 'Contact Sales',
        perMonth: '/month',
        starterTagline: 'Your business, online for free',
        starterTrustLine: 'No credit card required. Ever.',
        growthTagline: 'Everything you need to engage your customers',
        growthTrustLine: 'More visibility. More customers',
        proTagline: 'Run your business operations online',
        proTrustLine: 'Cancel anytime. No questions asked.',
        vipTagline: 'Tailored for your business. Nothing held back.',
        vipModal: {
          title: 'Contact us about VIP',
          name: 'Name',
          email: 'Email',
          message: 'Message',
          submit: 'Send',
          success: 'Thank you for your interest. We will get back to you soon.',
          close: 'Close',
          nameRequired: 'Name is required.',
          emailRequired: 'Email is required.',
          emailInvalid: 'Please enter a valid email address.',
          messageRequired: 'Message is required.',
          messageMaxLength: 'Message must be at most {max} characters.',
        },
        mostPopular: 'Most Popular',
        pricesInYourCurrency: 'Prices in your local currency.',
        retry: 'Try again',
        currencyLabel: 'Currency',
        errorLoading: 'We couldn\'t load your local prices.',
        usingUsdFallback: 'Showing prices in USD. You can try again below.',
        freePrice: '$0',
        free: { name: 'Free', tagline: 'For getting started', price: '$0' },
        growth: { name: 'Growth', tagline: 'For growing brands', price: '—' },
        pro: { name: 'Pro', tagline: 'For growing brands', price: '—' },
        enterprise: { name: 'Enterprise', tagline: 'Bespoke solutions', price: 'Custom' },
        features: {
          publicMenu: 'Public catalog & subdomain',
          public_menu: 'Public catalog & subdomain',
          subdomain: 'Public catalog & subdomain',
          qrCode: 'QR code & redirect',
          qr_codes: 'QR code & redirect',
          feedback: 'Customer feedback & reviews',
          openingHours: 'Opening hours',
          opening_hours: 'Opening hours',
          menuItemAvailability: 'Catalog & item availability',
          menu_item_availability: 'Catalog & item availability',
          simpleOrdering: 'Simple ordering (cart -> WhatsApp/SMS)',
          simple_ordering: 'Simple ordering (cart -> WhatsApp/SMS)',
          mobileAppOrdering: 'Mobile app ordering',
          mobile_app_ordering: 'Mobile app ordering',
          mobileAppUsage: 'Mobile app usage',
          mobile_app_usage: 'Mobile app usage',
          mobilePickup: 'Pickup orders (mobile app)',
          mobile_pickup: 'Pickup orders (mobile app)',
          tableReservation: 'Booking & reservations (with SMS)',
          mailingList: 'Mailing list (email your customers)',
          siteAnnouncements: 'Announcements on your site',
          announcements: 'Announcements on your site',
          multipleRestaurants: 'Multiple businesses',
          restaurants: 'Businesses',
          restaurants1: '1 business',
          restaurants3: 'Up to 3 businesses',
          restaurantsUnlimited: 'Unlimited businesses',
          noAds: 'Can remove QRestoo branding',
          prioritySupport: 'Priority support',
          priority_support: 'Priority support',
          customIntegrations: 'Custom integrations',
          custom_integrations: 'Custom integrations',
          analytics: 'Analytics',
          siteContent: 'VIP Page: custom fields, gallery & unique QR (VIP)',
          orderAndPickup: 'Pickup orders (mobile app)',
        },
      },
      cta: {
        title: 'Ready to get your business online?',
        subtitle: 'Join businesses already using QRestoo to get found faster, serve better, and grow.',
        startTrial: 'Create a free account',
        bookDemo: 'View {businessType} demo',
        bookDemoGeneric: 'View demo',
      },
      faq: {
        headline: 'Questions? We\'ve got answers.',
        q1: 'Is the free plan really free?',
        a1: 'Yes — completely free, forever. No credit card, no trial period, no surprise charges. Your business gets a live page and QR code at zero cost.',
        q2: 'Can I cancel anytime?',
        a2: 'Absolutely. No contracts, no cancellation fees. Cancel in one click from your dashboard anytime.',
        q3: 'What happens to my business page if I cancel?',
        a3: 'If you cancel a paid plan, you drop back to the free Starter plan. Your page stays live — you never lose your business page.',
        q4: 'Do I need technical skills to set this up?',
        a4: 'None at all. If you can use WhatsApp, you can use QRestoo. Most businesses are live in under 10 minutes.',
        q5: 'What is the VIP plan exactly?',
        a5: 'We could tell you — but where\'s the fun in that? VIP is our most powerful plan, built around your specific needs. Hit \'Let\'s Talk\' and we\'ll show you what\'s possible. You might be surprised. 😉',
      },
      footer: {
        appName: 'QRestoo',
        copyrightHolder: 'DLR Web Solutions LLC',
        tagline: 'The simplest way to get your business online.',
        poweredBy: 'Powered by',
        poweredByLink: 'DLR Web Solutions',
        product: 'Product',
        company: 'Company',
        support: 'Support',
        integrations: 'Integrations',
        appStore: 'App Store',
        about: 'About Us',
        careers: 'Careers',
        press: 'Press',
        blog: 'Blog',
        helpCenter: 'Help Center',
        contact: 'Contact',
        contactModalTitle: 'Contact us',
        contactModalSuccess: 'Thanks! We\'ll get back to you soon.',
        apiDocs: 'API Docs',
        legal: 'Legal',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        cookies: 'Cookie Settings',
      },
    },
    reservation: {
      sectionTitle: 'Table reservation (with SMS)',
      reserveButton: 'Book a Table',
      reserveButtonServices: 'Book',
      heading: 'Book a table',
      headingServices: 'Book',
      pax: 'Number of guests',
      paxServices: 'Pax',
      paxInvalid: 'Please enter a number between 1 and 20.',
      date: 'Date',
      dateRequired: 'Please select a date.',
      dateFuture: 'Please select today or a future date.',
      dateMinTwoDays: 'Please select a date at least 2 days from today.',
      time: 'Time',
      timeRequired: 'Please select a time.',
      customerName: 'Your name',
      nameRequired: 'Your name is required.',
      nameMax: 'Name must be at most 150 characters.',
      customerMobile: 'Phone number',
      mobilePlaceholder: 'e.g. +15551234567',
      mobileRequired: 'Phone number is required.',
      mobileInvalidInternational: 'Please enter a valid phone number with country code (e.g. +1 555 123 4567).',
      sendVerificationCode: 'Send verification code',
      enterCodePrompt: 'Enter the 6-digit code we sent to your phone.',
      verificationCode: 'Verification code',
      codePlaceholder: '000000',
      codeRequired: 'Please enter the verification code.',
      codeInvalid: 'Please enter exactly 6 digits.',
      codeInvalidOrExpired: 'Invalid or expired verification code. Request a new code.',
      requestNewCode: 'Request new code',
      confirmReservation: 'Confirm reservation',
      submit: 'Request reservation',
      success: 'Your reservation request has been received.',
      notAvailable: 'Reservations are not available.',
      errorGeneric: 'Something went wrong. Please try again.',
    },
    public: {
      about: 'About',
      contactUs: 'Contact Us',
      reviewsAndFeedback: 'Reviews & feedback',
      t1NavMenu: 'Menu',
      reviewsNav: 'Reviews',
      contactNav: 'Contact',
      t2MenuNav: 'Menu',
      t2ViewServices: 'Services',
      t2ReviewsNav: 'Reviews',
      t2ContactNav: 'Contact',
      navMenu: 'Menu',
      navServices: 'Services',
      navProducts: 'Products',
      ownerNavBookingUnavailable: 'Booking. Account upgrade needed.',
      ownerNavReviewsUnavailable: 'Reviews. Account upgrade needed.',
      ownerNavLocationUnavailable: 'Location. Add address or map in settings.',
      ownerNavAboutUnavailable: 'Our Story. Add description in settings.',
      ownerNavHoursUnavailable: 'Hours. Add opening hours in settings.',
      t2CommunicationsLink: 'Communications Link',
      noLogoSetOwnerMessage: 'No logo set. Add one in your business settings.',
      ownerEditLogoAria: 'Edit logo and banner',
      heroEstYear: 'Est. {year}',
      noTaglineOrDescriptionSetOwnerMessage: 'No tagline or description set. Add them in your business settings.',
      noDescriptionSetOwnerMessage: 'No description set. Add one in your business settings.',
      noMapSetOwnerMessage: 'No map set. Add address or map link in your business settings.',
      noBookingAvailableOwnerMessage: 'Booking is not available. Enable it in your business settings.',
      noMailingListAvailableOwnerMessage: 'Mailing list is not available. Enable it in your business settings.',
      noFeedbackAvailableOwnerMessage: 'Reviews and feedback are not available. Enable them in your business settings.',
      t2NewsletterTitle: 'NEWSLETTER SUBSCRIPTION',
      ourMenu: 'Our Menu',
      ourProducts: 'Products',
      ourServices: 'Our Services',
      ourCatalog: 'Our catalog',
      viewMenu: 'View Menu',
      viewCatalog: 'View Catalog',
      viewServices: 'View Services',
      leaveFeedback: 'Leave your feedback',
      ratingLabel: 'Rating (1–5)',
      yourName: 'Your name',
      yourMessage: 'Your message',
      sendFeedback: 'Send feedback',
      t2FeedbackPerformanceLogging: 'Performance Logging',
      t2FeedbackOperationalLog: 'OPERATIONAL LOG',
      t2FeedbackTransmitCopy: 'Transmit your experience report for quality assurance and unit optimization.',
      t2FeedbackSessionMeta: 'Session Metadata:',
      t2FeedbackTerminalId: 'Terminal ID: TGT-LOG-992',
      t2FeedbackProtocol: 'Protocol: feedback_v2.0.sh',
      t2FeedbackRatingLabel: 'UNIT PERFORMANCE RATING',
      t2FeedbackRatingHint: 'Select evaluation metrics (1-5)',
      t2FeedbackSitrepLabel: 'SITREP / DETAILED FEEDBACK',
      t2FeedbackPlaceholder: 'Input log data here...',
      t2FeedbackSubmitReport: 'SUBMIT REPORT',
      t2FeedbackDisclaimer: 'By submitting, you agree to data archiving protocols.',
      captchaRequired: 'Please complete the captcha.',
      formErrorGeneric: 'Something went wrong. Please try again.',
      noReviewsYet: 'No reviews yet. Be the first to leave feedback below.',
      guestExperiences: 'Guest Experiences',
      reviewsSubtitle: 'What our patrons are saying',
      announcementTitle: 'Announcement',
      announcementHideForFourDays: "Don't show again for 4 days",
      close: 'Close',
      mailingListHeading: 'Stay updated',
      mailingListDescription: 'Subscribe to get news and offers from us.',
      mailingListEmailLabel: 'Email',
      mailingListEmailPlaceholder: "you{'@'}example.com",
      mailingListSubscribe: 'Subscribe',
      mailingListSuccess: 'Subscribed.',
      mailingListError: 'Something went wrong. Please try again.',
      mailingListEmailRequired: 'Email is required.',
      mailingListEmailInvalid: 'Please enter a valid email address.',
      openingHours: 'Opening Hours',
      noContactListed: 'No contact numbers or links listed.',
      whatsapp: 'WhatsApp',
      mobile: 'Mobile',
      email: 'Email',
      phone: 'Phone',
      fax: 'Fax',
      other: 'Contact',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      website: 'Website',
      viewingAsOwner: 'You are viewing this as the owner.',
      viewAsCustomer: 'View as Customer',
      viewAsOwner: 'View as owner',
      restaurantNotFoundTitle: 'Restaurant not found',
      backToHome: 'Back to home',
      ownerNeedsDataIntro: 'This site needs more data. Add a description, menu, or other content in the admin.',
      openAdminPage: 'Open admin page',
      addBannerInAdmin: 'Add a cover image in Settings',
      bookTable: 'Book a table',
      bookAppointment: 'Book an appointment',
      mapPlaceholder: 'Map',
      mapAriaLabel: 'Map showing restaurant location',
      openInMaps: 'Open in maps',
      mapIframeTitle: 'Google Map for this location',
      mapOpenExternallyHint:
        "Tap below to open this location in your maps app. If you want a map shown here, add latitude and longitude or a Google Maps link that includes a place pin ({'@'}lat,lng) in business settings.",
      allRightsReserved: 'All rights reserved.',
      siteCreatedViaQRestoo: 'Site created by QRestoo',
      createdByQRestoo: 'Created on QRestoo',
      sitePoweredByDLR: 'Site powered by DLR Web Solutions',
      surpriseMe: 'Surprise me',
      surpriseMeAria: 'Surprise me – pick a random menu item',
      noMenuItems: 'No menu items',
      noProducts: 'No products',
      noServices: 'No services',
      noCatalogItems: 'No items in catalog',
      fromPrice: 'From {price}',
      notAvailable: 'Not available',
      notAvailableWithNextTime: 'Not available now. Next available: {time}.',
      sizeAndPriceOptions: 'Size and price options',
      option: 'Option',
      comboContents: 'Combo contents',
      ownerEditMenuItemInCatalogAria: 'Edit item in catalog (opens admin)',
      closeMenu: 'Close menu',
      closeCatalogModal: 'Close catalog',
      closeServicesModal: 'Close services',
      ariaViewMenu: 'View menu',
      ariaViewCatalog: 'View catalog',
      ariaViewServices: 'View services',
      modalTitleMenu: 'Menu',
      modalTitleCatalog: 'Catalog',
      modalTitleProducts: 'Products',
      modalTitleServices: 'Services',
      ariaLocation: 'Location',
      ariaMap: 'Map',
      category: 'Category',
      mapAriaLabel: 'Map showing restaurant location',
      openNow: 'Open Now',
      closedNow: 'Closed',
      businessHoursNotListed: 'Hours not listed',
      open24_7: 'Open 24/7',
      daySunday: 'Sunday',
      dayMonday: 'Monday',
      dayTuesday: 'Tuesday',
      dayWednesday: 'Wednesday',
      dayThursday: 'Thursday',
      dayFriday: 'Friday',
      daySaturday: 'Saturday',
      t3NavMenu: 'Menu',
      t3NavHours: 'Hours',
      t3NavLocation: 'Location',
      t3NavReviews: 'Reviews',
      t3NavAbout: 'About',
      t3OurStory: 'Our Story',
      t3ExquisiteSelections: 'Exquisite Selections',
      t3SeasonalMenu: 'The Seasonal Menu',
      t3GuestExperiences: 'Guest Experiences',
      t3VerifiedDiner: 'Verified Diner',
      t3HoursAvailability: 'Hours & Availability',
      t3QuickLinks: 'Quick Links',
      t3FooterTagline: 'Crafting unforgettable dining experiences. Excellence in every plate.',
      t3FooterTaglineRestaurants: 'Crafting unforgettable dining experiences. Excellence in every plate.',
      t3FooterTaglineEcommerce: 'Quality products, carefully curated. Excellence in every order.',
      t3FooterTaglineServices: 'Delivering exceptional service. Excellence in every detail.',
      t3FooterContact: 'Get in touch for reservations and inquiries.',
      t3ShareExperience: 'Share Your Experience',
      t3FeedbackHelps: 'Your feedback helps us refine our craft.',
      t3FeedbackPlaceholder: 'How was your evening with us?',
      t3SubmitReview: 'Submit Review',
      t3JoinInnerCircle: 'Join Our Inner Circle',
      t3JoinInnerCircleDesc: 'Receive exclusive invitations to tasting events and seasonal menu launches.',
      t3Join: 'Join',
      t5TableIcon: 'Icon',
      t5TableImage: 'Image',
      t5TableProduct: 'Product',
      t5TablePrice: 'Price',
      t5TableStatus: 'Status',
      t5TableAction: 'Action',
      t5ViewDetails: 'View details',
      t5InStock: 'In stock',
      selectVariantToAdd: 'Select a variant to add to cart',
      selectRequiredModifierChoice: 'Choose one option in each required group before adding to cart.',
      modifiersHeading: 'Add-ons (optional)',
      modifiersAria: 'Optional add-ons',
      itemDetailDialogAria: 'Menu item details',
      itemUntitled: 'Untitled',
      itemNotAvailable: 'Not available',
      variantOptionNotAvailable: 'Not available',
      variantOptionsAria: 'Options and prices',
      variantImageAlt: 'Variant',
      priceFrom: 'From {price}',
      comboContentsAria: 'Combo contents',
      modifierUnnamed: 'Add-on',
      modifierNoExtraCost: 'No extra charge',
      quickOrder: {
        addToCart: 'Add to cart',
        updateCart: 'Update cart',
        yourOrder: 'Your Order',
        clearAll: 'Clear all',
        name: 'Your name',
        namePlaceholder: 'e.g. Juan',
        notes: 'Notes',
        notesPlaceholder: 'e.g. Extra sauce, no onions...',
        sendViaEmail: 'Send via Email',
        sendViaWhatsApp: 'Send via WhatsApp',
        sendViaViber: 'Send via Viber',
        sendViaSMS: 'Send via SMS',
        orderSent: 'Order sent!',
        orderSentDesc: 'Your order has been sent to the business.',
        allowPopups: 'Please allow popups for this site to open WhatsApp or SMS. Check your browser or device settings (e.g. address bar icon), then try again.',
        allowPopupsHint: 'Allow popups for this site to send via WhatsApp or SMS.',
        nameRequired: 'Name is required.',
        nameMaxLength: 'Name must be at most 100 characters.',
        notesMaxLength: 'Notes must be at most 500 characters.',
        notesRequired: 'Notes are required.',
        cartEmpty: 'Your cart is empty.',
        quantity: 'Quantity',
        remove: 'Remove',
        itemAdded: 'Added to cart!',
        itemUpdated: 'Cart updated!',
        ariaClose: 'Close',
        ariaDecrease: 'Decrease quantity',
        ariaIncrease: 'Increase quantity',
        ariaRemoveItem: 'Remove item',
        fulfillment: 'Pickup or dine-in',
        fulfillmentPickup: 'Pickup',
        fulfillmentDineIn: 'Dine in',
        dineInReferenceLabel: 'Table / reference (optional)',
        dineInReferencePlaceholder: 'e.g. Table 5, Patio 2',
        checkout: 'Checkout',
        orderSummary: 'Order summary',
        confirmCheckout: 'Place order',
        checkoutSubmitting: 'Submitting…',
        checkoutFailed: 'Could not place your order. Please try again.',
        subtotal: 'Subtotal',
        ariaCloseSummary: 'Close order summary',
        mobile: 'Mobile number',
        mobilePlaceholder: '+15551234567',
        mobileHint: 'Start with + and your country code (e.g. +1 555 123 4567). We will text you a one-time code to confirm pickup.',
        mobileRequired: 'Mobile number is required for pickup orders.',
        mobileInvalid: 'Please enter a valid phone number with country code (e.g. +1 555 123 4567).',
        verificationCode: 'Verification code',
        verificationCodePlaceholder: '6-digit code',
        verificationCodeInvalid: 'Enter the 6-digit code from your SMS.',
        sendVerificationCode: 'Send code',
        pickupServiceFee: 'Pickup service fee ({percent}%)',
        total: 'Total',
      },
      cookieConsent: {
        barTitle: 'Cookies and similar technologies',
        barBody:
          'We use essential functions to run the site. With your permission we add first-party analytics and third-party tools (e.g. security checks or sign-in). For details, open the ',
        acceptAll: 'Accept all',
        rejectNonEssential: 'Reject non-essential',
        customize: 'Customize',
        modalTitle: 'Cookie preferences',
        necessaryTitle: 'Strictly necessary',
        necessaryDesc: 'Needed for security, login, and core features. Always on.',
        analyticsTitle: 'Analytics (first-party)',
        analyticsDesc: 'Helps us improve public pages. Data is processed on our servers.',
        thirdPartyTitle: 'Third-party features',
        thirdPartyDesc: 'Loads tools such as Google reCAPTCHA when you use certain forms or sign-in.',
        save: 'Save preferences',
        privacyLink: 'Privacy Policy',
      },
    },
    tour: {
      welcomeTitle: 'Welcome!',
      welcomeDescription: 'Let\'s take a quick tour of your dashboard. You\'ll see where to manage businesses, catalogs, and more.',
      step1Title: 'Main area',
      step1Description: 'Your content appears here. Use the sidebar to switch between Dashboard, Businesses, and other sections.',
      step2Title: 'Navigation',
      step2Description: 'Use this sidebar to move between Dashboard, Businesses, Item catalog, Feedbacks, and Profile.',
      step2DescriptionSuperadmin: 'Use this sidebar to move between Dashboard, Users, Owner feedbacks, Businesses, and more.',
      step3Title: 'Dashboard',
      step3Description: 'Your home base. See an overview of your businesses, catalog items, and feedback at a glance.',
      step4Title: 'Businesses',
      step4Description: 'Manage your business(es). Add new ones, edit details, and organize catalog per business.',
      step5Title: 'Item catalog',
      step5Description: 'Create and edit items (products, services, combos). Add them to categories inside each business\'s catalog.',
      step6Title: 'Feedbacks',
      step6Description: 'View and moderate customer reviews from your public pages. Approve or reject to show or hide them.',
      step7Title: 'Feature request',
      step7Description: 'Send feedback or feature requests to the team. You can also see your past submissions here.',
      step8Title: 'Profile & Settings',
      step8Description: 'Update your name, email, password, and app language.',
      step9Title: 'Announcements',
      step9Description: 'Post announcements and promotions to your public page. Paid feature.',
      step10Title: 'Reservations',
      step10Description: 'Manage table reservations. View, approve, or reject requests; set your SMS number and opening hours. Paid feature.',
      step11Title: 'Mailing list',
      step11Description: 'View subscribers and send emails to your mailing list from your public page. Paid feature.',
      stepUsersTitle: 'Users',
      stepUsersDescription: 'Manage user accounts. View and edit users, roles, and verification status.',
      stepOwnerFeedbacksTitle: 'Owner feedbacks',
      stepOwnerFeedbacksDescription: 'Review feature requests and feedback submitted by business owners.',
      stepLegalTitle: 'Legal content',
      stepLegalDescription: 'Edit legal pages (e.g. Privacy Policy, Terms of Service) shown on the public site.',
      stepPlansTitle: 'Plans',
      stepPlansDescription: 'Manage subscription plans and features (Growth, Pro, etc.).',
      stepSmsTemplatesTitle: 'SMS templates',
      stepSmsTemplatesDescription: 'Manage SMS message templates used for reservation confirmations, reminders, and more.',
      next: 'Next',
      prev: 'Previous',
      done: 'Done',
      close: 'Close',
      skip: 'Skip tour',
    },
    restaurantsTour: {
      step1Title: 'Businesses list',
      step1Description: 'This is your businesses list. Add new businesses, edit details, and manage catalog from here.',
      step2Title: 'Add business',
      step2Description: 'Click here to create a new business and set its name, address, and web address.',
      step3Title: 'Your businesses',
      step3Description: 'Your businesses appear as cards. Click a card to open that business, or use the empty state to create your first one.',
      step4Title: 'Business card',
      step4Description: 'Click a business to edit it, manage catalog, set availability, and get the public link or QR code.',
      tooltip: 'Businesses tour',
    },
    welcome: {
      title: 'Create your first restaurant',
      subtitle: 'Get your menu online, share your address and hours, and let customers find you in minutes.',
      cta: 'Create my first restaurant',
    },
    wizard: {
      stepOf: 'Step {current} of {total}',
      step1Title: 'Name your restaurant',
      nameLabel: 'Restaurant name',
      taglineOptional: 'Tagline (optional)',
      slugPreview: 'Your web address',
      templateLabel: 'Public page template',
      templateHint: 'Choose the layout for your public page.',
      next: 'Next',
      back: 'Back',
      skipForNow: 'Skip for now',
      doLater: "I'll do this later",
      step2Title: 'Location & contact (optional)',
      youreAllSet: "You're all set",
      sublineLive: 'Your restaurant page is live. Share the link or QR so customers can find you.',
      copyAddress: 'Copy address',
      copied: 'Copied!',
      saveQr: 'Save QR code',
      scanHint: 'Customers scan this to open your page',
      goToMyRestaurant: 'Go to my restaurant',
      backToDashboard: 'Back to dashboard',
      firstTimeLink: 'First time? Create your first restaurant',
    },
    startTour: 'Start app tour',
    startTourDescription: 'Run the guided tour again to see the main areas of the app.',
  },
  es: {
    app: {
      name: 'RMS',
      dashboard: 'Panel',
      login: 'Iniciar sesión',
      register: 'Crear una cuenta',
      logout: 'Cerrar sesión',
      profile: 'Perfil y configuración',
      profileTab: 'Perfil',
      restaurants: 'Empresas',
      menu: 'Catálogo',
      menuItems: 'Catálogo de elementos',
      feedbacks: 'Comentarios',
      featureRequest: 'Solicitud de función',
      users: 'Usuarios',
      ownerFeedbacks: 'Comentarios de propietarios',
      inquiries: 'Consultas',
      languages: 'Idiomas y descripción',
      default: 'Predeterminado',
      addLanguage: 'Añadir idioma',
      defaultLanguage: 'Idioma predeterminado',
      languagesHint: 'Añade o quita idiomas. El idioma predeterminado se usa cuando no se selecciona ninguno en el sitio público.',
      description: 'Descripción',
      save: 'Guardar',
      cancel: 'Cancelar',
      cropApply: 'Aplicar',
      cropTitle: 'Recortar imagen',
      cropDescription: 'Ajusta el área de recorte y luego aplica.',
      close: 'Cerrar',
      loading: 'Cargando…',
      noContent: 'Aún no hay contenido.',
      remove: 'Eliminar',
      add: 'Añadir',
      translate: 'Traducir desde predeterminado',
      addItem: 'Añadir elemento',
      editItem: 'Editar elemento',
      createItem: 'Añadir elemento',
      languageLabel: 'Idioma',
      languageLabelTooltip: 'Este idioma es solo para la interfaz de la app de administración, no para tus páginas públicas o catálogos.',
      menuItemsSubtitle: 'Añade y edita elementos. Puedes añadirlos a categorías del catálogo dentro de cada empresa.',
      catalogNavInfoTooltip: 'Acerca del catálogo',
      catalogNavInfoModalTitle: 'Acerca del catálogo',
      catalogNavInfoModalDescription: 'Cómo se relaciona el Catálogo del menú lateral con las empresas y los artículos.',
      catalogNavInfoModalP1: 'Aquí todo gira en torno a los artículos del catálogo: tu biblioteca compartida de productos, servicios, combos y variantes.',
      catalogNavInfoModalP2: 'Las imágenes de los artículos se gestionan aquí: abre un artículo del catálogo para subir o cambiar fotos (incluidas las de variantes cuando aplique).',
      catalogNavInfoModalP3: 'Añade artículos del catálogo desde Empresas: abre una empresa y usa su pestaña Catálogo para crear artículos y organizarlos en categorías para tu sitio público.',
      catalogNavInfoGoToBusinesses: 'Ir a Empresas',
      catalogNotAssignedBadge: 'No está en ningún menú',
      catalogAssignedToPrefix: 'Asignado a:',
      catalogAssignedMoreCount: '+{count}',
      catalogListResultSummary: 'Mostrando {shown} de {total} elementos',
      catalogListNoMatches: 'Ningún elemento coincide con tu búsqueda o filtros.',
      catalogListLoadMore: 'Cargar más',
      catalogFilterAllTypes: 'Todos',
      ariaCatalogTypeFilter: 'Filtrar por tipo de elemento del catálogo',
      catalogVariantsFromPrice: 'Desde {price}',
      addFirstMenuItem: 'Añade tu primer elemento',
      menuItemsEmptyHint: 'Crea productos, servicios o listados. Añádelos a categorías en la pestaña Catálogo de cada empresa.',
      deleteMenuItem: 'Eliminar elemento',
      deleteMenuItemConfirm: 'No se puede deshacer. El elemento se eliminará de todas las empresas que lo usen.',
      menuItemFormSubtitleEdit: 'Nombre y descripción por idioma. Al menos el idioma predeterminado es obligatorio.',
      menuItemFormSubtitleEditSingle: 'Actualiza el nombre y la descripción opcional.',
      menuItemFormSubtitleCreate: 'Añade el nombre y la descripción opcional. Podrás añadir otros idiomas después de crear el elemento.',
      menuItemFormSubtitleCreateSingle: 'Añade el nombre y la descripción opcional del elemento.',
      menuItemCatalogPerBusinessHint:
        'Estás editando el elemento compartido del catálogo. Si este artículo está personalizado por empresa—precio, descripción, disponibilidad o visibilidad en el menú público—abre esa empresa en el panel y edítalo en la pestaña Menú para actualizar el sitio correcto.',
      catalogSummaryTypePrefix: 'Tipo:',
      catalogComboEntriesHint: 'Entradas (edita abajo para cambiar):',
      catalogComboPricePrefix: 'Precio del combo:',
      catalogOptionGroupsHeading: 'Grupos de opciones:',
      catalogVariantsHeadingPrice: 'Variantes (precio):',
      catalogVariantsHeadingPriceImage: 'Variantes (precio, imagen):',
      itemTypeSimple: 'Simple',
      itemTypeCombo: 'Combo',
      itemTypeWithVariants: 'Con variantes',
      catalogItemTypeSimpleHint: 'Un solo elemento con un precio',
      catalogItemTypeComboHint: 'Paquete de otros elementos del catálogo',
      catalogItemTypeVariantsHint: 'Opciones (p. ej. tamaño, tipo) con precio por combinación',
      ariaMenuItemTypeRadiogroup: 'Tipo de elemento del catálogo',
      catalogTypeSectionTitle: 'Tipo',
      priceFieldLabel: 'Precio',
      priceOptionalLabel: 'Precio (opcional)',
      comboPriceOptionalLabel: 'Precio del combo (opcional)',
      placeholderPriceExample: 'p. ej. 10.00',
      placeholderComboPriceExample: 'p. ej. 12.00',
      nameFieldLabel: 'Nombre',
      descriptionOptionalLabel: 'Descripción (opcional)',
      shortDescriptionPlaceholder: 'Descripción breve',
      catalogAddPageTitle: 'Añadir elemento al catálogo',
      catalogAddPageSubtitle: 'Elige un tipo y luego añade el nombre y los detalles según el tipo.',
      assignCatalogPageTitle: 'Añadir elemento a un negocio',
      assignCatalogPageSubtitle:
        'Elige un negocio y una categoría. El elemento sigue en tu catálogo; esto lo coloca en el menú público de ese negocio.',
      assignCatalogSheetEyebrow: 'Elemento del catálogo',
      assignCatalogSuccessEyebrow: 'Listo',
      assignCatalogItemLabel: 'Elemento',
      assignCatalogChooseBusiness: 'Negocio',
      assignCatalogChooseBusinessPlaceholder: '— Selecciona un negocio —',
      assignCatalogFirstMenuHint: 'Las categorías se toman del primer menú del negocio (por orden).',
      assignCatalogCategoryModeLabel: 'Categoría',
      assignCatalogCategoryModeExisting: 'Categoría existente',
      assignCatalogCategoryModeNew: 'Categoría nueva',
      assignCatalogSelectCategory: 'Categoría',
      assignCatalogSelectCategoryPlaceholder: '— Selecciona una categoría —',
      assignCatalogNewCategoryName: 'Nombre de la categoría nueva',
      assignCatalogNewCategoryPlaceholder: 'p. ej. Entrantes',
      assignCatalogAssignButton: 'Añadir al menú',
      assignCatalogSkip: 'Omitir por ahora',
      assignCatalogNoBusinesses: 'Aún no tienes ningún negocio. Crea uno en Negocios y vuelve aquí.',
      assignCatalogGoToBusinesses: 'Ir a Negocios',
      assignCatalogNoMenus: 'Este negocio aún no tiene menú. Abre la pestaña Catálogo del negocio y añade un menú primero.',
      assignCatalogOpenCatalogTab: 'Abrir pestaña Catálogo',
      assignCatalogNoCategoriesHint: 'Aún no hay categorías: usa «Categoría nueva» arriba o añádelas desde el Catálogo del negocio.',
      assignCatalogErrRestaurant: 'Selecciona un negocio.',
      assignCatalogErrCategory: 'Selecciona una categoría.',
      assignCatalogErrNewCategoryName: 'Introduce un nombre para la categoría nueva.',
      assignCatalogNotFound: 'No se encontró este elemento del catálogo.',
      assignCatalogNotStandalone: 'Este elemento no es solo de catálogo. Edítalo desde el menú del negocio.',
      assignToBusiness: 'Añadir a un negocio',
      assignCatalogSuccessTitle: 'Añadido a tu menú',
      assignCatalogSuccessBody: 'Crea otro elemento del catálogo o abre la categoría donde lo colocaste.',
      assignCatalogCreateAnotherItem: 'Crear otro elemento',
      assignCatalogGoToCategory: 'Ir a la categoría asignada',
      comboEntriesTitle: 'Entradas del combo',
      addComboEntry: 'Añadir entrada',
      entryNumber: 'Entrada {n}',
      ariaRemoveComboEntry: 'Quitar entrada',
      selectCatalogItemPlaceholder: '— Seleccionar elemento —',
      selectVariantPlaceholder: '— Seleccionar variante —',
      variantFieldLabel: 'Variante',
      quantityFieldLabel: 'Cantidad',
      modifierOptionalLabel: 'Modificador (opcional)',
      placeholderModifierExample: 'p. ej. Sin hielo',
      optionGroupsTitle: 'Grupos de opciones',
      addOptionGroup: 'Añadir grupo',
      removeOptionGroupAria: 'Quitar grupo',
      catalogGroupNameLabel: 'Nombre del grupo',
      catalogGroupNamePlaceholder: 'p. ej. Tamaño',
      catalogGroupNameLabelWithHint: 'Nombre del grupo (p. ej. {example})',
      exampleSize: 'Tamaño',
      exampleType: 'Tipo',
      optionValuesOnePerLine: 'Valores (uno por línea o separados por comas)',
      optionValuesCommaSeparated: 'Valores (separados por comas)',
      variantSizesPlaceholder: 'Pequeño, Mediano, Grande',
      combinationColumn: 'Combinación',
      priceColumn: 'Precio',
      variantPricesTitle: 'Precios por variante',
      ariaSelectMenuItemForCombo: 'Seleccionar elemento del catálogo',
      ariaSelectVariant: 'Seleccionar variante',
      placeholderVariantPrice: '0.00',
      creatingItem: 'Creando…',
      createItemSubmit: 'Crear elemento',
      menuItemDeletedToast: 'Elemento eliminado.',
      menuItemCreatedToast: 'Elemento creado.',
      menuItemUpdatedToast: 'Elemento actualizado.',
      restaurantCreatedToast: 'Empresa creada.',
      restaurantUpdatedToast: 'Empresa actualizada.',
      selectLanguageForItemAria: 'Seleccionar idioma para nombre y descripción',
      translateFromDefaultAria: 'Traducir desde el idioma predeterminado',
      descriptionInLanguagePlaceholder: 'Descripción en {lang}',
      restoreFromCatalogTitle: 'Restaurar el nombre, la descripción y el precio originales del catálogo',
      saveChangesConfirmTitle: '¿Guardar cambios?',
      menuItemSaveSensitiveDescription: 'Confirma guardar cambios que eliminan variantes o entradas del combo, o actualizan el texto.',
      menuItemSaveSensitiveBody: 'Has quitado variantes o entradas del combo, o has cambiado el nombre o la descripción. ¿Guardar estos cambios?',
      overridePriceLabel: 'Precio personalizado (deja vacío para usar el base: {base})',
      sameAsBasePlaceholder: 'Igual que el base',
      noCustomFieldsToReset: 'No hay nombre ni precio personalizados que restablecer',
      resettingShort: 'Restableciendo…',
      resetToOriginal: 'Restaurar original',
      menuItemImageSectionTitle: 'Imagen',
      menuItemImageHint:
        'Opcional. JPEG, PNG, GIF o WebP, máx. 2 MB. Recorta como quieras; se guarda hasta 512 px en el lado mayor (mantiene la proporción).',
      menuItemBaseImageTitle: 'Imagen principal del producto',
      menuItemBaseImageHintVariants: 'Se muestra en tu menú público (la tarjeta del producto). Las imágenes por variante abajo son opcionales.',
      ariaRemoveItemImage: 'Quitar imagen',
      beautifyWithAi: 'Embellecer con IA',
      variantPhotoLabel: 'Foto',
      variantGalleryLabel: 'Galería',
      variantChangeShort: 'Cambiar',
      variantAddShort: 'Añadir',
      ariaPriceForVariant: 'Precio para {label}',
      genericVariant: 'variante',
      validateNameRequired: 'El nombre es obligatorio.',
      validateDefaultNameRequired: 'El nombre es obligatorio en el idioma predeterminado.',
      validatePriceNonNegative: 'El precio debe ser 0 o mayor.',
      validateComboPriceNonNegative: 'El precio del combo debe ser 0 o mayor.',
      validateComboEntriesMin: 'Añade al menos una entrada al combo.',
      validateSelectMenuItem: 'Selecciona un elemento del catálogo.',
      validateSelectVariant: 'Selecciona una variante para este elemento.',
      validateQuantityMin: 'La cantidad debe ser al menos 1.',
      validateOptionGroupsMin: 'Añade al menos un grupo de opciones con valores.',
      validateVariantPricesAll: 'Indica un precio para cada variante.',
      validateModifierNameRequired: 'Escribe un nombre para este modificador en al menos un idioma.',
      validateModifierGroupNameRequired: 'Escribe un nombre para este grupo de modificadores.',
      validateModifierPriceDelta: 'El precio del modificador debe ser 0 o mayor.',
      quickOrderNameMaxLength: 'El nombre puede tener como máximo 100 caracteres.',
      quickOrderNotesMaxLength: 'Las notas pueden tener como máximo 500 caracteres.',
      menuItemModifiersSectionTitle: 'Modificadores (opcional)',
      menuItemModifiersHint:
        'Grupos opcionales (p. ej. Mejoras, Guarniciones). Pon nombre al grupo, elige si el cliente elige una opción o varias, y añade opciones con nombre y precio extra. Arrastra los asas para reordenar grupos y opciones.',
      menuItemModifiersHintCreate:
        'Grupos opcionales. Usa el mismo idioma que el nombre del artículo. Nombra cada grupo, elige una opción (radio) o varias (casillas), y añade opciones y precios.',
      addModifier: 'Añadir modificador',
      addModifierGroup: 'Añadir grupo',
      addModifierOption: 'Añadir opción',
      removeModifierGroupAria: 'Quitar grupo de modificadores',
      modifierGroupNameLabel: 'Nombre del grupo',
      modifierGroupSelectionTypeLabel: 'Cómo eligen los clientes',
      modifierSelectionSingle: 'Solo una opción (radio)',
      modifierSelectionMultiple: 'Cualquier combinación (casillas)',
      modifierGroupOptionsHeading: 'Opciones',
      modifierOptionNameLabel: 'Nombre de la opción',
      catalogModifierGroupSingleHint: 'En tu sitio público el cliente elegirá como máximo una opción de este grupo.',
      removeModifierAria: 'Quitar modificador',
      modifierPriceDeltaLabel: 'Precio extra',
      modifierActiveLabel: 'Activo (visible en el catálogo)',
      modifierNameInLocale: 'Nombre ({locale})',
      catalogModifierVisibilityTitle: 'Modificadores para este elemento',
      catalogModifierVisibilityHint:
        'Desmarca para ocultar un modificador solo para este elemento. Sigue en tu catálogo.',
      catalogModifierShowOnMenu: 'Mostrar en el sitio público',
      catalogVariantVisibilityTitle: 'Variantes para este elemento',
      catalogVariantVisibilityHint:
        'Oculta una combinación de variantes en tu sitio público, o déjala visible pero márcala como no disponible para pedidos. Las definiciones del catálogo no cambian.',
      catalogVariantShowOnMenu: 'Mostrar en el sitio público',
      catalogVariantAvailableForOrder: 'Disponible para pedidos',
      fixErrorsBelow: 'Corrige los errores siguientes.',
      checkFieldsMarkedRed: 'Revisa cada campo marcado en rojo.',
      alsoFixErrorsInFormBelow: 'Corrige también los errores del formulario de abajo.',
      phoneAllowedCharsOnly: 'Usa solo números y + - ( ) espacios.',
      phoneMinDigits: 'Introduce un número de teléfono válido (al menos 6 dígitos).',
      restaurantNameRequired: 'El nombre del negocio es obligatorio.',
      slugRequired: 'El subdominio / slug es obligatorio.',
      slugFormatInvalid: 'El slug debe contener solo letras minúsculas, números y guiones (ej. mi-negocio).',
      slugReserved: 'Este slug está reservado (ej. servidor de desarrollo) y no se puede usar. Elige otro.',
      customDomainInvalid: 'Introduce un dominio válido (ej. tunegocio.com).',
      smsRequiredForQuickOrdering: 'El número SMS es obligatorio para pedidos rápidos.',
      enterValidEmail: 'Introduce un correo electrónico válido.',
      enterValidUrl: 'Introduce una URL válida.',
      enterValidYear: 'Introduce un año válido (ej. 1995).',
      fixScheduleForDays: 'Corrige el horario para {days}.',
      somethingWentWrongTryAgain: 'Algo salió mal. Inténtalo de nuevo.',
      copyToClipboardFailed: 'No se pudo copiar al portapapeles.',
      generateQrFailed: 'No se pudo generar el código QR.',
      nameProducesReservedSlug: 'Este nombre genera un slug reservado. Prueba agregar una palabra o un número.',
      restoredToOriginalToast: 'Restaurado al original.',
      imageRemovedToast: 'Imagen eliminada.',
      imageUpdatedToast: 'Imagen actualizada.',
      uploadImageFirstToast: 'Sube una imagen primero.',
      imageBeautifiedFallback: 'Imagen embellecida.',
      fillDefaultLanguageFirstToast: 'Completa el nombre o la descripción en el idioma predeterminado primero.',
      translationNotAvailableToast: 'Traducción no disponible para este idioma. Se muestra el texto original — puedes editarlo.',
      translationAppliedToast: 'Traducción aplicada. Revísala y guarda.',
      catalogComboEntryUnknownItem: 'Elemento {id}…',
      menuItemNamePlaceholder: 'p. ej. Pizza Margarita',
      catalogItemFieldLabel: 'Elemento del catálogo',
      delete: 'Eliminar',
      deleting: 'Eliminando…',
      dashboardSubtitle: 'Gestiona y supervisa tu restaurante en un solo lugar.',
      dashboardSuperadminSubtitle: 'Vista general del sistema.',
      dashboardWelcome: 'Bienvenido,',
      dashboardSignedIn: 'Conectado como {email}. Usa Perfil y configuración para actualizar tu cuenta o contraseña.',
      dashboardSuperadminHint: 'Usa Usuarios o Restaurantes para gestionar el sistema.',
      totalOrders: 'Pedidos totales',
      revenue: 'Ingresos',
      paidUsers: 'Usuarios de pago',
      locations: 'Ubicaciones',
      manageRestaurants: 'Gestionar empresas',
      yourRestaurants: 'Tus empresas',
      restaurantsListSubtitle: 'Gestiona catálogo y detalles de cada empresa.',
      restaurantsListSubtitleEmbed: 'Cambia a otra empresa o añade una nueva.',
      restaurantsPaginationPageOf: 'Página {current} de {total}',
      restaurantsCountLabel: '{count} {count, plural, one {empresa} other {empresas}}',
      addRestaurant: 'Añadir empresa',
      addFirstLocation: 'Añade tu primera empresa',
      addFirstLocationHint: 'Crea un restaurante para poner tu menú en línea, compartir dirección y horario, y que los clientes te encuentren.',
      firstTimeLink: '¿Primera vez? Crea tu primer restaurante',
      createRestaurant: 'Crear empresa',
      welcomeTitle: 'Crea tu primer restaurante',
      welcomeSubtitle: 'Pon tu menú en línea, comparte tu dirección y horarios, y deja que los clientes te encuentren en minutos.',
      welcomeCta: 'Crear mi primer restaurante',
      wizardStepOf: 'Paso {current} de {total}',
      wizardBusinessTypeStepHint: 'Primero elige el tipo de negocio. Elegiremos automáticamente la mejor plantilla inicial.',
      wizardStep1Title: 'Detalles esenciales',
      wizardNameLabel: 'Nombre del restaurante',
      wizardTaglineOptional: 'Eslogan (opcional)',
      wizardSlugPreview: 'Tu dirección web',
      wizardTemplateLabel: 'Plantilla de página pública',
      wizardTemplateHint: 'Elige el diseño de tu página pública.',
      wizardTemplateAutoSelectedHint: 'Plantilla seleccionada automáticamente según tu tipo de negocio',
      wizardDefaultLanguageLabel: 'Idioma por defecto de tu sitio público',
      wizardDefaultLanguageHint:
        'Sugerimos español si tu dispositivo parece estar en España (zona horaria o configuración regional de España); si no, inglés. Puedes cambiarlo en la configuración del negocio.',
      wizardDefaultLanguageInvalid: 'Elige inglés o español.',
      contactAndLinks: 'Contacto y enlaces',
      contactQuickOrderLabel: 'Contacto para pedidos rápidos',
      smsNumberLabel: 'Número SMS (obligatorio)',
      smsNumberPlaceholder: 'ej. +34123456789 (para pedidos rápidos)',
      whatsappOptionalLabel: 'WhatsApp (opcional)',
      whatsappOptionalPlaceholder: 'ej. +34123456789',
      viberOptionalLabel: 'Viber (opcional)',
      viberOptionalPlaceholder: 'ej. +34123456789',
      contactQuickOrderNote: 'Estos números se usarán para pedidos rápidos en la página pública del restaurante.',
      contactWhatsAppViberSuggested: 'WhatsApp y Viber son opcionales pero recomendados para que los clientes puedan enviar sus pedidos.',
      restaurantEditPhoneLabel: 'SMS / teléfono del negocio',
      restaurantEditPhoneHint:
        'Se muestra en tu sitio público y se usa para los SMS de pedidos rápidos si esa función está activa. Añade WhatsApp, Viber u otros números en Contacto y enlaces.',
      wizardNext: 'Siguiente',
      wizardBack: 'Atrás',
      wizardSkipForNow: 'Omitir por ahora',
      wizardDoLater: 'Lo haré más tarde',
      wizardStep2Title: 'Ubicación y contacto (opcional)',
      wizardYoureAllSet: 'Todo listo',
      wizardSublineLive: 'La página de tu restaurante está en línea. Comparte el enlace o el código QR para que los clientes te encuentren.',
      wizardCopyAddress: 'Copiar dirección',
      wizardCopied: '¡Copiado!',
      wizardSaveQr: 'Guardar código QR',
      wizardScanHint: 'Los clientes escanean esto para abrir tu página',
      wizardGoToMyRestaurant: 'Ir a mi restaurante',
      wizardBackToDashboard: 'Volver al panel',
      restaurantNotFound: 'Empresa no encontrada.',
      locationOrItemNotFound: 'Empresa o elemento no encontrado.',
      feedbacksSubtitle: 'Consulta y modera las opiniones de clientes de cada restaurante.',
      feedbacksTotal: 'Opiniones (total)',
      feedbacksApproved: 'Aprobadas',
      feedbacksRejected: 'Rechazadas',
      feedbacksCreateFirst: 'Crea primero un restaurante para recibir y gestionar opiniones.',
      feedbacksListSubtitle: 'Aprueba los comentarios para mostrarlos en tu página pública. Rechaza o elimina para ocultarlos o quitarlos.',
      feedbacksListEmpty: 'Aún no hay comentarios. Aparecerán aquí cuando los clientes envíen reseñas.',
      feedbacksLoadError: 'Error al cargar los comentarios.',
      retry: 'Reintentar',
      redirectingToFeedbacks: 'Redirigiendo a opiniones…',
      viewModerateFeedbacks: 'Ver y moderar opiniones',
      viewAll: 'Ver todo',
      featureRequestSubtitle: 'Envía una solicitud de función o comentarios al equipo. Revisamos todas las enviadas.',
      sendFeedbackHeading: 'Enviar comentarios',
      myFeatureRequests: 'Mis solicitudes de función',
      yourRecentSubmissions: 'Tus envíos recientes (los más nuevos primero).',
      noSubmissionsYet: 'Aún no hay envíos. Usa el formulario de arriba para enviar tu primera solicitud.',
      sending: 'Enviando…',
      messageLabel: 'Mensaje',
      messagePlaceholder: 'Describe tu solicitud de función o comentarios…',
      messageRequired: 'El mensaje es obligatorio.',
      messageMaxLength: 'El mensaje debe tener como máximo {max} caracteres.',
      titleOptional: 'Título (opcional)',
      titlePlaceholder: 'Resumen breve',
      titleMaxLength: 'El título debe tener como máximo {max} caracteres.',
      restaurantOptional: 'Restaurante (opcional)',
      noneOption: '— Ninguno —',
      maxCharactersHint: 'Máx. 65.535 caracteres.',
      reviewed: 'Revisado',
      pending: 'Pendiente',
      emailAddress: 'Correo electrónico',
      emailPlaceholder: "tu{'@'}ejemplo.com",
      passwordPlaceholder: '••••••••',
      fullName: 'Nombre completo',
      fullNamePlaceholder: 'María García',
      repeatPassword: 'Repetir contraseña',
      passwordHintPlaceholder: 'Al menos 8 caracteres',
      restaurantName: 'Nombre del negocio',
      restaurantNamePlaceholder: 'ej. Mama Fina, Peluquería José, Tienda Tech',
      taglineOptional: 'Eslogan (opcional)',
      taglinePlaceholder: 'ej. Ingredientes frescos, sabores intensos',
      addressPlaceholder: 'Calle, ciudad, provincia',
      descriptionPlaceholder: 'Breve descripción de tu restaurante para la web pública',
      emailOptional: 'Correo (opcional)',
      contactEmailPlaceholder: "contacto{'@'}restaurante.com",
      websiteOptional: 'Sitio web (opcional)',
      websitePlaceholder: 'https://ejemplo.com',
      yearEstablishedOptional: 'Año de fundación (opcional)',
      yearPlaceholder: 'ej. 1995',
      categoryName: 'Nombre de categoría',
      categoryNamePlaceholder: 'ej. Más vendidos, Popular',
      filterByNamePlaceholder: 'Filtrar por nombre…',
      shortDescription: 'Descripción breve',
      shortDescriptionPlaceholder: 'Descripción breve de esta categoría',
      menuNamePlaceholder: 'ej. Menú del día, Bebidas',
      saveChanges: 'Guardar cambios',
      createRestaurantBtn: 'Crear empresa',
      editRestaurant: 'Editar empresa',
      addNewRestaurant: 'Añadir nuevo restaurante',
      saving: 'Guardando…',
      signIn: 'Iniciar sesión',
      signingIn: 'Iniciando sesión…',
      createAccount: 'Crear cuenta',
      creatingAccount: 'Creando cuenta…',
      resendLink: 'Reenviar enlace',
      resendIn: 'Reenviar en {seconds}s',
      sendingResend: 'Enviando…',
      resetPassword: 'Restablecer contraseña',
      resetting: 'Restableciendo…',
      saveProfile: 'Guardar perfil',
      savingProfile: 'Guardando perfil…',
      currentPassword: 'Contraseña actual',
      newPassword: 'Nueva contraseña',
      updatePassword: 'Actualizar contraseña',
      updatingPassword: 'Actualizando contraseña…',
      ownerOnlyMessage: 'Este mensaje solo lo ves tú, el propietario.',
      feedbackSubmitted: 'Comentarios enviados.',
      password: 'Contraseña',
      confirmPassword: 'Confirmar contraseña',
      forgotPassword: '¿Olvidaste la contraseña?',
      rememberMe: 'Recordarme 30 días',
      continue: 'Continuar',
      back: 'Atrás',
      minimumCharacters: 'Mínimo 8 caracteres',
      passwordsDontMatch: 'Las contraseñas no coinciden',
      accessDashboard: 'Accede al panel de tu restaurante',
      startManaging: 'Empieza a gestionar tu restaurante gratis',
      orContinueWith: 'O continuar con',
      orSignUpWithEmail: 'O registrarse con correo',
      continueWithGoogle: 'Continuar con Google',
      continueWithGoogleConfigure: 'Continuar con Google (configurar client ID)',
      continueWithFacebook: 'Continuar con Facebook',
      continueWithFacebookConfigure: 'Continuar con Facebook (configurar en .env)',
      continueWithInstagram: 'Continuar con Instagram',
      signInFailedTryAgain: 'No se pudo iniciar sesión. Inténtalo de nuevo.',
      googleSignInCancelledOrFailed: 'El inicio de sesión con Google se canceló o no se completó. Inténtalo de nuevo o usa correo y contraseña.',
      googleSignInGenericFailure: 'El inicio de sesión con Google no se completó. Inténtalo de nuevo o usa correo y contraseña.',
      googleSignInBackendFailed: 'No pudimos completar el inicio de sesión con Google. Inténtalo de nuevo o usa correo y contraseña.',
      googleSignInScriptLoadFailed: 'No se pudo cargar el inicio de sesión de Google (red o bloqueador). Comprueba la conexión, permite accounts.google.com y actualiza la página.',
      googleSignInNotReady: 'El inicio de sesión de Google aún se está cargando. Espera un momento e inténtalo de nuevo.',
      googleSignInInitFailed: 'No se pudo iniciar el inicio de sesión de Google en esta página. Actualiza e inténtalo de nuevo.',
      googleSignInCredentialError: 'Error de inicio de sesión con Google: {detail}',
      googleSignInNoSession: 'No hay sesión de Google o el inicio de sesión fue bloqueado. Inicia sesión en Google en este navegador, permite cookies de terceros si se solicita, o usa correo y contraseña.',
      googleSignInSuppressedByUser: 'El inicio de sesión de Google se cerró recientemente. Espera un poco o usa correo y contraseña.',
      googleSignInUnregisteredOrigin: 'La URL de este sitio no está permitida para Google. El administrador debe añadir este origen en Google Cloud Console (cliente OAuth).',
      googleSignInSecureHttpRequired: 'Google requiere HTTPS. Abre el sitio con una conexión segura.',
      googleSignInBrowserNotSupported: 'Este navegador no admite bien el inicio de sesión de Google. Prueba otro o usa correo y contraseña.',
      googleSignInInvalidClient: 'Google está mal configurado (cliente no válido). Contacta con soporte.',
      googleSignInMissingClientId: 'Google está mal configurado (falta el ID de cliente). Contacta con soporte.',
      googleSignInUnknownReason: 'No se pudo mostrar el inicio de sesión de Google. Inténtalo de nuevo o usa correo y contraseña.',
      googleSignInSkippedUserCancel: 'Se cerró el inicio de sesión de Google antes de completar. Inténtalo de nuevo o usa correo y contraseña.',
      googleSignInSkippedTapOutside: 'Se cerró el inicio de sesión de Google. Inténtalo de nuevo o usa correo y contraseña.',
      googleSignInSkippedIssuingFailed: 'Google no pudo emitir el token de acceso. Inténtalo de nuevo o usa correo y contraseña.',
      googleSignInDismissedCancel: 'Se canceló el inicio de sesión con Google. Inténtalo de nuevo o usa correo y contraseña.',
      googleSignInPromptNoResponse:
        'No se abrió el inicio de sesión con Google. Puede estar bloqueado por un bloqueador de ventanas emergentes o una extensión del navegador. Permite ventanas emergentes en este sitio, actualiza la página o usa correo y contraseña abajo.',
      googleSignInHintShort:
        'Puede abrirse una ventana de inicio de sesión de Google. Si no ves nada, permite ventanas emergentes en este sitio. También puedes usar correo abajo.',
      step2Of2: 'Paso 2 de 2',
      alreadyHaveAccount: '¿Ya tienes cuenta?',
      showPassword: 'Mostrar contraseña',
      hidePassword: 'Ocultar contraseña',
      agreeTermsIntro: 'Acepto los',
      agreeTermsRequired: 'Por favor acepta los Términos de servicio y la Política de privacidad.',
      and: 'y',
      termsOfService: 'Términos de servicio',
      privacyPolicy: 'Política de privacidad',
      legalContent: 'Términos y privacidad',
      legalContentSubtitle: 'Edita los Términos de servicio y la Política de privacidad. Se muestran en un modal cuando el usuario hace clic en los enlaces de la página de registro.',
      localeEn: 'Inglés',
      localeEs: 'Español',
      localeAr: 'Árabe',
      legalHtmlHint: 'Se admite HTML (p. ej. <p>, <strong>, <ul>, <li>, <a>).',
      termsPlaceholder: 'Introduce el contenido de los Términos de servicio…',
      privacyPlaceholder: 'Introduce el contenido de la Política de privacidad…',
      errorLoading: 'Error al cargar.',
      errorSaving: 'Error al guardar.',
      legalContentUpdated: 'Contenido legal actualizado.',
      siteSettings: 'Ajustes del sitio',
      siteSettingsSubtitle: 'Opciones globales para pedidos rápidos públicos (porcentaje de recargo por recogida).',
      pickupAdditionalCostPercent: 'Coste adicional por recogida (%)',
      pickupAdditionalCostHint: 'Se suma al subtotal del carrito si el cliente elige recogida. El total siempre lo calcula el servidor.',
      siteSettingsUpdated: 'Ajustes del sitio guardados.',
      saveSiteSettings: 'Guardar ajustes',
      siteSettingsLoadFailed: 'No se pudieron cargar los ajustes del sitio.',
      siteSettingsSaveFailed: 'No se pudieron guardar los ajustes.',
      pickupPercentInvalid: 'Introduce un porcentaje entre 0 y 100.',
      basicInformation: 'Información básica',
      businessTimezone: 'Zona horaria del negocio',
      businessTimezoneHint: 'Se usa para los periodos de analíticas y las horas en pedidos rápidos y correos. Por defecto se toma la zona de tu navegador al crear el negocio; cámbiala si tu local está en otra zona.',
      businessTimezoneClear: 'Sin definir (el servidor usa UTC)',
      businessTimezoneUpdated: 'Zona horaria actualizada.',
      settingsStoreBasicsSaved: 'Datos básicos guardados.',
      businessTypeLabel: 'Tipo de negocio',
      businessTypeRestaurants: 'Restaurante',
      businessTypeEcommerce: 'E-commerce',
      businessTypeServices: 'Servicio',
      businessTypeRequired: 'Selecciona un tipo de negocio.',
      selectBusinessTypePlaceholder: 'Selecciona el tipo de negocio',
      settingsPageSubtitle: 'Tipo de negocio, idioma, moneda y descripción por idioma.',
      settingsGroupBasicsTitle: 'Datos básicos del negocio',
      settingsGroupBasicsHint: 'Tipo de negocio y zona horaria para analíticas y marcas de tiempo en pedidos.',
      settingsGroupPricingTitle: 'Precios y apariencia',
      settingsGroupPricingHint: 'Moneda en tu sitio público y color de acento opcional.',
      settingsGroupContentTitle: 'Idiomas y descripciones',
      settingsGroupContentHint: 'Idiomas disponibles para visitantes y la descripción en cada uno.',
      settingsGroupLayoutTitle: 'Diseño de la página pública',
      settingsGroupLayoutHint: 'Elige cómo se estructura y se ve tu sitio público.',
      settingsGroupVisitorTitle: 'Sitio y pedidos',
      settingsGroupVisitorHint: 'Créditos del pie y cómo los clientes hacen pedidos rápidos.',
      settingsBusinessTypeHint: 'Controla las etiquetas de sección en tu sitio público (p. ej. Carta, Productos, Servicios).',
      currencySectionTitle: 'Moneda',
      currencySectionHint: 'Moneda usada para los precios en tu menú y sitio público.',
      currencyUpdated: 'Moneda actualizada.',
      primaryColorSectionTitle: 'Color principal',
      primaryColorSectionHint: 'Color de acento usado en tu página pública (botones, enlaces y destacados). Déjalo vacío para usar el predeterminado.',
      primaryColorPickAria: 'Elegir color principal',
      hexLabel: 'Hex',
      defaultLanguageUpdated: 'Idioma predeterminado actualizado.',
      setAsDefault: 'Establecer como predeterminado',
      adding: 'Añadiendo…',
      showAllLanguagesTotal: 'Mostrar todos los idiomas ({total} en total)',
      showLess: 'Mostrar menos',
      descriptionByLanguageTitle: 'Descripción por idioma',
      descriptionByLanguageHint: 'Descripción opcional mostrada por idioma en el sitio público.',
      editDescriptionForLanguageAria: 'Editar descripción para el idioma',
      descriptionPlaceholderForLanguage: 'Descripción ({locale})',
      translatingDescriptionAria: 'Traduciendo descripción desde el idioma predeterminado',
      publicPageTemplateTitle: 'Plantilla de página pública',
      publicPageTemplateHint: 'Elige el diseño para tu página pública. Esto afecta cómo se muestra tu menú e información a los visitantes.',
      removeLanguageTitle: '¿Quitar idioma?',
      removeLanguageBody: 'Se eliminará {locale}. También se eliminará la descripción de este idioma.',
      languageAdded: 'Se añadió {locale}.',
      languageRemoved: 'Idioma eliminado.',
      descriptionSaved: 'Descripción guardada.',
      chooseValidTemplate: 'Por favor elige una plantilla válida.',
      footerCreditsHidden: 'Créditos del pie ocultos.',
      footerCreditsVisible: 'Los créditos del pie están visibles en tu sitio público.',
      footerCreditsPaidOnly: 'Los créditos del pie solo se pueden ocultar en un plan de pago.',
      quickOrderEnabledToast: 'Pedido rápido activado.',
      quickOrderDisabledToast: 'Pedido rápido desactivado.',
      quickOrderViaAppEnabledToast: 'Pedido por app móvil activado.',
      quickOrderViaAppDisabledToast: 'Pedido por app móvil desactivado.',
      defaultDescriptionEmpty: 'La descripción predeterminada está vacía. Añade primero una descripción en el idioma predeterminado.',
      translationFallbackInfo: 'La traducción no está disponible para este idioma. Se muestra el texto original; puedes editarlo.',
      businessTypeUpdated: 'Tipo de negocio actualizado.',
      formSubtitleEdit: 'Actualiza detalles y medios.',
      formSubtitleCreate: 'Rellena lo esencial para listar tu restaurante. Podrás configurar horarios y contacto después.',
      slugHint: 'La dirección web del restaurante se creará automáticamente a partir del nombre.',
      slugLabel: 'Dirección web del menú',
      slugPlaceholder: 'ej. mi-restaurante',
      slugHintEdit: 'Cambiar el slug solo cambia la URL del subdominio. Los códigos QR y enlaces existentes usan el ID del restaurante y seguirán funcionando. Subdominio: {subdomain}',
      customDomainLabel: 'Dominio personalizado (VIP)',
      customDomainPlaceholder: 'ej. menu.turestaurante.com',
      customDomainHint: 'Si lo configuras, los escaneos QR y tu enlace público usarán este dominio. Tu sitio en este dominio puede llamar a nuestra API (CORS permitido). Déjalo en blanco para usar el subdominio.',
      customDomainVipOnly: 'Disponible solo en el plan VIP. Actualiza para configurar un dominio personalizado.',
      descriptionHint: 'Se muestra en tu página pública. Añade más idiomas en Ajustes.',
      addressOptional: 'Dirección (opcional)',
      descriptionOptional: 'Descripción (opcional)',
      profileSubtitle: 'Actualiza los datos de tu cuenta y la contraseña.',
      profileSection: 'Perfil',
      changePassword: 'Cambiar contraseña',
      socialSignInPasswordInfo:
        'Esta cuenta usa inicio de sesión social (Google, Facebook o Instagram). No se muestra el cambio de contraseña aquí. Para añadir una contraseña e iniciar sesión con correo, usa «Olvidé mi contraseña» en la página de acceso.',
      name: 'Nombre',
      forgotCheckEmail: 'Revisa tu correo para ver el enlace de restablecimiento.',
      forgotIntro: 'Introduce tu correo y te enviaremos un enlace para restablecer tu contraseña.',
      sendResetLink: 'Enviar enlace de restablecimiento',
      checkInbox: 'Revisa tu bandeja de entrada',
      sentResetLinkTo: 'Hemos enviado un enlace de restablecimiento a {email}. El enlace caduca en 1 hora.',
      didntGetEmail: '¿No recibiste el correo?',
      tryAgain: 'Intentar de nuevo',
      dontHaveAccount: '¿No tienes cuenta?',
      createOne: 'Crear una',
      somethingWentWrong: 'Algo ha ido mal. Por favor, inténtalo de nuevo.',
      authHeroTitle: 'Gestiona tu restaurante.',
      authHeroTitleHighlight: 'Sencillo y gratis.',
      authHeroSubtitle: 'Menús, códigos QR y horarios en un solo lugar. Para dueños que cuidan los detalles.',
      authHeroFooter: '© Sistema de Gestión de Restaurantes. Gratis para restauradores.',
      ariaMainNav: 'Navegación principal',
      ariaAppLanguage: 'Idioma de la aplicación',
      ariaOpenMenu: 'Abrir menú',
      ariaScrollUp: 'Desplazar hacia arriba',
      ariaScrollDown: 'Desplazar hacia abajo',
      ownerFeedbacksSubtitle: 'Solicitudes de funciones y comentarios de propietarios. Cambia el estado cuando los revises.',
      superadminInquiriesSubtitle: 'Envíos del formulario de contacto VIP y de la landing. Los más recientes primero.',
      ownerFeedbacksEmpty: 'Aún no hay comentarios de propietarios.',
      markPending: 'Marcar pendiente',
      markReviewed: 'Marcar revisado',
      feedbacksUpdateForbidden: 'No tienes permiso para actualizar comentarios.',
      feedbackNotFound: 'Comentario no encontrado.',
      feedbackUpdated: 'Comentario actualizado.',
      feedbackApproved: 'Comentario aprobado.',
      feedbackRejected: 'Comentario rechazado.',
      failedToUpdate: 'Error al actualizar.',
      feedbackDeleted: 'Comentario eliminado.',
      failedToDelete: 'Error al eliminar.',
      deleteFeedbackConfirm: '¿Eliminar este comentario? No se puede deshacer.',
      superadminUsersSubtitle: 'Gestiona cuentas: estado de pago y activar/desactivar.',
      paginationPrevious: 'Anterior',
      paginationNext: 'Siguiente',
      paginationPageOf: 'Página {current} de {last}',
      paginationPerPage: 'Por página',
      paginationShowing: 'Mostrando {from}–{to} de {total}',
      noUsersFound: 'No se encontraron usuarios.',
      search: 'Buscar',
      searchUsersPlaceholder: 'Buscar por nombre o correo…',
      noUsersMatchSearch: 'Ningún usuario coincide con la búsqueda.',
      paid: 'De pago',
      free: 'Gratis',
      active: 'Activo',
      deactivated: 'Desactivado',
      superadmin: 'Superadmin',
      markPaid: 'Marcar de pago',
      removePaid: 'Quitar de pago',
      activate: 'Activar',
      deactivate: 'Desactivar',
      ariaDeactivateUser: 'Desactivar usuario',
      ariaActivateUser: 'Activar usuario',
      vip: 'VIP',
      setVip: 'Dar VIP',
      removeVip: 'Quitar VIP',
      ariaSetVip: 'Permitir inicio en app móvil (Dar VIP)',
      ariaRemoveVip: 'No permitir inicio en app móvil (Quitar VIP)',
      ariaVipBadge: 'VIP: inicio en app móvil habilitado',
      superadminVerifyEmail: 'Verificar',
      ariaSuperadminVerifyEmail: 'Marcar el correo del usuario como verificado',
      superadminEmailVerifiedToast: 'Correo marcado como verificado.',
      emailUnverified: 'Sin verificar',
      pendingEmailChange: 'Cambio de correo pendiente',
      you: '(tú)',
      usersForbidden: 'No tienes permiso para ver usuarios.',
      superadminRestaurantsSubtitle: 'Lista de solo lectura de todos los restaurantes.',
      noRestaurantsFound: 'No se encontraron restaurantes.',
      searchRestaurantsPlaceholder: 'Buscar por nombre o slug…',
      noRestaurantsMatchSearch: 'Ningún restaurante coincide con la búsqueda.',
      restaurantsForbidden: 'No tienes permiso para ver restaurantes.',
      availability: 'Disponibilidad',
      availabilitySubtitle: 'Cuándo está abierto tu restaurante. Cambia los horarios abajo y pulsa Guardar.',
      deleteRestaurant: 'Eliminar restaurante',
      logoAndBanner: 'Logo y banner',
      logoBannerDescription: 'Cambia o actualiza el logo y la imagen de cabecera del restaurante.',
      uploadImagesHint: 'Sube imágenes. JPEG, PNG, GIF o WebP. Máx. 2 MB cada una.',
      logo: 'Logo',
      banner: 'Banner',
      takePhoto: 'Tomar foto',
      chooseFromGallery: 'Elegir de la galería',
      changeLogo: 'Cambiar logo',
      changeBanner: 'Cambiar banner',
      removeLogo: 'Quitar logo',
      removeBanner: 'Quitar banner',
      confirmRemoveLogoTitle: '¿Quitar el logo?',
      confirmRemoveLogoMessage: 'La página pública no mostrará logo hasta que subas uno nuevo.',
      confirmRemoveBannerTitle: '¿Quitar el banner?',
      confirmRemoveBannerMessage: 'La página pública no mostrará imagen de cabecera hasta que subas una nueva.',
      logoRemovedToast: 'Logo quitado.',
      bannerRemovedToast: 'Banner quitado.',
      ariaRemoveLogoImage: 'Quitar logo',
      ariaRemoveBannerImage: 'Quitar banner',
      done: 'Listo',
      deleteRestaurantConfirm: '¿Eliminar empresa?',
      cannotBeUndone: 'No se puede deshacer.',
      typeToConfirm: 'Escribe {slug} para confirmar',
      ariaUploadingLogo: 'Subiendo logo',
      ariaUploadingBanner: 'Subiendo banner',
      ariaUploadLogo: 'Subir logo',
      ariaUploadBanner: 'Subir banner',
      ariaUploadLogoFromGallery: 'Subir logo desde galería',
      ariaUploadBannerFromGallery: 'Subir banner desde galería',
      ariaTakePhotoLogo: 'Tomar foto para logo',
      ariaTakePhotoBanner: 'Tomar foto para banner',
      settings: 'Ajustes',
      themeTab: 'Tema',
      themeTabSubtitle: 'Color principal del sitio y plantilla de la página pública.',
      // Quick-add item wizard
      quickAddItem: 'Añadir ítem',
      quickAddItemAria: 'Añadir ítem al menú',
      quickAddStepOf: 'Paso {step} de {total}',
      quickAddStep0Title: 'Tipo de <em>ítem</em>',
      quickAddStep1Title: 'Elige una <em>categoría</em>',
      quickAddStep2Title: '<em>Detalles</em> del ítem',
      quickAddStep3Title: 'Revisar y <em>guardar</em>',
      quickAddWhatKind: '¿Qué tipo de ítem?',
      quickAddTypeSimpleSub: 'Un precio',
      quickAddTypeVariantsSub: 'Tallas, opciones…',
      quickAddTypeCombSub: 'Paquete',
      quickAddTypeSimpleHint: 'Un producto o plato con un precio fijo.',
      quickAddTypeVariantsHint: 'Un ítem con varias opciones, cada una con su precio (ej. Pequeño / Grande).',
      quickAddTypeComboHint: 'Un paquete de ítems vendidos juntos (ej. Menú completo).',
      quickAddNoCats: 'Sin categorías',
      quickAddNoCatsSub: 'Crea tu primera categoría abajo para agrupar tus ítems.',
      quickAddOrCreate: 'o crear nueva',
      quickAddFirstCatName: 'Nombre de tu primera categoría',
      quickAddNewCat: 'Nueva categoría',
      quickAddCatPlaceholder: 'ej. Principales, Bebidas, Servicios…',
      quickAddCreate: 'Crear',
      quickAddCatWillCreate: 'Se creará: {name}',
      quickAddItem1: 'ítem',
      quickAddItemN: 'ítems',
      quickAddNameLabel: 'Nombre del ítem',
      quickAddNamePlaceholder: 'ej. Pizza Margarita',
      quickAddPriceOptional: 'Precio (opcional)',
      quickAddComboPriceOptional: 'Precio del combo (opcional)',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: 'Descripción (opcional)',
      quickAddDescPlaceholder: 'Se mostrará en la tarjeta…',
      quickAddVariantsLabel: 'Variantes',
      quickAddVariantName: 'Nombre de la opción',
      quickAddVariantNamePlaceholder: 'ej. Grande',
      quickAddVariantPrice: 'Precio',
      quickAddRemoveVariant: 'Eliminar variante',
      quickAddAddVariant: 'Añadir variante',
      quickAddComboItems: 'Ítems en el combo',
      quickAddPickItem: 'Elige un ítem…',
      quickAddAdd: 'Añadir',
      quickAddRemoveComboItem: 'Eliminar ítem',
      quickAddNoItemsForCombo: 'Sin ítems aún — añade ítems simples o con variantes primero.',
      quickAddReviewItem: 'Ítem',
      quickAddReviewName: 'Nombre',
      quickAddReviewType: 'Tipo',
      quickAddReviewPrice: 'Precio',
      quickAddReviewIncludes: 'Incluye',
      quickAddReviewCategory: 'Categoría',
      quickAddReviewCategoryLabel: 'Categoría',
      quickAddNoPrice: 'Sin precio',
      quickAddNoCategory: 'Ninguna',
      quickAddNew: 'nueva',
      quickAddPublishNow: 'Publicar inmediatamente',
      quickAddPublishNowSub: 'Se activa en tu sitio de inmediato',
      quickAddAddAnother: 'Añadir otro tras guardar',
      quickAddAddAnotherSub: 'Mantener este flujo abierto',
      quickAddSaveItem: 'Guardar ítem',
      quickAddReview: 'Revisar',
      quickAddContinue: 'Continuar',
      quickAddBack: 'Atrás',
      quickAddSaved: 'Ítem guardado.',
      quickAddSavedSub: '{name} añadido a {category}.',
      quickAddAnotherItem: '+ Añadir otro ítem',
      quickAddDone: 'Listo',
      quickAddBusinessContext: 'Gestionas tu menú: los cambios se aplican a tu catálogo en vivo.',
      quickAddPhotoSection: 'Foto del ítem',
      quickAddPhotoHint: 'Opcional. Se muestra en tu menú público.',
      quickAddPhotoHintCrop: 'Opcional. Se muestra en tu menú público. Recorta la imagen después de elegirla.',
      quickAddPhotoRemove: 'Quitar foto',
      quickAddPhotoUpload: 'Subir foto',
      quickAddPhotoReplace: 'Cambiar foto',
      quickAddPhotoPreviewAlt: 'Vista previa de la foto del ítem',
      quickAddModifiersHint: 'Complementos opcionales (ej. extras). Los clientes pueden elegirlos al pedir.',
      quickAddReviewPhoto: 'Foto',
      quickAddReviewPhotoYes: 'Adjunta',
      quickAddReviewPhotoNone: 'Ninguna',
      quickAddReviewModifiers: 'Complementos',
      quickAddReviewModifiersNone: 'Ninguno',
      quickAddPhotoUploadFailed: 'El ítem se guardó, pero no se pudo subir la foto. Añádela desde Ítems del menú en el panel.',
      quickAddIntentLabel: '¿Cómo quieres añadir?',
      quickAddIntentCreate: 'Crear ítem nuevo',
      quickAddIntentCreateSub: 'Añade algo nuevo a tu catálogo y a este menú.',
      quickAddIntentAssign: 'Asignar del catálogo',
      quickAddIntentAssignSub: 'Coloca un ítem que ya creaste en Ítems del menú dentro de una categoría.',
      quickAddIntentAssignHint: 'Elige una categoría y luego un ítem del catálogo. No se crea nada nuevo en el catálogo.',
      quickAddStep0TitleIntent: '¿Cómo quieres <em>añadir</em>?',
      quickAddStep2TitleAssign: 'Elige un ítem del <em>catálogo</em>',
      quickAddPickCatalogItem: 'Ítem del catálogo',
      quickAddReviewAssignFromCatalog: 'Desde catálogo',
      quickAddAssignSearchPlaceholder: 'Buscar por nombre…',
      quickAddAssignEmpty: 'No hay ítems del catálogo para añadir aquí. Créalos en Ítems del menú primero, o ya están en esta categoría.',
      quickAddAssignNoCategories: 'Usa el formulario de abajo para nombrar tu primera categoría, pulsa Crear y continúa.',
      quickAddAssignCategoryHint: 'Elige una categoría existente o crea una nueva — igual que al añadir un ítem nuevo.',
      quickAddNoMenuForCategory: 'Este negocio aún no tiene menú. Añade un menú en el panel y vuelve a intentarlo.',
      catalogTab: 'Catálogo',
      primaryColorLabel: 'Color principal',
      primaryColorHint: 'Color de acento en tu página pública (botones, enlaces). Déjalo vacío para usar el predeterminado.',
      ariaPickPrimaryColor: 'Elegir color principal',
      clear: 'Borrar',
      primaryColorUpdated: 'Color principal actualizado.',
      primaryColorCleared: 'Color principal borrado.',
      primaryColorInvalid: 'Introduce un color hex válido (ej. #e85a2b o #fff).',
      themeTemplateHint: 'Elige el diseño de tu página pública.',
      templateInvalid: 'Elige una plantilla válida.',
      templateUpdated: 'Plantilla actualizada.',
      seeWebsite: 'Ver sitio web',
      goToSite: 'Ir al sitio',
      ariaManageSections: 'Secciones de gestión del negocio',
      qrScanHint: 'Escanea para visitar tu restaurante',
      qrSaveButton: 'Guardar código QR',
      qrSaveTooltip: 'Guarda o imprime esto para tu restaurante',
      contactAndDetails: 'Contacto y detalles',
      address: 'Dirección',
      email: 'Correo electrónico',
      social: 'Redes sociales',
      addLanguageEllipsis: 'Añadir idioma…',
      addLanguageHint: 'Añade al menos un idioma arriba para gestionar descripciones.',
      editDescriptionFor: 'Editar descripción para:',
      ariaSelectTemplate: 'Seleccionar plantilla',
      templateBestFor: 'Ideal para: {type}',
      templateBusinessType_restaurants: 'Restaurante',
      templateBusinessType_services: 'Servicio',
      templateBusinessType_ecommerce: 'E-commerce',
      templateBusinessType_general: 'General',
      footerCreditsSectionTitle: 'Créditos del pie en el sitio público',
      footerCreditsSectionHint: 'Controla si se muestra el bloque "Sitio creado con QRestoo" y créditos DLR en el pie de tu página pública.',
      hideFooterCreditsLabel: 'Ocultar créditos del pie en mi sitio público',
      showCurrencySectionTitle: 'Mostrar moneda en precios',
      showCurrencySectionHint: 'Si está activado, los precios en tu sitio público muestran la moneda (ej. 10,00 €). Si está desactivado, solo se muestra el número (ej. 10,00).',
      showCurrencyLabel: 'Mostrar moneda junto a los precios',
      showCurrencySavedOn: 'La moneda se mostrará junto a los precios en tu sitio público.',
      showCurrencySavedOff: 'Los precios mostrarán solo el número en tu sitio público.',
      orderingModeSectionTitle: 'Modo de pedidos',
      orderingModeSectionDescription:
        'Activa Pedido rápido para que los clientes añadan artículos al carrito en tu sitio público. En Pro y VIP también puedes permitir el pago con la app móvil QRestoo.',
      orderingModeQuickOrderLabel: 'Pedido rápido',
      orderingModeMobileAppLabel: 'Pedido por app móvil',
      orderingModeOptionOff: 'Desactivado — sin carrito en tu sitio público',
      orderingModeOptionQuick:
        'Pedido rápido — los clientes usan WhatsApp, Viber o SMS si tienes un número de contacto',
      orderingModeOptionMessaging:
        'Pedido rápido por mensaje — los clientes usan WhatsApp, Viber o SMS si tienes un número de contacto',
      orderingModeOptionApp: 'Pedido rápido por app móvil — los clientes pagan en la app QRestoo (para llevar)',
      ordersReceived: 'Pedidos recibidos',
      menusAndCategories: 'Catálogo y categorías',
      createFirstMenuHint: 'Crea tu primer menú para añadir categorías.',
      createCategoryFirstHint: 'Crea primero una categoría para añadir elementos a este menú.',
      menuItemsSettingsHint: 'Añade platos desde la página de cada categoría. Las traducciones usan Ajustes del restaurante.',
      createCategoryAboveHint: 'Crea una categoría arriba para añadir elementos.',
      renameMenu: 'Renombrar menú',
      selectMenu: 'Seleccionar menú',
      editCategory: 'Editar categoría',
      removeCategory: 'Eliminar categoría',
      addMenu: 'Añadir menú',
      addCategory: 'Añadir categoría',
      categoryAvailability: 'Disponibilidad de categoría',
      removeCategoryImage: 'Eliminar imagen de categoría',
      addMenuOrCategory: 'Añadir menú o categoría',
      editIn: 'Editar en',
      changeImage: 'Cambiar imagen',
      uploadImage: 'Subir imagen',
      translating: 'Traduciendo…',
      removing: 'Eliminando…',
      mainMenuDefault: 'Menú principal',
      categoriesHeading: 'Categorías',
      manageItemsInCategory: 'Gestionar elementos',
      categoryRowNoItems: 'Aún no hay elementos en esta categoría.',
      moreCategoryActions: 'Más acciones',
      menuManagement: 'Gestión del catálogo',
      menuManagementSubtitle: 'Catálogos, categorías y elementos. Arrastra para reordenar.',
      menuTabIntro: 'Crea categorías abajo y añade elementos desde la gestión del catálogo.',
      menuTabContextHint: 'En esta pestaña solo creas categorías y asignas elementos. Crear, editar y eliminar elementos del catálogo se hace en Gestión del catálogo.',
      noCategoriesYet: 'Aún no hay categorías. Añade una con el botón de abajo o el +.',
      noMenuItemsYet: 'Aún no hay elementos. Usa el botón + de abajo para añadir uno.',
      categoryNameRequired: 'El nombre de la categoría es obligatorio.',
      categoryUpdated: 'Categoría actualizada.',
      categoryCreated: 'Categoría creada.',
      categoryModalDescEdit: 'Actualiza el nombre y la descripción de la categoría en cada idioma.',
      categoryModalDescAdd: 'Crea una categoría nueva (p. ej. Entradas, Principales). Añade un nombre para cada idioma que uses.',
      selectLanguageToEditAria: 'Seleccionar idioma a editar',
      localeDefaultSuffix: ' (Predeterminado)',
      categoryFieldNameInLanguage: 'Nombre ({lang})',
      categoryFieldDescriptionInLanguageOptional: 'Descripción ({lang}) — opcional',
      translatingCategoryFromDefaultAria: 'Traduciendo categoría desde el idioma predeterminado',
      translateCategoryFromDefaultAria: 'Traducir desde el idioma predeterminado',
      searchLabel: 'Buscar',
      categoryItemsPageFallbackTitle: 'Elementos de la categoría',
      categoryItemsSubtitle: 'Añade y reordena elementos en esta categoría. Arrastra para cambiar el orden.',
      categoryEmptyTitle: 'Añade elementos a esta categoría',
      categoryEmptyHint: 'Añade elementos existentes de tu catálogo para mostrarlos en esta categoría. Podrás reordenarlos después.',
      addItemToCategoryButton: 'Añadir elemento',
      categoryItemCount: '{count} elementos',
      dragToReorderAria: 'Arrastrar para reordenar',
      setAvailabilityForItemAria: 'Configurar horarios de disponibilidad para este elemento',
      markAvailableOnPublicMenuShort: 'Marcar disponible en menú público',
      markNotAvailableOnPublicMenuShort: 'Marcar no disponible en menú público',
      showOnPublicCatalogAria: 'Mostrar en menú público',
      hideOnPublicCatalogAria: 'Ocultar del menú público',
      assignTagsToItemAria: 'Asignar etiquetas a este elemento',
      editCatalogItemAria: 'Editar elemento',
      removeFromCategoryTitle: 'Quitar de la categoría',
      removeFromCategoryListAria: 'Quitar este elemento de esta categoría',
      addItemToCategoryModalTitle: 'Añadir elemento a la categoría',
      addItemToCategoryModalDesc: 'Añade o quita elementos en esta categoría. Los elementos vienen de tu catálogo. Busca para filtrar.',
      addAllVariantsHint: 'Añade todas las variantes a esta categoría',
      inMenuAsLabel: 'Se muestra como: {name}',
      removeFromMenuButton: 'Quitar del menú',
      addToCategoryAria: 'Añadir {name} a la categoría',
      removeFromCategoryNamedAria: 'Quitar {name} de la categoría',
      loadingCatalogItems: 'Cargando elementos…',
      noItemsMatchSearch: 'Ningún elemento coincide con la búsqueda o aún no tienes elementos simples, combos o con variantes en el catálogo.',
      done: 'Listo',
      addItemWithVariantsTitle: 'Añadir elemento con variantes',
      addItemWithVariantsBody: 'Este elemento tiene varias variantes. Al añadirlo se añadirán todas las variantes a esta categoría.',
      addAllVariantsButton: 'Añadir todas las variantes',
      assignTagsModalTitlePlain: 'Asignar etiquetas',
      assignTagsToItemTitle: 'Asignar etiquetas a {name}',
      assignTagsModalDescription: 'Elige etiquetas para mostrarlas en este elemento en tu página pública.',
      noTagsAvailable: 'No hay etiquetas disponibles.',
      tagClassificationGeneral: 'General',
      tagClassificationRestaurant: 'Restaurante',
      tagClassificationService: 'Servicio',
      tagClassificationEcommerce: 'Comercio electrónico',
      tagAssignedTooltip: '{name} (asignada)',
      tagFallbackLabel: 'Etiqueta',
      untitledTag: 'Sin título',
      removeCategoryConfirmLong: '¿Eliminar «{name}»? Los elementos de esta categoría quedarán sin categoría. No se puede deshacer. Si no estás seguro, oculta la categoría en el menú público.',
      removingCategory: 'Eliminando…',
      removeCategoryButton: 'Eliminar categoría',
      categoriesTotalCount: '{count} en total',
      hideCategoryOnPublicTitle: 'Ocultar categoría en menú público',
      showCategoryOnPublicTitle: 'Mostrar categoría en menú público',
      hideCategoryOnPublicAria: 'Ocultar categoría en menú público',
      showCategoryOnPublicAria: 'Mostrar categoría en menú público',
      menuTabInstructionAria: 'Ayuda de la pestaña de catálogo',
      menuNameLabel: 'Nombre del catálogo',
      menuNamePlaceholderShort: 'p. ej. Almuerzo, Servicios, Tienda',
      menuDescriptionPlaceholderShort: 'Descripción breve de este catálogo',
      addMenuModalTitle: 'Añadir catálogo',
      addMenuModalDescription: 'Crea otro catálogo para esta empresa. Añade nombre y descripción opcional por idioma.',
      editMenuModalTitle: 'Editar catálogo',
      editMenuModalDescription: 'Cambia el nombre y la descripción de este catálogo para cada idioma.',
      createMenuButton: 'Crear catálogo',
      creatingMenu: 'Creando…',
      menuCreatedToast: 'Catálogo creado.',
      menuUpdatedToast: 'Catálogo actualizado.',
      menuHiddenPublicToast: 'Catálogo oculto en el sitio público.',
      menuVisiblePublicToast: 'Catálogo visible en el sitio público.',
      categoryRemovedToast: 'Categoría eliminada.',
      defaultCategoryNameEmptyToast: 'El nombre de categoría predeterminado está vacío. Añade primero un nombre en el idioma predeterminado.',
      translationFailedShort: 'Error en la traducción.',
      translationAppliedClickSaveToast: 'Traducción aplicada. Pulsa Guardar para guardar.',
      validateAtLeastOneName: 'Se requiere al menos un nombre.',
      validateNameMaxLengthInLocale: 'El nombre ({lang}) debe tener como máximo 255 caracteres.',
      itemAddedToCategoryToast: 'Elemento añadido a la categoría.',
      variantsAddedToCategoryToast: '{count} variantes añadidas a la categoría.',
      itemAlreadyInCategoryToast: 'Este elemento ya está en esta categoría.',
      itemAlreadyInCategoryAsWholeToast: 'Este elemento ya está en esta categoría (añadido completo).',
      allVariantsAlreadyInCategoryToast: 'Todas las variantes ya están en esta categoría.',
      removedFromCategoryToast: 'Quitado de la categoría.',
      itemMarkedAvailableToast: 'Elemento marcado como disponible.',
      itemMarkedNotAvailableToast: 'Elemento marcado como no disponible.',
      itemVisibleOnPublicToast: 'Elemento visible en el menú público.',
      itemHiddenFromPublicToast: 'Elemento oculto del menú público.',
      categoryHiddenPublicToast: 'Categoría oculta del menú público.',
      categoryVisiblePublicToast: 'Categoría visible en el menú público.',
      orderUpdatedToast: 'Orden actualizado.',
      availabilityUpdatedToast: 'Disponibilidad actualizada.',
      tagsUpdatedToast: 'Etiquetas actualizadas.',
      fabAddCategoryAria: 'Añadir categoría',
      fabCloseAria: 'Cerrar',
      unnamedMenu: 'Catálogo sin nombre',
      menuFallbackName: 'Catálogo',
      addMenuItemsToCategory: 'Añadir elementos a esta categoría',
      noMenuItemsOrMatch: 'No hay elementos o ninguno coincide con la búsqueda.',
      assignTags: 'Asignar etiquetas',
      itemAvailability: 'Disponibilidad del elemento',
      dragToReorder: 'Arrastra para reordenar',
      setAvailabilityTimes: 'Configurar horarios',
      available: 'Disponible',
      notAvailable: 'No disponible',
      markNotAvailable: 'Marcar no disponible',
      markAvailableOnMenu: 'Marcar disponible en menú público',
      visibleOnMenu: 'Visible en menú',
      hiddenFromMenu: 'Oculto en menú',
      addMenuItemToCategory: 'Añadir elemento a categoría',
      menuItemTags: 'Etiquetas de elementos',
      createRestaurantFirstForMenus: 'Crea primero una empresa para gestionar catálogo y elementos.',
      ratingOutOf5: 'Valoración: {n} de 5',
      anonymous: 'Anónimo',
      approve: 'Aprobar',
      reject: 'Rechazar',
      ariaApproveFeedback: 'Aprobar comentario',
      ariaRejectFeedback: 'Rechazar comentario',
      ariaDeleteFeedback: 'Eliminar comentario',
      resetPasswordSuccess: 'Tu contraseña se ha restablecido. Ya puedes iniciar sesión.',
      resetPasswordEnterNew: 'Introduce tu nueva contraseña abajo.',
      type: 'Tipo',
      labelOptional: 'Etiqueta (opcional)',
      placeholderUrl: 'https://...',
      placeholderPhone: 'ej. +34 612 345 678',
      placeholderContactLabel: 'ej. Reservas, Cocina',
      showOnPublicPage: 'Mostrar en página pública',
      hideOnPublicPage: 'Ocultar en página pública',
      ariaEditContact: 'Editar contacto',
      ariaDeleteContact: 'Eliminar contacto',
      noContactsYet: 'Aún no hay contactos ni enlaces. Añade uno para que te contacten.',
      deleteContactConfirm: '¿Eliminar este contacto?',
      ariaPagination: 'Paginación',
      weeklyScheduleTitle: 'Horario semanal',
      itemAvailabilityTitle: 'Disponibilidad del elemento',
      itemAvailabilityInfo: 'Define cuándo está disponible este elemento. Puedes añadir varias franjas para disponibilidad partida (p. ej., almuerzo y cena).',
      restaurantAvailabilityInfo: 'El horario afecta la disponibilidad de pedidos en línea y cómo los clientes ven cuándo estás abierto.',
      itemAvailabilityHint: 'Define horas de apertura y cierre para cada día. Añade varias franjas para horario partido.',
      restaurantAvailabilityHint: 'Define horas de apertura y cierre de tu restaurante para cada día. Añade varias franjas para horario partido (p. ej., almuerzo y cena).',
      openForBusinessAria: '{day} abierto al público',
      openingHourAria: 'Hora de apertura',
      openingMinuteAria: 'Minuto de apertura',
      closingHourAria: 'Hora de cierre',
      closingMinuteAria: 'Minuto de cierre',
      addAnotherTimeSlot: 'Añadir otra franja horaria',
      closed: 'Cerrado',
      to: 'a',
      ariaOpeningTime: 'Hora de apertura',
      ariaClosingTime: 'Hora de cierre',
      removeTimeSlot: 'Eliminar franja horaria',
      open24_7: 'Abierto 24/7',
      open24_7ConfirmTitle: 'Abierto 24/7',
      open24_7ConfirmMessage: 'Activar Abierto 24/7 borrará tu horario actual. Tu restaurante se mostrará como siempre abierto. ¿Continuar?',
      confirm: 'Confirmar',
      menuItemLabel: 'Elemento del menú',
      addMenuItem: 'Añadir elemento al menú',
      ariaRemoveImage: 'Eliminar imagen',
      ariaRemoveVariantImage: 'Eliminar imagen de variante',
      ariaRemoveEntry: 'Eliminar entrada',
      ariaRemoveGroup: 'Eliminar grupo',
      ariaShowHelp: 'Mostrar ayuda: ¿qué significan los botones?',
      help: 'Ayuda',
      ariaButtonLegend: 'Leyenda de botones',
      helpLegendDescription: 'Significado de los iconos en filas de menú y categoría.',
      legendDragHandle: 'Asa de arrastre',
      legendDragDesc: 'Arrastra para reordenar',
      legendSchedule: 'Horario',
      legendScheduleDesc: 'Configurar horario',
      legendAvailability: 'Disponibilidad',
      legendAvailabilityDesc: 'Cuándo está disponible',
      legendVisibility: 'Visibilidad',
      legendVisibilityDesc: 'Mostrar u ocultar en menú público',
      legendTags: 'Etiquetas',
      legendTagsDesc: 'Asignar etiquetas',
      paidFeature: 'Función de pago',
      paidFeatureHeadline: 'Desbloquea Pro',
      paidFeatureSubhead: 'Esto y más está incluido en Pro.',
      paidFeaturePricingHint: 'Cancela cuando quieras.',
      paidFeaturePricingLine: 'Consulta Facturación para el precio actual. Cancela cuando quieras.',
      paidFeatureWhatIncluded: 'Qué incluye Pro:',
      upgradeToUse: 'Actualiza para usar esta función.',
      paidFeatureAiDescription: 'Mejorar imágenes del menú con IA está disponible en plan de pago.',
      paidFeatureAnnouncementDescription: 'Muestra anuncios en el sitio público de tu restaurante hasta 4 días. Actualiza para desbloquear esta y otras funciones Pro.',
      paidFeatureBenefitsIntro: 'Beneficios de actualizar:',
      paidFeatureBenefitAnnouncements: 'Anuncios en tu sitio público',
      paidFeatureBenefitTableReservation: 'Reservas y citas (con SMS)',
      paidFeatureBenefitMailingList: 'Lista de correo',
      paidFeatureBenefitNoAds: 'Puede quitar la marca QRestoo',
      paidFeatureBenefitMultipleRestaurants: 'Varios negocios',
      analytics: 'Analíticas',
      analyticsSubtitle: 'Seguimiento de escaneos QR, vistas de menú, artículos populares y más.',
      analyticsCreateFirst: 'Crea primero un restaurante para ver analíticas.',
      analyticsQrScans: 'Escaneos QR',
      analyticsMenuViews: 'Vistas de menú',
      analyticsItemClicks: 'Clics en artículos',
      analyticsViews: 'Vistas',
      analyticsReservations: 'Reservas',
      analyticsReviews: 'Reseñas',
      analyticsRating: 'Valoración',
      analyticsPopularItems: 'Artículos populares',
      analyticsPeakHours: 'Horas pico',
      analyticsReservationTrends: 'Tendencias de reservas',
      analyticsMonthlyOverview: 'Resumen mensual',
      analyticsRevenue: 'Ingresos',
      analyticsMultiLocation: 'Varias empresas',
      analyticsCustomerInsights: 'Información de clientes',
      analyticsExportCsv: 'Exportar CSV',
      analyticsExportPdf: 'Exportar PDF',
      analyticsPeriodWeek: 'Semana',
      analyticsPeriodMonth: 'Mes',
      analyticsPeriodAll: 'Todo el tiempo',
      analyticsNoData: 'Aún no hay datos',
      analyticsSelectRestaurant: 'Seleccionar restaurante',
      analyticsSubtotal: 'Subtotal',
      announcement: 'Anuncio',
      announcementSubtitle: 'Muestra un mensaje en el sitio público de tu restaurante hasta 4 días. Solo planes de pago.',
      announcementSelectRestaurant: 'Selecciona un restaurante',
      announcementLanguage: 'Idioma',
      announcementMessage: 'Mensaje',
      announcementMessagePlaceholder: 'ej. Cerrado por evento privado este sábado.',
      announcementImage: 'Imagen (opcional)',
      announcementUploadImage: 'Subir imagen',
      announcementRemoveImage: 'Quitar imagen',
      announcementImageHelp: 'Sube una imagen para mostrarla con tu anuncio en el sitio público. JPEG, PNG, GIF o WebP, máx. 2MB.',
      announcementImageTypeInvalid: 'Elige una imagen JPEG, PNG, GIF o WebP.',
      announcementImageSizeInvalid: 'La imagen debe ser menor de {max}MB.',
      announcementImageUpdated: 'Imagen actualizada.',
      announcementImageRemoved: 'Imagen eliminada.',
      uploading: 'Subiendo…',
      announcementActive: 'Mostrar en el sitio público',
      announcementActiveHelp: 'Cuando está activo, el mensaje aparece en tu sitio público hasta que lo desactives.',
      announcementSave: 'Guardar',
      announcementSaved: 'Anuncio guardado.',
      vipSiteFieldsNav: 'Campos del sitio',
      vipSiteFieldsLabel: 'VIP',
      vipSiteFieldsTitle: 'Campos personalizados del sitio',
      vipSiteFieldsSubtitle: 'Añade claves que tu sitio público o integraciones puedan leer (texto, HTML o imagen por campo). El texto y HTML usan cada idioma instalado en el negocio seleccionado.',
      vipSiteFieldsValueForLanguage: 'Valor ({label})',
      vipSiteFieldsNoLanguages: 'Añade al menos un idioma a este negocio en la configuración del restaurante antes de usar campos de texto o HTML.',
      vipSiteFieldsHtmlPlaceholder: 'Escribe contenido con formato para este idioma…',
      vipSiteFieldsSelectBusiness: 'Selecciona un negocio',
      vipSiteFieldsExisting: 'Tus campos',
      vipSiteFieldsAddField: 'Añadir campo',
      vipSiteFieldsEditField: 'Editar campo',
      vipSiteFieldsKey: 'Clave del campo',
      vipSiteFieldsKeyPlaceholder: 'ej. promo_banner',
      vipSiteFieldsKeyHelp: 'Letras minúsculas, números y guión bajo. Debe empezar por letra.',
      vipSiteFieldsKeyRequired: 'Introduce una clave.',
      vipSiteFieldsKeyInvalid: 'Empieza con letra minúscula; luego letras, números o guiones bajos (máx. 64).',
      vipSiteFieldsType: 'Tipo',
      vipSiteFieldsTypeRequired: 'Elige un tipo.',
      vipSiteFieldsTypeText: 'Texto plano',
      vipSiteFieldsTypeHtml: 'HTML',
      vipSiteFieldsTypeImage: 'Imagen',
      vipSiteFieldsImageUrl: 'URL de imagen (opcional)',
      vipSiteFieldsImageUrlPlaceholder: 'https://…',
      vipSiteFieldsImageUrlInvalid: 'Introduce una URL válida.',
      vipSiteFieldsUploadImage: 'Subir imagen',
      vipSiteFieldsUploadImageHelp: 'Guarda el campo primero; luego edítalo para subir un archivo (sustituye la URL). JPEG, PNG, GIF o WebP.',
      vipSiteFieldsNotVip: 'Esta función solo está disponible en VIP.',
      vipSiteFieldsLoadFailed: 'No se pudieron cargar los campos.',
      vipSiteFieldsSaved: 'Campo actualizado.',
      vipSiteFieldsCreated: 'Campo creado.',
      vipSiteFieldsDeleted: 'Campo eliminado.',
      vipSiteFieldsSaveFailed: 'No se pudo guardar el campo.',
      vipSiteFieldsDeleteFailed: 'No se pudo eliminar el campo.',
      vipSiteFieldsImageUpdated: 'Imagen actualizada.',
      vipSiteFieldsImageUploadFailed: 'Error al subir la imagen.',
      siteContentNav: 'Página VIP',
      vipOrdersNav: 'Pedidos',
      vipOrdersEyebrow: 'Pedidos',
      vipOrdersTitle: 'Pedidos',
      vipOrdersSubtitle:
        'Pedidos rápidos desde tu sitio público o la app (últimos 50 por negocio). Abre una fila o toca Detalles para ver el desglose.',
      vipOrdersSelectBusiness: 'Selecciona un negocio',
      vipOrdersCreateFirst: 'Crea un negocio primero para ver pedidos.',
      vipOrdersEmpty: 'Aún no hay pedidos para este negocio.',
      vipOrdersLoadFailed: 'No se pudieron cargar los pedidos.',
      vipOrdersColWhen: 'Fecha',
      vipOrdersColCustomer: 'Cliente',
      vipOrdersColStatus: 'Estado',
      vipOrdersColTotal: 'Total',
      vipOrdersColActions: 'Acciones',
      vipOrdersViewDetails: 'Detalles',
      vipOrdersDetailTitle: 'Detalle del pedido',
      vipOrdersFulfillment: 'Entrega',
      vipOrdersFulfillmentPickup: 'Para llevar',
      vipOrdersFulfillmentDineIn: 'En local',
      vipOrdersEmail: 'Correo',
      vipOrdersPhone: 'Teléfono',
      vipOrdersPhoneVerified: 'Teléfono verificado',
      vipOrdersLineItems: 'Artículos',
      vipOrdersSubtotal: 'Subtotal',
      vipOrdersPickupFee: 'Cargo por recogida ({percent}%)',
      vipOrdersTotal: 'Total',
      vipOrdersNotes: 'Notas',
      vipOrdersActionAccept: 'Aceptar pedido',
      vipOrdersActionReject: 'Rechazar',
      vipOrdersActionMarkReady: 'Marcar como listo',
      vipOrdersActionPickedUp: 'Marcar como recogido',
      vipOrdersActionServed: 'Marcar como servido',
      vipOrdersStatusUpdateFailed: 'No se pudo actualizar el estado del pedido.',
      vipOrdersRejectModalTitle: 'Rechazar pedido',
      vipOrdersRejectHint:
        'En pedidos para llevar con teléfono del cliente, puede recibir este motivo por SMS si el servidor tiene mensajería configurada.',
      vipOrdersRejectReasonLabel: 'Motivo del rechazo',
      vipOrdersRejectCancel: 'Cancelar',
      vipOrdersRejectConfirm: 'Rechazar pedido',
      vipOrdersRejectReasonRequired: 'Indica un motivo para rechazar.',
      vipOrdersRejectReasonMinLength: 'Escribe al menos 3 caracteres.',
      vipOrdersRejectionReason: 'Motivo del rechazo',
      vipOrdersStatus_pending: 'Pendiente',
      vipOrdersStatus_received: 'Recibido',
      vipOrdersStatus_ready: 'Listo',
      vipOrdersStatus_picked_up: 'Recogido',
      vipOrdersStatus_rejected: 'Rechazado',
      vipOrdersStatus_cancelled: 'Cancelado',
      paidFeatureOwnerOrdersDescription:
        'Pro y VIP pueden revisar pedidos rápidos en el panel: importes, artículos, contacto del cliente y notas.',
      paidFeatureBenefitOwnerOrders: 'Bandeja de pedidos (Pro / VIP)',
      siteContentLabel: 'VIP',
      siteContentTitle: 'Página VIP',
      siteContentSubtitle:
        'Campos personalizados (texto, HTML o imagen), una galería de fotos y códigos QR únicos para tu sitio público. Elige un negocio y usa las pestañas. El texto y la galería usan cada idioma instalado en ese negocio.',
      siteContentUniqueQrHint:
        'Cada código abre tu dirección pública en vivo (dominio personalizado o subdominio) con ?unique_id=… para mesas, campañas o personal.',
      siteContentUniqueQrLabel: 'ID único',
      siteContentUniqueQrPlaceholder: 'p. ej. 1',
      siteContentUniqueQrNoPublicUrl:
        'Configura primero la dirección pública de este negocio (subdominio/slug o dominio personalizado VIP en ajustes del negocio).',
      siteContentUniqueQrEmptyHint: 'Escribe un ID arriba para ver el código QR y el pie.',
      siteContentUniqueQrHideAppUrl: 'Ocultar la marca QRestoo',
      siteContentUniqueQrDownload: 'Descargar QR',
      siteContentSelectBusiness: 'Selecciona un negocio',
      siteContentPickBusinessHint: 'Selecciona un negocio arriba.',
      siteContentTabCustomFields: 'Campos personalizados',
      siteContentTabGallery: 'Galería',
      siteContentTabUniqueQr: 'QR único',
      siteContentGalleryAdd: 'Añadir imagen a la galería',
      siteContentGalleryNoLanguages:
        'Añade al menos un idioma a este negocio en la configuración del restaurante antes de editar textos de la galería.',
      siteContentGalleryUntitled: 'Sin título',
      siteContentGalleryVisible: 'Visible en el sitio público',
      siteContentGalleryHidden: 'Oculto en el sitio público',
      siteContentGalleryShowOnSite: 'Mostrar en el sitio',
      siteContentGalleryTitleForLang: 'Título ({label})',
      siteContentGalleryDescriptionForLang: 'Descripción ({label})',
      siteContentGalleryTitlePlaceholder: 'Título breve',
      siteContentGalleryDescriptionPlaceholder: 'Descripción opcional',
      siteContentGalleryImageUrl: 'URL de imagen (opcional)',
      siteContentGalleryExternalImageUrl: 'Enlace de imagen externa',
      siteContentGalleryExternalImageUrlHelp:
        'Enlace opcional a una imagen en otro sitio. Si subes un archivo abajo, se usará ese archivo; no pegues aquí la URL de la subida.',
      siteContentGalleryImagePreview: 'Vista previa (como en tu sitio)',
      siteContentGalleryUploadImage: 'Subir imagen',
      siteContentGalleryMoveUp: 'Subir',
      siteContentGalleryMoveDown: 'Bajar',
      siteContentGalleryDragOrderHint:
        'Arrastra el asa de la izquierda para cambiar el orden en tu sitio público. También puedes usar Subir / Bajar.',
      siteContentGalleryDragHandleAria: 'Arrastrar para reordenar imágenes de la galería en tu sitio',
      siteContentGalleryLoadFailed: 'No se pudo cargar la galería.',
      siteContentGallerySaveFailed: 'No se pudo guardar el elemento.',
      siteContentGalleryReorderFailed: 'No se pudo reordenar.',
      siteContentGalleryDeleteFailed: 'No se pudo eliminar el elemento.',
      siteContentGallerySaved: 'Elemento de galería actualizado.',
      siteContentGalleryItemCreated: 'Elemento de galería añadido.',
      siteContentGalleryItemDeleted: 'Elemento de galería eliminado.',
      siteContentGalleryOrderSaved: 'Orden guardado.',
      siteContentGalleryImageUpdated: 'Imagen actualizada.',
      siteContentGalleryImageFailed: 'Error al subir la imagen.',
      siteContentGalleryOptional: ' (opcional)',
      siteContentGalleryBatchDropTitle: 'Arrastra imágenes aquí o haz clic para buscar',
      siteContentGalleryBatchDropHint:
        'Hasta 30 imágenes (JPEG, PNG, GIF, WebP, HEIC). Añade títulos y descripciones después en cada fila.',
      siteContentGalleryBatchNoImages: 'No hay imágenes compatibles en tu selección.',
      siteContentGalleryBatchTooMany: 'Solo se subirán las primeras {n} imágenes.',
      siteContentGalleryBatchSuccess: '{n} imagen(es) añadida(s) a la galería.',
      siteContentGalleryBatchPartial: '{added} añadida(s); {failed} no se pudieron subir.',
      siteContentGalleryBatchAllFailed: 'No se pudo añadir ninguna imagen.',
      paidFeatureBenefitSiteContent: 'Página VIP: campos, galería y QR únicos (VIP)',
      paidFeatureSiteContentDescription:
        'VIP incluye campos personalizados para integraciones, una galería con títulos, descripciones y mostrar/ocultar por imagen, y códigos QR únicos para tu URL pública con ?unique_id=…',
      announcementTranslateNoDefault: 'Añade un mensaje en el idioma por defecto primero, luego usa Traducir desde predeterminado.',
      announcementTranslated: 'Mensaje traducido desde el idioma por defecto.',
      reservations: 'Reservas',
      reservationsNav: 'Reservas de mesa',
      reservationsSubtitle: 'Gestiona las reservas de mesa y configura el número de teléfono para recibir SMS de nuevas reservas.',
      reservationsCreateFirst: 'Crea una empresa primero para usar las reservas de mesa.',
      reservationsSelectRestaurant: 'Seleccionar empresa',
      reservationsPendingCount: '{n} pendientes',
      reservationsRestaurantFilter: 'Empresa',
      reservationsDateFrom: 'Desde fecha',
      reservationsDateTo: 'Hasta fecha',
      reservationsApplyFilter: 'Aplicar',
      reservationsTableDate: 'Fecha',
      reservationsTableTime: 'Hora',
      reservationsTablePax: 'Comensales',
      reservationsTableCustomer: 'Cliente',
      reservationsTableMobile: 'Teléfono',
      reservationsTableStatus: 'Estado',
      reservationsApprove: 'Aprobar',
      reservationsReject: 'Rechazar',
      reservationsRemove: 'Quitar',
      reservationsCancel: 'Cancelar reserva',
      reservationApproveConfirmTitle: '¿Aprobar reserva?',
      reservationApproveConfirmMessage: 'Se notificará al cliente que su reserva está confirmada.',
      reservationRejectConfirmTitle: '¿Rechazar reserva?',
      reservationRejectConfirmMessage: 'Se notificará al cliente que su reserva no fue aceptada.',
      reservationsEdit: 'Editar fecha y hora',
      reservationsEditDateTime: 'Cambiar fecha y hora de la reserva',
      reservationsApproved: 'Reserva aprobada.',
      reservationsRejected: 'Reserva rechazada.',
      reservationsCancelled: 'Reserva cancelada.',
      reservationsRemoved: 'Reserva eliminada.',
      reservationUpdated: 'Reserva actualizada.',
      reservationsPendingBadge: '{n} reservas pendientes',
      reservationSmsToPlaceholder: 'ej. +34123456789',
      reservationSmsToInvalid: 'Introduce un número de teléfono válido (ej. +34123456789).',
      phoneMustStartWithPlus: 'El número debe comenzar con + (ej. +34123456789).',
      reservationSmsToLabel: 'Número para notificaciones SMS',
      reservationSmsToHint: 'Formato internacional (ej. +34123456789). Recibirás SMS de nuevas reservas y recordatorios en este número.',
      reservationSmsToSaved: 'Número SMS guardado.',
      reservationsPax: '{count} comensales',
      reservationsOnDay: 'Reservas del {date}',
      reservationsNoReservationsMonth: 'No hay reservas este mes.',
      reservationsListEmpty: 'Aún no hay reservas.',
      reservationsLoadError: 'Error al cargar las reservas.',
      reservationsForbidden: 'Las reservas de mesa requieren un plan de pago.',
      reservationStatus_pending: 'Pendiente',
      reservationStatus_approved: 'Aprobada',
      reservationStatus_rejected: 'Rechazada',
      reservationStatus_cancelled: 'Cancelada',
      reservationDetailTitle: 'Detalles de la reserva',
      reservationDetailCreatedAt: 'Solicitada el',
      reservationDetailReminderSent: 'Recordatorio enviado el',
      reminderSent: 'Recordatorio enviado',
      guest: 'invitado',
      guests: 'invitados',
      reservationsCalendarPrev: 'Mes anterior',
      reservationsCalendarNext: 'Mes siguiente',
      reservationsCount: 'reservas',
      reservationOne: 'reserva',
      daySunday: 'Domingo',
      dayMonday: 'Lunes',
      dayTuesday: 'Martes',
      dayWednesday: 'Miércoles',
      dayThursday: 'Jueves',
      dayFriday: 'Viernes',
      daySaturday: 'Sábado',
      ariaApproveReservation: 'Aprobar reserva',
      ariaRejectReservation: 'Rechazar reserva',
      reservationApproved: 'Reserva aprobada.',
      reservationRejected: 'Reserva rechazada.',
      smsTemplates: 'Plantillas SMS',
      smsTemplatesSubtitle: 'Gestiona las plantillas SMS para reservas y otras notificaciones. Marcadores: {customer_name}, {date}, {time}, {restaurant_name}, {pax}.',
      smsTemplatesAdd: 'Añadir plantilla',
      smsTemplatesEdit: 'Editar plantilla',
      smsTemplatesEmpty: 'Aún no hay plantillas SMS.',
      smsTemplatesDeleteConfirm: '¿Eliminar la plantilla "{key}"? No se puede deshacer.',
      smsTemplatesFormSubtitle: 'La clave es única y la usa el sistema. El cuerpo puede incluir marcadores.',
      smsTemplatesKey: 'Clave',
      smsTemplatesKeyReadonly: 'La clave no se puede cambiar al editar.',
      smsTemplatesName: 'Nombre',
      smsTemplatesBody: 'Cuerpo',
      smsTemplatesBodyPlaceholders: 'Marcadores: {customer_name}, {date}, {time}, {restaurant_name}, {pax}',
      smsTemplatesBodyPlaceholder: 'ej. Hola {customer_name}, tu mesa para {pax} el {date} a las {time} está confirmada. — {restaurant_name}',
      smsTemplatesKeyRequired: 'La clave es obligatoria.',
      smsTemplatesNameRequired: 'El nombre es obligatorio.',
      smsTemplatesBodyRequired: 'El cuerpo es obligatorio.',
      superadminMenuItemTagsSubtitle: 'Gestiona las etiquetas por defecto de elementos del menú (p. ej. Picante, Vegano) visibles para todos los propietarios.',
      superadminItemTagsSearchPlaceholder: 'Buscar por texto de etiqueta…',
      superadminItemTagsAdd: 'Añadir etiqueta',
      superadminItemTagsEdit: 'Editar etiqueta',
      superadminItemTagsEmpty: 'Aún no hay etiquetas.',
      superadminItemTagsNoMatch: 'Ninguna etiqueta coincide con la búsqueda.',
      superadminItemTagsForbidden: 'No tienes permiso para gestionar etiquetas.',
      superadminItemTagText: 'Etiqueta',
      superadminItemTagTextPlaceholder: 'p. ej. Picante, Vegano',
      superadminItemTagTextRequired: 'La etiqueta es obligatoria.',
      superadminItemTagTextMax: 'La etiqueta debe tener 100 caracteres o menos.',
      superadminItemTagColor: 'Color (hex)',
      superadminItemTagColorMax: 'El color debe tener 50 caracteres o menos.',
      superadminItemTagIcon: 'Icono (nombre Material icon)',
      superadminItemTagIconPlaceholder: 'p. ej. local_fire_department, star',
      superadminItemTagIconMax: 'El icono debe tener 100 caracteres o menos.',
      superadminItemTagClassification: 'Clasificación',
      superadminItemTagCreated: 'Etiqueta creada.',
      superadminItemTagUpdated: 'Etiqueta actualizada.',
      superadminItemTagDeleted: 'Etiqueta eliminada.',
      superadminItemTagDeleteConfirm: '¿Eliminar la etiqueta "{text}"? Se quitará de los elementos del menú que la usen.',
      mailingList: 'Lista de correo',
      mailingListSubtitle: 'Ver suscriptores y enviar correos. Solo planes de pago.',
      mailingListCreateFirst: 'Crea una empresa primero para usar la lista de correo.',
      mailingListSelectRestaurant: 'Selecciona una empresa',
      mailingListSubscribers: 'Suscriptores',
      mailingListNoSubscribers: 'Aún no hay suscriptores.',
      mailingListSubscribedAt: 'Suscrito',
      mailingListSelectAll: 'Seleccionar todos en esta página',
      mailingListSelectSubscriber: 'Seleccionar suscriptor',
      mailingListSelectOneSubscriber: 'Selecciona al menos un suscriptor para enviar.',
      mailingListRemoveSelected: 'Quitar seleccionados',
      mailingListRemoveConfirmTitle: '¿Quitar suscriptores?',
      mailingListRemoveConfirmMessage: '¿Quitar {count} suscriptor(es) de la lista? Ya no recibirán correos. Esta acción no se puede deshacer.',
      mailingListRemoveConfirmButton: 'Quitar',
      mailingListRemovedSuccess: 'Suscriptor(es) quitados.',
      mailingListPageInfo: 'Mostrando {from}–{to} de {total}',
      language: 'Idioma',
      mailingListSendEmail: 'Enviar correo',
      mailingListSubject: 'Asunto',
      mailingListSubjectPlaceholder: 'ej. Noticias y ofertas',
      mailingListBody: 'Cuerpo (HTML)',
      mailingListBodyPlaceholder: 'Escribe tu mensaje…',
      mailingListSendButton: 'Enviar correo',
      mailingListSubjectRequired: 'El asunto es obligatorio.',
      mailingListBodyRequired: 'El cuerpo es obligatorio.',
      mailingListBodyMaxCharacters: 'El cuerpo debe tener como máximo {max} caracteres.',
      mailingListSentSuccess: 'Correo enviado a {count} suscriptores.',
      mailingListPaidOnly: 'Esta función requiere un plan de pago.',
      paidFeatureMailingListDescription: 'Recopila correos de suscriptores y envía boletines desde la página pública de tu restaurante. Actualiza para desbloquear la lista de correo.',
      paidFeatureVipSiteFieldsDescription: 'Define campos personalizados de texto, HTML o imagen para tu sitio público (VIP). Contáctanos para pasar a VIP.',
      paidFeatureBenefitVipSiteFields: 'Campos personalizados del sitio (integraciones VIP)',
      maybeLater: 'Quizá más tarde',
      upgrade: 'Actualizar',
      billingTitle: 'Facturación y mejora',
      billingSubtitle: 'Suscríbete a Pro para desbloquear más funciones.',
      billingCurrentPlan: 'Plan actual',
      growthPlan: 'Plan Growth',
      growthPlanBenefits: 'Hasta 3 negocios, feedback, anuncios. Cancela cuando quieras.',
      proPlan: 'Plan Pro',
      proPlanBenefits: 'Negocios ilimitados, lista de correo y todas las funciones Growth. Cancela cuando quieras.',
      proPlanPrice: '—',
      subscribeToPlan: 'Suscribirse',
      subscriptionSection: 'Suscripción',
      upgradeToPro: 'Pasar a Pro',
      upgradeToGrowth: 'Pasar a Growth',
      upgradeToVip: 'Pasar a VIP',
      planStarter: 'Gratis',
      planGrowth: 'Growth',
      planPro: 'Pro',
      planVip: 'VIP',
      manageSubscription: 'Gestionar suscripción',
      cancelSubscription: 'Cancelar suscripción',
      planUntilDate: '{plan} hasta el {date}',
      billingNotAvailable: 'La facturación no está disponible ahora. Inténtalo más tarde.',
      noBillingAccount: 'No hay cuenta de facturación. Suscríbete primero.',
      freePlanLabel: 'Estás en el plan gratuito.',
      subscriptionSuccessToast: 'Suscripción activa. ¡Bienvenido a {plan}!',
      subscriptionPending: 'Tu suscripción se está procesando. Actualiza en un momento.',
      subscriptionRefreshHint: 'Si tu plan no se actualiza en breve, prueba a actualizar la página.',
      ariaNotifications: 'Notificaciones',
      dismiss: 'Cerrar',
      reservationsHowItWorksTitle: 'Cómo funcionan las reservas de mesa',
      reservationsHowItWorksIntro: 'Los clientes pueden solicitar mesa desde la página pública de tu restaurante. Tú apruebas, rechazas o editas las reservas desde aquí.',
      reservationsHowItWorksPhoneRequired: 'Se requiere un número de teléfono válido para cada reserva para poder enviar confirmaciones y recordatorios por SMS.',
      reservationsHowItWorksSteps: 'Configura el número de notificación SMS de cada empresa para recibir avisos de nuevas reservas. Aprueba o rechaza las solicitudes; los clientes reciben un SMS. También puedes editar fecha, hora o número de comensales y cancelar; los clientes serán notificados por SMS.',
      reservationsHowItWorksTooltip: 'Cómo funcionan las reservas',
    },
    verify: {
      checkYourEmail: 'Revisa tu correo',
      sentToEmail: 'Hemos enviado un enlace de verificación a {email}. Haz clic para continuar.',
      oneStepAway: 'Estás a un paso de gestionar tu menú.',
      checkSpam: 'Revisa la carpeta de spam. ¿No lo recibiste?',
      backToSignIn: 'Volver a iniciar sesión',
      goToSignIn: 'Ir a iniciar sesión',
      signOut: 'Cerrar sesión',
      resend: 'Reenviar',
      sending: 'Enviando…',
      resendSuccessDefault: 'Correo de verificación enviado. Revisa tu bandeja.',
      resendSuccessGuestDefault: 'Si ese correo está registrado y sin verificar, hemos enviado un nuevo enlace.',
      emailRequired: 'Introduce tu correo electrónico.',
      emailInvalid: 'Introduce un correo electrónico válido.',
      verifying: 'Verificando tu correo…',
      verifyingNew: 'Verificando tu nuevo correo…',
      emailVerified: 'Correo verificado',
      emailUpdated: 'Correo actualizado',
      verificationFailed: 'Verificación fallida',
      signIn: 'Iniciar sesión',
      goToDashboard: 'Ir al panel',
      verifySuccessDefault: 'Ya puedes iniciar sesión.',
      verifyNewSuccessDefault: 'Tu correo se ha actualizado y verificado.',
      invalidLinkDefault: 'Enlace de verificación inválido o faltante. Usa el enlace del correo.',
      invalidExpiredDefault: 'Enlace inválido o caducado. Solicita un nuevo correo de verificación.',
      invalidExpiredNewDefault: 'Enlace inválido o caducado. Intenta cambiar el correo de nuevo desde Perfil y configuración.',
    },
    errors: {
      forbidden: 'No tiene permiso para hacer esto.',
      validation: 'Por favor revise su entrada.',
      pageNotFound: 'Página no encontrada',
      pageNotFoundDescription: 'La página que buscas no existe o ha sido movida.',
    },
    landing: {
      nav: {
        features: 'Características',
        solutions: 'Cómo funciona',
        pricing: 'Precios',
        resources: 'Recursos',
        getStarted: 'Crear cuenta gratuita',
        goToApp: 'Ir a la app',
      },
      hero: {
        badge: 'Tecnología para tu negocio, simplificada',
        titleBefore: 'Tu negocio, ',
        titleHighlight: 'en línea en 10 minutos.',
        subtitle: 'Pon tu negocio en línea hoy — Catálogo de productos, Horarios, Reservas y más. Empieza gratis. Sin conocimientos técnicos.',
        trustedBy: 'Gratis para siempre. No se requiere tarjeta.',
      },
      stats: {
        activeRestaurants: 'Restaurantes activos',
        ordersProcessed: 'Pedidos procesados',
        globalReach: 'Alcance global',
        countries: 'Países',
      },
      features: {
        eyebrow: 'QUÉ INCLUYE',
        title: 'Todo lo que tu negocio necesita en línea',
        subtitle: 'Desde tu catálogo hasta reservas y más — todo en un solo lugar, listo en minutos.',
        menu: { title: 'Catálogo', desc: 'Organiza productos, servicios o platos en categorías, precios y descripciones, y reordena con arrastrar y soltar.' },
        qr: { title: 'Código QR', desc: 'Tu negocio tiene un enlace y código QR estables que redirigen a los clientes a tu página pública.' },
        website: { title: 'Sitio de tu negocio', desc: 'Subdominio y página pública con catálogo, horarios, contacto y reservas.' },
        multilingual: { title: 'Varios idiomas', desc: 'Localiza moneda e idioma para que tu catálogo funcione para cada cliente.' },
        feedback: { title: 'Gestión de opiniones', desc: 'Los clientes dejan valoraciones y comentarios; tú apruebas y los muestras en tu sitio.' },
        booking: { title: 'Reservas', desc: 'Los clientes reservan citas, mesas o servicios directamente desde tu página pública.' },
      },
      pricing: {
        eyebrow: 'PRECIOS',
        title: 'Empieza gratis. Crece cuando quieras.',
        subtitle: 'Sin contratos. Sin cargos ocultos. Cancela cuando quieras.',
        startFree: 'Empezar gratis',
        getGrowth: 'Comenzar a crecer',
        getPro: 'Hazte Pro',
        contactUs: 'Hablemos',
        vipValueLine: 'Hecho a medida para tu negocio. Sin límites.',
        vipMysteryDesc: 'Para negocios que quieren la experiencia completa. Pedidos, marca blanca, soporte dedicado y más. Hablemos de lo que necesitas.',
        vipFeatureExtra: '+ Más funciones exclusivas bajo petición ✨',
        vipSmallText: 'Te responderemos en menos de 24 horas.',
        contactSales: 'Contactar ventas',
        perMonth: '/mes',
        starterTagline: 'Tu negocio, en línea gratis',
        starterTrustLine: 'No se requiere tarjeta. Nunca.',
        growthTagline: 'Todo lo que necesitas para conectar con tus clientes',
        growthTrustLine: 'Más visibilidad. Más clientes',
        proTagline: 'Gestiona las operaciones de tu negocio en línea',
        proTrustLine: 'Cancela cuando quieras. Sin preguntas.',
        vipTagline: 'Hecho a medida para tu negocio. Sin límites.',
        vipModal: {
          title: 'Contáctanos sobre VIP',
          name: 'Nombre',
          email: 'Correo electrónico',
          message: 'Mensaje',
          submit: 'Enviar',
          success: 'Gracias por tu interés. Nos pondremos en contacto pronto.',
          close: 'Cerrar',
          nameRequired: 'El nombre es obligatorio.',
          emailRequired: 'El correo electrónico es obligatorio.',
          emailInvalid: 'Introduce una dirección de correo válida.',
          messageRequired: 'El mensaje es obligatorio.',
          messageMaxLength: 'El mensaje debe tener como máximo {max} caracteres.',
        },
        mostPopular: 'Más popular',
        pricesInYourCurrency: 'Precios en tu moneda local.',
        retry: 'Reintentar',
        errorLoading: 'No pudimos cargar los precios en tu región.',
        usingUsdFallback: 'Mostrando precios en USD. Puedes reintentar abajo.',
        free: { name: 'Gratis', tagline: 'Para empezar', price: '$0' },
        growth: { name: 'Growth', tagline: 'Para marcas en crecimiento', price: '—' },
        pro: { name: 'Pro', tagline: 'Para marcas en crecimiento', price: '—' },
        enterprise: { name: 'Enterprise', tagline: 'Soluciones a medida', price: 'Personalizado' },
        features: {
          publicMenu: 'Catálogo público y subdominio',
          public_menu: 'Catálogo público y subdominio',
          subdomain: 'Catálogo público y subdominio',
          qrCode: 'Código QR y redirección',
          qr_codes: 'Código QR y redirección',
          feedback: 'Opiniones y valoraciones de clientes',
          openingHours: 'Horario',
          opening_hours: 'Horario',
          menuItemAvailability: 'Disponibilidad de catálogo e ítems',
          menu_item_availability: 'Disponibilidad de catálogo e ítems',
          simpleOrdering: 'Pedidos simples (carrito -> WhatsApp/SMS)',
          simple_ordering: 'Pedidos simples (carrito -> WhatsApp/SMS)',
          mobileAppOrdering: 'Pedidos con la app móvil',
          mobile_app_ordering: 'Pedidos con la app móvil',
          mobileAppUsage: 'Uso de la app móvil',
          mobile_app_usage: 'Uso de la app móvil',
          mobilePickup: 'Recogida en la app móvil',
          mobile_pickup: 'Recogida en la app móvil',
          tableReservation: 'Reservas y citas (con SMS)',
          mailingList: 'Lista de correo (emails a tus clientes)',
          siteAnnouncements: 'Anuncios en tu sitio',
          announcements: 'Anuncios en tu sitio',
          multipleRestaurants: 'Varios negocios',
          restaurants: 'Negocios',
          restaurants1: '1 negocio',
          restaurants3: 'Hasta 3 negocios',
          restaurantsUnlimited: 'Negocios ilimitados',
          noAds: 'Puede quitar la marca QRestoo',
          prioritySupport: 'Soporte prioritario',
          priority_support: 'Soporte prioritario',
          customIntegrations: 'Integraciones a medida',
          custom_integrations: 'Integraciones a medida',
          analytics: 'Analítica',
          siteContent: 'Página VIP: campos, galería y QR únicos (VIP)',
          orderAndPickup: 'Recogida en la app móvil',
        },
      },
      cta: {
        title: '¿Listo para tener tu negocio en línea?',
        subtitle: 'Únete a negocios que ya usan QRestoo para ser más visibles, servir mejor y crecer.',
        startTrial: 'Crear cuenta gratuita',
        bookDemo: 'Ver demo de {businessType}',
        bookDemoGeneric: 'Ver demo',
      },
      faq: {
        headline: '¿Preguntas? Tenemos respuestas.',
        q1: '¿El plan gratis es realmente gratis?',
        a1: 'Sí — completamente gratis, para siempre. Sin tarjeta, sin período de prueba, sin sorpresas. Tu negocio tendrá una página y código QR en vivo sin coste.',
        q2: '¿Puedo cancelar cuando quiera?',
        a2: 'Por supuesto. Sin contratos, sin gastos de cancelación. Cancela con un clic desde tu panel cuando quieras.',
        q3: '¿Qué pasa con mi página si cancelo?',
        a3: 'Si cancelas un plan de pago, pasas al plan Starter gratuito. Tu página sigue en vivo — nunca pierdes tu página de negocio.',
        q4: '¿Necesito conocimientos técnicos para configurarlo?',
        a4: 'Ninguno. Si sabes usar WhatsApp, sabes usar QRestoo. La mayoría de negocios están en vivo en menos de 10 minutos.',
        q5: '¿Qué incluye exactamente el plan VIP?',
        a5: 'Podríamos contártelo — pero ¿dónde estaría la gracia? VIP es nuestro plan más potente, adaptado a lo que necesitas. Pulsa «Hablemos» y te mostramos qué es posible. Puede que te sorprenda. 😉',
      },
      footer: {
        appName: 'QRestoo',
        copyrightHolder: 'DLR Web Solutions LLC',
        tagline: 'La forma más sencilla de tener tu negocio en línea.',
        poweredBy: 'Desarrollado por',
        poweredByLink: 'DLR Web Solutions',
        product: 'Producto',
        company: 'Empresa',
        support: 'Soporte',
        integrations: 'Integraciones',
        appStore: 'App Store',
        about: 'Nosotros',
        careers: 'Carreras',
        press: 'Prensa',
        blog: 'Blog',
        helpCenter: 'Centro de ayuda',
        contact: 'Contacto',
        contactModalTitle: 'Contáctanos',
        contactModalSuccess: 'Gracias. Te responderemos pronto.',
        apiDocs: 'API',
        legal: 'Legal',
        privacy: 'Política de privacidad',
        terms: 'Términos de servicio',
        cookies: 'Cookies',
      },
    },
    public: {
      about: 'Nosotros',
      contactUs: 'Contáctenos',
      reviewsAndFeedback: 'Opiniones y comentarios',
      t1NavMenu: 'Menú',
      reviewsNav: 'Opiniones',
      contactNav: 'Contacto',
      t2MenuNav: 'Menú',
      t2ViewServices: 'Servicios',
      t2ReviewsNav: 'Opiniones',
      t2ContactNav: 'Contacto',
      navMenu: 'Menú',
      navServices: 'Servicios',
      navProducts: 'Productos',
      ownerNavBookingUnavailable: 'Reservas. Actualización de cuenta necesaria.',
      ownerNavReviewsUnavailable: 'Opiniones. Actualización de cuenta necesaria.',
      ownerNavLocationUnavailable: 'Ubicación. Añade dirección o mapa en ajustes.',
      ownerNavAboutUnavailable: 'Nuestra historia. Añade descripción en ajustes.',
      ownerNavHoursUnavailable: 'Horario. Añade horario de apertura en ajustes.',
      t2CommunicationsLink: 'Enlace de comunicaciones',
      noLogoSetOwnerMessage: 'Sin logo. Añade uno en la configuración de tu negocio.',
      ownerEditLogoAria: 'Editar logo y banner',
      heroEstYear: 'Fund. {year}',
      noTaglineOrDescriptionSetOwnerMessage: 'Sin eslogan ni descripción. Añádelos en la configuración de tu negocio.',
      noDescriptionSetOwnerMessage: 'Sin descripción. Añade una en la configuración de tu negocio.',
      noMapSetOwnerMessage: 'Sin mapa. Añade dirección o enlace de mapa en la configuración de tu negocio.',
      noBookingAvailableOwnerMessage: 'Las reservas no están disponibles. Actívalas en la configuración de tu negocio.',
      noMailingListAvailableOwnerMessage: 'La lista de correo no está disponible. Actívala en la configuración de tu negocio.',
      noFeedbackAvailableOwnerMessage: 'Las reseñas y comentarios no están disponibles. Actívalos en la configuración de tu negocio.',
      t2NewsletterTitle: 'SUSCRIPCIÓN AL BOLETÍN',
      ourMenu: 'Nuestro menú',
      ourProducts: 'Productos',
      ourServices: 'Nuestros servicios',
      ourCatalog: 'Nuestro catálogo',
      viewMenu: 'Ver menú',
      viewCatalog: 'Ver catálogo',
      viewServices: 'Ver servicios',
      leaveFeedback: 'Deja tu opinión',
      ratingLabel: 'Valoración (1–5)',
      yourName: 'Tu nombre',
      yourMessage: 'Tu mensaje',
      sendFeedback: 'Enviar opinión',
      t2FeedbackPerformanceLogging: 'Registro de rendimiento',
      t2FeedbackOperationalLog: 'REGISTRO OPERATIVO',
      t2FeedbackTransmitCopy: 'Transmita su informe de experiencia para garantía de calidad y optimización.',
      t2FeedbackSessionMeta: 'Metadatos de sesión:',
      t2FeedbackTerminalId: 'ID de terminal: TGT-LOG-992',
      t2FeedbackProtocol: 'Protocolo: feedback_v2.0.sh',
      t2FeedbackRatingLabel: 'VALORACIÓN DE RENDIMIENTO',
      t2FeedbackRatingHint: 'Seleccione métricas de evaluación (1-5)',
      t2FeedbackSitrepLabel: 'INFORME / COMENTARIO DETALLADO',
      t2FeedbackPlaceholder: 'Introduzca los datos del registro aquí...',
      t2FeedbackSubmitReport: 'ENVIAR INFORME',
      t2FeedbackDisclaimer: 'Al enviar, acepta los protocolos de archivo de datos.',
      captchaRequired: 'Por favor, completa el captcha.',
      formErrorGeneric: 'Algo ha fallado. Por favor, inténtalo de nuevo.',
      noReviewsYet: 'Aún no hay opiniones. Sé el primero en dejar un comentario abajo.',
      guestExperiences: 'Experiencias de clientes',
      reviewsSubtitle: 'Lo que dicen nuestros comensales',
      announcementTitle: 'Aviso',
      announcementHideForFourDays: 'No mostrar de nuevo durante 4 días',
      close: 'Cerrar',
      mailingListHeading: 'Mantente informado',
      mailingListDescription: 'Suscríbete para recibir noticias y ofertas.',
      mailingListEmailLabel: 'Correo electrónico',
      mailingListEmailPlaceholder: "tu{'@'}ejemplo.com",
      mailingListSubscribe: 'Suscribirse',
      mailingListSuccess: 'Suscrito.',
      mailingListError: 'Algo salió mal. Inténtalo de nuevo.',
      mailingListEmailRequired: 'El correo es obligatorio.',
      mailingListEmailInvalid: 'Introduce una dirección de correo válida.',
      openingHours: 'Horario',
      noContactListed: 'No hay teléfonos ni enlaces listados.',
      whatsapp: 'WhatsApp',
      mobile: 'Móvil',
      email: 'Correo electrónico',
      phone: 'Teléfono',
      fax: 'Fax',
      other: 'Contacto',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      website: 'Sitio web',
      viewingAsOwner: 'Estás viendo esto como propietario.',
      viewAsCustomer: 'Ver como cliente',
      viewAsOwner: 'Ver como propietario',
      restaurantNotFoundTitle: 'Restaurante no encontrado',
      backToHome: 'Volver al inicio',
      openInMaps: 'Abrir en mapas',
      mapIframeTitle: 'Mapa de Google de esta ubicación',
      mapOpenExternallyHint:
        "Toca abajo para abrir esta ubicación en tu app de mapas. Para mostrar un mapa aquí, añade latitud y longitud o un enlace de Google Maps con pin ({'@'}lat,lng) en la configuración del negocio.",
      ownerNeedsDataIntro: 'Este sitio necesita más datos. Añade descripción, menú u otro contenido en el panel.',
      openAdminPage: 'Abrir panel de administración',
      addBannerInAdmin: 'Añade una imagen de portada en Ajustes',
      bookTable: 'Reservar mesa',
      bookAppointment: 'Reservar una cita',
      mapPlaceholder: 'Mapa',
      allRightsReserved: 'Todos los derechos reservados.',
      siteCreatedViaQRestoo: 'Sitio creado con QRestoo',
      createdByQRestoo: 'Creado en QRestoo',
      sitePoweredByDLR: 'Sitio impulsado por DLR Web Solutions',
      surpriseMe: 'Sorpréndeme',
      surpriseMeAria: 'Sorpréndeme – elegir un elemento al azar',
      noMenuItems: 'Sin elementos en el menú',
      noProducts: 'No hay productos',
      noServices: 'No hay servicios',
      noCatalogItems: 'Sin elementos en el catálogo',
      fromPrice: 'Desde {price}',
      notAvailable: 'No disponible',
      notAvailableWithNextTime: 'No disponible ahora. Próxima disponibilidad: {time}.',
      sizeAndPriceOptions: 'Tamaño y precios',
      option: 'Opción',
      comboContents: 'Contenido del combo',
      ownerEditMenuItemInCatalogAria: 'Editar elemento en el catálogo (abre el administrador)',
      closeMenu: 'Cerrar menú',
      closeCatalogModal: 'Cerrar catálogo',
      closeServicesModal: 'Cerrar servicios',
      ariaViewMenu: 'Ver menú',
      ariaViewCatalog: 'Ver catálogo',
      ariaViewServices: 'Ver servicios',
      modalTitleMenu: 'Menú',
      modalTitleCatalog: 'Catálogo',
      modalTitleProducts: 'Productos',
      modalTitleServices: 'Servicios',
      ariaLocation: 'Ubicación',
      ariaMap: 'Mapa',
      category: 'Categoría',
      mapAriaLabel: 'Mapa con la ubicación del restaurante',
      openNow: 'Abierto ahora',
      closedNow: 'Cerrado',
      businessHoursNotListed: 'Horario no publicado',
      open24_7: 'Abierto 24/7',
      daySunday: 'Domingo',
      dayMonday: 'Lunes',
      dayTuesday: 'Martes',
      dayWednesday: 'Miércoles',
      dayThursday: 'Jueves',
      dayFriday: 'Viernes',
      daySaturday: 'Sábado',
      t3NavMenu: 'Menú',
      t3NavHours: 'Horario',
      t3NavLocation: 'Ubicación',
      t3NavReviews: 'Opiniones',
      t3NavAbout: 'Nosotros',
      t3OurStory: 'Nuestra historia',
      t3ExquisiteSelections: 'Selecciones exquisitas',
      t3SeasonalMenu: 'El menú de temporada',
      t3GuestExperiences: 'Experiencias de clientes',
      t3VerifiedDiner: 'Cliente verificado',
      t3HoursAvailability: 'Horario y disponibilidad',
      t3QuickLinks: 'Enlaces rápidos',
      t3FooterTagline: 'Creando experiencias gastronómicas inolvidables.',
      t3FooterTaglineRestaurants: 'Creando experiencias gastronómicas inolvidables. Excelencia en cada plato.',
      t3FooterTaglineEcommerce: 'Productos de calidad, cuidadosamente seleccionados. Excelencia en cada pedido.',
      t3FooterTaglineServices: 'Servicio excepcional. Excelencia en cada detalle.',
      t3FooterContact: 'Contáctanos para reservas e información.',
      t3ShareExperience: 'Comparte tu experiencia',
      t3FeedbackHelps: 'Tu opinión nos ayuda a mejorar.',
      t3FeedbackPlaceholder: '¿Cómo fue tu velada con nosotros?',
      t3SubmitReview: 'Enviar reseña',
      t3JoinInnerCircle: 'Únete a nuestro círculo',
      t3JoinInnerCircleDesc: 'Recibe invitaciones a catas y lanzamientos de menú de temporada.',
      t3Join: 'Unirse',
      t5TableIcon: 'Icono',
      t5TableImage: 'Imagen',
      t5TableProduct: 'Producto',
      t5TablePrice: 'Precio',
      t5TableStatus: 'Estado',
      t5TableAction: 'Acción',
      t5ViewDetails: 'Ver detalles',
      t5InStock: 'En stock',
      selectVariantToAdd: 'Selecciona una variante para añadir al carrito',
      selectRequiredModifierChoice: 'Elige una opción en cada grupo obligatorio antes de añadir al carrito.',
      modifiersHeading: 'Complementos (opcional)',
      modifiersAria: 'Complementos opcionales',
      itemDetailDialogAria: 'Detalle del elemento del menú',
      itemUntitled: 'Sin título',
      itemNotAvailable: 'No disponible',
      variantOptionNotAvailable: 'No disponible',
      variantOptionsAria: 'Opciones y precios',
      variantImageAlt: 'Variante',
      priceFrom: 'Desde {price}',
      comboContentsAria: 'Contenido del combo',
      modifierUnnamed: 'Complemento',
      modifierNoExtraCost: 'Sin coste adicional',
      quickOrder: {
        addToCart: 'Añadir al carrito',
        updateCart: 'Actualizar carrito',
        yourOrder: 'Tu pedido',
        clearAll: 'Vaciar todo',
        name: 'Tu nombre',
        namePlaceholder: 'ej. Juan',
        notes: 'Notas',
        notesPlaceholder: 'ej. Salsa extra, sin cebolla...',
        sendViaEmail: 'Enviar por Email',
        sendViaWhatsApp: 'Enviar por WhatsApp',
        sendViaViber: 'Enviar por Viber',
        sendViaSMS: 'Enviar por SMS',
        orderSent: '¡Pedido enviado!',
        orderSentDesc: 'Tu pedido ha sido enviado al negocio.',
        allowPopups: 'Permite ventanas emergentes para este sitio para abrir WhatsApp o SMS. Revisa la configuración del navegador o del dispositivo (p. ej. el icono de la barra de direcciones) e inténtalo de nuevo.',
        allowPopupsHint: 'Permite ventanas emergentes para enviar por WhatsApp o SMS.',
        nameRequired: 'El nombre es obligatorio.',
        nameMaxLength: 'El nombre puede tener como máximo 100 caracteres.',
        notesMaxLength: 'Las notas pueden tener como máximo 500 caracteres.',
        notesRequired: 'Las notas son obligatorias.',
        cartEmpty: 'Tu carrito está vacío.',
        quantity: 'Cantidad',
        remove: 'Eliminar',
        itemAdded: '¡Añadido al carrito!',
        itemUpdated: '¡Carrito actualizado!',
        ariaClose: 'Cerrar',
        ariaDecrease: 'Disminuir cantidad',
        ariaIncrease: 'Aumentar cantidad',
        ariaRemoveItem: 'Eliminar artículo',
        fulfillment: 'Para llevar o en el local',
        fulfillmentPickup: 'Para llevar',
        fulfillmentDineIn: 'En el local',
        dineInReferenceLabel: 'Mesa / referencia (opcional)',
        dineInReferencePlaceholder: 'ej. Mesa 5, Terraza 2',
        checkout: 'Pagar',
        orderSummary: 'Resumen del pedido',
        confirmCheckout: 'Confirmar pedido',
        checkoutSubmitting: 'Enviando…',
        checkoutFailed: 'No se pudo enviar tu pedido. Inténtalo de nuevo.',
        subtotal: 'Subtotal',
        ariaCloseSummary: 'Cerrar resumen del pedido',
        mobile: 'Móvil',
        mobilePlaceholder: '+34600111222',
        mobileHint: 'Empieza con + y el prefijo del país (ej. +34 612 345 678). Te enviaremos un código por SMS.',
        mobileRequired: 'El móvil es obligatorio para recogida.',
        mobileInvalid: 'Introduce un número válido con prefijo (ej. +34 600 111 222).',
        verificationCode: 'Código de verificación',
        verificationCodePlaceholder: 'Código de 6 dígitos',
        verificationCodeInvalid: 'Introduce el código de 6 dígitos del SMS.',
        sendVerificationCode: 'Enviar código',
        pickupServiceFee: 'Cargo por recogida ({percent}%)',
        total: 'Total',
      },
      cookieConsent: {
        barTitle: 'Cookies y tecnologías similares',
        barBody:
          'Usamos funciones esenciales para el sitio. Con tu permiso añadimos analítica de primera parte y herramientas de terceros (p. ej. comprobaciones de seguridad o inicio de sesión). Para más detalles, abre la ',
        acceptAll: 'Aceptar todo',
        rejectNonEssential: 'Rechazar no esenciales',
        customize: 'Personalizar',
        modalTitle: 'Preferencias de cookies',
        necessaryTitle: 'Estrictamente necesarias',
        necessaryDesc: 'Necesarias para seguridad, inicio de sesión y funciones básicas. Siempre activas.',
        analyticsTitle: 'Analítica (primera parte)',
        analyticsDesc: 'Nos ayuda a mejorar las páginas públicas. Los datos se procesan en nuestros servidores.',
        thirdPartyTitle: 'Funciones de terceros',
        thirdPartyDesc: 'Carga herramientas como Google reCAPTCHA en ciertos formularios o al iniciar sesión.',
        save: 'Guardar preferencias',
        privacyLink: 'Política de privacidad',
      },
    },
    tour: {
      welcomeTitle: '¡Bienvenido!',
      welcomeDescription: 'Hagamos un breve recorrido por tu panel. Verás dónde gestionar empresas, catálogos y más.',
      step1Title: 'Área principal',
      step1Description: 'Tu contenido aparece aquí. Usa la barra lateral para cambiar entre Panel, Empresas y otras secciones.',
      step2Title: 'Navegación',
      step2Description: 'Usa esta barra lateral para ir al Panel, Empresas, Catálogo de elementos, Comentarios y Perfil.',
      step2DescriptionSuperadmin: 'Usa esta barra lateral para ir al Panel, Usuarios, Comentarios de propietarios, Empresas y más.',
      step3Title: 'Panel',
      step3Description: 'Tu base. Consulta un resumen de tus empresas, elementos del catálogo y comentarios de un vistazo.',
      step4Title: 'Empresas',
      step4Description: 'Gestiona tu(s) empresa(s). Añade nuevas, edita detalles y organiza el catálogo por empresa.',
      step5Title: 'Catálogo de elementos',
      step5Description: 'Crea y edita elementos (productos, servicios, combos). Añádelos a categorías dentro del catálogo de cada empresa.',
      step6Title: 'Comentarios',
      step6Description: 'Consulta y modera las opiniones de clientes de tus páginas públicas. Aprueba o rechaza para mostrarlas u ocultarlas.',
      step7Title: 'Solicitud de función',
      step7Description: 'Envía comentarios o solicitudes de función al equipo. También puedes ver aquí tus envíos anteriores.',
      step8Title: 'Perfil y configuración',
      step8Description: 'Actualiza tu nombre, correo, contraseña e idioma de la app.',
      next: 'Siguiente',
      prev: 'Anterior',
      done: 'Listo',
      close: 'Cerrar',
      skip: 'Omitir recorrido',
    },
    restaurantsTour: {
      step1Title: 'Lista de empresas',
      step1Description: 'Esta es tu lista de empresas. Añade nuevas, edita detalles y gestiona el catálogo desde aquí.',
      step2Title: 'Añadir empresa',
      step2Description: 'Haz clic aquí para crear una nueva empresa y configurar nombre, dirección y dirección web.',
      step3Title: 'Tus empresas',
      step3Description: 'Tus empresas aparecen como tarjetas. Haz clic en una para abrirla o usa el estado vacío para crear la primera.',
      step4Title: 'Tarjeta de empresa',
      step4Description: 'Haz clic en una empresa para editarla, gestionar el catálogo, disponibilidad y obtener el enlace público o código QR.',
      tooltip: 'Tour de empresas',
    },
    welcome: {
      title: 'Crea tu primer restaurante',
      subtitle: 'Pon tu menú en línea, comparte tu dirección y horarios, y deja que los clientes te encuentren en minutos.',
      cta: 'Crear mi primer restaurante',
    },
    wizard: {
      stepOf: 'Paso {current} de {total}',
      step1Title: 'Nombre de tu restaurante',
      nameLabel: 'Nombre del restaurante',
      taglineOptional: 'Eslogan (opcional)',
      slugPreview: 'Tu dirección web',
      templateLabel: 'Plantilla de página pública',
      templateHint: 'Elige el diseño de tu página pública.',
      next: 'Siguiente',
      back: 'Atrás',
      skipForNow: 'Omitir por ahora',
      doLater: 'Lo haré más tarde',
      step2Title: 'Ubicación y contacto (opcional)',
      youreAllSet: 'Todo listo',
      sublineLive: 'La página de tu restaurante está en línea. Comparte el enlace o el código QR para que los clientes te encuentren.',
      copyAddress: 'Copiar dirección',
      copied: '¡Copiado!',
      saveQr: 'Guardar código QR',
      scanHint: 'Los clientes escanean esto para abrir tu página',
      goToMyRestaurant: 'Ir a mi restaurante',
      backToDashboard: 'Volver al panel',
      firstTimeLink: '¿Primera vez? Crea tu primer restaurante',
    },
    reservation: {
      reserveButton: 'Reservar mesa',
      reserveButtonServices: 'Reservar',
      heading: 'Reservar mesa',
      headingServices: 'Reservar',
      pax: 'Número de comensales',
      paxServices: 'Tamaño del grupo',
      nameRequired: 'El nombre es obligatorio.',
      nameMax: 'El nombre debe tener como máximo 150 caracteres.',
      dateMinTwoDays: 'Por favor elige una fecha al menos 2 días a partir de hoy.',
      sendVerificationCode: 'Enviar código de verificación',
      enterCodePrompt: 'Introduce el código de 6 dígitos que te enviamos al teléfono.',
      verificationCode: 'Código de verificación',
      codePlaceholder: '000000',
      codeRequired: 'Introduce el código de verificación.',
      codeInvalid: 'Introduce exactamente 6 dígitos.',
      codeInvalidOrExpired: 'Código no válido o caducado. Solicita uno nuevo.',
      errorGeneric: 'Algo ha fallado. Por favor, inténtalo de nuevo.',
      requestNewCode: 'Solicitar nuevo código',
      confirmReservation: 'Confirmar reserva',
    },
    startTour: 'Iniciar tour de la app',
    startTourDescription: 'Vuelve a ejecutar el recorrido guiado para ver las áreas principales de la app.',
  },
  ar: {
    app: {
      name: 'RMS',
      dashboard: 'لوحة التحكم',
      login: 'تسجيل الدخول',
      register: 'إنشاء حساب',
      logout: 'تسجيل الخروج',
      profile: 'الملف والإعدادات',
      profileTab: 'الملف',
      restaurants: 'الشركات',
      menu: 'الكتالوج',
      menuItems: 'كتالوج العناصر',
      feedbacks: 'التقييمات',
      featureRequest: 'طلب ميزة',
      users: 'المستخدمون',
      ownerFeedbacks: 'تقييمات المالكين',
      inquiries: 'الاستفسارات',
      languages: 'اللغات والوصف',
      default: 'افتراضي',
      addLanguage: 'إضافة لغة',
      defaultLanguage: 'اللغة الافتراضية',
      languagesHint: 'أضف أو احذف اللغات. تُستخدم اللغة الافتراضية عندما لا يتم اختيار لغة في الموقع العام.',
      description: 'الوصف',
      save: 'حفظ',
      cancel: 'إلغاء',
      cropApply: 'تطبيق',
      cropTitle: 'اقتصاص الصورة',
      cropDescription: 'اضبط منطقة الاقتصاص ثم طبّق.',
      close: 'إغلاق',
      loading: 'جاري التحميل…',
      noContent: 'لا يوجد محتوى بعد.',
      remove: 'إزالة',
      add: 'إضافة',
      translate: 'ترجمة من الافتراضي',
      addItem: 'إضافة عنصر',
      editItem: 'تعديل عنصر',
      createItem: 'إضافة عنصر',
      languageLabel: 'اللغة',
      languageLabelTooltip: 'هذه اللغة لواجهة تطبيق الإدارة فقط، وليست لصفحاتك العامة أو كتالوجاتك.',
      menuItemsSubtitle: 'أضف العناصر وعدّلها. يمكنك إضافتها إلى فئات الكتالوج داخل كل شركة.',
      catalogNavInfoTooltip: 'حول الكتالوج',
      catalogNavInfoModalTitle: 'حول الكتالوج',
      catalogNavInfoModalDescription: 'علاقة عنصر «الكتالوج» في القائمة الجانبية بالشركات وعناصر الكتالوج.',
      catalogNavInfoModalP1: 'كل ما هنا يتعلق بعناصر الكتالوج—مكتبتك المشتركة للمنتجات والخدمات والحزم والمتغيرات.',
      catalogNavInfoModalP2: 'تُدار صور العناصر من هنا: افتح عنصر كتالوج لرفع الصور أو تغييرها (بما في ذلك صور المتغيرات عند الحاجة).',
      catalogNavInfoModalP3: 'أضف عناصر الكتالوج من صفحة الشركات: افتح شركة ثم استخدم تبويب الكتالوج لإنشاء العناصر وتنظيمها في فئات لموقعك العام.',
      catalogNavInfoGoToBusinesses: 'فتح الشركات',
      catalogNotAssignedBadge: 'غير مضاف إلى أي قائمة',
      catalogAssignedToPrefix: 'مُسند إلى:',
      catalogAssignedMoreCount: '+{count}',
      catalogListResultSummary: 'عرض {shown} من {total} عناصر',
      catalogListNoMatches: 'لا توجد عناصر تطابق البحث أو المرشحات.',
      catalogListLoadMore: 'تحميل المزيد',
      catalogFilterAllTypes: 'الكل',
      ariaCatalogTypeFilter: 'تصفية حسب نوع عنصر الكتالوج',
      catalogVariantsFromPrice: 'ابتداءً من {price}',
      addFirstMenuItem: 'أضف أول عنصر',
      menuItemsEmptyHint: 'أنشئ منتجات أو خدمات أو قوائم. أضفها إلى الفئات في تبويب الكتالوج لكل شركة.',
      deleteMenuItem: 'حذف عنصر',
      deleteMenuItemConfirm: 'لا يمكن التراجع عن ذلك. سيُزال العنصر من جميع الشركات التي تستخدمه.',
      menuItemFormSubtitleEdit: 'الاسم والوصف لكل لغة. اللغة الافتراضية مطلوبة على الأقل.',
      menuItemFormSubtitleEditSingle: 'حدّث الاسم والوصف الاختياري.',
      menuItemFormSubtitleCreate: 'أضف الاسم والوصف الاختياري. يمكنك إضافة لغات أخرى بعد إنشاء العنصر.',
      menuItemFormSubtitleCreateSingle: 'أضف الاسم والوصف الاختياري للعنصر.',
      menuItemCatalogPerBusinessHint:
        'أنت تعدّل عنصر الكتالوج المشترك. إذا كان هذا العنصر مخصصًا لكل شركة—السعر أو الوصف أو التوفر أو الظهور في القائمة العامة—افتح تلك الشركة في لوحة التحكم وعدّله من تبويب القائمة لتحديث الموقع الصحيح.',
      catalogSummaryTypePrefix: 'النوع:',
      catalogComboEntriesHint: 'المدخلات (عدّل أدناه للتغيير):',
      catalogComboPricePrefix: 'سعر الكومبو:',
      catalogOptionGroupsHeading: 'مجموعات الخيارات:',
      catalogVariantsHeadingPrice: 'المتغيرات (السعر):',
      catalogVariantsHeadingPriceImage: 'المتغيرات (السعر، الصورة):',
      itemTypeSimple: 'بسيط',
      itemTypeCombo: 'كومبو',
      itemTypeWithVariants: 'مع متغيرات',
      catalogItemTypeSimpleHint: 'عنصر واحد بسعر واحد',
      catalogItemTypeComboHint: 'حزمة من عناصر أخرى في الكتالوج',
      catalogItemTypeVariantsHint: 'خيارات (مثل الحجم أو النوع) مع سعر لكل تركيبة',
      ariaMenuItemTypeRadiogroup: 'نوع عنصر الكتالوج',
      catalogTypeSectionTitle: 'النوع',
      priceFieldLabel: 'السعر',
      priceOptionalLabel: 'السعر (اختياري)',
      comboPriceOptionalLabel: 'سعر الكومبو (اختياري)',
      placeholderPriceExample: 'مثال: 10.00',
      placeholderComboPriceExample: 'مثال: 12.00',
      nameFieldLabel: 'الاسم',
      descriptionOptionalLabel: 'الوصف (اختياري)',
      shortDescriptionPlaceholder: 'وصف قصير',
      catalogAddPageTitle: 'إضافة عنصر للكتالوج',
      catalogAddPageSubtitle: 'اختر النوع ثم أضف الاسم والتفاصيل حسب النوع.',
      assignCatalogPageTitle: 'إضافة العنصر إلى نشاط تجاري',
      assignCatalogPageSubtitle:
        'اختر النشاط والفئة. يبقى العنصر في كتالوجك؛ يظهر هذا على القائمة العامة لذلك النشاط.',
      assignCatalogSheetEyebrow: 'عنصر الكتالوج',
      assignCatalogSuccessEyebrow: 'تم',
      assignCatalogItemLabel: 'العنصر',
      assignCatalogChooseBusiness: 'النشاط التجاري',
      assignCatalogChooseBusinessPlaceholder: '— اختر نشاطًا —',
      assignCatalogFirstMenuHint: 'الفئات مأخوذة من أول قائمة للنشاط (حسب الترتيب).',
      assignCatalogCategoryModeLabel: 'الفئة',
      assignCatalogCategoryModeExisting: 'فئة موجودة',
      assignCatalogCategoryModeNew: 'فئة جديدة',
      assignCatalogSelectCategory: 'الفئة',
      assignCatalogSelectCategoryPlaceholder: '— اختر فئة —',
      assignCatalogNewCategoryName: 'اسم الفئة الجديدة',
      assignCatalogNewCategoryPlaceholder: 'مثال: المقبلات',
      assignCatalogAssignButton: 'إضافة إلى القائمة',
      assignCatalogSkip: 'تخطّ الآن',
      assignCatalogNoBusinesses: 'لا يوجد لديك نشاط بعد. أنشئ واحدًا من «الأنشطة» ثم عد إلى هنا.',
      assignCatalogGoToBusinesses: 'فتح الأنشطة',
      assignCatalogNoMenus: 'لا توجد قائمة لهذا النشاط بعد. افتح تبويب الكتالوج وأضف قائمة أولًا.',
      assignCatalogOpenCatalogTab: 'فتح تبويب الكتالوج',
      assignCatalogNoCategoriesHint: 'لا توجد فئات بعد—استخدم «فئة جديدة» أعلاه أو أضفها من كتالوج النشاط.',
      assignCatalogErrRestaurant: 'اختر نشاطًا تجاريًا.',
      assignCatalogErrCategory: 'اختر فئة.',
      assignCatalogErrNewCategoryName: 'أدخل اسمًا للفئة الجديدة.',
      assignCatalogNotFound: 'لم يُعثر على عنصر الكتالوج هذا.',
      assignCatalogNotStandalone: 'هذا العنصر ليس عنصر كتالوج فقط. عدّله من قائمة النشاط.',
      assignToBusiness: 'إضافة إلى نشاط',
      assignCatalogSuccessTitle: 'تمت الإضافة إلى قائمتك',
      assignCatalogSuccessBody: 'أنشئ عنصرًا آخر في الكتالوج أو افتح الفئة التي وضعت العنصر فيها.',
      assignCatalogCreateAnotherItem: 'إنشاء عنصر آخر',
      assignCatalogGoToCategory: 'الانتقال إلى الفئة المعيّنة',
      comboEntriesTitle: 'مدخلات الكومبو',
      addComboEntry: 'إضافة مدخل',
      entryNumber: 'المدخل {n}',
      ariaRemoveComboEntry: 'إزالة المدخل',
      selectCatalogItemPlaceholder: '— اختر عنصرًا —',
      selectVariantPlaceholder: '— اختر متغيرًا —',
      variantFieldLabel: 'المتغير',
      quantityFieldLabel: 'الكمية',
      modifierOptionalLabel: 'معدّل (اختياري)',
      placeholderModifierExample: 'مثال: بدون ثلج',
      optionGroupsTitle: 'مجموعات الخيارات',
      addOptionGroup: 'إضافة مجموعة',
      removeOptionGroupAria: 'إزالة المجموعة',
      catalogGroupNameLabel: 'اسم المجموعة',
      catalogGroupNamePlaceholder: 'مثال: الحجم',
      catalogGroupNameLabelWithHint: 'اسم المجموعة (مثال: {example})',
      exampleSize: 'الحجم',
      exampleType: 'النوع',
      optionValuesOnePerLine: 'القيم (سطر لكل قيمة أو مفصولة بفواصل)',
      optionValuesCommaSeparated: 'القيم (مفصولة بفواصل)',
      variantSizesPlaceholder: 'صغير، متوسط، كبير',
      combinationColumn: 'التركيبة',
      priceColumn: 'السعر',
      variantPricesTitle: 'أسعار المتغيرات',
      ariaSelectMenuItemForCombo: 'اختر عنصر الكتالوج',
      ariaSelectVariant: 'اختر المتغير',
      placeholderVariantPrice: '0.00',
      creatingItem: 'جاري الإنشاء…',
      createItemSubmit: 'إنشاء العنصر',
      menuItemDeletedToast: 'تم حذف العنصر.',
      menuItemCreatedToast: 'تم إنشاء العنصر.',
      menuItemUpdatedToast: 'تم تحديث العنصر.',
      restaurantCreatedToast: 'تم إنشاء الشركة.',
      restaurantUpdatedToast: 'تم تحديث الشركة.',
      selectLanguageForItemAria: 'اختر اللغة للاسم والوصف',
      translateFromDefaultAria: 'ترجمة من اللغة الافتراضية',
      descriptionInLanguagePlaceholder: 'الوصف بـ {lang}',
      restoreFromCatalogTitle: 'استعادة الاسم والوصف والسعر الأصليين من الكتالوج',
      saveChangesConfirmTitle: 'حفظ التغييرات؟',
      menuItemSaveSensitiveDescription: 'أكّد حفظ التغييرات التي تزيل المتغيرات أو مدخلات الكومبو، أو تُحدّث النص.',
      menuItemSaveSensitiveBody: 'أزلت متغيرات أو مدخلات كومبو، أو عدّلت الاسم أو الوصف. هل تريد حفظ هذه التغييرات؟',
      overridePriceLabel: 'سعر مخصص (اتركه فارغًا لاستخدام الأساس: {base})',
      sameAsBasePlaceholder: 'نفس الأساس',
      noCustomFieldsToReset: 'لا يوجد اسم أو سعر مخصص لإعادة التعيين',
      resettingShort: 'جاري إعادة التعيين…',
      resetToOriginal: 'استعادة الأصل',
      menuItemImageSectionTitle: 'الصورة',
      menuItemImageHint:
        'اختياري. JPEG أو PNG أو GIF أو WebP، بحد أقصى 2 ميجابايت. قص كما تريد؛ تُحفظ حتى 512 بكسل في أطول ضلع مع الحفاظ على النسبة.',
      menuItemBaseImageTitle: 'الصورة الرئيسية للمنتج',
      menuItemBaseImageHintVariants: 'تظهر في قائمتك العامة (بطاقة المنتج). صور الخيارات أدناه اختيارية لكل خيار.',
      ariaRemoveItemImage: 'إزالة الصورة',
      beautifyWithAi: 'تحسين بالذكاء الاصطناعي',
      variantPhotoLabel: 'صورة',
      variantGalleryLabel: 'معرض',
      variantChangeShort: 'تغيير',
      variantAddShort: 'إضافة',
      ariaPriceForVariant: 'السعر لـ {label}',
      genericVariant: 'متغير',
      validateNameRequired: 'الاسم مطلوب.',
      validateDefaultNameRequired: 'الاسم مطلوب في اللغة الافتراضية.',
      validatePriceNonNegative: 'يجب أن يكون السعر 0 أو أكبر.',
      validateComboPriceNonNegative: 'يجب أن يكون سعر الكومبو 0 أو أكبر.',
      validateComboEntriesMin: 'أضف مدخل كومبو واحدًا على الأقل.',
      validateSelectMenuItem: 'اختر عنصرًا من الكتالوج.',
      validateSelectVariant: 'اختر متغيرًا لهذا العنصر.',
      validateQuantityMin: 'يجب أن تكون الكمية 1 على الأقل.',
      validateOptionGroupsMin: 'أضف مجموعة خيارات واحدة على الأقل مع قيم.',
      validateVariantPricesAll: 'حدّد سعرًا لكل متغير.',
      validateModifierNameRequired: 'أدخل اسمًا لهذا المعدّل بلغة واحدة على الأقل.',
      validateModifierGroupNameRequired: 'أدخل اسمًا لمجموعة المعدّلات هذه.',
      validateModifierPriceDelta: 'يجب أن يكون سعر المعدّل 0 أو أكبر.',
      quickOrderNameMaxLength: 'يجب ألا يزيد الاسم عن 100 حرف.',
      quickOrderNotesMaxLength: 'يجب ألا تزيد الملاحظات عن 500 حرف.',
      menuItemModifiersSectionTitle: 'معدّلات (اختياري)',
      menuItemModifiersHint:
        'مجموعات اختيارية (مثل ترقيات أو أطباق جانبية). سمِّ كل مجموعة، واختر ما إذا كان الزبون يختار خيارًا واحدًا أو عدة خيارات، ثم أضف الخيارات بأسماء وأسعار إضافية. اسحب المقابض لإعادة ترتيب المجموعات والخيارات.',
      menuItemModifiersHintCreate:
        'مجموعات اختيارية. استخدم نفس لغة اسم العنصر. سمِّ كل مجموعة، واختر خيارًا واحدًا (زر اختيار) أو عدة خيارات (مربعات)، ثم أضف الخيارات والأسعار.',
      addModifier: 'إضافة معدّل',
      addModifierGroup: 'إضافة مجموعة',
      addModifierOption: 'إضافة خيار',
      removeModifierGroupAria: 'إزالة مجموعة المعدّلات',
      modifierGroupNameLabel: 'اسم المجموعة',
      modifierGroupSelectionTypeLabel: 'كيف يختار الزبائن',
      modifierSelectionSingle: 'خيار واحد فقط (زر اختيار)',
      modifierSelectionMultiple: 'أي مجموعة (مربعات اختيار)',
      modifierGroupOptionsHeading: 'الخيارات',
      modifierOptionNameLabel: 'اسم الخيار',
      catalogModifierGroupSingleHint: 'في موقعك العام سيختار الزبون خيارًا واحدًا على الأكثر من هذه المجموعة.',
      removeModifierAria: 'إزالة المعدّل',
      modifierPriceDeltaLabel: 'سعر إضافي',
      modifierActiveLabel: 'نشط (يظهر في الكتالوج)',
      modifierNameInLocale: 'الاسم ({locale})',
      catalogModifierVisibilityTitle: 'المعدّلات لهذا العنصر',
      catalogModifierVisibilityHint:
        'ألغِ التحديد لإخفاء معدّل لهذا العنصر فقط. يبقى في كتالوجك.',
      catalogModifierShowOnMenu: 'إظهار على الموقع العام',
      catalogVariantVisibilityTitle: 'المتغيرات لهذا العنصر',
      catalogVariantVisibilityHint:
        'أخفِ توليفة متغيرات من موقعك العام، أو اتركها ظاهرة وعلّمها كغير متاحة للطلب. تعريفات الكتالوج لا تتغير.',
      catalogVariantShowOnMenu: 'إظهار على الموقع العام',
      catalogVariantAvailableForOrder: 'متاح للطلب',
      fixErrorsBelow: 'يرجى تصحيح الأخطاء أدناه.',
      checkFieldsMarkedRed: 'تحقق من كل حقل محدد باللون الأحمر.',
      alsoFixErrorsInFormBelow: 'يرجى أيضًا تصحيح أخطاء النموذج أدناه.',
      phoneAllowedCharsOnly: 'استخدم الأرقام و + - ( ) والمسافات فقط.',
      phoneMinDigits: 'أدخل رقم هاتف صالحًا (6 أرقام على الأقل).',
      restaurantNameRequired: 'اسم النشاط مطلوب.',
      slugRequired: 'الرابط الفرعي / الـ slug مطلوب.',
      slugFormatInvalid: 'يجب أن يحتوي الـ slug على حروف صغيرة وأرقام وشرطات فقط (مثال: my-business).',
      slugReserved: 'هذا الـ slug محجوز (مثال: خادم التطوير) ولا يمكن استخدامه. اختر آخر.',
      customDomainInvalid: 'أدخل اسم نطاق صالحًا (مثل yourrestaurant.com).',
      smsRequiredForQuickOrdering: 'رقم SMS مطلوب للطلبات السريعة.',
      enterValidEmail: 'يرجى إدخال بريد إلكتروني صالح.',
      enterValidUrl: 'يرجى إدخال رابط صالح.',
      enterValidYear: 'يرجى إدخال سنة صالحة (مثل 1995).',
      fixScheduleForDays: 'يرجى تصحيح جدول الأيام التالية: {days}.',
      somethingWentWrongTryAgain: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
      copyToClipboardFailed: 'تعذر النسخ إلى الحافظة.',
      generateQrFailed: 'فشل إنشاء رمز QR.',
      nameProducesReservedSlug: 'هذا الاسم يُنتج slug محجوزًا. جرّب إضافة كلمة أو رقم.',
      restoredToOriginalToast: 'تمت الاستعادة إلى الأصل.',
      imageRemovedToast: 'تمت إزالة الصورة.',
      imageUpdatedToast: 'تم تحديث الصورة.',
      uploadImageFirstToast: 'ارفع صورة أولاً.',
      imageBeautifiedFallback: 'تم تحسين الصورة.',
      fillDefaultLanguageFirstToast: 'أكمل الاسم أو الوصف باللغة الافتراضية أولاً.',
      translationNotAvailableToast: 'الترجمة غير متاحة لهذه اللغة. يُعرض النص الأصلي — يمكنك تعديله.',
      translationAppliedToast: 'تم تطبيق الترجمة. راجعها واحفظ.',
      catalogComboEntryUnknownItem: 'عنصر {id}…',
      menuItemNamePlaceholder: 'مثال: بيتزا مارغريتا',
      catalogItemFieldLabel: 'عنصر الكتالوج',
      delete: 'حذف',
      deleting: 'جاري الحذف…',
      dashboardSubtitle: 'إدارة ومتابعة مطعمك من مكان واحد.',
      dashboardSuperadminSubtitle: 'نظرة عامة على النظام.',
      dashboardWelcome: 'مرحباً،',
      dashboardSignedIn: 'تم تسجيل الدخول كـ {email}. استخدم الملف والإعدادات لتحديث حسابك أو كلمة المرور.',
      dashboardSuperadminHint: 'استخدم المستخدمين أو المطاعم لإدارة النظام.',
      totalOrders: 'إجمالي الطلبات',
      revenue: 'الإيرادات',
      paidUsers: 'المستخدمون المدفوعون',
      locations: 'الشركات',
      manageRestaurants: 'إدارة الشركات',
      yourRestaurants: 'مواقعك',
      restaurantsListSubtitle: 'إدارة الكتالوج والتفاصيل لكل شركة.',
      restaurantsListSubtitleEmbed: 'التبديل إلى شركة أخرى أو إضافة جديدة.',
      restaurantsPaginationPageOf: 'الصفحة {current} من {total}',
      restaurantsCountLabel: '{count} {count, plural, one {شركة} other {شركات}}',
      addRestaurant: 'إضافة شركة',
      addFirstLocation: 'أضف شركتك الأولى',
      addFirstLocationHint: 'أنشئ مطعماً لوضع قائمتك على الإنترنت ومشاركة العنوان والمواعيد وتمكين العملاء من إيجادك.',
      firstTimeLink: 'أول مرة؟ أنشئ مطعمك الأول',
      createRestaurant: 'إنشاء شركة',
      welcomeTitle: 'أنشئ مطعمك الأول',
      welcomeSubtitle: 'انشر قائمتك على الإنترنت، شارك عنوانك وساعات العمل، ودع العملاء يجدونك خلال دقائق.',
      welcomeCta: 'إنشاء مطعمي الأول',
      wizardStepOf: 'الخطوة {current} من {total}',
      wizardBusinessTypeStepHint: 'اختر نوع نشاطك أولاً. سنحدد تلقائيًا أفضل قالب للبدء.',
      wizardStep1Title: 'التفاصيل الأساسية',
      wizardNameLabel: 'اسم المطعم',
      wizardTaglineOptional: 'شعار (اختياري)',
      wizardSlugPreview: 'عنوانك على الويب',
      wizardTemplateLabel: 'قالب الصفحة العامة',
      wizardTemplateHint: 'اختر التصميم لصفحتك العامة.',
      wizardTemplateAutoSelectedHint: 'تم اختيار القالب تلقائيًا حسب نوع نشاطك',
      wizardDefaultLanguageLabel: 'اللغة الافتراضية لموقعك العام',
      wizardDefaultLanguageHint:
        'نقترح الإسبانية إذا بدا أن جهازك في إسبانيا (المنطقة الزمنية أو إعدادات إسبانيا)، وإلا الإنجليزية. يمكنك تغيير ذلك لاحقًا من إعدادات المطعم.',
      wizardDefaultLanguageInvalid: 'اختر الإنجليزية أو الإسبانية.',
      contactAndLinks: 'جهات الاتصال والروابط',
      contactQuickOrderLabel: 'جهة اتصال الطلبات السريعة',
      smsNumberLabel: 'رقم الرسائل القصيرة (مطلوب)',
      smsNumberPlaceholder: 'مثال: ٩٦٥١٢٣٤٥٦٧٨ (للطلبات السريعة)',
      whatsappOptionalLabel: 'واتساب (اختياري)',
      whatsappOptionalPlaceholder: 'مثال: ٩٦٥١٢٣٤٥٦٧٨',
      viberOptionalLabel: 'فايبر (اختياري)',
      viberOptionalPlaceholder: 'مثال: ٩٦٥١٢٣٤٥٦٧٨',
      contactQuickOrderNote: 'ستُستخدم هذه الأرقام للطلبات السريعة على الموقع العام للمطعم.',
      contactWhatsAppViberSuggested: 'واتساب وفايبر اختياريان لكن يُنصح بهما حتى يتمكن العملاء من إرسال طلباتهم.',
      restaurantEditPhoneLabel: 'رسائل قصيرة / هاتف النشاط',
      restaurantEditPhoneHint:
        'يظهر في موقعك العام ويُستخدم لرسائل الطلب السريع عند تفعيل الميزة. أضف واتساب أو فايبر أو أرقامًا إضافية من جهات الاتصال والروابط.',
      wizardNext: 'التالي',
      wizardBack: 'رجوع',
      wizardSkipForNow: 'تخطي الآن',
      wizardDoLater: 'سأفعل ذلك لاحقاً',
      wizardStep2Title: 'الموقع والاتصال (اختياري)',
      wizardYoureAllSet: 'تم بنجاح',
      wizardSublineLive: 'صفحة مطعمك ناشرة. شارك الرابط أو رمز QR ليصل العملاء إليك.',
      wizardCopyAddress: 'نسخ العنوان',
      wizardCopied: 'تم النسخ!',
      wizardSaveQr: 'حفظ رمز QR',
      wizardScanHint: 'العملاء يمسحون هذا لفتح صفحتك',
      wizardGoToMyRestaurant: 'الذهاب إلى مطعمي',
      wizardBackToDashboard: 'العودة للوحة التحكم',
      restaurantNotFound: 'الشركة غير موجودة.',
      locationOrItemNotFound: 'الشركة أو العنصر غير موجود.',
      feedbacksSubtitle: 'عرض ومراجعة تعليقات العملاء لكل مطعم.',
      feedbacksTotal: 'التعليقات (الإجمالي)',
      feedbacksApproved: 'معتمدة',
      feedbacksRejected: 'مرفوضة',
      feedbacksCreateFirst: 'أنشئ مطعماً أولاً لاستقبال وإدارة التعليقات.',
      feedbacksListSubtitle: 'وافق على التعليق ليعرض في صفحتك العامة. ارفض أو احذف لإخفائه أو إزالته.',
      feedbacksListEmpty: 'لا توجد تعليقات بعد. ستظهر هنا عندما يرسل العملاء التقييمات.',
      feedbacksLoadError: 'فشل تحميل التعليقات.',
      retry: 'إعادة المحاولة',
      redirectingToFeedbacks: 'جاري التوجيه إلى التعليقات…',
      viewModerateFeedbacks: 'عرض ومراجعة التعليقات',
      viewAll: 'عرض الكل',
      featureRequestSubtitle: 'أرسل طلب ميزة أو تعليقات إلى الفريق. نراجع كل الطلبات.',
      sendFeedbackHeading: 'إرسال تعليقات',
      myFeatureRequests: 'طلبات الميزات الخاصة بي',
      yourRecentSubmissions: 'إرسالاتك الأخيرة (الأحدث أولاً).',
      noSubmissionsYet: 'لا توجد إرسالات بعد. استخدم النموذج أعلاه لإرسال أول طلب ميزة.',
      sending: 'جاري الإرسال…',
      messageLabel: 'الرسالة',
      messagePlaceholder: 'صف طلب الميزة أو تعليقك…',
      messageRequired: 'الرسالة مطلوبة.',
      messageMaxLength: 'يجب ألا تتجاوز الرسالة {max} حرفاً.',
      titleOptional: 'العنوان (اختياري)',
      titlePlaceholder: 'ملخص قصير',
      titleMaxLength: 'يجب ألا يتجاوز العنوان {max} حرفاً.',
      restaurantOptional: 'المطعم (اختياري)',
      noneOption: '— لا شيء —',
      maxCharactersHint: 'الحد الأقصى 65,535 حرفاً.',
      reviewed: 'تمت المراجعة',
      pending: 'قيد الانتظار',
      emailAddress: 'البريد الإلكتروني',
      emailPlaceholder: "you{'@'}example.com",
      passwordPlaceholder: '••••••••',
      fullName: 'الاسم الكامل',
      fullNamePlaceholder: 'فاطمة أحمد',
      repeatPassword: 'تأكيد كلمة المرور',
      passwordHintPlaceholder: '8 أحرف على الأقل',
      restaurantName: 'اسم النشاط',
      restaurantNamePlaceholder: 'مثال: ماما فينا، صالون جوز، متجر تك',
      taglineOptional: 'الشعار (اختياري)',
      taglinePlaceholder: 'مثال: مكونات طازجة ونكهات قوية',
      addressPlaceholder: 'الشارع، المدينة، الولاية',
      descriptionPlaceholder: 'وصف قصير لمطعمك للموقع العام',
      emailOptional: 'البريد (اختياري)',
      contactEmailPlaceholder: "contact{'@'}restaurant.com",
      websiteOptional: 'الموقع (اختياري)',
      websitePlaceholder: 'https://example.com',
      yearEstablishedOptional: 'سنة التأسيس (اختياري)',
      yearPlaceholder: 'مثال: 1995',
      categoryName: 'اسم الفئة',
      categoryNamePlaceholder: 'مثال: الأكثر مبيعاً، شعبي',
      filterByNamePlaceholder: 'تصفية بالاسم…',
      shortDescription: 'وصف قصير',
      shortDescriptionPlaceholder: 'وصف قصير لهذه الفئة',
      menuNamePlaceholder: 'مثال: قائمة الغداء، المشروبات',
      saveChanges: 'حفظ التغييرات',
      createRestaurantBtn: 'إنشاء شركة',
      editRestaurant: 'تعديل الشركة',
      addNewRestaurant: 'إضافة مطعم جديد',
      saving: 'جاري الحفظ…',
      signIn: 'تسجيل الدخول',
      signingIn: 'جاري تسجيل الدخول…',
      createAccount: 'إنشاء حساب',
      creatingAccount: 'جاري إنشاء الحساب…',
      resendLink: 'إعادة إرسال الرابط',
      resendIn: 'إعادة الإرسال خلال {seconds}ث',
      sendingResend: 'جاري الإرسال…',
      resetPassword: 'إعادة تعيين كلمة المرور',
      resetting: 'جاري إعادة التعيين…',
      saveProfile: 'حفظ الملف',
      savingProfile: 'جاري حفظ الملف…',
      currentPassword: 'كلمة المرور الحالية',
      newPassword: 'كلمة المرور الجديدة',
      updatePassword: 'تحديث كلمة المرور',
      updatingPassword: 'جاري تحديث كلمة المرور…',
      ownerOnlyMessage: 'هذه الرسالة تظهر لك أنت المالك فقط.',
      ownerNotice: 'إشعار للمالك',
      adminLinkUnavailable: 'رابط لوحة التحكم غير متاح. سجّل الدخول من التطبيق الرئيسي.',
      feedbackSubmitted: 'تم إرسال التعليقات.',
      password: 'كلمة المرور',
      confirmPassword: 'تأكيد كلمة المرور',
      forgotPassword: 'نسيت كلمة المرور؟',
      rememberMe: 'تذكرني 30 يوماً',
      continue: 'متابعة',
      back: 'رجوع',
      minimumCharacters: '8 أحرف على الأقل',
      passwordsDontMatch: 'كلمتا المرور غير متطابقتين',
      accessDashboard: 'ادخل إلى لوحة مطعمك',
      startManaging: 'ابدأ إدارة مطعمك مجاناً',
      orContinueWith: 'أو المتابعة باستخدام',
      orSignUpWithEmail: 'أو التسجيل بالبريد',
      continueWithGoogle: 'المتابعة مع Google',
      continueWithGoogleConfigure: 'المتابعة مع Google (تكوين معرف العميل)',
      continueWithFacebook: 'المتابعة مع فيسبوك',
      continueWithFacebookConfigure: 'المتابعة مع فيسبوك (الإعداد في .env)',
      continueWithInstagram: 'المتابعة مع إنستغرام',
      signInFailedTryAgain: 'تعذر تسجيل الدخول. حاول مرة أخرى.',
      googleSignInCancelledOrFailed: 'تم إلغاء تسجيل الدخول عبر Google أو لم يكتمل. حاول مرة أخرى أو استخدم البريد وكلمة المرور.',
      googleSignInGenericFailure: 'لم يكتمل تسجيل الدخول عبر Google. حاول مرة أخرى أو استخدم البريد وكلمة المرور.',
      googleSignInBackendFailed: 'تعذر إكمال تسجيل الدخول عبر Google. حاول مرة أخرى أو استخدم البريد وكلمة المرور.',
      googleSignInScriptLoadFailed: 'تعذر تحميل تسجيل الدخول عبر Google (الشبكة أو مانع الإعلانات). تحقق من الاتصال، اسمح بـ accounts.google.com، وحدّث الصفحة.',
      googleSignInNotReady: 'ما زال تسجيل الدخول عبر Google يُحمّل. انتظر قليلاً وحاول مرة أخرى.',
      googleSignInInitFailed: 'تعذر بدء تسجيل الدخول عبر Google في هذه الصفحة. حدّث الصفحة وحاول مرة أخرى.',
      googleSignInCredentialError: 'خطأ في تسجيل الدخول عبر Google: {detail}',
      googleSignInNoSession: 'لم يُعثر على جلسة Google أو تم حظر تسجيل الدخول. سجّل الدخول إلى Google في هذا المتصفح، اسمح بملفات تعريف الارتباط لجهات خارجية إن طُلب ذلك، أو استخدم البريد وكلمة المرور.',
      googleSignInSuppressedByUser: 'تم إغلاق تسجيل الدخول عبر Google مؤخراً. حاول لاحقاً أو استخدم البريد وكلمة المرور.',
      googleSignInUnregisteredOrigin: 'عنوان هذا الموقع غير مسموح لتسجيل الدخول عبر Google. يجب على المالك إضافة هذا الأصل في Google Cloud Console (عميل OAuth).',
      googleSignInSecureHttpRequired: 'يتطلب Google اتصالاً آمناً (HTTPS). افتح الموقع عبر HTTPS.',
      googleSignInBrowserNotSupported: 'هذا المتصفح لا يدعم تسجيل الدخول عبر Google هنا. جرّب متصفحاً آخر أو استخدم البريد وكلمة المرور.',
      googleSignInInvalidClient: 'إعداد Google غير صالح (عميل غير صالح). تواصل مع الدعم.',
      googleSignInMissingClientId: 'إعداد Google غير مكتمل (معرّف العميل مفقود). تواصل مع الدعم.',
      googleSignInUnknownReason: 'تعذر عرض تسجيل الدخول عبر Google. حاول مرة أخرى أو استخدم البريد وكلمة المرور.',
      googleSignInSkippedUserCancel: 'أُغلق تسجيل الدخول عبر Google قبل الإكمال. حاول مرة أخرى أو استخدم البريد وكلمة المرور.',
      googleSignInSkippedTapOutside: 'أُغلق تسجيل الدخول عبر Google. حاول مرة أخرى أو استخدم البريد وكلمة المرور.',
      googleSignInSkippedIssuingFailed: 'تعذر على Google إصدار رمز تسجيل الدخول. حاول مرة أخرى أو استخدم البريد وكلمة المرور.',
      googleSignInDismissedCancel: 'أُلغي تسجيل الدخول عبر Google. حاول مرة أخرى أو استخدم البريد وكلمة المرور.',
      googleSignInPromptNoResponse:
        'لم يُفتح تسجيل الدخول عبر Google. قد يمنعه مانع النوافذ المنبثقة أو إضافة في المتصفح. اسمح بالنوافذ المنبثقة لهذا الموقع، حدّث الصفحة، أو استخدم البريد وكلمة المرور أدناه.',
      googleSignInHintShort:
        'قد تُفتح نافذة تسجيل دخول Google. إذا لم يظهر شيء، اسمح بالنوافذ المنبثقة لهذا الموقع. يمكنك أيضاً استخدام البريد أدناه.',
      step2Of2: 'الخطوة 2 من 2',
      alreadyHaveAccount: 'لديك حساب بالفعل؟',
      showPassword: 'إظهار كلمة المرور',
      hidePassword: 'إخفاء كلمة المرور',
      agreeTermsIntro: 'أوافق على',
      agreeTermsRequired: 'يرجى الموافقة على شروط الخدمة وسياسة الخصوصية.',
      and: 'و',
      termsOfService: 'شروط الخدمة',
      privacyPolicy: 'سياسة الخصوصية',
      legalContent: 'الشروط والخصوصية',
      legalContentSubtitle: 'تحرير شروط الخدمة وسياسة الخصوصية. تظهر في نافذة منبثقة عند النقر على الروابط في صفحة التسجيل.',
      localeEn: 'الإنجليزية',
      localeEs: 'الإسبانية',
      localeAr: 'العربية',
      legalHtmlHint: 'يدعم HTML (مثل <p>, <strong>, <ul>, <li>, <a>).',
      termsPlaceholder: 'أدخل محتوى شروط الخدمة…',
      privacyPlaceholder: 'أدخل محتوى سياسة الخصوصية…',
      errorLoading: 'فشل التحميل.',
      errorSaving: 'فشل الحفظ.',
      legalContentUpdated: 'تم تحديث المحتوى القانوني.',
      siteSettings: 'إعدادات الموقع',
      siteSettingsSubtitle: 'خيارات عامة لطلبات الضيوف السريعة (نسبة رسوم الاستلام).',
      pickupAdditionalCostPercent: 'تكلفة إضافية للاستلام (%)',
      pickupAdditionalCostHint: 'تُضاف إلى مجمل السلة عند اختيار الاستلام. يُحسب المجموع دائماً على الخادم.',
      siteSettingsUpdated: 'تم حفظ إعدادات الموقع.',
      saveSiteSettings: 'حفظ الإعدادات',
      siteSettingsLoadFailed: 'تعذر تحميل إعدادات الموقع.',
      siteSettingsSaveFailed: 'تعذر حفظ الإعدادات.',
      pickupPercentInvalid: 'أدخل نسبة بين 0 و 100.',
      basicInformation: 'المعلومات الأساسية',
      businessTimezone: 'المنطقة الزمنية للنشاط',
      businessTimezoneHint: 'تُستخدم لفترات التحليلات والطوابع الزمنية في الطلبات السريعة والبريد. تُهيأ افتراضياً من منطقة متصفحك عند إنشاء النشاط؛ غيّرها إذا كان مكانك في منطقة أخرى.',
      businessTimezoneClear: 'غير محدد (الخادم يستخدم UTC)',
      businessTimezoneUpdated: 'تم تحديث المنطقة الزمنية.',
      settingsStoreBasicsSaved: 'تم حفظ الأساسيات.',
      businessTypeLabel: 'نوع النشاط',
      businessTypeRestaurants: 'مطعم',
      businessTypeEcommerce: 'تجارة إلكترونية',
      businessTypeServices: 'خدمة',
      businessTypeRequired: 'يرجى اختيار نوع النشاط.',
      selectBusinessTypePlaceholder: 'اختر نوع النشاط',
      settingsPageSubtitle: 'نوع النشاط واللغة والعملة والوصف حسب اللغة.',
      settingsGroupBasicsTitle: 'أساسيات النشاط',
      settingsGroupBasicsHint: 'نوع النشاط والمنطقة الزمنية للتحليلات وطوابع الطلبات.',
      settingsGroupPricingTitle: 'التسعير والمظهر',
      settingsGroupPricingHint: 'العملة على موقعك العام ولون التمييز اختياري.',
      settingsGroupContentTitle: 'اللغات والأوصاف',
      settingsGroupContentHint: 'لغات الزوار والوصف المعروض لكل لغة.',
      settingsGroupLayoutTitle: 'تخطيط الصفحة العامة',
      settingsGroupLayoutHint: 'اختر شكل وهيكل موقعك العام.',
      settingsGroupVisitorTitle: 'الموقع والطلبات',
      settingsGroupVisitorHint: 'اعتمادات التذييل وكيفية طلب الضيوف للطلب السريع.',
      settingsBusinessTypeHint: 'يتحكم في تسميات الأقسام في موقعك العام (مثل القائمة، المنتجات، الخدمات).',
      currencySectionTitle: 'العملة',
      currencySectionHint: 'العملة المستخدمة لأسعار قائمتك وموقعك العام.',
      currencyUpdated: 'تم تحديث العملة.',
      primaryColorSectionTitle: 'اللون الرئيسي',
      primaryColorSectionHint: 'لون التمييز المستخدم في صفحتك العامة (الأزرار والروابط والعناصر البارزة). اتركه فارغاً لاستخدام اللون الافتراضي.',
      primaryColorPickAria: 'اختر اللون الرئيسي',
      hexLabel: 'Hex',
      defaultLanguageUpdated: 'تم تحديث اللغة الافتراضية.',
      setAsDefault: 'تعيين كافتراضي',
      adding: 'جاري الإضافة…',
      showAllLanguagesTotal: 'عرض كل اللغات (المجموع {total})',
      showLess: 'عرض أقل',
      descriptionByLanguageTitle: 'الوصف حسب اللغة',
      descriptionByLanguageHint: 'وصف اختياري يظهر لكل لغة في الموقع العام.',
      editDescriptionForLanguageAria: 'تحرير الوصف لهذه اللغة',
      descriptionPlaceholderForLanguage: 'الوصف ({locale})',
      translatingDescriptionAria: 'جاري ترجمة الوصف من اللغة الافتراضية',
      publicPageTemplateTitle: 'قالب الصفحة العامة',
      publicPageTemplateHint: 'اختر تصميم صفحتك العامة. يؤثر هذا على كيفية عرض قائمتك ومعلوماتك للزوار.',
      removeLanguageTitle: 'إزالة اللغة؟',
      removeLanguageBody: 'سيتم حذف {locale}. وسيتم حذف وصف هذه اللغة أيضاً.',
      languageAdded: 'تمت إضافة {locale}.',
      languageRemoved: 'تمت إزالة اللغة.',
      descriptionSaved: 'تم حفظ الوصف.',
      chooseValidTemplate: 'يرجى اختيار قالب صالح.',
      footerCreditsHidden: 'تم إخفاء اعتمادات التذييل.',
      footerCreditsVisible: 'اعتمادات التذييل ظاهرة في موقعك العام.',
      footerCreditsPaidOnly: 'يمكن إخفاء اعتمادات التذييل في الخطة المدفوعة فقط.',
      quickOrderEnabledToast: 'تم تشغيل الطلب السريع.',
      quickOrderDisabledToast: 'تم إيقاف الطلب السريع.',
      quickOrderViaAppEnabledToast: 'تم تشغيل الطلب عبر التطبيق.',
      quickOrderViaAppDisabledToast: 'تم إيقاف الطلب عبر التطبيق.',
      defaultDescriptionEmpty: 'الوصف الافتراضي فارغ. أضف وصفاً للغة الافتراضية أولاً.',
      translationFallbackInfo: 'الترجمة غير متاحة لهذه اللغة. تم عرض النص الأصلي ويمكنك تعديله.',
      businessTypeUpdated: 'تم تحديث نوع النشاط.',
      formSubtitleEdit: 'تحديث التفاصيل والوسائط.',
      formSubtitleCreate: 'أدخل الأساسيات لإدراج مطعمك. يمكنك ضبط الساعات والاتصال لاحقاً.',
      slugHint: 'سيتم إنشاء عنوان المطعم على الويب تلقائياً من هذا الاسم.',
      slugLabel: 'عنوان صفحة القائمة على الويب',
      slugPlaceholder: 'مثال: مطعمي',
      slugHintEdit: 'تغيير الـ slug يغيّر فقط رابط النطاق الفرعي. رموز QR والروابط الحالية تستخدم معرف المطعم وستبقى تعمل. النطاق الفرعي: {subdomain}',
      customDomainLabel: 'النطاق المخصص (VIP)',
      customDomainPlaceholder: 'مثال: menu.example.com',
      customDomainHint: 'عند التعيين، مسح QR ورابطك العام يستخدمان هذا النطاق. موقعك على هذا النطاق يمكنه استدعاء واجهة API (CORS مسموح). اتركه فارغاً لاستخدام النطاق الفرعي.',
      customDomainVipOnly: 'متاح على خطة VIP فقط. ترقية لتعيين نطاق مخصص.',
      descriptionHint: 'يظهر في صفحتك العامة. أضف لغاتاً أخرى في الإعدادات.',
      addressOptional: 'العنوان (اختياري)',
      descriptionOptional: 'الوصف (اختياري)',
      profileSubtitle: 'تحديث تفاصيل حسابك وكلمة المرور.',
      profileSection: 'الملف الشخصي',
      changePassword: 'تغيير كلمة المرور',
      socialSignInPasswordInfo:
        'هذا الحساب يستخدم تسجيل الدخول الاجتماعي (Google أو Facebook أو Instagram). لا يظهر تغيير كلمة المرور هنا. لإضافة كلمة مرور لتسجيل الدخول بالبريد، استخدم «نسيت كلمة المرور» في صفحة تسجيل الدخول.',
      name: 'الاسم',
      forgotCheckEmail: 'تحقق من بريدك للحصول على رابط إعادة التعيين.',
      forgotIntro: 'أدخل بريدك وسنرسل لك رابطاً لإعادة تعيين كلمة المرور.',
      sendResetLink: 'إرسال رابط إعادة التعيين',
      checkInbox: 'تحقق من صندوق الوارد',
      sentResetLinkTo: 'أرسلنا رابط إعادة التعيين إلى {email}. ينتهي الرابط خلال ساعة.',
      didntGetEmail: 'لم يصلك البريد؟',
      tryAgain: 'حاول مرة أخرى',
      dontHaveAccount: 'ليس لديك حساب؟',
      createOne: 'إنشاء حساب',
      somethingWentWrong: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
      authHeroTitle: 'أدر مطعمك.',
      authHeroTitleHighlight: 'بسيط ومجاني.',
      authHeroSubtitle: 'قوائم ورمز QR وساعات العمل في مكان واحد. لأصحاب المطاعم الذين يهتمون بالوضوح.',
      authHeroFooter: '© نظام إدارة المطاعم. مجاني لأصحاب المطاعم.',
      ariaMainNav: 'القائمة الرئيسية',
      ariaAppLanguage: 'لغة التطبيق',
      ariaOpenMenu: 'فتح القائمة',
      ariaScrollUp: 'تمرير لأعلى',
      ariaScrollDown: 'تمرير لأسفل',
      ownerFeedbacksSubtitle: 'طلبات الميزات والتعليقات من أصحاب المطاعم. غيّر الحالة عند المراجعة.',
      superadminInquiriesSubtitle: 'طلبات نموذج اتصل بنا (VIP والصفحة الرئيسية). الأحدث أولاً.',
      ownerFeedbacksEmpty: 'لا توجد تعليقات من المالكين بعد.',
      markPending: 'تعليم كمعلق',
      markReviewed: 'تعليم كمراجع',
      feedbacksUpdateForbidden: 'ليس لديك صلاحية لتحديث التعليقات.',
      feedbackNotFound: 'التعليق غير موجود.',
      feedbackUpdated: 'تم تحديث التعليق.',
      feedbackApproved: 'تمت الموافقة على التعليق.',
      feedbackRejected: 'تم رفض التعليق.',
      failedToUpdate: 'فشل التحديث.',
      feedbackDeleted: 'تم حذف التعليق.',
      failedToDelete: 'فشل الحذف.',
      deleteFeedbackConfirm: 'حذف هذا التعليق؟ لا يمكن التراجع.',
      superadminUsersSubtitle: 'إدارة الحسابات: حالة الدفع وتفعيل/إلغاء التفعيل.',
      paginationPrevious: 'السابق',
      paginationNext: 'التالي',
      paginationPageOf: 'صفحة {current} من {last}',
      paginationPerPage: 'لكل صفحة',
      paginationShowing: 'عرض {from}–{to} من {total}',
      noUsersFound: 'لم يتم العثور على مستخدمين.',
      search: 'بحث',
      searchUsersPlaceholder: 'البحث بالاسم أو البريد الإلكتروني…',
      noUsersMatchSearch: 'لا يوجد مستخدمون يطابقون البحث.',
      paid: 'مدفوع',
      free: 'مجاني',
      active: 'نشط',
      deactivated: 'معطل',
      superadmin: 'مشرف',
      markPaid: 'تعليم كمدفوع',
      removePaid: 'إزالة مدفوع',
      activate: 'تفعيل',
      deactivate: 'إلغاء التفعيل',
      ariaDeactivateUser: 'إلغاء تفعيل المستخدم',
      ariaActivateUser: 'تفعيل المستخدم',
      vip: 'VIP',
      setVip: 'تعيين VIP',
      removeVip: 'إزالة VIP',
      ariaSetVip: 'السماح بتسجيل الدخول من التطبيق (تعيين VIP)',
      ariaRemoveVip: 'عدم السماح بتسجيل الدخول من التطبيق (إزالة VIP)',
      ariaVipBadge: 'VIP: تسجيل الدخول من التطبيق مفعّل',
      superadminVerifyEmail: 'تحقق',
      ariaSuperadminVerifyEmail: 'تعليم بريد المستخدم كمُحقَّق',
      superadminEmailVerifiedToast: 'تم تعليم البريد كمُحقَّق.',
      emailUnverified: 'غير مُحقَّق',
      pendingEmailChange: 'تغيير بريد قيد الانتظار',
      you: '(أنت)',
      usersForbidden: 'ليس لديك صلاحية لعرض المستخدمين.',
      superadminRestaurantsSubtitle: 'قائمة قراءة فقط لجميع المطاعم.',
      noRestaurantsFound: 'لم يتم العثور على مطاعم.',
      searchRestaurantsPlaceholder: 'البحث بالاسم أو الرابط…',
      noRestaurantsMatchSearch: 'لا يوجد مطاعم تطابق البحث.',
      restaurantsForbidden: 'ليس لديك صلاحية لعرض المطاعم.',
      availability: 'التوفر',
      availabilitySubtitle: 'عندما يكون مطعمك مفتوحاً. غيّر الأوقات واضغط حفظ.',
      deleteRestaurant: 'حذف المطعم',
      logoAndBanner: 'الشعار والبانر',
      logoBannerDescription: 'تغيير أو تحديث شعار وبانر المطعم.',
      uploadImagesHint: 'رفع صور. JPEG, PNG, GIF أو WebP. 2 ميجا كحد أقصى.',
      logo: 'الشعار',
      banner: 'البانر',
      takePhoto: 'التقاط صورة',
      chooseFromGallery: 'اختيار من المعرض',
      changeLogo: 'تغيير الشعار',
      changeBanner: 'تغيير البانر',
      removeLogo: 'إزالة الشعار',
      removeBanner: 'إزالة البانر',
      confirmRemoveLogoTitle: 'إزالة الشعار؟',
      confirmRemoveLogoMessage: 'لن يظهر شعار على صفحتك العامة حتى ترفع شعاراً جديداً.',
      confirmRemoveBannerTitle: 'إزالة البانر؟',
      confirmRemoveBannerMessage: 'لن تظهر صورة غلاف على صفحتك العامة حتى ترفع صورة جديدة.',
      logoRemovedToast: 'تمت إزالة الشعار.',
      bannerRemovedToast: 'تمت إزالة البانر.',
      ariaRemoveLogoImage: 'إزالة الشعار',
      ariaRemoveBannerImage: 'إزالة البانر',
      done: 'تم',
      deleteRestaurantConfirm: 'حذف الشركة؟',
      cannotBeUndone: 'لا يمكن التراجع.',
      typeToConfirm: 'اكتب {slug} للتأكيد',
      ariaUploadingLogo: 'جاري رفع الشعار',
      ariaUploadingBanner: 'جاري رفع البانر',
      ariaUploadLogo: 'رفع الشعار',
      ariaUploadBanner: 'رفع البانر',
      ariaUploadLogoFromGallery: 'رفع الشعار من المعرض',
      ariaUploadBannerFromGallery: 'رفع البانر من المعرض',
      ariaTakePhotoLogo: 'التقاط صورة للشعار',
      ariaTakePhotoBanner: 'التقاط صورة للبانر',
      settings: 'الإعدادات',
      themeTab: 'المظهر',
      themeTabSubtitle: 'لون الموقع الرئيسي وقالب الصفحة العامة.',
      // Quick-add item wizard
      quickAddItem: 'إضافة عنصر',
      quickAddItemAria: 'إضافة عنصر للقائمة',
      quickAddStepOf: 'الخطوة {step} من {total}',
      quickAddStep0Title: '<em>نوع</em> العنصر',
      quickAddStep1Title: 'اختر <em>الفئة</em>',
      quickAddStep2Title: '<em>تفاصيل</em> العنصر',
      quickAddStep3Title: 'مراجعة و<em>حفظ</em>',
      quickAddWhatKind: 'ما نوع العنصر؟',
      quickAddTypeSimpleSub: 'سعر واحد',
      quickAddTypeVariantsSub: 'أحجام، خيارات…',
      quickAddTypeCombSub: 'حزمة',
      quickAddTypeSimpleHint: 'منتج أو طبق بسعر ثابت.',
      quickAddTypeVariantsHint: 'عنصر بعدة خيارات لكل منها سعر (مثل صغير / كبير).',
      quickAddTypeComboHint: 'حزمة عناصر تُباع معاً (مثل وجبة كاملة).',
      quickAddNoCats: 'لا توجد فئات بعد',
      quickAddNoCatsSub: 'أنشئ فئتك الأولى أدناه لتنظيم عناصرك.',
      quickAddOrCreate: 'أو أنشئ جديدة',
      quickAddFirstCatName: 'اسم فئتك الأولى',
      quickAddNewCat: 'فئة جديدة',
      quickAddCatPlaceholder: 'مثل: الرئيسيات، المشروبات، الخدمات…',
      quickAddCreate: 'إنشاء',
      quickAddCatWillCreate: 'سيتم إنشاء: {name}',
      quickAddItem1: 'عنصر',
      quickAddItemN: 'عناصر',
      quickAddNameLabel: 'اسم العنصر',
      quickAddNamePlaceholder: 'مثل: بيتزا مارغريتا',
      quickAddPriceOptional: 'السعر (اختياري)',
      quickAddComboPriceOptional: 'سعر الحزمة (اختياري)',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: 'الوصف (اختياري)',
      quickAddDescPlaceholder: 'يظهر على بطاقة العنصر…',
      quickAddVariantsLabel: 'الخيارات',
      quickAddVariantName: 'اسم الخيار',
      quickAddVariantNamePlaceholder: 'مثل: كبير',
      quickAddVariantPrice: 'السعر',
      quickAddRemoveVariant: 'إزالة الخيار',
      quickAddAddVariant: 'إضافة خيار',
      quickAddComboItems: 'عناصر الحزمة',
      quickAddPickItem: 'اختر عنصراً…',
      quickAddAdd: 'إضافة',
      quickAddRemoveComboItem: 'إزالة العنصر',
      quickAddNoItemsForCombo: 'لا توجد عناصر بعد — أضف عناصر بسيطة أو بخيارات أولاً.',
      quickAddReviewItem: 'العنصر',
      quickAddReviewName: 'الاسم',
      quickAddReviewType: 'النوع',
      quickAddReviewPrice: 'السعر',
      quickAddReviewIncludes: 'يتضمن',
      quickAddReviewCategory: 'الفئة',
      quickAddReviewCategoryLabel: 'الفئة',
      quickAddNoPrice: 'بدون سعر',
      quickAddNoCategory: 'بدون',
      quickAddNew: 'جديدة',
      quickAddPublishNow: 'نشر فوراً',
      quickAddPublishNowSub: 'يظهر في موقعك على الفور',
      quickAddAddAnother: 'إضافة آخر بعد الحفظ',
      quickAddAddAnotherSub: 'إبقاء هذا النموذج مفتوحاً',
      quickAddSaveItem: 'حفظ العنصر',
      quickAddReview: 'مراجعة',
      quickAddContinue: 'متابعة',
      quickAddBack: 'رجوع',
      quickAddSaved: 'تم حفظ العنصر.',
      quickAddSavedSub: 'تمت إضافة {name} إلى {category}.',
      quickAddAnotherItem: '+ إضافة عنصر آخر',
      quickAddDone: 'تم',
      quickAddBusinessContext: 'إدارة قائمتك — التغييرات تُطبَّق على الكتالوج المباشر.',
      quickAddPhotoSection: 'صورة العنصر',
      quickAddPhotoHint: 'اختياري. تظهر في قائمتك العامة.',
      quickAddPhotoHintCrop: 'اختياري. تظهر في قائمتك العامة. قص الصورة بعد اختيارها.',
      quickAddPhotoRemove: 'إزالة الصورة',
      quickAddPhotoUpload: 'رفع صورة',
      quickAddPhotoReplace: 'استبدال الصورة',
      quickAddPhotoPreviewAlt: 'معاينة صورة العنصر',
      quickAddModifiersHint: 'إضافات اختيارية (مثل إضافات إضافية). يمكن للزبائن اختيارها عند الطلب.',
      quickAddReviewPhoto: 'الصورة',
      quickAddReviewPhotoYes: 'مرفقة',
      quickAddReviewPhotoNone: 'لا',
      quickAddReviewModifiers: 'الإضافات',
      quickAddReviewModifiersNone: 'لا يوجد',
      quickAddPhotoUploadFailed: 'تم حفظ العنصر لكن لم يُرفع الصورة. أضفها من عناصر القائمة في لوحة التحكم.',
      quickAddIntentLabel: 'كيف تريد الإضافة؟',
      quickAddIntentCreate: 'إنشاء عنصر جديد',
      quickAddIntentCreateSub: 'أضف شيئاً جديداً إلى كتالوجك وهذه القائمة.',
      quickAddIntentAssign: 'تعيين من الكتالوج',
      quickAddIntentAssignSub: 'ضع عنصراً أنشأته مسبقاً في «عناصر القائمة» داخل فئة.',
      quickAddIntentAssignHint: 'اختر فئة ثم عنصراً من الكتالوج. لا يُنشأ عنصر جديد في الكتالوج.',
      quickAddStep0TitleIntent: 'كيف تريد <em>الإضافة</em>؟',
      quickAddStep2TitleAssign: 'اختر عنصراً من <em>الكتالوج</em>',
      quickAddPickCatalogItem: 'عنصر الكتالوج',
      quickAddReviewAssignFromCatalog: 'من الكتالوج',
      quickAddAssignSearchPlaceholder: 'البحث بالاسم…',
      quickAddAssignEmpty: 'لا توجد عناصر كتالوج للإضافة هنا. أنشئها في «عناصر القائمة» أولاً، أو قد تكون بالفعل في هذه الفئة.',
      quickAddAssignNoCategories: 'استخدم النموذج أدناه لتسمية أول فئة، ثم اضغط إنشاء وتابع.',
      quickAddAssignCategoryHint: 'اختر فئة موجودة أو أنشئ فئة جديدة — كما عند إضافة عنصر جديد تماماً.',
      quickAddNoMenuForCategory: 'لا يوجد قائمة لهذا النشاط بعد. أضف قائمة من لوحة التحكم ثم أعد المحاولة.',
      catalogTab: 'الكتالوج',
      primaryColorLabel: 'اللون الرئيسي',
      primaryColorHint: 'لون التمييز في صفحتك العامة (الأزرار، الروابط). اتركه فارغاً للافتراضي.',
      ariaPickPrimaryColor: 'اختر اللون الرئيسي',
      clear: 'مسح',
      primaryColorUpdated: 'تم تحديث اللون الرئيسي.',
      primaryColorCleared: 'تم مسح اللون الرئيسي.',
      primaryColorInvalid: 'أدخل لون hex صالح (مثلاً #e85a2b أو #fff).',
      themeTemplateHint: 'اختر تخطيط صفحتك العامة.',
      templateInvalid: 'اختر قالباً صالحاً.',
      templateUpdated: 'تم تحديث القالب.',
      seeWebsite: 'عرض الموقع',
      goToSite: 'الذهاب إلى الموقع',
      ariaManageSections: 'أقسام إدارة النشاط التجاري',
      qrScanHint: 'امسح لزيارة مطعمك',
      qrSaveButton: 'حفظ رمز QR',
      qrSaveTooltip: 'احفظ هذا أو اطبعه لمطعمك',
      contactAndDetails: 'الاتصال والتفاصيل',
      address: 'العنوان',
      email: 'البريد الإلكتروني',
      social: 'التواصل الاجتماعي',
      addLanguageEllipsis: 'إضافة لغة…',
      addLanguageHint: 'أضف لغة واحدة على الأقل أعلاه لإدارة الوصف.',
      editDescriptionFor: 'تحرير الوصف لـ:',
      ariaSelectTemplate: 'اختيار القالب',
      templateBestFor: 'الأفضل لـ: {type}',
      templateBusinessType_restaurants: 'مطعم',
      templateBusinessType_services: 'خدمة',
      templateBusinessType_ecommerce: 'تجارة إلكترونية',
      templateBusinessType_general: 'عام',
      footerCreditsSectionTitle: 'ائتمانات التذييل في الموقع العام',
      footerCreditsSectionHint: 'التحكم في عرض كتلة "الموقع من QRestoo" وائتمانات DLR في أسفل صفحة مطعمك العامة.',
      hideFooterCreditsLabel: 'إخفاء ائتمانات التذييل في موقعي العام',
      showCurrencySectionTitle: 'إظهار العملة في الأسعار',
      showCurrencySectionHint: 'عند التفعيل، تظهر الأسعار في موقعك العام مع العملة (مثلاً 10.00 €). عند الإيقاف، يظهر الرقم فقط (مثلاً 10.00).',
      showCurrencyLabel: 'إظهار العملة بجانب الأسعار',
      showCurrencySavedOn: 'ستظهر العملة بجانب الأسعار في موقعك العام.',
      showCurrencySavedOff: 'ستظهر الأسعار كأرقام فقط في موقعك العام.',
      orderingModeSectionTitle: 'وضع الطلب',
      orderingModeSectionDescription:
        'فعّل الطلب السريع ليضيف الزوار عناصر إلى السلة على موقعك العام. في باقتي Pro وVIP يمكنك أيضاً السماح بالدفع عبر تطبيق QRestoo للجوال.',
      orderingModeQuickOrderLabel: 'طلب سريع',
      orderingModeMobileAppLabel: 'طلب عبر التطبيق',
      orderingModeOptionOff: 'معطّل — لا سلة في موقعك العام',
      orderingModeOptionQuick: 'طلب سريع — يستخدم الضيوف واتساب أو فايبر أو الرسائل عند وجود رقم تواصل',
      orderingModeOptionMessaging: 'طلب سريع عبر رسالة — واتساب أو فايبر أو الرسائل عند وجود رقم تواصل',
      orderingModeOptionApp: 'طلب سريع عبر تطبيق الجوال — إتمام الطلب في تطبيق QRestoo (استلام)',
      ordersReceived: 'الطلبات المستلمة',
      menusAndCategories: 'الكتالوج والفئات',
      createFirstMenuHint: 'أنشئ قائمتك الأولى لإضافة فئات.',
      createCategoryFirstHint: 'أنشئ فئة أولاً لإضافة عناصر إلى هذه القائمة.',
      menuItemsSettingsHint: 'أضف الأطباق من صفحة كل فئة. الترجمات تستخدم إعدادات المطعم.',
      createCategoryAboveHint: 'أنشئ فئة أعلاه لإضافة عناصر.',
      renameMenu: 'إعادة تسمية القائمة',
      selectMenu: 'اختيار القائمة',
      editCategory: 'تحرير الفئة',
      removeCategory: 'إزالة الفئة',
      addMenu: 'إضافة قائمة',
      addCategory: 'إضافة فئة',
      categoryAvailability: 'توفر الفئة',
      removeCategoryImage: 'إزالة صورة الفئة',
      addMenuOrCategory: 'إضافة قائمة أو فئة',
      editIn: 'تحرير في',
      changeImage: 'تغيير الصورة',
      uploadImage: 'رفع صورة',
      translating: 'جاري الترجمة…',
      removing: 'جاري الإزالة…',
      mainMenuDefault: 'القائمة الرئيسية',
      categoriesHeading: 'الفئات',
      manageItemsInCategory: 'إدارة العناصر',
      categoryRowNoItems: 'لا توجد عناصر في هذه الفئة بعد.',
      moreCategoryActions: 'المزيد من الإجراءات',
      menuManagement: 'إدارة الكتالوج',
      menuManagementSubtitle: 'الكتالوجات والفئات والعناصر. اسحب لإعادة الترتيب.',
      menuTabIntro: 'أنشئ فئات أدناه، ثم أضف العناصر من إدارة الكتالوج.',
      menuTabContextHint: 'في هذه التبويب تنشئ الفئات فقط وتُعيِّن العناصر لها. إنشاء وتحرير وحذف عناصر الكتالوج يتم من إدارة الكتالوج.',
      noCategoriesYet: 'لا توجد فئات بعد. أضف واحدة بالزر أدناه أو زر +.',
      noMenuItemsYet: 'لا توجد عناصر بعد. استخدم زر + أدناه.',
      categoryNameRequired: 'اسم الفئة مطلوب.',
      categoryUpdated: 'تم تحديث الفئة.',
      categoryCreated: 'تم إنشاء الفئة.',
      categoryModalDescEdit: 'حدّث اسم الفئة ووصفها لكل لغة.',
      categoryModalDescAdd: 'أنشئ فئة جديدة (مثل المقبلات أو الأطباق الرئيسية). أضف اسماً لكل لغة تستخدمها.',
      selectLanguageToEditAria: 'اختر اللغة للتحرير',
      localeDefaultSuffix: ' (الافتراضي)',
      categoryFieldNameInLanguage: 'الاسم ({lang})',
      categoryFieldDescriptionInLanguageOptional: 'الوصف ({lang}) — اختياري',
      translatingCategoryFromDefaultAria: 'جاري ترجمة الفئة من اللغة الافتراضية',
      translateCategoryFromDefaultAria: 'ترجمة من اللغة الافتراضية',
      searchLabel: 'بحث',
      categoryItemsPageFallbackTitle: 'عناصر الفئة',
      categoryItemsSubtitle: 'أضف وأعد ترتيب العناصر في هذه الفئة. اسحب لتغيير الترتيب.',
      categoryEmptyTitle: 'أضف عناصر إلى هذه الفئة',
      categoryEmptyHint: 'أضف عناصر موجودة من كتالوجك لعرضها في هذه الفئة. يمكنك إعادة ترتيبها لاحقاً.',
      addItemToCategoryButton: 'إضافة عنصر',
      categoryItemCount: '{count} عناصر',
      dragToReorderAria: 'اسحب لإعادة الترتيب',
      setAvailabilityForItemAria: 'تعيين أوقات التوفر لهذا العنصر',
      markAvailableOnPublicMenuShort: 'تعليم متاح في القائمة العامة',
      markNotAvailableOnPublicMenuShort: 'تعليم غير متاح في القائمة العامة',
      showOnPublicCatalogAria: 'إظهار في القائمة العامة',
      hideOnPublicCatalogAria: 'إخفاء من القائمة العامة',
      assignTagsToItemAria: 'تعيين وسوم لهذا العنصر',
      editCatalogItemAria: 'تحرير العنصر',
      removeFromCategoryTitle: 'إزالة من الفئة',
      removeFromCategoryListAria: 'إزالة هذا العنصر من هذه الفئة',
      addItemToCategoryModalTitle: 'إضافة عنصر إلى الفئة',
      addItemToCategoryModalDesc: 'أضف أو أزل عناصر في هذه الفئة. العناصر من كتالوجك. ابحث للتصفية.',
      addAllVariantsHint: 'يضيف كل المتغيرات إلى هذه الفئة',
      inMenuAsLabel: 'يُعرض كـ: {name}',
      removeFromMenuButton: 'إزالة من القائمة',
      addToCategoryAria: 'إضافة {name} إلى الفئة',
      removeFromCategoryNamedAria: 'إزالة {name} من الفئة',
      loadingCatalogItems: 'جاري تحميل العناصر…',
      noItemsMatchSearch: 'لا توجد عناصر تطابق البحث، أو لا توجد عناصر بسيطة أو كومبو أو متغيرات في الكتالوج بعد.',
      done: 'تم',
      addItemWithVariantsTitle: 'إضافة عنصر بمتغيرات',
      addItemWithVariantsBody: 'لهذا العنصر عدة متغيرات. إضافته ستضيف كل المتغيرات إلى هذه الفئة.',
      addAllVariantsButton: 'إضافة كل المتغيرات',
      assignTagsModalTitlePlain: 'تعيين الوسوم',
      assignTagsToItemTitle: 'تعيين وسوم لـ {name}',
      assignTagsModalDescription: 'اختر الوسوم لعرضها على هذا العنصر في صفحتك العامة.',
      noTagsAvailable: 'لا توجد وسوم متاحة.',
      tagClassificationGeneral: 'عام',
      tagClassificationRestaurant: 'مطعم',
      tagClassificationService: 'خدمة',
      tagClassificationEcommerce: 'تجارة إلكترونية',
      tagAssignedTooltip: '{name} (معيّن)',
      tagFallbackLabel: 'وسم',
      untitledTag: 'بدون عنوان',
      removeCategoryConfirmLong: 'إزالة «{name}»؟ ستصبح عناصر هذه الفئة غير مصنّفة. لا يمكن التراجع. إن لم تكن متأكداً، أخفِ الفئة من القائمة العامة بدلاً من ذلك.',
      removingCategory: 'جاري الإزالة…',
      removeCategoryButton: 'إزالة الفئة',
      categoriesTotalCount: '{count} إجمالي',
      hideCategoryOnPublicTitle: 'إخفاء الفئة من القائمة العامة',
      showCategoryOnPublicTitle: 'إظهار الفئة في القائمة العامة',
      hideCategoryOnPublicAria: 'إخفاء الفئة من القائمة العامة',
      showCategoryOnPublicAria: 'إظهار الفئة في القائمة العامة',
      menuTabInstructionAria: 'تلميح تبويب الكتالوج',
      menuNameLabel: 'اسم الكتالوج',
      menuNamePlaceholderShort: 'مثال: الغداء، الخدمات، المتجر',
      menuDescriptionPlaceholderShort: 'وصف قصير لهذا الكتالوج',
      addMenuModalTitle: 'إضافة كتالوج',
      addMenuModalDescription: 'أنشئ كتالوجاً آخر لهذه الشركة. أضف اسماً ووصفاً اختيارياً لكل لغة.',
      editMenuModalTitle: 'تحرير الكتالوج',
      editMenuModalDescription: 'غيّر اسم هذا الكتالوج ووصفه لكل لغة.',
      createMenuButton: 'إنشاء كتالوج',
      creatingMenu: 'جاري الإنشاء…',
      menuCreatedToast: 'تم إنشاء الكتالوج.',
      menuUpdatedToast: 'تم تحديث الكتالوج.',
      menuHiddenPublicToast: 'تم إخفاء الكتالوج من الموقع العام.',
      menuVisiblePublicToast: 'ظهور الكتالوج في الموقع العام.',
      categoryRemovedToast: 'تمت إزالة الفئة.',
      defaultCategoryNameEmptyToast: 'اسم الفئة الافتراضي فارغ. أضف اسماً للغة الافتراضية أولاً.',
      translationFailedShort: 'فشلت الترجمة.',
      translationAppliedClickSaveToast: 'تم تطبيق الترجمة. اضغط حفظ للتخزين.',
      validateAtLeastOneName: 'مطلوب اسم واحد على الأقل.',
      validateNameMaxLengthInLocale: 'يجب ألا يتجاوز الاسم ({lang}) 255 حرفاً.',
      itemAddedToCategoryToast: 'تمت إضافة العنصر إلى الفئة.',
      variantsAddedToCategoryToast: 'تمت إضافة {count} متغيرات إلى الفئة.',
      itemAlreadyInCategoryToast: 'هذا العنصر موجود بالفعل في هذه الفئة.',
      itemAlreadyInCategoryAsWholeToast: 'هذا العنصر موجود بالفعل في هذه الفئة (مضاف كاملاً).',
      allVariantsAlreadyInCategoryToast: 'كل المتغيرات موجودة بالفعل في هذه الفئة.',
      removedFromCategoryToast: 'تمت الإزالة من الفئة.',
      itemMarkedAvailableToast: 'تم تعليم العنصر كمتاح.',
      itemMarkedNotAvailableToast: 'تم تعليم العنصر كغير متاح.',
      itemVisibleOnPublicToast: 'العنصر مرئي في القائمة العامة.',
      itemHiddenFromPublicToast: 'العنصر مخفي من القائمة العامة.',
      categoryHiddenPublicToast: 'الفئة مخفية من القائمة العامة.',
      categoryVisiblePublicToast: 'الفئة مرئية في القائمة العامة.',
      orderUpdatedToast: 'تم تحديث الترتيب.',
      availabilityUpdatedToast: 'تم تحديث التوفر.',
      tagsUpdatedToast: 'تم تحديث الوسوم.',
      fabAddCategoryAria: 'إضافة فئة',
      fabCloseAria: 'إغلاق',
      unnamedMenu: 'كتالوج بدون اسم',
      menuFallbackName: 'الكتالوج',
      addMenuItemsToCategory: 'إضافة عناصر إلى هذه الفئة',
      noMenuItemsOrMatch: 'لا توجد عناصر أو لا تطابق البحث.',
      assignTags: 'تعيين الوسوم',
      itemAvailability: 'توفر العنصر',
      dragToReorder: 'اسحب لإعادة الترتيب',
      setAvailabilityTimes: 'تعيين أوقات التوفر',
      available: 'متاح',
      notAvailable: 'غير متاح',
      markNotAvailable: 'تعليم غير متاح',
      markAvailableOnMenu: 'تعليم متاح في القائمة العامة',
      visibleOnMenu: 'مرئي في القائمة',
      hiddenFromMenu: 'مخفي من القائمة',
      addMenuItemToCategory: 'إضافة عنصر إلى الفئة',
      menuItemTags: 'وسوم عناصر القائمة',
      createRestaurantFirstForMenus: 'أنشئ شركة أولاً لإدارة الكتالوج والعناصر.',
      ratingOutOf5: 'التقييم: {n} من 5',
      anonymous: 'مجهول',
      approve: 'موافقة',
      reject: 'رفض',
      ariaApproveFeedback: 'الموافقة على التعليق',
      ariaRejectFeedback: 'رفض التعليق',
      ariaDeleteFeedback: 'حذف التعليق',
      resetPasswordSuccess: 'تم إعادة تعيين كلمة المرور. يمكنك تسجيل الدخول.',
      resetPasswordEnterNew: 'أدخل كلمة المرور الجديدة أدناه.',
      type: 'النوع',
      labelOptional: 'التسمية (اختياري)',
      placeholderUrl: 'https://...',
      placeholderPhone: 'مثال: +966 50 123 4567',
      placeholderContactLabel: 'مثال: الحجوزات، المطبخ',
      showOnPublicPage: 'إظهار في الصفحة العامة',
      hideOnPublicPage: 'إخفاء من الصفحة العامة',
      ariaEditContact: 'تحرير جهة الاتصال',
      ariaDeleteContact: 'حذف جهة الاتصال',
      noContactsYet: 'لا جهات اتصال أو روابط بعد. أضف واحدة للتواصل.',
      deleteContactConfirm: 'حذف جهة الاتصال هذه؟',
      ariaPagination: 'التنقل بين الصفحات',
      weeklyScheduleTitle: 'الجدول الأسبوعي',
      itemAvailabilityTitle: 'توفر العنصر',
      itemAvailabilityInfo: 'حدّد متى يكون هذا العنصر متاحاً. يمكنك إضافة فترات متعددة للتوفر المقسّم (مثل الغداء والعشاء).',
      restaurantAvailabilityInfo: 'ساعات العمل تؤثر على توفر الطلبات عبر الإنترنت وعلى ما يراه العملاء عن أوقات فتحك.',
      itemAvailabilityHint: 'حدّد وقت الفتح والإغلاق لكل يوم. أضف فترات متعددة للساعات المقسّمة.',
      restaurantAvailabilityHint: 'حدّد أوقات فتح وإغلاق مطعمك لكل يوم. أضف فترات متعددة للساعات المقسّمة (مثل الغداء والعشاء).',
      openForBusinessAria: '{day} مفتوح للعملاء',
      openingHourAria: 'ساعة الفتح',
      openingMinuteAria: 'دقيقة الفتح',
      closingHourAria: 'ساعة الإغلاق',
      closingMinuteAria: 'دقيقة الإغلاق',
      addAnotherTimeSlot: 'إضافة فترة زمنية أخرى',
      closed: 'مغلق',
      to: 'إلى',
      ariaOpeningTime: 'وقت الفتح',
      ariaClosingTime: 'وقت الإغلاق',
      removeTimeSlot: 'إزالة الفترة',
      open24_7: 'مفتوح 24/7',
      open24_7ConfirmTitle: 'مفتوح 24/7',
      open24_7ConfirmMessage: 'تفعيل مفتوح 24/7 سيحذف جدولك الحالي. سيظهر مطعمك كمفتوح دائماً. متابعة؟',
      confirm: 'تأكيد',
      menuItemLabel: 'عنصر القائمة',
      addMenuItem: 'إضافة عنصر قائمة',
      ariaRemoveImage: 'إزالة الصورة',
      ariaRemoveVariantImage: 'إزالة صورة المتغير',
      ariaRemoveEntry: 'إزالة المدخل',
      ariaRemoveGroup: 'إزالة المجموعة',
      ariaShowHelp: 'عرض المساعدة: ماذا تعني الأزرار؟',
      help: 'المساعدة',
      ariaButtonLegend: 'شرح الأزرار',
      helpLegendDescription: 'معنى الأيقونات في صفوف القائمة والفئة.',
      legendDragHandle: 'مقبض السحب',
      legendDragDesc: 'اسحب لإعادة الترتيب',
      legendSchedule: 'الجدول',
      legendScheduleDesc: 'تعيين أوقات العمل',
      legendAvailability: 'التوفر',
      legendAvailabilityDesc: 'متى يكون هذا العنصر متاحاً',
      legendVisibility: 'الرؤية',
      legendVisibilityDesc: 'إظهار أو إخفاء في القائمة العامة',
      legendTags: 'الوسوم',
      legendTagsDesc: 'تعيين وسوم للعناصر',
      paidFeature: 'ميزة مدفوعة',
      paidFeatureHeadline: 'فتح Pro',
      paidFeatureSubhead: 'هذا والمزيد متضمن في Pro.',
      paidFeaturePricingHint: 'إلغاء في أي وقت.',
      paidFeaturePricingLine: 'راجع الفواتير للأسعار الحالية. إلغاء في أي وقت.',
      paidFeatureWhatIncluded: 'ما يتضمنه Pro:',
      upgradeToUse: 'ترقية لاستخدام هذه الميزة.',
      paidFeatureAiDescription: 'تحسين صور القائمة بالذكاء الاصطناعي متاح في الخطة المدفوعة.',
      paidFeatureAnnouncementDescription: 'اعرض إعلانات على الموقع العام لمطعمك لمدة تصل إلى 4 أيام. قم بالترقية لفتح هذه الميزة وميزات Pro الأخرى.',
      paidFeatureBenefitsIntro: 'فوائد الترقية:',
      paidFeatureBenefitAnnouncements: 'إعلانات على موقعك العام',
      paidFeatureBenefitTableReservation: 'الحجز والمواعيد (مع الرسائل النصية)',
      paidFeatureBenefitMailingList: 'قائمة بريدية',
      paidFeatureBenefitNoAds: 'يمكن إزالة علامة QRestoo',
      paidFeatureBenefitMultipleRestaurants: 'أعمال متعددة',
      analytics: 'التحليلات',
      analyticsSubtitle: 'تتبع مسح QR وعروض القائمة والعناصر الشائعة والمزيد.',
      analyticsCreateFirst: 'أنشئ مطعماً أولاً لعرض التحليلات.',
      analyticsQrScans: 'مسح QR',
      analyticsMenuViews: 'عروض القائمة',
      analyticsItemClicks: 'نقرات على العناصر',
      analyticsViews: 'عروض',
      analyticsReservations: 'الحجوزات',
      analyticsReviews: 'المراجعات',
      analyticsRating: 'التقييم',
      analyticsPopularItems: 'العناصر الشائعة',
      analyticsPeakHours: 'ساعات الذروة',
      analyticsReservationTrends: 'اتجاهات الحجوزات',
      analyticsMonthlyOverview: 'نظرة شهرية',
      analyticsRevenue: 'الإيرادات',
      analyticsMultiLocation: 'شركات متعددة',
      analyticsCustomerInsights: 'رؤى العملاء',
      analyticsExportCsv: 'تصدير CSV',
      analyticsExportPdf: 'تصدير PDF',
      analyticsPeriodWeek: 'أسبوع',
      analyticsPeriodMonth: 'شهر',
      analyticsPeriodAll: 'كل الوقت',
      analyticsNoData: 'لا توجد بيانات بعد',
      analyticsSelectRestaurant: 'اختر المطعم',
      analyticsSubtotal: 'المجموع الفرعي',
      announcement: 'إعلان',
      announcementSubtitle: 'اعرض رسالة على الموقع العام لمطعمك لمدة تصل إلى 4 أيام. خطط مدفوعة فقط.',
      announcementSelectRestaurant: 'اختر مطعماً',
      announcementLanguage: 'اللغة',
      announcementMessage: 'الرسالة',
      announcementMessagePlaceholder: 'مثال: مغلق لحدث خاص يوم السبت.',
      announcementImage: 'الصورة (اختياري)',
      announcementUploadImage: 'رفع صورة',
      announcementRemoveImage: 'إزالة الصورة',
      announcementImageHelp: 'ارفع صورة لعرضها مع إعلانك على الموقع العام. JPEG أو PNG أو GIF أو WebP، بحد أقصى 2 ميجابايت.',
      announcementImageTypeInvalid: 'اختر صورة بصيغة JPEG أو PNG أو GIF أو WebP.',
      announcementImageSizeInvalid: 'يجب أن تكون الصورة أقل من {max} ميجابايت.',
      announcementImageUpdated: 'تم تحديث الصورة.',
      announcementImageRemoved: 'تم إزالة الصورة.',
      uploading: 'جاري الرفع…',
      announcementActive: 'إظهار على الموقع العام',
      announcementActiveHelp: 'عند التفعيل، تظهر الرسالة على موقعك العام حتى تقوم بإيقافها.',
      announcementSave: 'حفظ',
      announcementSaved: 'تم حفظ الإعلان.',
      vipSiteFieldsNav: 'حقول الموقع',
      vipSiteFieldsLabel: 'VIP',
      vipSiteFieldsTitle: 'حقول موقع مخصصة',
      vipSiteFieldsSubtitle: 'أضف مفاتيح يمكن لموقعك العام أو التكاملات قراءتها (نص أو HTML أو صورة لكل حقل). النص وHTML يستخدمان كل لغة مثبتة على العمل المحدد.',
      vipSiteFieldsValueForLanguage: 'القيمة ({label})',
      vipSiteFieldsNoLanguages: 'أضف لغة واحدة على الأقل لهذا العمل في إعدادات المطعم قبل استخدام حقول النص أو HTML.',
      vipSiteFieldsHtmlPlaceholder: 'اكتب محتوى منسقاً لهذه اللغة…',
      vipSiteFieldsSelectBusiness: 'اختر عملاً',
      vipSiteFieldsExisting: 'حقولك',
      vipSiteFieldsAddField: 'إضافة حقل',
      vipSiteFieldsEditField: 'تعديل حقل',
      vipSiteFieldsKey: 'مفتاح الحقل',
      vipSiteFieldsKeyPlaceholder: 'مثال promo_banner',
      vipSiteFieldsKeyHelp: 'أحرف صغيرة وأرقام وشرطة سفلية. يجب أن يبدأ بحرف.',
      vipSiteFieldsKeyRequired: 'أدخل مفتاح الحقل.',
      vipSiteFieldsKeyInvalid: 'ابدأ بحرف صغير ثم أحرف أو أرقام أو شرطة سفلية (حد أقصى 64).',
      vipSiteFieldsType: 'النوع',
      vipSiteFieldsTypeRequired: 'اختر نوعاً.',
      vipSiteFieldsTypeText: 'نص عادي',
      vipSiteFieldsTypeHtml: 'HTML',
      vipSiteFieldsTypeImage: 'صورة',
      vipSiteFieldsImageUrl: 'رابط الصورة (اختياري)',
      vipSiteFieldsImageUrlPlaceholder: 'https://…',
      vipSiteFieldsImageUrlInvalid: 'أدخل رابطاً صالحاً.',
      vipSiteFieldsUploadImage: 'رفع صورة',
      vipSiteFieldsUploadImageHelp: 'احفظ الحقل أولاً ثم عدّله لرفع ملف (يستبدل الرابط). JPEG أو PNG أو GIF أو WebP.',
      vipSiteFieldsNotVip: 'هذه الميزة متاحة لـ VIP فقط.',
      vipSiteFieldsLoadFailed: 'تعذر تحميل الحقول.',
      vipSiteFieldsSaved: 'تم تحديث الحقل.',
      vipSiteFieldsCreated: 'تم إنشاء الحقل.',
      vipSiteFieldsDeleted: 'تم حذف الحقل.',
      vipSiteFieldsSaveFailed: 'تعذر حفظ الحقل.',
      vipSiteFieldsDeleteFailed: 'تعذر حذف الحقل.',
      vipSiteFieldsImageUpdated: 'تم تحديث الصورة.',
      vipSiteFieldsImageUploadFailed: 'فشل رفع الصورة.',
      siteContentNav: 'صفحة VIP',
      vipOrdersNav: 'الطلبات',
      vipOrdersEyebrow: 'الطلبات',
      vipOrdersTitle: 'الطلبات',
      vipOrdersSubtitle:
        'طلبات سريعة من موقعك العام أو التطبيق (آخر 50 لكل عمل). افتح صفًا أو اضغط التفاصيل للعرض الكامل.',
      vipOrdersSelectBusiness: 'اختر عملاً',
      vipOrdersCreateFirst: 'أنشئ عملاً أولاً لعرض الطلبات.',
      vipOrdersEmpty: 'لا توجد طلبات لهذا العمل بعد.',
      vipOrdersLoadFailed: 'تعذر تحميل الطلبات.',
      vipOrdersColWhen: 'الوقت',
      vipOrdersColCustomer: 'العميل',
      vipOrdersColStatus: 'الحالة',
      vipOrdersColTotal: 'الإجمالي',
      vipOrdersColActions: 'إجراءات',
      vipOrdersViewDetails: 'التفاصيل',
      vipOrdersDetailTitle: 'تفاصيل الطلب',
      vipOrdersFulfillment: 'طريقة الاستلام',
      vipOrdersFulfillmentPickup: 'استلام',
      vipOrdersFulfillmentDineIn: 'في المطعم',
      vipOrdersEmail: 'البريد',
      vipOrdersPhone: 'الهاتف',
      vipOrdersPhoneVerified: 'تم التحقق من الهاتف',
      vipOrdersLineItems: 'الأصناف',
      vipOrdersSubtotal: 'المجموع الفرعي',
      vipOrdersPickupFee: 'رسوم الاستلام ({percent}٪)',
      vipOrdersTotal: 'الإجمالي',
      vipOrdersNotes: 'ملاحظات',
      vipOrdersActionAccept: 'قبول الطلب',
      vipOrdersActionReject: 'رفض',
      vipOrdersActionMarkReady: 'تعيين كجاهز',
      vipOrdersActionPickedUp: 'تعيين كمستلم',
      vipOrdersActionServed: 'تعيين كمُقدَّم',
      vipOrdersStatusUpdateFailed: 'تعذر تحديث حالة الطلب.',
      vipOrdersRejectModalTitle: 'رفض الطلب',
      vipOrdersRejectHint:
        'لطلبات الاستلام مع رقم هاتف العميل، قد يصله السبب برسالة نصية عند تفعيل الرسائل على الخادم.',
      vipOrdersRejectReasonLabel: 'سبب الرفض',
      vipOrdersRejectCancel: 'إلغاء',
      vipOrdersRejectConfirm: 'رفض الطلب',
      vipOrdersRejectReasonRequired: 'يرجى إدخال سبب الرفض.',
      vipOrdersRejectReasonMinLength: 'أدخل 3 أحرف على الأقل.',
      vipOrdersRejectionReason: 'سبب الرفض',
      vipOrdersStatus_pending: 'قيد الانتظار',
      vipOrdersStatus_received: 'مستلم',
      vipOrdersStatus_ready: 'جاهز',
      vipOrdersStatus_picked_up: 'تم الاستلام',
      vipOrdersStatus_rejected: 'مرفوض',
      vipOrdersStatus_cancelled: 'ملغى',
      paidFeatureOwnerOrdersDescription:
        'يمكن لـ Pro وVIP مراجعة الطلبات السريعة في لوحة التحكم: المبالغ والأصناف وبيانات التواصل والملاحظات.',
      paidFeatureBenefitOwnerOrders: 'صندوق الطلبات (Pro / VIP)',
      siteContentLabel: 'VIP',
      siteContentTitle: 'صفحة VIP',
      siteContentSubtitle:
        'حقول مخصصة (نص أو HTML أو صورة) ومعرض صور ورموز QR فريدة لموقعك العام. اختر عملاً ثم استخدم التبويبات. النص والمعرض يستخدمان كل لغة مثبتة على ذلك العمل.',
      siteContentUniqueQrHint:
        'يفتح كل رمز عنوان موقعك العام المباشر (نطاق مخصص أو نطاق فرعي) مع ?unique_id=… للطاولات أو الحملات أو الموظفين.',
      siteContentUniqueQrLabel: 'معرّف فريد',
      siteContentUniqueQrPlaceholder: 'مثال: 1',
      siteContentUniqueQrNoPublicUrl:
        'اضبط أولاً العنوان العام لهذا العمل (النطاق الفرعي/الـ slug أو النطاق المخصص VIP في إعدادات العمل).',
      siteContentUniqueQrEmptyHint: 'اكتب معرّفاً أعلاه لمعاينة رمز QR والتسمية التوضيحية.',
      siteContentUniqueQrHideAppUrl: 'إخفاء علامة QRestoo',
      siteContentUniqueQrDownload: 'تنزيل QR',
      siteContentSelectBusiness: 'اختر عملاً',
      siteContentPickBusinessHint: 'اختر عملاً أعلاه.',
      siteContentTabCustomFields: 'حقول مخصصة',
      siteContentTabGallery: 'معرض',
      siteContentTabUniqueQr: 'QR فريد',
      siteContentGalleryAdd: 'إضافة صورة للمعرض',
      siteContentGalleryNoLanguages:
        'أضف لغة واحدة على الأقل لهذا العمل في إعدادات المطعم قبل تعديل نصوص المعرض.',
      siteContentGalleryUntitled: 'بدون عنوان',
      siteContentGalleryVisible: 'يظهر في الموقع العام',
      siteContentGalleryHidden: 'مخفي عن الموقع العام',
      siteContentGalleryShowOnSite: 'إظهار في الموقع',
      siteContentGalleryTitleForLang: 'العنوان ({label})',
      siteContentGalleryDescriptionForLang: 'الوصف ({label})',
      siteContentGalleryTitlePlaceholder: 'عنوان قصير',
      siteContentGalleryDescriptionPlaceholder: 'وصف اختياري',
      siteContentGalleryImageUrl: 'رابط الصورة (اختياري)',
      siteContentGalleryExternalImageUrl: 'رابط صورة خارجية',
      siteContentGalleryExternalImageUrlHelp:
        'رابط اختياري لصورة على موقع آخر. إذا رفعت ملفًا أدناه، يُعرض الملف المرفوع — لا تلصق هنا رابط رفع النظام.',
      siteContentGalleryImagePreview: 'معاينة (كما تظهر في موقعك)',
      siteContentGalleryUploadImage: 'رفع صورة',
      siteContentGalleryMoveUp: 'أعلى',
      siteContentGalleryMoveDown: 'أسفل',
      siteContentGalleryDragOrderHint:
        'اسحب المقبض على اليسار لتغيير الترتيب في موقعك العام. يمكنك أيضًا استخدام أعلى / أسفل.',
      siteContentGalleryDragHandleAria: 'اسحب لإعادة ترتيب صور المعرض في موقعك',
      siteContentGalleryLoadFailed: 'تعذر تحميل المعرض.',
      siteContentGallerySaveFailed: 'تعذر حفظ عنصر المعرض.',
      siteContentGalleryReorderFailed: 'تعذر إعادة الترتيب.',
      siteContentGalleryDeleteFailed: 'تعذر حذف العنصر.',
      siteContentGallerySaved: 'تم تحديث عنصر المعرض.',
      siteContentGalleryItemCreated: 'تمت إضافة عنصر المعرض.',
      siteContentGalleryItemDeleted: 'تم حذف عنصر المعرض.',
      siteContentGalleryOrderSaved: 'تم حفظ الترتيب.',
      siteContentGalleryImageUpdated: 'تم تحديث الصورة.',
      siteContentGalleryImageFailed: 'فشل رفع الصورة.',
      siteContentGalleryOptional: ' (اختياري)',
      siteContentGalleryBatchDropTitle: 'أسقط الصور هنا أو انقر للاستعراض',
      siteContentGalleryBatchDropHint:
        'حتى 30 صورة (JPEG وPNG وGIF وWebP وHEIC). أضف العناوين والأوصاف لاحقًا في كل صف.',
      siteContentGalleryBatchNoImages: 'لا توجد صور مدعومة في اختيارك.',
      siteContentGalleryBatchTooMany: 'سيتم رفع أول {n} صور فقط.',
      siteContentGalleryBatchSuccess: 'تمت إضافة {n} صورة/صور إلى المعرض.',
      siteContentGalleryBatchPartial: 'تمت إضافة {added}؛ تعذر رفع {failed}.',
      siteContentGalleryBatchAllFailed: 'تعذر إضافة أي صور.',
      paidFeatureBenefitSiteContent: 'صفحة VIP: حقول ومعرض وQR فريد (VIP)',
      paidFeatureSiteContentDescription:
        'يشمل VIP حقول موقع مخصصة للتكاملات ومعرض صور متعدد مع عناوين وأوصاف وإظهار/إخفاء لكل صورة، ورموز QR فريدة لرابط موقعك العام مع ?unique_id=…',
      announcementTranslateNoDefault: 'أضف رسالة باللغة الافتراضية أولاً، ثم استخدم الترجمة من الافتراضي.',
      announcementTranslated: 'تمت ترجمة الرسالة من اللغة الافتراضية.',
      reservations: 'الحجوزات',
      reservationsNav: 'حجوزات الطاولات',
      reservationsSubtitle: 'إدارة حجوزات الطاولات وتعيين رقم الهاتف لاستقبال رسائل الحجوزات الجديدة.',
      reservationsCreateFirst: 'أنشئ شركة أولاً لاستخدام حجوزات الطاولات.',
      reservationsSelectRestaurant: 'اختر شركة',
      reservationsPendingCount: '{n} قيد الانتظار',
      reservationsRestaurantFilter: 'الشركة',
      reservationsDateFrom: 'من تاريخ',
      reservationsDateTo: 'إلى تاريخ',
      reservationsApplyFilter: 'تطبيق',
      reservationsTableDate: 'التاريخ',
      reservationsTableTime: 'الوقت',
      reservationsTablePax: 'الضيوف',
      reservationsTableCustomer: 'العميل',
      reservationsTableMobile: 'الهاتف',
      reservationsTableStatus: 'الحالة',
      reservationsApprove: 'موافقة',
      reservationsReject: 'رفض',
      reservationsRemove: 'إزالة',
      reservationsCancel: 'إلغاء الحجز',
      reservationApproveConfirmTitle: 'الموافقة على الحجز؟',
      reservationApproveConfirmMessage: 'سيتم إبلاغ الضيف بأن حجزه مؤكد.',
      reservationRejectConfirmTitle: 'رفض الحجز؟',
      reservationRejectConfirmMessage: 'سيتم إبلاغ الضيف بأن حجزه لم يُقبل.',
      reservationsEdit: 'تعديل التاريخ والوقت',
      reservationsEditDateTime: 'تغيير تاريخ ووقت الحجز',
      reservationsApproved: 'تمت الموافقة على الحجز.',
      reservationsRejected: 'تم رفض الحجز.',
      reservationsCancelled: 'تم إلغاء الحجز.',
      reservationsRemoved: 'تمت إزالة الحجز.',
      reservationUpdated: 'تم تحديث الحجز.',
      reservationsPendingBadge: '{n} حجوزات قيد الانتظار',
      reservationSmsToPlaceholder: 'مثال: +966501234567',
      reservationSmsToInvalid: 'أدخل رقماً صحيحاً (مثال: +966501234567).',
      phoneMustStartWithPlus: 'يجب أن يبدأ رقم الهاتف بـ + (مثال: +966501234567).',
      reservationSmsToLabel: 'رقم إشعارات الرسائل القصيرة',
      reservationSmsToHint: 'رقم دولي (مثال: +966501234567). ستستلم رسائل الحجوزات الجديدة والتذكيرات على هذا الرقم.',
      reservationSmsToSaved: 'تم حفظ رقم الرسائل القصيرة.',
      reservationsPax: '{count} ضيوف',
      reservationsOnDay: 'حجوزات يوم {date}',
      reservationsNoReservationsMonth: 'لا توجد حجوزات هذا الشهر.',
      reservationsListEmpty: 'لا توجد حجوزات بعد.',
      reservationsLoadError: 'فشل تحميل الحجوزات.',
      reservationsForbidden: 'حجوزات الطاولات تتطلب خطة مدفوعة.',
      reservationStatus_pending: 'قيد الانتظار',
      reservationStatus_approved: 'موافق عليه',
      reservationStatus_rejected: 'مرفوض',
      reservationStatus_cancelled: 'ملغى',
      reservationDetailTitle: 'تفاصيل الحجز',
      reservationDetailCreatedAt: 'طُلب في',
      reservationDetailReminderSent: 'أُرسل التذكير في',
      reminderSent: 'تم إرسال التذكير',
      guest: 'ضيف',
      guests: 'ضيوف',
      reservationsCalendarPrev: 'الشهر السابق',
      reservationsCalendarNext: 'الشهر التالي',
      reservationsCount: 'حجوزات',
      reservationOne: 'حجز',
      daySunday: 'الأحد',
      dayMonday: 'الإثنين',
      dayTuesday: 'الثلاثاء',
      dayWednesday: 'الأربعاء',
      dayThursday: 'الخميس',
      dayFriday: 'الجمعة',
      daySaturday: 'السبت',
      ariaApproveReservation: 'الموافقة على الحجز',
      ariaRejectReservation: 'رفض الحجز',
      reservationApproved: 'تمت الموافقة على الحجز.',
      reservationRejected: 'تم رفض الحجز.',
      smsTemplates: 'قوالب الرسائل القصيرة',
      smsTemplatesSubtitle: 'إدارة قوالب الرسائل القصيرة للحجوزات والإشعارات الأخرى. المتغيرات: {customer_name}, {date}, {time}, {restaurant_name}, {pax}.',
      smsTemplatesAdd: 'إضافة قالب',
      smsTemplatesEdit: 'تعديل القالب',
      smsTemplatesEmpty: 'لا توجد قوالب رسائل قصيرة بعد.',
      smsTemplatesDeleteConfirm: 'حذف القالب "{key}"؟ لا يمكن التراجع عن ذلك.',
      smsTemplatesFormSubtitle: 'المفتاح فريد ويستخدمه النظام. يمكن أن يتضمن المحتوى متغيرات.',
      smsTemplatesKey: 'المفتاح',
      smsTemplatesKeyReadonly: 'لا يمكن تغيير المفتاح عند التعديل.',
      smsTemplatesName: 'الاسم',
      smsTemplatesBody: 'المحتوى',
      smsTemplatesBodyPlaceholders: 'المتغيرات: {customer_name}, {date}, {time}, {restaurant_name}, {pax}',
      smsTemplatesBodyPlaceholder: 'مثال: مرحباً {customer_name}، حجز طاولتك لـ {pax} في {date} الساعة {time} مؤكد. — {restaurant_name}',
      smsTemplatesKeyRequired: 'المفتاح مطلوب.',
      smsTemplatesNameRequired: 'الاسم مطلوب.',
      smsTemplatesBodyRequired: 'المحتوى مطلوب.',
      superadminMenuItemTagsSubtitle: 'إدارة وسوم عناصر القائمة الافتراضية (مثل حار، نباتي) المعروضة لجميع المالكين.',
      superadminItemTagsSearchPlaceholder: 'البحث بنص الوسم…',
      superadminItemTagsAdd: 'إضافة وسم',
      superadminItemTagsEdit: 'تعديل الوسم',
      superadminItemTagsEmpty: 'لا توجد وسوم بعد.',
      superadminItemTagsNoMatch: 'لا تطابق وسوم بحثك.',
      superadminItemTagsForbidden: 'ليس لديك صلاحية لإدارة وسوم العناصر.',
      superadminItemTagText: 'التسمية',
      superadminItemTagTextPlaceholder: 'مثل حار، نباتي',
      superadminItemTagTextRequired: 'التسمية مطلوبة.',
      superadminItemTagTextMax: 'التسمية 100 حرف كحد أقصى.',
      superadminItemTagColor: 'اللون (hex)',
      superadminItemTagColorMax: 'اللون 50 حرف كحد أقصى.',
      superadminItemTagIcon: 'الأيقونة (اسم Material icon)',
      superadminItemTagIconPlaceholder: 'مثل local_fire_department, star',
      superadminItemTagIconMax: 'الأيقونة 100 حرف كحد أقصى.',
      superadminItemTagClassification: 'التصنيف',
      superadminItemTagCreated: 'تم إنشاء الوسم.',
      superadminItemTagUpdated: 'تم تحديث الوسم.',
      superadminItemTagDeleted: 'تم حذف الوسم.',
      superadminItemTagDeleteConfirm: 'حذف الوسم "{text}"؟ سيُزال من عناصر القائمة التي تستخدمه.',
      mailingList: 'القائمة البريدية',
      mailingListSubtitle: 'عرض المشتركين وإرسال رسائل. خطط مدفوعة فقط.',
      mailingListCreateFirst: 'أنشئ شركة أولاً لاستخدام قائمة البريد.',
      mailingListSelectRestaurant: 'اختر شركة',
      mailingListSubscribers: 'المشتركون',
      mailingListNoSubscribers: 'لا مشتركين بعد.',
      mailingListSubscribedAt: 'تاريخ الاشتراك',
      mailingListSelectAll: 'تحديد الكل في هذه الصفحة',
      mailingListSelectSubscriber: 'تحديد المشترك',
      mailingListSelectOneSubscriber: 'حدد مشتركاً واحداً على الأقل للإرسال إليه.',
      mailingListRemoveSelected: 'إزالة المحدد',
      mailingListRemoveConfirmTitle: 'إزالة المشتركين؟',
      mailingListRemoveConfirmMessage: 'إزالة {count} مشترك/مشتركين من القائمة؟ لن يستلموا رسائل بعد الآن. لا يمكن التراجع.',
      mailingListRemoveConfirmButton: 'إزالة',
      mailingListRemovedSuccess: 'تمت إزالة المشترك/ين.',
      mailingListPageInfo: 'عرض {from}–{to} من {total}',
      language: 'اللغة',
      mailingListSendEmail: 'إرسال بريد',
      mailingListSubject: 'الموضوع',
      mailingListSubjectPlaceholder: 'مثال: أخبار وعروض',
      mailingListBody: 'المحتوى (HTML)',
      mailingListBodyPlaceholder: 'اكتب رسالتك…',
      mailingListSendButton: 'إرسال البريد',
      mailingListSubjectRequired: 'الموضوع مطلوب.',
      mailingListBodyRequired: 'المحتوى مطلوب.',
      mailingListBodyMaxCharacters: 'يجب ألا يتجاوز المحتوى {max} حرفاً.',
      mailingListSentSuccess: 'تم إرسال البريد إلى {count} مشترك.',
      mailingListPaidOnly: 'هذه الميزة تتطلب خطة مدفوعة.',
      paidFeatureMailingListDescription: 'اجمع بريد المشتركين وأرسل النشرات من الصفحة العامة لمطعمك. قم بالترقية لفتح القائمة البريدية.',
      paidFeatureVipSiteFieldsDescription: 'عرّف حقول نص أو HTML أو صورة مخصصة لموقعك العام (VIP). تواصل معنا للترقية إلى VIP.',
      paidFeatureBenefitVipSiteFields: 'حقول موقع مخصصة (تكامل VIP)',
      maybeLater: 'ربما لاحقاً',
      upgrade: 'ترقية',
      billingTitle: 'الفواتير والترقية',
      billingSubtitle: 'اشترك في Pro لفتح المزيد من الميزات.',
      billingCurrentPlan: 'خطتك الحالية',
      growthPlan: 'خطة النمو',
      growthPlanBenefits: 'حتى 3 أعمال، تعليقات، إعلانات. إلغاء في أي وقت.',
      proPlan: 'خطة Pro',
      proPlanBenefits: 'أعمال غير محدودة، قائمة بريد وجميع ميزات النمو. إلغاء في أي وقت.',
      proPlanPrice: '—',
      subscribeToPlan: 'اشتراك',
      subscriptionSection: 'الاشتراك',
      upgradeToPro: 'الترقية إلى Pro',
      upgradeToGrowth: 'الترقية إلى Growth',
      upgradeToVip: 'الترقية إلى VIP',
      planStarter: 'مجاني',
      planGrowth: 'Growth',
      planPro: 'Pro',
      planVip: 'VIP',
      manageSubscription: 'إدارة الاشتراك',
      cancelSubscription: 'إلغاء الاشتراك',
      planUntilDate: '{plan} حتى {date}',
      billingNotAvailable: 'الفوترة غير متاحة حالياً. يرجى المحاولة لاحقاً.',
      noBillingAccount: 'لم يتم العثور على حساب فوترة. اشترك أولاً.',
      freePlanLabel: 'أنت على الخطة المجانية.',
      subscriptionSuccessToast: 'الاشتراك نشط. مرحباً بك في {plan}!',
      subscriptionPending: 'اشتراكك قيد المعالجة. يرجى التحديث بعد لحظة.',
      subscriptionRefreshHint: 'إذا لم يتحدّث خطتك قريباً، جرّب تحديث الصفحة.',
      ariaNotifications: 'الإشعارات',
      dismiss: 'إغلاق',
      reservationsHowItWorksTitle: 'كيف تعمل حجوزات الطاولات',
      reservationsHowItWorksIntro: 'يمكن للضيوف طلب طاولة من الصفحة العامة لمطعمك. أنت توافق أو ترفض أو تعدّل الحجوزات من هنا.',
      reservationsHowItWorksPhoneRequired: 'رقم هاتف صالح مطلوب لكل حجز لإرسال التأكيدات والتذكيرات عبر الرسائل القصيرة.',
      reservationsHowItWorksSteps: 'حدد رقم إشعار الرسائل القصيرة لكل شركة لاستقبال تنبيهات الحجوزات الجديدة. وافق أو ارفض الطلبات؛ يستلم الضيوف رسالة. يمكنك أيضاً تعديل التاريخ أو الوقت أو عدد الأشخاص والإلغاء؛ يُبلّغ الضيوف برسالة.',
      reservationsHowItWorksTooltip: 'كيف تعمل الحجوزات',
    },
    verify: {
      checkYourEmail: 'تحقق من بريدك',
      sentToEmail: 'أرسلنا رابط التحقق إلى {email}. انقر للمتابعة.',
      oneStepAway: 'أنت على بُعد خطوة من إدارة قائمتك.',
      checkSpam: 'تحقق من مجلد الرسائل غير المرغوبة. لم يصلك؟',
      backToSignIn: 'العودة لتسجيل الدخول',
      goToSignIn: 'الذهاب لتسجيل الدخول',
      signOut: 'تسجيل الخروج',
      resend: 'إعادة الإرسال',
      sending: 'جاري الإرسال…',
      resendSuccessDefault: 'تم إرسال بريد التحقق. تحقق من صندوق الوارد.',
      resendSuccessGuestDefault: 'إذا كان ذلك البريد مسجلاً وغير موثق، أرسلنا رابطاً جديداً.',
      emailRequired: 'يرجى إدخال بريدك الإلكتروني.',
      emailInvalid: 'يرجى إدخال بريد إلكتروني صحيح.',
      verifying: 'جاري التحقق من بريدك…',
      verifyingNew: 'جاري التحقق من بريدك الجديد…',
      emailVerified: 'تم التحقق من البريد',
      emailUpdated: 'تم تحديث البريد',
      verificationFailed: 'فشل التحقق',
      signIn: 'تسجيل الدخول',
      goToDashboard: 'الذهاب إلى لوحة التحكم',
      verifySuccessDefault: 'يمكنك تسجيل الدخول.',
      verifyNewSuccessDefault: 'تم تحديث بريدك والتحقق منه.',
      invalidLinkDefault: 'رابط التحقق غير صالح أو مفقود. استخدم الرابط من بريدك.',
      invalidExpiredDefault: 'رابط غير صالح أو منتهي. اطلب بريد تحقق جديد.',
      invalidExpiredNewDefault: 'رابط غير صالح أو منتهي. حاول تغيير البريد من الملف والإعدادات.',
    },
    errors: {
      forbidden: 'ليس لديك صلاحية لتنفيذ هذا.',
      validation: 'يرجى التحقق من المدخلات.',
      pageNotFound: 'الصفحة غير موجودة',
      pageNotFoundDescription: 'الصفحة التي تبحث عنها غير موجودة أو تم نقلها.',
    },
    landing: {
      nav: {
        features: 'المميزات',
        solutions: 'كيف يعمل',
        pricing: 'الأسعار',
        resources: 'الموارد',
        getStarted: 'إنشاء حساب مجاني',
        goToApp: 'انتقل للتطبيق',
      },
      hero: {
        badge: 'تقنية الأعمال، ببساطة',
        titleBefore: 'عملك، ',
        titleHighlight: 'عبر الإنترنت في 10 دقائق.',
        subtitle: 'انشر عملك على الإنترنت اليوم — كتالوج المنتجات، المواعيد، الحجوزات والمزيد. ابدأ مجاناً. بدون حاجة لمهارات تقنية.',
        trustedBy: 'مجاني للأبد. لا حاجة لبطاقة ائتمان.',
      },
      stats: {
        activeRestaurants: 'المطاعم النشطة',
        ordersProcessed: 'الطلبات المعالجة',
        globalReach: 'التغطية العالمية',
        countries: 'الدول',
      },
      features: {
        eyebrow: 'ما الذي تحصل عليه',
        title: 'كل ما يحتاجه عملك على الإنترنت',
        subtitle: 'من كتالوجك إلى الحجز والمزيد — كل ذلك في مكان واحد، يُنشأ خلال دقائق.',
        menu: { title: 'الكتالوج', desc: 'رتب المنتجات أو الخدمات أو أصناف القائمة في فئات، حدد الأسعار والوصف، وأعد الترتيب بالسحب والإفلات.' },
        qr: { title: 'رمز QR', desc: 'عملك يحصل على رابط ورمز QR ثابتين يوجهان العملاء إلى صفحتك العامة.' },
        website: { title: 'موقع عملك', desc: 'نطاق فرعي وصفحة عامة لعملك تعرض الكتالوج والمواعيد ومعلومات الاتصال والحجز.' },
        multilingual: { title: 'دعم متعدد اللغات', desc: 'محلية العملة واللغة ليعمل كتالوجك مع كل عميل.' },
        feedback: { title: 'إدارة التقييمات', desc: 'العملاء يتركون تقييمات وتعليقات؛ أنت توافق وتعرضها على موقعك.' },
        booking: { title: 'الحجز', desc: 'دع العملاء يحجزون مواعيد أو طاولات أو خدمات مباشرة من صفحتك العامة.' },
      },
      pricing: {
        eyebrow: 'الأسعار',
        title: 'ابدأ مجاناً. انمُ عندما تشاء.',
        subtitle: 'بدون عقود. بدون رسوم خفية. ألغِ متى شئت.',
        startFree: 'ابدأ مجاناً',
        getGrowth: 'ابدأ النمو',
        getPro: 'انتقل إلى Pro',
        contactUs: 'لنتحدث',
        vipValueLine: 'مصمم لعملك. بدون حدود.',
        vipMysteryDesc: 'للأعمال التي تريد التجربة الكاملة. الطلبات، العلامة البيضاء، الدعم المخصص والمزيد. لنتحدث عما تحتاجه.',
        vipFeatureExtra: '+ ميزات حصرية إضافية عند الطلب ✨',
        vipSmallText: 'سنرد عليك خلال 24 ساعة.',
        contactSales: 'تواصل مع المبيعات',
        perMonth: '/شهر',
        starterTagline: 'عملك، على الإنترنت مجاناً',
        starterTrustLine: 'لا حاجة لبطاقة ائتمان. أبداً.',
        growthTagline: 'كل ما تحتاجه للتواصل مع عملائك',
        growthTrustLine: 'مزيد من الظهور. مزيد من العملاء',
        proTagline: 'شغّل عمليات عملك على الإنترنت',
        proTrustLine: 'ألغِ متى شئت. بدون أسئلة.',
        vipTagline: 'مصمم لعملك. بدون حدود.',
        vipModal: {
          title: 'تواصل معنا بخصوص VIP',
          name: 'الاسم',
          email: 'البريد الإلكتروني',
          message: 'الرسالة',
          submit: 'إرسال',
          success: 'شكراً لاهتمامك. سنتواصل معك قريباً.',
          close: 'إغلاق',
          nameRequired: 'الاسم مطلوب.',
          emailRequired: 'البريد الإلكتروني مطلوب.',
          emailInvalid: 'يرجى إدخال بريد إلكتروني صحيح.',
          messageRequired: 'الرسالة مطلوبة.',
          messageMaxLength: 'يجب ألا تتجاوز الرسالة {max} حرفاً.',
        },
        mostPopular: 'الأكثر شعبية',
        pricesInYourCurrency: 'الأسعار بعملتك المحلية.',
        retry: 'إعادة المحاولة',
        errorLoading: 'لم نتمكن من تحميل الأسعار في منطقتك.',
        usingUsdFallback: 'عرض الأسعار بالدولار. يمكنك المحاولة مرة أخرى أدناه.',
        free: { name: 'مجاني', tagline: 'للمبتدئين', price: '$0' },
        growth: { name: 'Growth', tagline: 'للعلامات النامية', price: '—' },
        pro: { name: 'Pro', tagline: 'للعلامات النامية', price: '—' },
        enterprise: { name: 'Enterprise', tagline: 'حلول مخصصة', price: 'مخصص' },
        features: {
          publicMenu: 'كتالوج عام ونطاق فرعي',
          public_menu: 'كتالوج عام ونطاق فرعي',
          subdomain: 'كتالوج عام ونطاق فرعي',
          qrCode: 'رمز QR وإعادة التوجيه',
          qr_codes: 'رمز QR وإعادة التوجيه',
          feedback: 'تقييمات وتعليقات العملاء',
          openingHours: 'ساعات العمل',
          opening_hours: 'ساعات العمل',
          menuItemAvailability: 'توفر الكتالوج والعناصر',
          menu_item_availability: 'توفر الكتالوج والعناصر',
          simpleOrdering: 'طلب بسيط (سلة -> واتساب/رسالة)',
          simple_ordering: 'طلب بسيط (سلة -> واتساب/رسالة)',
          mobileAppOrdering: 'الطلب عبر التطبيق',
          mobile_app_ordering: 'الطلب عبر التطبيق',
          mobileAppUsage: 'استخدام تطبيق الجوال',
          mobile_app_usage: 'استخدام تطبيق الجوال',
          mobilePickup: 'الاستلام عبر التطبيق',
          mobile_pickup: 'الاستلام عبر التطبيق',
          tableReservation: 'الحجز والمواعيد (مع الرسائل النصية)',
          mailingList: 'قائمة بريدية (إرسال للعملاء)',
          siteAnnouncements: 'إعلانات على موقعك',
          announcements: 'إعلانات على موقعك',
          multipleRestaurants: 'أعمال متعددة',
          restaurants: 'أعمال',
          restaurants1: 'عمل واحد',
          restaurants3: 'حتى 3 أعمال',
          restaurantsUnlimited: 'أعمال غير محدودة',
          noAds: 'يمكن إزالة علامة QRestoo',
          prioritySupport: 'دعم ذو أولوية',
          priority_support: 'دعم ذو أولوية',
          customIntegrations: 'تكاملات مخصصة',
          custom_integrations: 'تكاملات مخصصة',
          analytics: 'تحليلات',
          siteContent: 'صفحة VIP: حقول ومعرض وQR فريد (VIP)',
          orderAndPickup: 'الاستلام عبر التطبيق',
        },
      },
      cta: {
        title: 'مستعد لوضع عملك على الإنترنت؟',
        subtitle: 'انضم إلى أعمال تستخدم QRestoo بالفعل لتكون أوضح، تخدم أفضل، وتنمو.',
        startTrial: 'إنشاء حساب مجاني',
        bookDemo: 'عرض تجريبي لـ {businessType}',
        bookDemoGeneric: 'عرض تجريبي',
      },
      faq: {
        headline: 'أسئلة؟ لدينا الإجابات.',
        q1: 'هل الخطة المجانية مجانية حقاً؟',
        a1: 'نعم — مجانية بالكامل، للأبد. بدون بطاقة ائتمان، بدون فترة تجريبية، بدون رسوم مفاجئة. عملك يحصل على صفحة ورمز QR مباشرة بدون أي تكلفة.',
        q2: 'هل يمكنني الإلغاء في أي وقت؟',
        a2: 'بالتأكيد. بدون عقود، بدون رسوم إلغاء. ألغِ بنقرة واحدة من لوحة التحكم في أي وقت.',
        q3: 'ماذا يحدث لصفحة عملي إذا ألغيت؟',
        a3: 'إذا ألغيت خطة مدفوعة، تعود إلى خطة Starter المجانية. صفحتك تبقى مباشرة — لا تفقد صفحة عملك أبداً.',
        q4: 'هل أحتاج مهارات تقنية للإعداد؟',
        a4: 'أبداً. إذا كنت تستخدم واتساب، تستطيع استخدام QRestoo. معظم الأعمال تكون مباشرة في أقل من 10 دقائق.',
        q5: 'ما هي خطة VIP بالضبط؟',
        a5: 'يمكننا أن نخبرك — لكن أين المتعة؟ VIP هي أقوى خططنا، مصممة حول احتياجاتك. اضغط «لنتحدث» ونريك ما هو ممكن. قد تفاجأ. 😉',
      },
      footer: {
        appName: 'QRestoo',
        copyrightHolder: 'DLR Web Solutions LLC',
        tagline: 'أبسط طريقة لوضع عملك على الإنترنت.',
        poweredBy: 'مدعوم من',
        poweredByLink: 'DLR Web Solutions',
        product: 'المنتج',
        company: 'الشركة',
        support: 'الدعم',
        integrations: 'التكاملات',
        appStore: 'متجر التطبيقات',
        about: 'من نحن',
        careers: 'الوظائف',
        press: 'الصحافة',
        blog: 'المدونة',
        helpCenter: 'مركز المساعدة',
        contact: 'اتصل',
        contactModalTitle: 'اتصل بنا',
        contactModalSuccess: 'شكراً. سنرد عليك قريباً.',
        apiDocs: 'توثيق API',
        legal: 'قانوني',
        privacy: 'سياسة الخصوصية',
        terms: 'شروط الخدمة',
        cookies: 'إعدادات ملفات التعريف',
      },
    },
    public: {
      about: 'من نحن',
      contactUs: 'اتصل بنا',
      reviewsAndFeedback: 'المراجعات والتعليقات',
      t1NavMenu: 'القائمة',
      reviewsNav: 'المراجعات',
      contactNav: 'اتصل',
      t2MenuNav: 'القائمة',
      t2ViewServices: 'الخدمات',
      t2ReviewsNav: 'المراجعات',
      t2ContactNav: 'اتصل',
      navMenu: 'القائمة',
      navServices: 'الخدمات',
      navProducts: 'المنتجات',
      ownerNavBookingUnavailable: 'الحجز. ترقية الحساب مطلوبة.',
      ownerNavReviewsUnavailable: 'المراجعات. ترقية الحساب مطلوبة.',
      ownerNavLocationUnavailable: 'الموقع. أضف العنوان أو الخريطة في الإعدادات.',
      ownerNavAboutUnavailable: 'قصتنا. أضف الوصف في الإعدادات.',
      ownerNavHoursUnavailable: 'الساعات. أضف ساعات العمل في الإعدادات.',
      t2CommunicationsLink: 'رابط الاتصالات',
      noLogoSetOwnerMessage: 'لم يتم تعيين شعار. أضف واحدًا من إعدادات نشاطك.',
      ownerEditLogoAria: 'تعديل الشعار واللافتة',
      heroEstYear: 'تأسيس {year}',
      noTaglineOrDescriptionSetOwnerMessage: 'لم يتم تعيين شعار أو وصف. أضفهما من إعدادات نشاطك.',
      noDescriptionSetOwnerMessage: 'لم يتم تعيين الوصف. أضف واحدًا من إعدادات نشاطك.',
      noMapSetOwnerMessage: 'لم يتم تعيين الخريطة. أضف العنوان أو رابط الخريطة من إعدادات نشاطك.',
      noBookingAvailableOwnerMessage: 'الحجز غير متاح. فعّله من إعدادات نشاطك.',
      noMailingListAvailableOwnerMessage: 'قائمة البريد غير متاحة. فعّلها من إعدادات نشاطك.',
      noFeedbackAvailableOwnerMessage: 'المراجعات والتعليقات غير متاحة. فعّلها من إعدادات نشاطك.',
      t2NewsletterTitle: 'الاشتراك في النشرة',
      ourMenu: 'قائمتنا',
      ourProducts: 'المنتجات',
      ourServices: 'خدماتنا',
      ourCatalog: 'كتالوجنا',
      viewMenu: 'عرض القائمة',
      viewCatalog: 'عرض الكتالوج',
      viewServices: 'عرض الخدمات',
      leaveFeedback: 'اترك تعليقك',
      ratingLabel: 'التقييم (1–5)',
      yourName: 'اسمك',
      yourMessage: 'رسالتك',
      sendFeedback: 'إرسال التعليق',
      t2FeedbackPerformanceLogging: 'تسجيل الأداء',
      t2FeedbackOperationalLog: 'السجل التشغيلي',
      t2FeedbackTransmitCopy: 'أرسل تقرير تجربتك لضمان الجودة والتحسين.',
      t2FeedbackSessionMeta: 'بيانات الجلسة:',
      t2FeedbackTerminalId: 'معرف الطرفية: TGT-LOG-992',
      t2FeedbackProtocol: 'البروتوكول: feedback_v2.0.sh',
      t2FeedbackRatingLabel: 'تقييم الأداء',
      t2FeedbackRatingHint: 'اختر مقاييس التقييم (1-5)',
      t2FeedbackSitrepLabel: 'التقرير / التعليق المفصل',
      t2FeedbackPlaceholder: 'أدخل بيانات السجل هنا...',
      t2FeedbackSubmitReport: 'إرسال التقرير',
      t2FeedbackDisclaimer: 'بالإرسال، فإنك توافق على بروتوكولات أرشفة البيانات.',
      captchaRequired: 'يرجى إكمال التحقق من أنك لست روبوتاً.',
      formErrorGeneric: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
      noReviewsYet: 'لا توجد مراجعات بعد. كن أول من يترك تعليقاً أدناه.',
      guestExperiences: 'تجارب الضيوف',
      reviewsSubtitle: 'ما يقوله زبائننا',
      announcementTitle: 'إعلان',
      announcementHideForFourDays: 'عدم الإظهار مرة أخرى لمدة 4 أيام',
      close: 'إغلاق',
      mailingListHeading: 'ابق على اطلاع',
      mailingListDescription: 'اشترك للحصول على أخبار وعروض منا.',
      mailingListEmailLabel: 'البريد الإلكتروني',
      mailingListEmailPlaceholder: "you{'@'}example.com",
      mailingListSubscribe: 'اشترك',
      mailingListSuccess: 'تم الاشتراك.',
      mailingListError: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
      mailingListEmailRequired: 'البريد الإلكتروني مطلوب.',
      mailingListEmailInvalid: 'يرجى إدخال بريد إلكتروني صحيح.',
      openingHours: 'ساعات العمل',
      noContactListed: 'لا أرقام أو روابط مسجلة.',
      whatsapp: 'واتساب',
      mobile: 'جوال',
      email: 'البريد الإلكتروني',
      phone: 'هاتف',
      fax: 'فاكس',
      other: 'اتصل',
      facebook: 'فيسبوك',
      instagram: 'إنستغرام',
      twitter: 'تويتر',
      website: 'الموقع',
      viewingAsOwner: 'أنت تشاهد هذا كمالك.',
      viewAsCustomer: 'عرض كعميل',
      viewAsOwner: 'عرض كمالك',
      restaurantNotFoundTitle: 'المطعم غير موجود',
      backToHome: 'العودة للرئيسية',
      openInMaps: 'فتح في الخرائط',
      mapIframeTitle: 'خريطة Google لهذا الموقع',
      mapOpenExternallyHint:
        "اضغط أدناه لفتح هذا الموقع في تطبيق الخرائط. لعرض خريطة هنا، أضف خط العرض والطول أو رابط Google Maps يتضمن دبوس المكان ({'@'}lat,lng) في إعدادات النشاط.",
      ownerNeedsDataIntro: 'هذا الموقع يحتاج إلى مزيد من البيانات. أضف وصفاً أو قائمة أو محتوى آخر من لوحة التحكم.',
      openAdminPage: 'فتح لوحة التحكم',
      addBannerInAdmin: 'أضف صورة غلاف من الإعدادات',
      bookTable: 'حجز طاولة',
      bookAppointment: 'حجز موعد',
      mapPlaceholder: 'الخريطة',
      allRightsReserved: 'جميع الحقوق محفوظة.',
      siteCreatedViaQRestoo: 'الموقع من QRestoo',
      createdByQRestoo: 'أُنشئ على QRestoo',
      sitePoweredByDLR: 'الموقع مدعوم من DLR Web Solutions',
      surpriseMe: 'فاجئني',
      surpriseMeAria: 'فاجئني – اختر عنصراً عشوائياً',
      noMenuItems: 'لا عناصر في القائمة',
      noProducts: 'لا منتجات',
      noServices: 'لا خدمات',
      noCatalogItems: 'لا عناصر في الكتالوج',
      fromPrice: 'من {price}',
      notAvailable: 'غير متاح',
      notAvailableWithNextTime: 'غير متاح حالياً. التوفر التالي: {time}.',
      sizeAndPriceOptions: 'خيارات الحجم والسعر',
      option: 'خيار',
      comboContents: 'محتوى الكومبو',
      ownerEditMenuItemInCatalogAria: 'تعديل العنصر في الكتالوج (يفتح لوحة الإدارة)',
      closeMenu: 'إغلاق القائمة',
      closeCatalogModal: 'إغلاق الكتالوج',
      closeServicesModal: 'إغلاق الخدمات',
      ariaViewMenu: 'عرض القائمة',
      ariaViewCatalog: 'عرض الكتالوج',
      ariaViewServices: 'عرض الخدمات',
      modalTitleMenu: 'القائمة',
      modalTitleCatalog: 'الكتالوج',
      modalTitleProducts: 'المنتجات',
      modalTitleServices: 'الخدمات',
      ariaLocation: 'الموقع',
      ariaMap: 'الخريطة',
      category: 'الفئة',
      mapAriaLabel: 'خريطة موقع المطعم',
      openNow: 'مفتوح الآن',
      closedNow: 'مغلق',
      businessHoursNotListed: 'لم يُنشر جدول الدوام',
      open24_7: 'مفتوح 24/7',
      daySunday: 'الأحد',
      dayMonday: 'الإثنين',
      dayTuesday: 'الثلاثاء',
      dayWednesday: 'الأربعاء',
      dayThursday: 'الخميس',
      dayFriday: 'الجمعة',
      daySaturday: 'السبت',
      t3NavMenu: 'القائمة',
      t3NavHours: 'الساعات',
      t3NavLocation: 'الموقع',
      t3NavReviews: 'المراجعات',
      t3NavAbout: 'من نحن',
      t3OurStory: 'قصتنا',
      t3ExquisiteSelections: 'اختيارات فاخرة',
      t3SeasonalMenu: 'قائمة المواسم',
      t3GuestExperiences: 'تجارب الضيوف',
      t3VerifiedDiner: 'زبون موثق',
      t3HoursAvailability: 'الساعات والتوفر',
      t3QuickLinks: 'روابط سريعة',
      t3FooterTagline: 'نصنع تجارب طعام لا تُنسى.',
      t3FooterTaglineRestaurants: 'نصنع تجارب طعام لا تُنسى. التميز في كل طبق.',
      t3FooterTaglineEcommerce: 'منتجات ذات جودة، مختارة بعناية. التميز في كل طلب.',
      t3FooterTaglineServices: 'خدمة استثنائية. التميز في كل تفصيل.',
      t3FooterContact: 'تواصل معنا للحجوزات والاستفسارات.',
      t3ShareExperience: 'شارك تجربتك',
      t3FeedbackHelps: 'ملاحظاتك تساعدنا على التحسين.',
      t3FeedbackPlaceholder: 'كيف كانت أمسيتك معنا؟',
      t3SubmitReview: 'إرسال المراجعة',
      t3JoinInnerCircle: 'انضم إلى دائرة الضيوف',
      t3JoinInnerCircleDesc: 'استلم دعوات حصرية لفعاليات التذوق وقوائم المواسم.',
      t3Join: 'انضم',
      t5TableIcon: 'أيقونة',
      t5TableImage: 'صورة',
      t5TableProduct: 'المنتج',
      t5TablePrice: 'السعر',
      t5TableStatus: 'الحالة',
      t5TableAction: 'إجراء',
      t5ViewDetails: 'عرض التفاصيل',
      t5InStock: 'متوفر',
      selectVariantToAdd: 'اختر نوعاً لإضافته إلى السلة',
      selectRequiredModifierChoice: 'اختر خياراً واحداً في كل مجموعة مطلوبة قبل الإضافة إلى السلة.',
      modifiersHeading: 'إضافات (اختياري)',
      modifiersAria: 'إضافات اختيارية',
      itemDetailDialogAria: 'تفاصيل عنصر القائمة',
      itemUntitled: 'بدون عنوان',
      itemNotAvailable: 'غير متوفر',
      variantOptionNotAvailable: 'غير متوفر',
      variantOptionsAria: 'الخيارات والأسعار',
      variantImageAlt: 'متغير',
      priceFrom: 'من {price}',
      comboContentsAria: 'محتويات الكومبو',
      modifierUnnamed: 'إضافة',
      modifierNoExtraCost: 'بدون تكلفة إضافية',
      quickOrder: {
        addToCart: 'أضف إلى السلة',
        updateCart: 'تحديث السلة',
        yourOrder: 'طلبك',
        clearAll: 'مسح الكل',
        name: 'اسمك',
        namePlaceholder: 'مثال: أحمد',
        notes: 'ملاحظات',
        notesPlaceholder: 'مثال: صلصة إضافية، بدون بصل...',
        sendViaEmail: 'إرسال عبر البريد',
        sendViaWhatsApp: 'إرسال عبر واتساب',
        sendViaViber: 'إرسال عبر فايبر',
        sendViaSMS: 'إرسال عبر رسالة نصية',
        orderSent: 'تم إرسال الطلب!',
        orderSentDesc: 'تم إرسال طلبك إلى النشاط التجاري.',
        allowPopups: 'يرجى السماح بالنوافذ المنبثقة لهذا الموقع لفتح واتساب أو الرسائل. تحقق من إعدادات المتصفح أو الجهاز (مثل أيقونة شريط العنوان) ثم أعد المحاولة.',
        allowPopupsHint: 'اسمح بالنوافذ المنبثقة لإرسال الطلب عبر واتساب أو الرسائل.',
        nameRequired: 'الاسم مطلوب.',
        nameMaxLength: 'يجب ألا يزيد الاسم عن 100 حرف.',
        notesMaxLength: 'يجب ألا تزيد الملاحظات عن 500 حرف.',
        notesRequired: 'الملاحظات مطلوبة.',
        cartEmpty: 'سلتك فارغة.',
        quantity: 'الكمية',
        remove: 'إزالة',
        itemAdded: 'تمت الإضافة إلى السلة!',
        itemUpdated: 'تم تحديث السلة!',
        ariaClose: 'إغلاق',
        ariaDecrease: 'تقليل الكمية',
        ariaIncrease: 'زيادة الكمية',
        ariaRemoveItem: 'إزالة العنصر',
        fulfillment: 'الاستلام أو في المطعم',
        fulfillmentPickup: 'استلام',
        fulfillmentDineIn: 'في المطعم',
        dineInReferenceLabel: 'الطاولة / مرجع (اختياري)',
        dineInReferencePlaceholder: 'مثال: طاولة 5',
        checkout: 'الدفع',
        orderSummary: 'ملخص الطلب',
        confirmCheckout: 'تأكيد الطلب',
        checkoutSubmitting: 'جاري الإرسال…',
        checkoutFailed: 'تعذر إتمام الطلب. حاول مرة أخرى.',
        subtotal: 'المجموع الفرعي',
        ariaCloseSummary: 'إغلاق ملخص الطلب',
        mobile: 'رقم الجوال',
        mobilePlaceholder: '+966501234567',
        mobileHint: 'ابدأ بعلامة + ثم رمز الدولة والرقم (مثال: +966501234567). سنرسل رمز تحقق برسالة نصية.',
        mobileRequired: 'رقم الجوال مطلوب للاستلام.',
        mobileInvalid: 'أدخل رقماً صالحاً مع رمز الدولة.',
        verificationCode: 'رمز التحقق',
        verificationCodePlaceholder: 'رمز من 6 أرقام',
        verificationCodeInvalid: 'أدخل الرمز المكوّن من 6 أرقام من الرسالة.',
        sendVerificationCode: 'إرسال الرمز',
        pickupServiceFee: 'رسوم الاستلام ({percent}٪)',
        total: 'الإجمالي',
      },
      cookieConsent: {
        barTitle: 'ملفات تعريف الارتباط والتقنيات المشابهة',
        barBody:
          'نستخدم وظائف أساسية لتشغيل الموقع. بإذنك نضيف تحليلات داخلية وأدوات طرف ثالث (مثل التحقق الأمني أو تسجيل الدخول). للتفاصيل، افتح ',
        acceptAll: 'قبول الكل',
        rejectNonEssential: 'رفض غير الضروري',
        customize: 'تخصيص',
        modalTitle: 'تفضيلات ملفات تعريف الارتباط',
        necessaryTitle: 'ضرورية للغاية',
        necessaryDesc: 'مطلوبة للأمان وتسجيل الدخول والوظائف الأساسية. دائماً مفعّلة.',
        analyticsTitle: 'التحليلات (داخلية)',
        analyticsDesc: 'تساعدنا على تحسين الصفحات العامة. تُعالج البيانات على خوادمنا.',
        thirdPartyTitle: 'ميزات طرف ثالث',
        thirdPartyDesc: 'تحمّل أدوات مثل Google reCAPTCHA في بعض النماذج أو عند تسجيل الدخول.',
        save: 'حفظ التفضيلات',
        privacyLink: 'سياسة الخصوصية',
      },
    },
    tour: {
      welcomeTitle: 'مرحباً!',
      welcomeDescription: 'لنأخذ جولة سريعة في لوحة التحكم. سنريك أين تدير الشركات والكتالوج والمزيد.',
      step1Title: 'المنطقة الرئيسية',
      step1Description: 'يظهر محتواك هنا. استخدم الشريطة الجانبية للتنقل بين لوحة التحكم والشركات والأقسام الأخرى.',
      step2Title: 'التنقل',
      step2Description: 'استخدم هذه الشريطة الجانبية للانتقال بين لوحة التحكم والشركات وكتالوج العناصر والتقييمات والملف الشخصي.',
      step2DescriptionSuperadmin: 'استخدم هذه الشريطة الجانبية للانتقال بين لوحة التحكم والمستخدمين وتقييمات المالكين والشركات والمزيد.',
      step3Title: 'لوحة التحكم',
      step3Description: 'قاعدتك. اعرض نظرة عامة على شركاتك وعناصر الكتالوج والتقييمات.',
      step4Title: 'الشركات',
      step4Description: 'أدر شركتك (شركاتك). أضف شركات جديدة وعدّل التفاصيل ونظّم الكتالوج لكل شركة.',
      step5Title: 'كتالوج العناصر',
      step5Description: 'أنشئ وعدّل العناصر (منتجات، خدمات، كومبو). أضفها إلى الفئات داخل كتالوج كل شركة.',
      step6Title: 'التقييمات',
      step6Description: 'اعرض واعتمد تقييمات العملاء من صفحاتك العامة. وافق أو ارفض لإظهارها أو إخفائها.',
      step7Title: 'طلب ميزة',
      step7Description: 'أرسل ملاحظاتك أو طلبات الميزات للفريق. يمكنك أيضاً رؤية إرسالاتك السابقة هنا.',
      step8Title: 'الملف والإعدادات',
      step8Description: 'حدّث اسمك وبريدك الإلكتروني وكلمة المرور ولغة التطبيق.',
      next: 'التالي',
      prev: 'السابق',
      done: 'تم',
      close: 'إغلاق',
      skip: 'تخطي الجولة',
    },
    restaurantsTour: {
      step1Title: 'قائمة الشركات',
      step1Description: 'هذه قائمة شركاتك. أضف شركات جديدة وعدّل التفاصيل وادِر الكتالوج من هنا.',
      step2Title: 'إضافة شركة',
      step2Description: 'انقر هنا لإنشاء شركة جديدة وتعيين الاسم والعنوان والعنوان على الويب.',
      step3Title: 'شركاتك',
      step3Description: 'تظهر شركاتك كبطاقات. انقر على بطاقة لفتح تلك الشركة أو استخدم الحالة الفارغة لإنشاء أول واحدة.',
      step4Title: 'بطاقة الشركة',
      step4Description: 'انقر على شركة لتعديلها وإدارة الكتالوج وتحديد التوفر والحصول على الرابط العام أو رمز QR.',
      tooltip: 'جولة الشركات',
    },
    welcome: {
      title: 'أنشئ مطعمك الأول',
      subtitle: 'انشر قائمتك على الإنترنت، شارك عنوانك وساعات العمل، ودع العملاء يجدونك خلال دقائق.',
      cta: 'إنشاء مطعمي الأول',
    },
    wizard: {
      stepOf: 'الخطوة {current} من {total}',
      step1Title: 'اسم مطعمك',
      nameLabel: 'اسم المطعم',
      taglineOptional: 'شعار (اختياري)',
      slugPreview: 'عنوانك على الويب',
      templateLabel: 'قالب الصفحة العامة',
      templateHint: 'اختر التصميم لصفحتك العامة.',
      next: 'التالي',
      back: 'رجوع',
      skipForNow: 'تخطي الآن',
      doLater: 'سأفعل ذلك لاحقاً',
      step2Title: 'الموقع والاتصال (اختياري)',
      youreAllSet: 'تم بنجاح',
      sublineLive: 'صفحة مطعمك ناشرة. شارك الرابط أو رمز QR ليصل العملاء إليك.',
      copyAddress: 'نسخ العنوان',
      copied: 'تم النسخ!',
      saveQr: 'حفظ رمز QR',
      scanHint: 'العملاء يمسحون هذا لفتح صفحتك',
      goToMyRestaurant: 'الذهاب إلى مطعمي',
      backToDashboard: 'العودة للوحة التحكم',
      firstTimeLink: 'أول مرة؟ أنشئ مطعمك الأول',
    },
    reservation: {
      reserveButton: 'حجز طاولة',
      reserveButtonServices: 'حجز',
      heading: 'حجز طاولة',
      headingServices: 'حجز',
      pax: 'عدد الضيوف',
      paxServices: 'حجم المجموعة',
      nameRequired: 'الاسم مطلوب.',
      nameMax: 'يجب ألا يتجاوز الاسم 150 حرفاً.',
      dateMinTwoDays: 'يرجى اختيار تاريخ بعد يومين على الأقل من اليوم.',
      sendVerificationCode: 'إرسال رمز التحقق',
      enterCodePrompt: 'أدخل الرمز المكون من 6 أرقام الذي أرسلناه إلى هاتفك.',
      verificationCode: 'رمز التحقق',
      codePlaceholder: '000000',
      codeRequired: 'يرجى إدخال رمز التحقق.',
      codeInvalid: 'يرجى إدخال 6 أرقام بالضبط.',
      codeInvalidOrExpired: 'رمز التحقق غير صالح أو منتهي الصلاحية. اطلب رمزاً جديداً.',
      errorGeneric: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
      requestNewCode: 'طلب رمز جديد',
      confirmReservation: 'تأكيد الحجز',
    },
    startTour: 'بدء جولة التطبيق',
    startTourDescription: 'شغّل الجولة الإرشادية مرة أخرى لرؤية المناطق الرئيسية في التطبيق.',
  },
  zh: {
    app: {
      name: 'RMS',
      login: '登录',
      register: '创建账户',
      logout: '退出登录',
      profile: '个人资料与设置',
      restaurants: '餐厅',
      menu: '菜单',
      languages: '语言与描述',
      addLanguage: '添加语言',
      defaultLanguage: '默认语言',
      languagesHint: '添加或移除语言。当公众站点未选择语言时，将使用默认语言。',
      description: '描述',
      save: '保存',
      cancel: '取消',
      remove: '移除',
      translate: '从默认翻译',
      addItem: '添加项目',
      editItem: '编辑菜单项',
      createItem: '添加菜单项',
      dashboard: 'Dashboard',
      feedbacks: 'Feedbacks',
      feedbacksListSubtitle: 'Approve feedback to show it on your public page. Reject or delete to hide or remove it.',
      feedbacksListEmpty: 'No feedbacks yet. They will appear here when customers submit reviews.',
      feedbacksLoadError: 'Failed to load feedbacks.',
      retry: 'Retry',
      ariaMainNav: 'Main navigation',
      ariaAppLanguage: 'App language',
      ariaOpenMenu: 'Open menu',
      ownerFeedbacksSubtitle: 'Feature requests and feedback from restaurant owners. Toggle status when reviewed.',
      superadminInquiriesSubtitle: 'VIP and landing page contact form submissions. Newest first.',
      ownerFeedbacksEmpty: 'No owner feedbacks yet.',
      markPending: 'Mark pending',
      markReviewed: 'Mark reviewed',
      feedbacksUpdateForbidden: "You don't have permission to update feedbacks.",
      feedbackNotFound: 'Feedback not found.',
      feedbackUpdated: 'Feedback updated.',
      feedbackApproved: 'Feedback approved.',
      feedbackRejected: 'Feedback rejected.',
      failedToUpdate: 'Failed to update.',
      feedbackDeleted: 'Feedback deleted.',
      failedToDelete: 'Failed to delete.',
      deleteFeedbackConfirm: 'Delete this feedback? This cannot be undone.',
      users: 'Users',
      superadminUsersSubtitle: 'Manage user accounts: paid status and active/deactivate.',
      noUsersFound: 'No users found.',
      paid: 'Paid',
      free: 'Free',
      active: 'Active',
      deactivated: 'Deactivated',
      superadmin: 'Superadmin',
      markPaid: 'Mark paid',
      removePaid: 'Remove paid',
      activate: 'Activate',
      deactivate: 'Deactivate',
      ariaDeactivateUser: 'Deactivate user',
      ariaActivateUser: 'Activate user',
      you: '(you)',
      usersForbidden: "You don't have permission to view users.",
      superadminRestaurantsSubtitle: 'View-only list of all restaurants in the system.',
      noRestaurantsFound: 'No restaurants found.',
      availability: 'Availability',
      availabilitySubtitle: 'When your restaurant is open. Change times below and tap Save to update.',
      deleteRestaurant: 'Delete restaurant',
      logoAndBanner: 'Logo & banner',
      logoBannerDescription: 'Change or update your restaurant logo and banner images.',
      uploadImagesHint: 'Upload new images. JPEG, PNG, GIF, WebP or HEIC (iPhone). Logo max 2MB, banner max 4MB.',
      logo: 'Logo',
      banner: 'Banner',
      takePhoto: 'Take photo',
      chooseFromGallery: 'Choose from gallery',
      changeLogo: 'Change logo',
      changeBanner: 'Change banner',
      done: 'Done',
      deleteRestaurantConfirm: 'Delete business?',
      cannotBeUndone: 'This cannot be undone.',
      typeToConfirm: 'Type {slug} to confirm',
      ariaUploadingLogo: 'Uploading logo',
      ariaUploadingBanner: 'Uploading banner',
      ariaUploadLogo: 'Upload logo',
      ariaUploadBanner: 'Upload banner',
      ariaUploadLogoFromGallery: 'Upload logo from gallery',
      ariaUploadBannerFromGallery: 'Upload banner from gallery',
      ariaTakePhotoLogo: 'Take photo for logo',
      ariaTakePhotoBanner: 'Take photo for banner',
      settings: 'Settings',
      ariaManageSections: 'Business management sections',
      contactAndDetails: 'Contact & details',
      address: 'Address',
      email: 'Email',
      social: 'Social',
      addLanguageEllipsis: 'Add language…',
      addLanguageHint: 'Add at least one language above to manage descriptions.',
      editDescriptionFor: 'Edit description for:',
      ariaSelectTemplate: 'Select template',
      menusAndCategories: 'Menus & categories',
      createFirstMenuHint: 'Create your first menu to add categories.',
      createCategoryFirstHint: 'Create a category first to add menu items to this menu.',
      menuItemsSettingsHint: "Add dishes to categories from each category's page. Item translations use this restaurant's Settings.",
      createCategoryAboveHint: 'Create a category above to add menu items.',
      renameMenu: 'Rename menu',
      selectMenu: 'Select menu',
      editCategory: 'Edit category',
      removeCategory: 'Remove category',
      addMenu: 'Add menu',
      addCategory: 'Add category',
      categoryAvailability: 'Category availability',
      removeCategoryImage: 'Remove category image',
      addMenuOrCategory: 'Add menu or category',
      editIn: 'Edit in',
      changeImage: 'Change image',
      uploadImage: 'Upload image',
      translating: 'Translating…',
      removing: 'Removing…',
      mainMenuDefault: 'Main menu',
      menuManagement: 'Menu management',
      menuManagementSubtitle: 'Menus, categories and items. Drag to reorder.',
      noMenuItemsYet: 'No menu items yet. Use the + button below to add one.',
      categoryNameRequired: 'Category name is required.',
      categoryUpdated: 'Category updated.',
      categoryCreated: 'Category created.',
      addMenuItemsToCategory: 'Add menu items to this category',
      noMenuItemsOrMatch: 'No menu items, or none match the search.',
      assignTags: 'Assign tags',
      itemAvailability: 'Item availability',
      dragToReorder: 'Drag to reorder',
      setAvailabilityTimes: 'Set availability times',
      available: 'Available',
      notAvailable: 'Not available',
      markNotAvailable: 'Mark not available',
      markAvailableOnMenu: 'Mark available on public menu',
      visibleOnMenu: 'Visible on menu',
      hiddenFromMenu: 'Hidden from menu',
      addMenuItemToCategory: 'Add menu item to category',
      menuItemTags: 'Menu item tags',
      menuItems: 'Menu items',
      createRestaurantFirstForMenus: 'Create a restaurant first to manage menus and items.',
      addRestaurant: 'Add restaurant',
      ratingOutOf5: 'Rating: {n} out of 5',
      anonymous: 'Anonymous',
      approve: 'Approve',
      reject: 'Reject',
      ariaApproveFeedback: 'Approve feedback',
      ariaRejectFeedback: 'Reject feedback',
      ariaDeleteFeedback: 'Delete feedback',
      resetPasswordSuccess: 'Your password has been reset. You can sign in.',
      resetPasswordEnterNew: 'Enter your new password below.',
      type: 'Type',
      labelOptional: 'Label (optional)',
      placeholderUrl: 'https://...',
      placeholderPhone: 'e.g. +1 234 567 8900',
      placeholderContactLabel: 'e.g. Reservations, Kitchen',
      showOnPublicPage: 'Show on public page',
      hideOnPublicPage: 'Hide on public page',
      ariaEditContact: 'Edit contact',
      ariaDeleteContact: 'Delete contact',
      noContactsYet: 'No contacts or links yet. Add one so customers can reach you.',
      deleteContactConfirm: 'Delete this contact?',
      ariaPagination: 'Pagination',
      ariaOpeningTime: 'Opening time',
      ariaClosingTime: 'Closing time',
      removeTimeSlot: 'Remove time slot',
      menuItemLabel: 'Menu item',
      addMenuItem: 'Add menu item',
      ariaRemoveImage: 'Remove image',
      ariaRemoveVariantImage: 'Remove variant image',
      ariaRemoveEntry: 'Remove entry',
      ariaRemoveGroup: 'Remove group',
      ariaShowHelp: 'Show help: what do the buttons mean?',
      help: 'Help',
      ariaButtonLegend: 'Button legend',
      helpLegendDescription:
        'Meaning of the icons you see on menu and category rows in your catalog editor.',
      legendDragHandle: 'Drag handle',
      legendDragDesc: 'Drag to reorder',
      legendSchedule: 'Schedule',
      legendScheduleDesc: 'Set opening hours',
      legendAvailability: 'Availability',
      legendAvailabilityDesc: 'When this item is available',
      legendVisibility: 'Visibility',
      legendVisibilityDesc: 'Show or hide on public menu',
      legendTags: 'Tags',
      legendTagsDesc: 'Assign tags to items',
      paidFeature: 'Paid feature',
      upgradeToUse: 'Upgrade to use this feature.',
      paidFeatureAiDescription: 'Beautify your menu images with AI is available on a paid plan. Upgrade to unlock AI-powered image enhancement.',
      maybeLater: 'Maybe later',
      upgrade: 'Upgrade',
      ariaNotifications: 'Notifications',
      dismiss: 'Dismiss',
      reservationsHowItWorksTitle: '餐桌预订如何运作',
      reservationsHowItWorksIntro: '客人可从您餐厅的公开页面请求订座。您在此批准、拒绝或修改预订。',
      reservationsHowItWorksPhoneRequired: '每笔预订均需提供有效电话号码，以便通过短信发送确认与提醒。',
      reservationsHowItWorksSteps: '为每家餐厅设置短信通知号码以接收新预订提醒。批准或拒绝请求后，客人会收到短信。您也可修改日期、时间或人数并取消预订，客人会收到短信通知。',
      reservationsHowItWorksTooltip: '预订如何运作',
      close: 'Close',
      loading: 'Loading…',
      back: 'Back',
      delete: 'Delete',
      pending: 'Pending',
      featureRequest: 'Feature request',
      ownerFeedbacks: 'Owner feedbacks',
    },
    errors: {
      forbidden: '您没有权限执行此操作。',
      validation: '请检查您的输入。',
      pageNotFound: '页面未找到',
      pageNotFoundDescription: '您访问的页面不存在或已被移动。',
    },
    landing: {
      hero: {
        badge: '商业技术，简单化',
        titleBefore: '您的业务，',
        titleHighlight: '10分钟上线。',
        subtitle: '立即将您的业务上线 — 商品目录、营业时间、预订等。免费开始，无需技术技能。',
        trustedBy: '永久免费。无需信用卡。',
      },
      cta: {
        bookDemo: '查看{businessType}演示',
        bookDemoGeneric: '查看演示',
      },
      footer: {
        cookies: 'Cookie 设置',
      },
    },
    public: {
      about: '关于我们',
      t3NavAbout: '关于',
      siteCreatedViaQRestoo: '本网站由 QRestoo 创建',
      createdByQRestoo: '创建于 QRestoo',
      sitePoweredByDLR: 'Site powered by DLR Web Solutions',
      contactUs: '联系我们',
      reviewsAndFeedback: '评价与反馈',
      t1NavMenu: '菜单',
      reviewsNav: '评价',
      contactNav: '联系',
      t2MenuNav: '菜单',
      t2ViewServices: '服务',
      t2ReviewsNav: '评价',
      t2ContactNav: '联系',
      navMenu: '菜单',
      navServices: '服务',
      navProducts: '产品',
      ownerNavBookingUnavailable: '预订。需要升级账户。',
      ownerNavReviewsUnavailable: '评价。需要升级账户。',
      ownerNavLocationUnavailable: '位置。在设置中添加地址或地图。',
      ownerNavAboutUnavailable: '我们的故事。在设置中添加描述。',
      ownerNavHoursUnavailable: '营业时间。在设置中添加营业时间。',
      t2CommunicationsLink: '通讯链接',
      noLogoSetOwnerMessage: '未设置标志。请在业务设置中添加。',
      ownerEditLogoAria: '编辑标志和横幅',
      heroEstYear: '创立于 {year} 年',
      noTaglineOrDescriptionSetOwnerMessage: '未设置标语或描述。请在业务设置中添加。',
      noDescriptionSetOwnerMessage: '未设置描述。请在业务设置中添加。',
      noMapSetOwnerMessage: '未设置地图。请在业务设置中添加地址或地图链接。',
      noBookingAvailableOwnerMessage: '预订不可用。请在业务设置中启用。',
      noMailingListAvailableOwnerMessage: '邮件列表不可用。请在业务设置中启用。',
      noFeedbackAvailableOwnerMessage: '评论与反馈不可用。请在业务设置中启用。',
      t2NewsletterTitle: '订阅简讯',
      t2FeedbackPerformanceLogging: '性能记录',
      t2FeedbackOperationalLog: '操作日志',
      t2FeedbackTransmitCopy: '提交您的体验报告，用于质量保证与优化。',
      t2FeedbackSessionMeta: '会话元数据：',
      t2FeedbackTerminalId: '终端 ID：TGT-LOG-992',
      t2FeedbackProtocol: '协议：feedback_v2.0.sh',
      t2FeedbackRatingLabel: '单元性能评分',
      t2FeedbackRatingHint: '选择评估指标 (1-5)',
      t2FeedbackSitrepLabel: '情况报告 / 详细反馈',
      t2FeedbackPlaceholder: '在此输入日志数据...',
      t2FeedbackSubmitReport: '提交报告',
      t2FeedbackDisclaimer: '提交即表示您同意数据归档协议。',
      ourMenu: '我们的菜单',
      ourProducts: '产品',
      ourServices: '我们的服务',
      ourCatalog: '我们的目录',
      viewMenu: '查看菜单',
      viewCatalog: '查看目录',
      viewServices: '查看服务',
      leaveFeedback: '留下您的反馈',
      ratingLabel: '评分（1–5）',
      yourName: '您的姓名',
      yourMessage: '您的留言',
      sendFeedback: '提交反馈',
      formErrorGeneric: '出了点问题，请重试。',
      mailingListError: '出了点问题，请重试。',
      noReviewsYet: '暂无评价。在下方留下您的第一条反馈吧。',
      openingHours: '营业时间',
      noContactListed: '暂无联系电话或链接。',
      whatsapp: 'WhatsApp',
      mobile: '手机',
      email: '电子邮件',
      phone: '电话',
      fax: '传真',
      other: '联系',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      website: '网站',
      viewingAsOwner: '您正在以店主身份查看此页。',
      viewAsCustomer: '以顾客身份查看',
      viewAsOwner: '以店主身份查看',
      restaurantNotFoundTitle: '未找到餐厅',
      backToHome: '返回首页',
      ownerNeedsDataIntro: '此站点需要更多内容。请在管理后台添加简介、菜单或其他内容。',
      openAdminPage: '打开管理后台',
      addBannerInAdmin: '在设置中添加封面图片',
      bookTable: '预订餐桌',
      bookAppointment: '预约',
      mapPlaceholder: '地图',
      allRightsReserved: '版权所有。',
      surpriseMe: 'Surprise me',
      surpriseMeAria: 'Surprise me – pick a random menu item',
      noMenuItems: 'No menu items',
      noProducts: 'No products',
      noServices: 'No services',
      noCatalogItems: 'No items in catalog',
      fromPrice: '从 {price} 起',
      notAvailable: '不可用',
      notAvailableWithNextTime: '当前不可用。下次可点：{time}。',
      sizeAndPriceOptions: '规格与价格',
      option: '选项',
      comboContents: '套餐内容',
      ownerEditMenuItemInCatalogAria: '在目录中编辑项目（打开管理后台）',
      closeMenu: '关闭菜单',
      closeCatalogModal: '关闭目录',
      closeServicesModal: '关闭服务',
      ariaViewMenu: '查看菜单',
      ariaViewCatalog: '查看目录',
      ariaViewServices: '查看服务',
      modalTitleMenu: '菜单',
      modalTitleCatalog: '目录',
      modalTitleProducts: '产品',
      modalTitleServices: '服务',
      ariaLocation: '位置',
      ariaMap: '地图',
      category: '分类',
      mapAriaLabel: '显示餐厅位置的地图',
      openInMaps: '在地图中打开',
      mapIframeTitle: '此地点的 Google 地图',
      mapOpenExternallyHint:
        "点击下方可在地图应用中打开此位置。若要在本页显示地图，请在商家设置中添加经纬度或包含地点标记的 Google 地图链接（{'@'}lat,lng）。",
      openNow: '营业中',
      closedNow: '休息中',
      businessHoursNotListed: '未公布营业时间',
      open24_7: '全天候营业',
      daySunday: '星期日',
      dayMonday: '星期一',
      dayTuesday: '星期二',
      dayWednesday: '星期三',
      dayThursday: '星期四',
      dayFriday: '星期五',
      daySaturday: '星期六',
      t5TableIcon: 'Icon',
      t5TableImage: 'Image',
      t5TableProduct: 'Product',
      t5TablePrice: 'Price',
      t5TableStatus: 'Status',
      t5TableAction: 'Action',
      t5ViewDetails: 'View details',
      t5InStock: 'In stock',
      selectVariantToAdd: '请选择要加入购物车的规格',
      selectRequiredModifierChoice: '加入购物车前，请在每个必选分组中各选一项。',
      modifiersHeading: '附加选项（可选）',
      modifiersAria: '可选附加项',
      itemDetailDialogAria: '菜单项详情',
      itemUntitled: '无标题',
      itemNotAvailable: '暂不提供',
      variantOptionNotAvailable: '暂不提供',
      variantOptionsAria: '规格与价格',
      variantImageAlt: '规格',
      priceFrom: '起价 {price}',
      comboContentsAria: '套餐内容',
      modifierUnnamed: '加购项',
      modifierNoExtraCost: '不额外收费',
      quickOrder: {
        addToCart: '加入购物车',
        updateCart: '更新购物车',
        yourOrder: '您的订单',
        clearAll: '全部清除',
        name: '您的姓名',
        namePlaceholder: '例如：王明',
        notes: '备注',
        notesPlaceholder: '例如：加辣酱，不要洋葱...',
        sendViaEmail: '通过邮件发送',
        sendViaWhatsApp: '通过 WhatsApp 发送',
        sendViaViber: '通过 Viber 发送',
        sendViaSMS: '通过短信发送',
        orderSent: '订单已发送！',
        orderSentDesc: '您的订单已发送给商家。',
        nameRequired: '请输入姓名。',
        nameMaxLength: '姓名最多 100 个字符。',
        notesMaxLength: '备注最多 500 个字符。',
        notesRequired: '请输入备注。',
        cartEmpty: '购物车为空。',
        quantity: '数量',
        remove: '移除',
        itemAdded: '已加入购物车！',
        itemUpdated: '购物车已更新！',
        ariaClose: '关闭',
        ariaDecrease: '减少数量',
        ariaIncrease: '增加数量',
        ariaRemoveItem: '移除商品',
        fulfillment: '取餐方式',
        fulfillmentPickup: '外带',
        fulfillmentDineIn: '堂食',
        dineInReferenceLabel: '桌号 / 备注（选填）',
        dineInReferencePlaceholder: '例如：5号桌',
        checkout: '结账',
        orderSummary: '订单摘要',
        confirmCheckout: '提交订单',
        checkoutSubmitting: '提交中…',
        checkoutFailed: '无法提交订单，请重试。',
        subtotal: '小计',
        ariaCloseSummary: '关闭订单摘要',
        mobile: '手机号',
        mobilePlaceholder: '+8613800138000',
        mobileHint: '请以 + 开头并包含国家区号（例如 +86…）。我们将通过短信发送验证码。',
        mobileRequired: '外带订单需要填写手机号。',
        mobileInvalid: '请输入带国家区号的有效号码。',
        verificationCode: '验证码',
        verificationCodePlaceholder: '6 位验证码',
        verificationCodeInvalid: '请输入短信中的 6 位验证码。',
        sendVerificationCode: '发送验证码',
        pickupServiceFee: '外带服务费（{percent}%）',
        total: '合计',
      },
      cookieConsent: {
        barTitle: 'Cookie 与类似技术',
        barBody:
          '我们使用必要的功能来运行网站。在您同意的情况下，我们会启用第一方分析及第三方工具（例如安全验证或登录）。详情见',
        acceptAll: '全部接受',
        rejectNonEssential: '拒绝非必要项',
        customize: '自定义',
        modalTitle: 'Cookie 偏好',
        necessaryTitle: '严格必要',
        necessaryDesc: '用于安全、登录和核心功能。始终开启。',
        analyticsTitle: '分析（第一方）',
        analyticsDesc: '帮助我们改进公开页面。数据在我们的服务器上处理。',
        thirdPartyTitle: '第三方功能',
        thirdPartyDesc: '在您使用某些表单或登录时加载 Google reCAPTCHA 等工具。',
        save: '保存偏好',
        privacyLink: '隐私政策',
      },
      // Quick-add item wizard (owner public site)
      quickAddItem: '添加菜品',
      quickAddItemAria: '添加菜单项',
      quickAddStepOf: '第 {step} 步，共 {total} 步',
      quickAddStep0Title: '菜品<em>类型</em>',
      quickAddStep1Title: '选择<em>分类</em>',
      quickAddStep2Title: '菜品<em>详情</em>',
      quickAddStep3Title: '检查并<em>保存</em>',
      quickAddWhatKind: '这是什么类型的菜品？',
      quickAddTypeSimpleSub: '单一价格',
      quickAddTypeVariantsSub: '规格、选项…',
      quickAddTypeCombSub: '套餐',
      quickAddTypeSimpleHint: '单一产品或菜品，固定价格。',
      quickAddTypeVariantsHint: '同一菜品有多种规格，每种价格不同（如小份/大份）。',
      quickAddTypeComboHint: '多个菜品组合销售（如套餐）。',
      quickAddNoCats: '暂无分类',
      quickAddNoCatsSub: '在下方创建第一个分类以整理菜品。',
      quickAddOrCreate: '或新建',
      quickAddFirstCatName: '第一个分类名称',
      quickAddNewCat: '新分类',
      quickAddCatPlaceholder: '如：主菜、饮品、服务…',
      quickAddCreate: '创建',
      quickAddCatWillCreate: '将创建：{name}',
      quickAddItem1: '项',
      quickAddItemN: '项',
      quickAddNameLabel: '菜品名称',
      quickAddNamePlaceholder: '如：玛格丽特披萨',
      quickAddPriceOptional: '价格（可选）',
      quickAddComboPriceOptional: '套餐价（可选）',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: '描述（可选）',
      quickAddDescPlaceholder: '显示在菜品卡片上…',
      quickAddVariantsLabel: '规格',
      quickAddVariantName: '选项名称',
      quickAddVariantNamePlaceholder: '如：大份',
      quickAddVariantPrice: '价格',
      quickAddRemoveVariant: '删除规格',
      quickAddAddVariant: '添加规格',
      quickAddComboItems: '套餐内菜品',
      quickAddPickItem: '选择菜品…',
      quickAddAdd: '添加',
      quickAddRemoveComboItem: '移除',
      quickAddNoItemsForCombo: '还没有菜品 — 请先添加简单或带规格的菜品。',
      quickAddReviewItem: '菜品',
      quickAddReviewName: '名称',
      quickAddReviewType: '类型',
      quickAddReviewPrice: '价格',
      quickAddReviewIncludes: '包含',
      quickAddReviewCategory: '分类',
      quickAddReviewCategoryLabel: '分类',
      quickAddNoPrice: '未设价格',
      quickAddNoCategory: '无',
      quickAddNew: '新',
      quickAddPublishNow: '立即发布',
      quickAddPublishNowSub: '马上在网站上显示',
      quickAddAddAnother: '保存后再添加一个',
      quickAddAddAnotherSub: '保持此流程打开',
      quickAddSaveItem: '保存菜品',
      quickAddReview: '检查',
      quickAddContinue: '继续',
      quickAddBack: '返回',
      quickAddSaved: '已保存。',
      quickAddSavedSub: '已将 {name} 添加到 {category}。',
      quickAddAnotherItem: '+ 再添加一个',
      quickAddDone: '完成',
    },
    tour: {
      welcomeTitle: '欢迎！',
      welcomeDescription: '来快速了解一下您的控制台。您将看到如何管理餐厅、菜单等。',
      step1Title: '主区域',
      step1Description: '您的内容显示在这里。使用侧边栏在控制台、餐厅和其他部分之间切换。',
      step2Title: '导航',
      step2Description: '使用此侧边栏在控制台、餐厅、菜单项、反馈和个人资料之间切换。',
      step2DescriptionSuperadmin: '使用此侧边栏在控制台、用户、业主反馈、餐厅等之间切换。',
      step3Title: '控制台',
      step3Description: '您的总览。一眼查看餐厅、菜单项和反馈。',
      step4Title: '餐厅',
      step4Description: '管理您的餐厅。添加地点、编辑详情、按餐厅组织菜单。',
      step5Title: '菜单项',
      step5Description: '创建和编辑菜品、饮料和套餐。将它们添加到每个餐厅菜单的类别中。',
      step6Title: '反馈',
      step6Description: '查看并管理来自您公开页面的客户评价。批准或拒绝以显示或隐藏。',
      step7Title: '功能建议',
      step7Description: '向团队发送反馈或功能建议。您也可以在此查看以往提交。',
      step8Title: '个人资料与设置',
      step8Description: '更新您的姓名、邮箱、密码和应用语言。',
      next: '下一步',
      prev: '上一步',
      done: '完成',
      close: '关闭',
      skip: '跳过导览',
    },
    restaurantsTour: {
      step1Title: '餐厅列表',
      step1Description: '这是您的餐厅列表。在此添加地点、编辑详情和管理菜单。',
      step2Title: '添加餐厅',
      step2Description: '点击此处创建新餐厅并设置名称、地址和子域名。',
      step3Title: '您的餐厅',
      step3Description: '您的餐厅以卡片形式显示。点击卡片打开该餐厅，或使用空状态创建第一家。',
      step4Title: '餐厅卡片',
      step4Description: '点击餐厅可进行编辑、管理菜单、设置可用性并获取公开链接或二维码。',
      tooltip: '餐厅导览',
    },
    welcome: {
      title: 'Create your first restaurant',
      subtitle: 'Get your menu online, share your address and hours, and let customers find you in minutes.',
      cta: 'Create my first restaurant',
    },
    wizard: {
      stepOf: 'Step {current} of {total}',
      step1Title: 'Name your restaurant',
      nameLabel: 'Restaurant name',
      taglineOptional: 'Tagline (optional)',
      slugPreview: 'Your web address',
      templateLabel: 'Public page template',
      templateHint: 'Choose the layout for your public page.',
      next: 'Next',
      back: 'Back',
      skipForNow: 'Skip for now',
      doLater: "I'll do this later",
      step2Title: 'Location & contact (optional)',
      youreAllSet: "You're all set",
      sublineLive: 'Your restaurant page is live. Share the link or QR so customers can find you.',
      copyAddress: 'Copy address',
      copied: 'Copied!',
      saveQr: 'Save QR code',
      scanHint: 'Customers scan this to open your page',
      goToMyRestaurant: 'Go to my restaurant',
      backToDashboard: 'Back to dashboard',
      firstTimeLink: 'First time? Create your first restaurant',
    },
    startTour: '开始应用导览',
    startTourDescription: '再次运行导览以查看应用的主要区域。',
  },
  fil: {
    app: {
      name: 'RMS',
      login: 'Mag-sign in',
      register: 'Gumawa ng account',
      logout: 'Mag-sign out',
      profile: 'Profile at mga setting',
      restaurants: 'Mga restaurant',
      menu: 'Menu',
      languages: 'Mga wika at paglalarawan',
      addLanguage: 'Magdagdag ng wika',
      defaultLanguage: 'Default na wika',
      languagesHint: 'Magdagdag o mag-alis ng mga wika. Ang default na wika ay ginagamit kapag walang wika na napili sa pampublikong site.',
      description: 'Paglalarawan',
      save: 'I-save',
      cancel: 'Kanselahin',
      remove: 'Alisin',
      translate: 'Isalin mula sa default',
      addItem: 'Magdagdag ng item',
      editItem: 'I-edit ang item sa menu',
      createItem: 'Magdagdag ng item sa menu',
      dashboard: 'Dashboard',
      feedbacks: 'Feedbacks',
      feedbacksListSubtitle: 'Approve feedback to show it on your public page. Reject or delete to hide or remove it.',
      feedbacksListEmpty: 'No feedbacks yet. They will appear here when customers submit reviews.',
      feedbacksLoadError: 'Failed to load feedbacks.',
      retry: 'Retry',
      ariaMainNav: 'Main navigation',
      ariaAppLanguage: 'App language',
      ariaOpenMenu: 'Open menu',
      ownerFeedbacksSubtitle: 'Feature requests and feedback from restaurant owners. Toggle status when reviewed.',
      superadminInquiriesSubtitle: 'VIP and landing page contact form submissions. Newest first.',
      ownerFeedbacksEmpty: 'No owner feedbacks yet.',
      markPending: 'Mark pending',
      markReviewed: 'Mark reviewed',
      feedbacksUpdateForbidden: "You don't have permission to update feedbacks.",
      feedbackNotFound: 'Feedback not found.',
      feedbackUpdated: 'Feedback updated.',
      feedbackApproved: 'Feedback approved.',
      feedbackRejected: 'Feedback rejected.',
      failedToUpdate: 'Failed to update.',
      feedbackDeleted: 'Feedback deleted.',
      failedToDelete: 'Failed to delete.',
      deleteFeedbackConfirm: 'Delete this feedback? This cannot be undone.',
      users: 'Users',
      superadminUsersSubtitle: 'Manage user accounts: paid status and active/deactivate.',
      noUsersFound: 'No users found.',
      paid: 'Paid',
      free: 'Free',
      active: 'Active',
      deactivated: 'Deactivated',
      superadmin: 'Superadmin',
      markPaid: 'Mark paid',
      removePaid: 'Remove paid',
      activate: 'Activate',
      deactivate: 'Deactivate',
      ariaDeactivateUser: 'Deactivate user',
      ariaActivateUser: 'Activate user',
      you: '(you)',
      usersForbidden: "You don't have permission to view users.",
      superadminRestaurantsSubtitle: 'View-only list of all restaurants in the system.',
      noRestaurantsFound: 'No restaurants found.',
      availability: 'Availability',
      availabilitySubtitle: 'When your restaurant is open. Change times below and tap Save to update.',
      deleteRestaurant: 'Delete restaurant',
      logoAndBanner: 'Logo & banner',
      logoBannerDescription: 'Change or update your restaurant logo and banner images.',
      uploadImagesHint: 'Upload new images. JPEG, PNG, GIF, WebP or HEIC (iPhone). Logo max 2MB, banner max 4MB.',
      logo: 'Logo',
      banner: 'Banner',
      takePhoto: 'Take photo',
      chooseFromGallery: 'Choose from gallery',
      changeLogo: 'Change logo',
      changeBanner: 'Change banner',
      done: 'Done',
      deleteRestaurantConfirm: 'Delete business?',
      cannotBeUndone: 'This cannot be undone.',
      typeToConfirm: 'Type {slug} to confirm',
      ariaUploadingLogo: 'Uploading logo',
      ariaUploadingBanner: 'Uploading banner',
      ariaUploadLogo: 'Upload logo',
      ariaUploadBanner: 'Upload banner',
      ariaUploadLogoFromGallery: 'Upload logo from gallery',
      ariaUploadBannerFromGallery: 'Upload banner from gallery',
      ariaTakePhotoLogo: 'Take photo for logo',
      ariaTakePhotoBanner: 'Take photo for banner',
      settings: 'Settings',
      ariaManageSections: 'Business management sections',
      contactAndDetails: 'Contact & details',
      address: 'Address',
      email: 'Email',
      social: 'Social',
      addLanguageEllipsis: 'Add language…',
      addLanguageHint: 'Add at least one language above to manage descriptions.',
      editDescriptionFor: 'Edit description for:',
      ariaSelectTemplate: 'Select template',
      menusAndCategories: 'Menus & categories',
      createFirstMenuHint: 'Create your first menu to add categories.',
      createCategoryFirstHint: 'Create a category first to add menu items to this menu.',
      menuItemsSettingsHint: "Add dishes to categories from each category's page. Item translations use this restaurant's Settings.",
      createCategoryAboveHint: 'Create a category above to add menu items.',
      renameMenu: 'Rename menu',
      selectMenu: 'Select menu',
      editCategory: 'Edit category',
      removeCategory: 'Remove category',
      addMenu: 'Add menu',
      addCategory: 'Add category',
      categoryAvailability: 'Category availability',
      removeCategoryImage: 'Remove category image',
      addMenuOrCategory: 'Add menu or category',
      editIn: 'Edit in',
      changeImage: 'Change image',
      uploadImage: 'Upload image',
      translating: 'Translating…',
      removing: 'Removing…',
      mainMenuDefault: 'Main menu',
      menuManagement: 'Menu management',
      menuManagementSubtitle: 'Menus, categories and items. Drag to reorder.',
      noMenuItemsYet: 'No menu items yet. Use the + button below to add one.',
      categoryNameRequired: 'Category name is required.',
      categoryUpdated: 'Category updated.',
      categoryCreated: 'Category created.',
      addMenuItemsToCategory: 'Add menu items to this category',
      noMenuItemsOrMatch: 'No menu items, or none match the search.',
      assignTags: 'Assign tags',
      itemAvailability: 'Item availability',
      dragToReorder: 'Drag to reorder',
      setAvailabilityTimes: 'Set availability times',
      available: 'Available',
      notAvailable: 'Not available',
      markNotAvailable: 'Mark not available',
      markAvailableOnMenu: 'Mark available on public menu',
      visibleOnMenu: 'Visible on menu',
      hiddenFromMenu: 'Hidden from menu',
      addMenuItemToCategory: 'Add menu item to category',
      menuItemTags: 'Menu item tags',
      menuItems: 'Menu items',
      createRestaurantFirstForMenus: 'Create a restaurant first to manage menus and items.',
      addRestaurant: 'Add restaurant',
      ratingOutOf5: 'Rating: {n} out of 5',
      anonymous: 'Anonymous',
      approve: 'Approve',
      reject: 'Reject',
      ariaApproveFeedback: 'Approve feedback',
      ariaRejectFeedback: 'Reject feedback',
      ariaDeleteFeedback: 'Delete feedback',
      resetPasswordSuccess: 'Your password has been reset. You can sign in.',
      resetPasswordEnterNew: 'Enter your new password below.',
      type: 'Type',
      labelOptional: 'Label (optional)',
      placeholderUrl: 'https://...',
      placeholderPhone: 'e.g. +1 234 567 8900',
      placeholderContactLabel: 'e.g. Reservations, Kitchen',
      showOnPublicPage: 'Show on public page',
      hideOnPublicPage: 'Hide on public page',
      ariaEditContact: 'Edit contact',
      ariaDeleteContact: 'Delete contact',
      noContactsYet: 'No contacts or links yet. Add one so customers can reach you.',
      deleteContactConfirm: 'Delete this contact?',
      ariaPagination: 'Pagination',
      ariaOpeningTime: 'Opening time',
      ariaClosingTime: 'Closing time',
      removeTimeSlot: 'Remove time slot',
      menuItemLabel: 'Menu item',
      addMenuItem: 'Add menu item',
      ariaRemoveImage: 'Remove image',
      ariaRemoveVariantImage: 'Remove variant image',
      ariaRemoveEntry: 'Remove entry',
      ariaRemoveGroup: 'Remove group',
      ariaShowHelp: 'Show help: what do the buttons mean?',
      help: 'Help',
      ariaButtonLegend: 'Button legend',
      helpLegendDescription:
        'Meaning of the icons you see on menu and category rows in your catalog editor.',
      legendDragHandle: 'Drag handle',
      legendDragDesc: 'Drag to reorder',
      legendSchedule: 'Schedule',
      legendScheduleDesc: 'Set opening hours',
      legendAvailability: 'Availability',
      legendAvailabilityDesc: 'When this item is available',
      legendVisibility: 'Visibility',
      legendVisibilityDesc: 'Show or hide on public menu',
      legendTags: 'Tags',
      legendTagsDesc: 'Assign tags to items',
      paidFeature: 'Paid feature',
      upgradeToUse: 'Upgrade to use this feature.',
      paidFeatureAiDescription: 'Beautify your menu images with AI is available on a paid plan. Upgrade to unlock AI-powered image enhancement.',
      maybeLater: 'Maybe later',
      upgrade: 'Upgrade',
      ariaNotifications: 'Notifications',
      dismiss: 'Dismiss',
      reservationsHowItWorksTitle: 'Paano gumagana ang mga reserbasyon ng mesa',
      reservationsHowItWorksIntro: 'Maaaring humiling ng mesa ang mga bisita mula sa pampublikong pahina ng iyong restaurant. Inaaprubahan, tinatanggihan, o ine-edit mo ang mga booking dito.',
      reservationsHowItWorksPhoneRequired: 'Kailangan ng wastong numero ng telepono para sa bawat reserbasyon para makapagpadala ng mga kumpirmasyon at paalala sa pamamagitan ng SMS.',
      reservationsHowItWorksSteps: 'Itakda ang numero ng abiso sa SMS para sa bawat restaurant para makatanggap ng mga alerto sa bagong reserbasyon. Aprubahan o tanggihan ang mga kahilingan; makakatanggap ang mga bisita ng SMS. Maaari mo ring i-edit ang petsa, oras, o bilang ng mga bisita at kanselahin—nao-notify ang mga bisita sa pamamagitan ng SMS.',
      reservationsHowItWorksTooltip: 'Paano gumagana ang mga reserbasyon',
      close: 'Close',
      loading: 'Loading…',
      back: 'Back',
      delete: 'Delete',
      pending: 'Pending',
      featureRequest: 'Feature request',
      ownerFeedbacks: 'Owner feedbacks',
    },
    errors: {
      forbidden: 'Wala kang pahintulot na gawin ito.',
      validation: 'Pakitingnan ang iyong input.',
      pageNotFound: 'Pahina hindi nahanap',
      pageNotFoundDescription: 'Ang pahinang hinahanap mo ay wala o nailipat na.',
    },
    landing: {
      hero: {
        badge: 'Teknolohiya para sa negosyo, pinasimple',
        titleBefore: 'Ang iyong negosyo, ',
        titleHighlight: 'online sa 10 minuto.',
        subtitle: 'Dalhin ang iyong negosyo online ngayon — Katalogo ng Produkto, Oras, Booking at marami pa. Libreng magsimula. Walang kailangan na teknikal na kasanayan.',
        trustedBy: 'Libre magpakailanman. Hindi kailangan ng credit card.',
      },
      cta: {
        bookDemo: 'Tingnan ang demo ng {businessType}',
        bookDemoGeneric: 'Tingnan ang demo',
      },
      footer: {
        cookies: 'Mga setting ng cookie',
      },
    },
    public: {
      about: 'Tungkol sa Amin',
      t3NavAbout: 'Tungkol',
      siteCreatedViaQRestoo: 'Site na nilikha ng QRestoo',
      createdByQRestoo: 'Ginawa sa QRestoo',
      sitePoweredByDLR: 'Site powered by DLR Web Solutions',
      contactUs: 'Makipag-ugnayan',
      reviewsAndFeedback: 'Mga review at feedback',
      t1NavMenu: 'Menu',
      reviewsNav: 'Mga review',
      contactNav: 'Contact',
      t2MenuNav: 'Menu',
      t2ViewServices: 'Services',
      t2ReviewsNav: 'Mga review',
      t2ContactNav: 'Contact',
      navMenu: 'Menu',
      navServices: 'Services',
      navProducts: 'Products',
      ownerNavBookingUnavailable: 'Booking. Kailangan i-upgrade ang account.',
      ownerNavReviewsUnavailable: 'Reviews. Kailangan i-upgrade ang account.',
      ownerNavLocationUnavailable: 'Lokasyon. Magdagdag ng address o mapa sa mga setting.',
      ownerNavAboutUnavailable: 'Ang aming kuwento. Magdagdag ng paglalarawan sa mga setting.',
      ownerNavHoursUnavailable: 'Oras. Magdagdag ng opening hours sa mga setting.',
      t3FooterTaglineRestaurants: 'Gumagawa ng hindi malilimutang karanasan sa pagkain. Kahusayan sa bawat plato.',
      t3FooterTaglineEcommerce: 'De-kalidad na mga produkto, maingat na kinuha. Kahusayan sa bawat order.',
      t3FooterTaglineServices: 'Naghahatid ng pambihirang serbisyo. Kahusayan sa bawat detalye.',
      t2CommunicationsLink: 'Communications Link',
      noLogoSetOwnerMessage: 'No logo set. Add one in your business settings.',
      ownerEditLogoAria: 'I-edit ang logo at banner',
      heroEstYear: 'Mula noong {year}',
      noTaglineOrDescriptionSetOwnerMessage: 'Walang tagline o deskripsyon. Idagdag sa mga setting ng negosyo.',
      noDescriptionSetOwnerMessage: 'Walang deskripsyon. Magdagdag sa mga setting ng negosyo.',
      noMapSetOwnerMessage: 'Walang mapa. Magdagdag ng address o link ng mapa sa mga setting ng negosyo.',
      noBookingAvailableOwnerMessage: 'Hindi available ang booking. I-enable ito sa mga setting ng negosyo.',
      noMailingListAvailableOwnerMessage: 'Hindi available ang mailing list. I-enable ito sa mga setting ng negosyo.',
      noFeedbackAvailableOwnerMessage: 'Hindi available ang reviews at feedback. I-enable ang mga ito sa mga setting ng negosyo.',
      t2NewsletterTitle: 'PAG-SUSCRIBE SA NEWSLETTER',
      t2FeedbackPerformanceLogging: 'Performance Logging',
      t2FeedbackOperationalLog: 'OPERATIONAL LOG',
      t2FeedbackTransmitCopy: 'Ipadala ang iyong experience report para sa quality assurance at optimization.',
      t2FeedbackSessionMeta: 'Session Metadata:',
      t2FeedbackTerminalId: 'Terminal ID: TGT-LOG-992',
      t2FeedbackProtocol: 'Protocol: feedback_v2.0.sh',
      t2FeedbackRatingLabel: 'UNIT PERFORMANCE RATING',
      t2FeedbackRatingHint: 'Piliin ang evaluation metrics (1-5)',
      t2FeedbackSitrepLabel: 'SITREP / DETAILED FEEDBACK',
      t2FeedbackPlaceholder: 'Ilagay ang log data dito...',
      t2FeedbackSubmitReport: 'ISUMITE ANG REPORT',
      t2FeedbackDisclaimer: 'Sa pagsusumite, sumasang-ayon ka sa data archiving protocols.',
      ourMenu: 'Ang Aming Menu',
      ourProducts: 'Mga Produkto',
      ourServices: 'Ang Aming Mga Serbisyo',
      ourCatalog: 'Ang Aming Katalogo',
      viewMenu: 'Tingnan ang menu',
      viewCatalog: 'Tingnan ang katalogo',
      viewServices: 'Tingnan ang mga serbisyo',
      leaveFeedback: 'Iwanan ang iyong feedback',
      ratingLabel: 'Rating (1–5)',
      yourName: 'Ang iyong pangalan',
      yourMessage: 'Ang iyong mensahe',
      sendFeedback: 'Magpadala ng feedback',
      formErrorGeneric: 'May nangyaring mali. Pakisubukang muli.',
      mailingListError: 'May nangyaring mali. Pakisubukang muli.',
      noReviewsYet: 'Wala pang mga review. Ikaw ang unang mag-iwan ng feedback sa ibaba.',
      openingHours: 'Oras ng pagbubukas',
      noContactListed: 'Walang nakalistang numero o link.',
      whatsapp: 'WhatsApp',
      mobile: 'Mobile',
      email: 'Email',
      phone: 'Telepono',
      fax: 'Fax',
      other: 'Contact',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      website: 'Website',
      viewingAsOwner: 'Tinitingnan mo ito bilang may-ari.',
      viewAsCustomer: 'Tingnan bilang Customer',
      viewAsOwner: 'Tingnan bilang may-ari',
      restaurantNotFoundTitle: 'Hindi nahanap ang restaurant',
      backToHome: 'Bumalik sa home',
      ownerNeedsDataIntro: 'Kailangan ng site na ito ng mas maraming data. Magdagdag ng paglalarawan, menu, o iba pang content sa admin.',
      openAdminPage: 'Buksan ang admin page',
      addBannerInAdmin: 'Magdagdag ng cover image sa Settings',
      bookTable: 'Mag-book ng mesa',
      bookAppointment: 'Mag-book ng appointment',
      mapPlaceholder: 'Mapa',
      allRightsReserved: 'Reserbado ang lahat ng karapatan.',
      surpriseMe: 'Surprise me',
      surpriseMeAria: 'Surprise me – pick a random menu item',
      noMenuItems: 'No menu items',
      noProducts: 'No products',
      noServices: 'No services',
      noCatalogItems: 'No items in catalog',
      fromPrice: 'Mula {price}',
      notAvailable: 'Hindi available',
      notAvailableWithNextTime: 'Hindi available ngayon. Susunod na available: {time}.',
      sizeAndPriceOptions: 'Mga opsyon sa laki at presyo',
      option: 'Opsyon',
      comboContents: 'Nilalaman ng combo',
      ownerEditMenuItemInCatalogAria: 'I-edit ang item sa catalog (bubuksan ang admin)',
      closeMenu: 'Isara ang menu',
      closeCatalogModal: 'Isara ang katalogo',
      closeServicesModal: 'Isara ang mga serbisyo',
      ariaViewMenu: 'Tingnan ang menu',
      ariaViewCatalog: 'Tingnan ang katalogo',
      ariaViewServices: 'Tingnan ang mga serbisyo',
      modalTitleMenu: 'Menu',
      modalTitleCatalog: 'Katalogo',
      modalTitleProducts: 'Mga Produkto',
      modalTitleServices: 'Mga Serbisyo',
      ariaLocation: 'Lokasyon',
      ariaMap: 'Mapa',
      category: 'Kategorya',
      mapAriaLabel: 'Mapa na nagpapakita ng lokasyon ng restaurant',
      openInMaps: 'Buksan sa mapa',
      mapIframeTitle: 'Google Map para sa lokasyong ito',
      mapOpenExternallyHint:
        "I-tap sa ibaba para buksan ang lokasyon sa maps app mo. Para magpakita ng mapa dito, magdagdag ng latitude/longitude o Google Maps link na may pin ({'@'}lat,lng) sa settings ng negosyo.",
      openNow: 'Bukas ngayon',
      closedNow: 'Sarado',
      businessHoursNotListed: 'Walang naka-post na oras',
      open24_7: 'Bukas 24/7',
      daySunday: 'Linggo',
      dayMonday: 'Lunes',
      dayTuesday: 'Martes',
      dayWednesday: 'Miyerkules',
      dayThursday: 'Huwebes',
      dayFriday: 'Biyernes',
      daySaturday: 'Sabado',
      t5TableIcon: 'Icon',
      t5TableImage: 'Image',
      t5TableProduct: 'Product',
      t5TablePrice: 'Price',
      t5TableStatus: 'Status',
      t5TableAction: 'Action',
      t5ViewDetails: 'View details',
      t5InStock: 'In stock',
      selectVariantToAdd: 'Pumili ng variant na idadagdag sa cart',
      selectRequiredModifierChoice: 'Pumili ng isang opsyon sa bawat kinakailangang grupo bago idagdag sa cart.',
      modifiersHeading: 'Mga add-on (opsyonal)',
      modifiersAria: 'Opsyonal na mga add-on',
      itemDetailDialogAria: 'Mga detalye ng item sa menu',
      itemUntitled: 'Walang pamagat',
      itemNotAvailable: 'Hindi available',
      variantOptionNotAvailable: 'Hindi available',
      variantOptionsAria: 'Mga opsyon at presyo',
      variantImageAlt: 'Variant',
      priceFrom: 'Mula {price}',
      comboContentsAria: 'Mga laman ng combo',
      modifierUnnamed: 'Add-on',
      modifierNoExtraCost: 'Walang dagdag na bayad',
      quickOrder: {
        addToCart: 'Idagdag sa cart',
        updateCart: 'I-update ang cart',
        yourOrder: 'Ang iyong order',
        clearAll: 'I-clear lahat',
        name: 'Iyong pangalan',
        namePlaceholder: 'hal. Juan',
        notes: 'Mga tala',
        notesPlaceholder: 'hal. Dagdag na sarsa, walang sibuyas...',
        sendViaEmail: 'Ipadala sa Email',
        sendViaWhatsApp: 'Ipadala sa WhatsApp',
        sendViaViber: 'Ipadala sa Viber',
        sendViaSMS: 'Ipadala sa SMS',
        orderSent: 'Naipadala na ang order!',
        orderSentDesc: 'Ang iyong order ay naipadala sa negosyo.',
        nameRequired: 'Kinakailangan ang pangalan.',
        nameMaxLength: 'Ang pangalan ay hanggang 100 character lamang.',
        notesMaxLength: 'Ang mga tala ay hanggang 500 character lamang.',
        notesRequired: 'Kinakailangan ang mga tala.',
        cartEmpty: 'Walang laman ang iyong cart.',
        quantity: 'Dami',
        remove: 'Alisin',
        itemAdded: 'Naidagdag sa cart!',
        itemUpdated: 'Na-update ang cart!',
        ariaClose: 'Isara',
        ariaDecrease: 'Bawasan ang dami',
        ariaIncrease: 'Dagdagan ang dami',
        ariaRemoveItem: 'Alisin ang item',
        fulfillment: 'Pickup o dine-in',
        fulfillmentPickup: 'Pickup',
        fulfillmentDineIn: 'Dine-in',
        dineInReferenceLabel: 'Mesa / sanggunian (opsyonal)',
        dineInReferencePlaceholder: 'hal. Mesa 5',
        checkout: 'Checkout',
        orderSummary: 'Buod ng order',
        confirmCheckout: 'Ilagay ang order',
        checkoutSubmitting: 'Ipinapadala…',
        checkoutFailed: 'Hindi maipadala ang order. Subukan muli.',
        subtotal: 'Subtotal',
        ariaCloseSummary: 'Isara ang buod ng order',
        mobile: 'Mobile number',
        mobilePlaceholder: '+639171234567',
        mobileHint: 'Magsimula sa + at country code (hal. +63…). Magpapadala kami ng one-time code sa SMS.',
        mobileRequired: 'Kailangan ang mobile para sa pickup.',
        mobileInvalid: 'Maglagay ng wastong numero na may country code.',
        verificationCode: 'Verification code',
        verificationCodePlaceholder: '6-digit code',
        verificationCodeInvalid: 'Ilagay ang 6-digit code mula sa SMS.',
        sendVerificationCode: 'Ipadala ang code',
        pickupServiceFee: 'Pickup service fee ({percent}%)',
        total: 'Total',
      },
      cookieConsent: {
        barTitle: 'Mga cookie at katulad na teknolohiya',
        barBody:
          'Gumagamit kami ng mahahalagang function para sa site. Kung may pahintulot ka, nagdaragdag kami ng first-party analytics at third-party tools (hal. security check o sign-in). Para sa detalye buksan ang ',
        acceptAll: 'Tanggapin lahat',
        rejectNonEssential: 'Tanggihan ang hindi kinakailangan',
        customize: 'I-customize',
        modalTitle: 'Mga kagustuhan sa cookie',
        necessaryTitle: 'Mahigpit na kinakailangan',
        necessaryDesc: 'Kailangan para sa seguridad, pag-sign in, at core features. Laging naka-on.',
        analyticsTitle: 'Analytics (first-party)',
        analyticsDesc: 'Tumutulong na mapabuti ang mga pampublikong pahina. Pinoproseso ang data sa aming mga server.',
        thirdPartyTitle: 'Mga feature ng third party',
        thirdPartyDesc: 'Naglo-load ng mga tool tulad ng Google reCAPTCHA kapag gumagamit ka ng ilang form o pag-sign in.',
        save: 'I-save ang mga kagustuhan',
        privacyLink: 'Patakaran sa Privacy',
      },
      // Quick-add item wizard (owner public site)
      quickAddItem: 'Magdagdag ng item',
      quickAddItemAria: 'Magdagdag ng menu item',
      quickAddStepOf: 'Hakbang {step} ng {total}',
      quickAddStep0Title: '<em>Uri</em> ng item',
      quickAddStep1Title: 'Pumili ng <em>kategorya</em>',
      quickAddStep2Title: '<em>Detalye</em> ng item',
      quickAddStep3Title: 'Suriin at <em>i-save</em>',
      quickAddWhatKind: 'Anong uri ng item?',
      quickAddTypeSimpleSub: 'Isang presyo',
      quickAddTypeVariantsSub: 'Laki, opsyon…',
      quickAddTypeCombSub: 'Bundle',
      quickAddTypeSimpleHint: 'Isang produkto o ulam na may isang fixed na presyo.',
      quickAddTypeVariantsHint: 'Isang item na may maraming opsyon — may sariling presyo ang bawat isa (hal. Maliit / Malaki).',
      quickAddTypeComboHint: 'Mga item na binibenta nang magkasama (hal. Meal deal).',
      quickAddNoCats: 'Wala pang kategorya',
      quickAddNoCatsSub: 'Gumawa ng unang kategorya sa ibaba para ayusin ang mga item.',
      quickAddOrCreate: 'o lumikha ng bago',
      quickAddFirstCatName: 'Pangalan ng unang kategorya',
      quickAddNewCat: 'Bagong kategorya',
      quickAddCatPlaceholder: 'hal. Main, Inumin, Serbisyo…',
      quickAddCreate: 'Lumikha',
      quickAddCatWillCreate: 'Lilikha: {name}',
      quickAddItem1: 'item',
      quickAddItemN: 'mga item',
      quickAddNameLabel: 'Pangalan ng item',
      quickAddNamePlaceholder: 'hal. Margherita Pizza',
      quickAddPriceOptional: 'Presyo (opsyonal)',
      quickAddComboPriceOptional: 'Presyo ng combo (opsyonal)',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: 'Paglalarawan (opsyonal)',
      quickAddDescPlaceholder: 'Ipapakita sa card ng item…',
      quickAddVariantsLabel: 'Mga variant',
      quickAddVariantName: 'Pangalan ng opsyon',
      quickAddVariantNamePlaceholder: 'hal. Malaki',
      quickAddVariantPrice: 'Presyo',
      quickAddRemoveVariant: 'Alisin ang variant',
      quickAddAddVariant: 'Magdagdag ng variant',
      quickAddComboItems: 'Mga item sa combo',
      quickAddPickItem: 'Pumili ng item…',
      quickAddAdd: 'Idagdag',
      quickAddRemoveComboItem: 'Alisin ang item',
      quickAddNoItemsForCombo: 'Wala pang item — magdagdag muna ng simple o may variant.',
      quickAddReviewItem: 'Item',
      quickAddReviewName: 'Pangalan',
      quickAddReviewType: 'Uri',
      quickAddReviewPrice: 'Presyo',
      quickAddReviewIncludes: 'Kasama',
      quickAddReviewCategory: 'Kategorya',
      quickAddReviewCategoryLabel: 'Kategorya',
      quickAddNoPrice: 'Walang presyo',
      quickAddNoCategory: 'Wala',
      quickAddNew: 'bago',
      quickAddPublishNow: 'I-publish agad',
      quickAddPublishNowSub: 'Lalabas agad sa iyong site',
      quickAddAddAnother: 'Magdagdag pa pagkatapos mag-save',
      quickAddAddAnotherSub: 'Panatilihing bukas ang flow na ito',
      quickAddSaveItem: 'I-save ang item',
      quickAddReview: 'Suriin',
      quickAddContinue: 'Magpatuloy',
      quickAddBack: 'Bumalik',
      quickAddSaved: 'Na-save ang item.',
      quickAddSavedSub: 'Idinagdag ang {name} sa {category}.',
      quickAddAnotherItem: '+ Magdagdag pa ng item',
      quickAddDone: 'Tapos na',
    },
    tour: {
      welcomeTitle: 'Maligayang pagdating!',
      welcomeDescription: 'Maglakad-lakad tayo sa iyong dashboard. Makikita mo kung saan pamahalaan ang mga restaurant, menu, at iba pa.',
      step1Title: 'Pangunahing lugar',
      step1Description: 'Lumalabas dito ang iyong content. Gamitin ang sidebar para magpalipat sa Dashboard, Restaurants, at iba pang seksyon.',
      step2Title: 'Nabigasyon',
      step2Description: 'Gamitin ang sidebar na ito para sa Dashboard, Restaurants, Menu items, Feedbacks, at Profile.',
      step2DescriptionSuperadmin: 'Gamitin ang sidebar na ito para sa Dashboard, Users, Owner feedbacks, Restaurants, at iba pa.',
      step3Title: 'Dashboard',
      step3Description: 'Ang iyong base. Tingnan ang overview ng mga restaurant, menu item, at feedback sa isang sulyap.',
      step4Title: 'Restaurants',
      step4Description: 'Pamahalaan ang iyong restaurant(s). Magdagdag ng lokasyon, i-edit ang detalye, at ayusin ang menu bawat restaurant.',
      step5Title: 'Menu items',
      step5Description: 'Gumawa at mag-edit ng mga ulam, inumin, at combo. Idagdag sa mga kategorya sa loob ng menu ng bawat restaurant.',
      step6Title: 'Feedbacks',
      step6Description: 'Tingnan at i-moderate ang mga review ng customer mula sa iyong public page. I-approve o i-reject para ipakita o itago.',
      step7Title: 'Feature request',
      step7Description: 'Magpadala ng feedback o feature request sa team. Makikita mo rin dito ang iyong mga naipasa noon.',
      step8Title: 'Profile at mga setting',
      step8Description: 'I-update ang iyong pangalan, email, password, at wika ng app.',
      next: 'Susunod',
      prev: 'Nakaraan',
      done: 'Tapos',
      close: 'Isara',
      skip: 'Laktawan ang tour',
    },
    restaurantsTour: {
      step1Title: 'Listahan ng mga restaurant',
      step1Description: 'Ito ang iyong listahan ng mga restaurant. Magdagdag ng mga lokasyon, i-edit ang mga detalye, at pamahalaan ang mga menu mula rito.',
      step2Title: 'Magdagdag ng restaurant',
      step2Description: 'I-click dito para lumikha ng bagong restaurant at itakda ang pangalan, address, at subdomain.',
      step3Title: 'Iyong mga restaurant',
      step3Description: 'Ang iyong mga restaurant ay lalabas bilang mga card. I-click ang isang card para buksan ang restaurant na iyon, o gamitin ang empty state para gumawa ng una.',
      step4Title: 'Card ng restaurant',
      step4Description: 'I-click ang isang restaurant para i-edit ito, pamahalaan ang mga menu, itakda ang availability, at makuha ang public link o QR code.',
      tooltip: 'Tour ng mga restaurant',
    },
    welcome: {
      title: 'Create your first restaurant',
      subtitle: 'Get your menu online, share your address and hours, and let customers find you in minutes.',
      cta: 'Create my first restaurant',
    },
    wizard: {
      stepOf: 'Step {current} of {total}',
      step1Title: 'Name your restaurant',
      nameLabel: 'Restaurant name',
      taglineOptional: 'Tagline (optional)',
      slugPreview: 'Your web address',
      templateLabel: 'Public page template',
      templateHint: 'Choose the layout for your public page.',
      next: 'Next',
      back: 'Back',
      skipForNow: 'Skip for now',
      doLater: "I'll do this later",
      step2Title: 'Location & contact (optional)',
      youreAllSet: "You're all set",
      sublineLive: 'Your restaurant page is live. Share the link or QR so customers can find you.',
      copyAddress: 'Copy address',
      copied: 'Copied!',
      saveQr: 'Save QR code',
      scanHint: 'Customers scan this to open your page',
      goToMyRestaurant: 'Go to my restaurant',
      backToDashboard: 'Back to dashboard',
      firstTimeLink: 'First time? Create your first restaurant',
    },
    startTour: 'Simulan ang app tour',
    startTourDescription: 'I-run muli ang guided tour para makita ang mga pangunahing lugar ng app.',
  },
  de: {
    app: {
      name: 'RMS',
      login: 'Anmelden',
      register: 'Konto erstellen',
      logout: 'Abmelden',
      profile: 'Profil & Einstellungen',
      restaurants: 'Restaurants',
      menu: 'Menü',
      languages: 'Sprachen & Beschreibung',
      addLanguage: 'Sprache hinzufügen',
      defaultLanguage: 'Standardsprache',
      languagesHint: 'Sprachen hinzufügen oder entfernen. Die Standardsprache wird verwendet, wenn auf der öffentlichen Seite keine Sprache ausgewählt ist.',
      description: 'Beschreibung',
      save: 'Speichern',
      cancel: 'Abbrechen',
      remove: 'Entfernen',
      translate: 'Aus Standard übersetzen',
      addItem: 'Eintrag hinzufügen',
      editItem: 'Menüpunkt bearbeiten',
      createItem: 'Menüpunkt hinzufügen',
      dashboard: 'Dashboard',
      feedbacks: 'Rückmeldungen',
      feedbacksListSubtitle: 'Feedback genehmigen, um es auf Ihrer öffentlichen Seite anzuzeigen. Ablehnen oder löschen, um es auszublenden.',
      feedbacksListEmpty: 'Noch keine Rückmeldungen. Sie erscheinen hier, wenn Kunden Bewertungen abgeben.',
      feedbacksLoadError: 'Rückmeldungen konnten nicht geladen werden.',
      retry: 'Erneut versuchen',
      ariaMainNav: 'Hauptnavigation',
      ariaAppLanguage: 'App-Sprache',
      ariaOpenMenu: 'Menü öffnen',
      ownerFeedbacksSubtitle: 'Feature-Anfragen und Feedback von Restaurantinhabern. Status bei Prüfung umschalten.',
      ownerFeedbacksEmpty: 'Noch kein Feedback von Inhabern.',
      markPending: 'Als ausstehend markieren',
      markReviewed: 'Als geprüft markieren',
      feedbacksUpdateForbidden: 'Sie haben keine Berechtigung, Feedback zu aktualisieren.',
      feedbackNotFound: 'Feedback nicht gefunden.',
      feedbackUpdated: 'Feedback aktualisiert.',
      feedbackApproved: 'Feedback genehmigt.',
      feedbackRejected: 'Feedback abgelehnt.',
      failedToUpdate: 'Aktualisierung fehlgeschlagen.',
      feedbackDeleted: 'Feedback gelöscht.',
      failedToDelete: 'Löschen fehlgeschlagen.',
      deleteFeedbackConfirm: 'Dieses Feedback löschen? Dies kann nicht rückgängig gemacht werden.',
      users: 'Benutzer',
      superadminUsersSubtitle: 'Benutzerkonten verwalten: Bezahlstatus und aktivieren/deaktivieren.',
      noUsersFound: 'Keine Benutzer gefunden.',
      paid: 'Bezahlt',
      free: 'Kostenlos',
      active: 'Aktiv',
      deactivated: 'Deaktiviert',
      superadmin: 'Superadmin',
      markPaid: 'Als bezahlt markieren',
      removePaid: 'Bezahlt entfernen',
      activate: 'Aktivieren',
      deactivate: 'Deaktivieren',
      ariaDeactivateUser: 'Benutzer deaktivieren',
      ariaActivateUser: 'Benutzer aktivieren',
      you: '(Sie)',
      usersForbidden: 'Sie haben keine Berechtigung, Benutzer anzuzeigen.',
      superadminRestaurantsSubtitle: 'Nur-Lese-Liste aller Restaurants im System.',
      noRestaurantsFound: 'Keine Restaurants gefunden.',
      availability: 'Verfügbarkeit',
      availabilitySubtitle: 'Wann Ihr Restaurant geöffnet hat. Zeiten unten ändern und Speichern tippen.',
      deleteRestaurant: 'Restaurant löschen',
      logoAndBanner: 'Logo & Banner',
      logoBannerDescription: 'Restaurant-Logo und Banner-Bilder ändern oder aktualisieren.',
      uploadImagesHint: 'Neue Bilder hochladen. JPEG, PNG, GIF oder WebP. Max. 2 MB pro Bild.',
      logo: 'Logo',
      banner: 'Banner',
      takePhoto: 'Foto aufnehmen',
      chooseFromGallery: 'Aus Galerie wählen',
      changeLogo: 'Logo ändern',
      changeBanner: 'Banner ändern',
      done: 'Fertig',
      deleteRestaurantConfirm: 'Restaurant löschen?',
      cannotBeUndone: 'Dies kann nicht rückgängig gemacht werden.',
      typeToConfirm: 'Geben Sie {slug} zur Bestätigung ein',
      ariaUploadingLogo: 'Logo wird hochgeladen',
      ariaUploadingBanner: 'Banner wird hochgeladen',
      ariaUploadLogo: 'Logo hochladen',
      ariaUploadBanner: 'Banner hochladen',
      ariaUploadLogoFromGallery: 'Logo aus Galerie hochladen',
      ariaUploadBannerFromGallery: 'Banner aus Galerie hochladen',
      ariaTakePhotoLogo: 'Foto für Logo aufnehmen',
      ariaTakePhotoBanner: 'Foto für Banner aufnehmen',
      settings: 'Einstellungen',
      ariaManageSections: 'Geschäftsverwaltungsbereiche',
      contactAndDetails: 'Kontakt & Details',
      address: 'Adresse',
      email: 'E-Mail',
      social: 'Soziales',
      addLanguageEllipsis: 'Sprache hinzufügen…',
      addLanguageHint: 'Fügen Sie mindestens eine Sprache hinzu, um Beschreibungen zu verwalten.',
      editDescriptionFor: 'Beschreibung bearbeiten für:',
      ariaSelectTemplate: 'Vorlage auswählen',
      menusAndCategories: 'Menüs & Kategorien',
      createFirstMenuHint: 'Erstellen Sie Ihr erstes Menü, um Kategorien hinzuzufügen.',
      createCategoryFirstHint: 'Erstellen Sie zuerst eine Kategorie, um Menüpunkte hinzuzufügen.',
      menuItemsSettingsHint: 'Gerichte über die Kategorieseite hinzufügen. Übersetzungen nutzen Restaurant-Einstellungen.',
      createCategoryAboveHint: 'Erstellen Sie oben eine Kategorie, um Menüpunkte hinzuzufügen.',
      renameMenu: 'Menü umbenennen',
      selectMenu: 'Menü auswählen',
      editCategory: 'Kategorie bearbeiten',
      removeCategory: 'Kategorie entfernen',
      addMenu: 'Menü hinzufügen',
      addCategory: 'Kategorie hinzufügen',
      categoryAvailability: 'Kategorieverfügbarkeit',
      removeCategoryImage: 'Kategoriebild entfernen',
      addMenuOrCategory: 'Menü oder Kategorie hinzufügen',
      editIn: 'Bearbeiten in',
      changeImage: 'Bild ändern',
      uploadImage: 'Bild hochladen',
      translating: 'Übersetzen…',
      removing: 'Entfernen…',
      mainMenuDefault: 'Hauptmenü',
      menuManagement: 'Menüverwaltung',
      menuManagementSubtitle: 'Menüs, Kategorien und Punkte. Zum Sortieren ziehen.',
      noMenuItemsYet: 'Noch keine Menüpunkte. Nutzen Sie die + Schaltfläche unten.',
      categoryNameRequired: 'Kategoriename ist erforderlich.',
      categoryUpdated: 'Kategorie aktualisiert.',
      categoryCreated: 'Kategorie erstellt.',
      addMenuItemsToCategory: 'Menüpunkte zu dieser Kategorie hinzufügen',
      noMenuItemsOrMatch: 'Keine Menüpunkte oder keine Treffer.',
      assignTags: 'Tags zuweisen',
      itemAvailability: 'Artikelverfügbarkeit',
      dragToReorder: 'Zum Sortieren ziehen',
      setAvailabilityTimes: 'Verfügbarkeitszeiten festlegen',
      available: 'Verfügbar',
      notAvailable: 'Nicht verfügbar',
      markNotAvailable: 'Als nicht verfügbar markieren',
      markAvailableOnMenu: 'Auf öffentlichem Menü als verfügbar markieren',
      visibleOnMenu: 'Im Menü sichtbar',
      hiddenFromMenu: 'Im Menü ausgeblendet',
      addMenuItemToCategory: 'Menüpunkt zu Kategorie hinzufügen',
      menuItemTags: 'Menüpunkt-Tags',
      menuItems: 'Menüpunkte',
      createRestaurantFirstForMenus: 'Erstellen Sie zuerst ein Restaurant, um Menüs zu verwalten.',
      addRestaurant: 'Restaurant hinzufügen',
      ratingOutOf5: 'Bewertung: {n} von 5',
      anonymous: 'Anonym',
      approve: 'Genehmigen',
      reject: 'Ablehnen',
      ariaApproveFeedback: 'Feedback genehmigen',
      ariaRejectFeedback: 'Feedback ablehnen',
      ariaDeleteFeedback: 'Feedback löschen',
      resetPasswordSuccess: 'Ihr Passwort wurde zurückgesetzt. Sie können sich anmelden.',
      resetPasswordEnterNew: 'Geben Sie unten Ihr neues Passwort ein.',
      type: 'Typ',
      labelOptional: 'Beschriftung (optional)',
      placeholderUrl: 'https://...',
      placeholderPhone: 'z. B. +49 123 456789',
      placeholderContactLabel: 'z. B. Reservierung, Küche',
      showOnPublicPage: 'Auf öffentlicher Seite anzeigen',
      hideOnPublicPage: 'Auf öffentlicher Seite ausblenden',
      ariaEditContact: 'Kontakt bearbeiten',
      ariaDeleteContact: 'Kontakt löschen',
      noContactsYet: 'Noch keine Kontakte oder Links. Fügen Sie einen hinzu.',
      deleteContactConfirm: 'Diesen Kontakt löschen?',
      ariaPagination: 'Seitennummerierung',
      ariaOpeningTime: 'Öffnungszeit',
      ariaClosingTime: 'Schließzeit',
      removeTimeSlot: 'Zeitfenster entfernen',
      menuItemLabel: 'Menüpunkt',
      addMenuItem: 'Menüpunkt hinzufügen',
      ariaRemoveImage: 'Bild entfernen',
      ariaRemoveVariantImage: 'Variantenbild entfernen',
      ariaRemoveEntry: 'Eintrag entfernen',
      ariaRemoveGroup: 'Gruppe entfernen',
      ariaShowHelp: 'Hilfe anzeigen: Was bedeuten die Schaltflächen?',
      help: 'Hilfe',
      ariaButtonLegend: 'Schaltflächenlegende',
      helpLegendDescription: 'Bedeutung der Symbole in Menü- und Kategoriezeilen.',
      legendDragHandle: 'Ziehgriff',
      legendDragDesc: 'Zum Sortieren ziehen',
      legendSchedule: 'Zeitplan',
      legendScheduleDesc: 'Öffnungszeiten festlegen',
      legendAvailability: 'Verfügbarkeit',
      legendAvailabilityDesc: 'Wann dieser Artikel verfügbar ist',
      legendVisibility: 'Sichtbarkeit',
      legendVisibilityDesc: 'Im öffentlichen Menü anzeigen oder ausblenden',
      legendTags: 'Tags',
      legendTagsDesc: 'Tags zuweisen',
      paidFeature: 'Kostenpflichtige Funktion',
      upgradeToUse: 'Upgrade, um diese Funktion zu nutzen.',
      paidFeatureAiDescription: 'Menübilder mit KI verschönern ist im kostenpflichtigen Plan verfügbar.',
      maybeLater: 'Vielleicht später',
      upgrade: 'Upgrade',
      ariaNotifications: 'Benachrichtigungen',
      dismiss: 'Schließen',
      reservationsHowItWorksTitle: 'So funktionieren Tischreservierungen',
      reservationsHowItWorksIntro: 'Gäste können einen Tisch auf der öffentlichen Seite Ihres Restaurants anfragen. Sie bestätigen, lehnen ab oder bearbeiten Buchungen hier.',
      reservationsHowItWorksPhoneRequired: 'Für jede Reservierung ist eine gültige Telefonnummer erforderlich, damit Sie Bestätigungen und Erinnerungen per SMS senden können.',
      reservationsHowItWorksSteps: 'Legen Sie die SMS-Benachrichtigungsnummer pro Restaurant fest, um neue Reservierungsmeldungen zu erhalten. Bestätigen oder lehnen Sie Anfragen ab; Gäste erhalten eine SMS. Sie können auch Datum, Uhrzeit oder Gästezahl ändern und stornieren – Gäste werden per SMS benachrichtigt.',
      reservationsHowItWorksTooltip: 'So funktionieren Reservierungen',
      close: 'Schließen',
      loading: 'Laden…',
      back: 'Zurück',
      delete: 'Löschen',
      pending: 'Ausstehend',
      featureRequest: 'Feature-Anfrage',
      ownerFeedbacks: 'Inhaber-Feedback',
    },
    errors: {
      forbidden: 'Sie haben keine Berechtigung dafür.',
      validation: 'Bitte überprüfen Sie Ihre Eingabe.',
      pageNotFound: 'Seite nicht gefunden',
      pageNotFoundDescription: 'Die gesuchte Seite existiert nicht oder wurde verschoben.',
    },
    landing: {
      hero: {
        badge: 'Geschäftstechnologie, einfach gemacht',
        titleBefore: 'Ihr Geschäft, ',
        titleHighlight: 'in 10 Minuten online.',
        subtitle: 'Bringen Sie Ihr Geschäft noch heute online — Produktkatalog, Öffnungszeiten, Buchung und mehr. Kostenlos starten. Keine technischen Kenntnisse nötig.',
        trustedBy: 'Für immer kostenlos. Keine Kreditkarte erforderlich.',
      },
      cta: {
        bookDemo: '{businessType}-Demo ansehen',
        bookDemoGeneric: 'Demo ansehen',
      },
      footer: {
        cookies: 'Cookie-Einstellungen',
      },
    },
    public: {
      about: 'Über uns',
      t3NavAbout: 'Über uns',
      siteCreatedViaQRestoo: 'Seite erstellt von QRestoo',
      createdByQRestoo: 'Erstellt mit QRestoo',
      sitePoweredByDLR: 'Site powered by DLR Web Solutions',
      contactUs: 'Kontakt',
      reviewsAndFeedback: 'Bewertungen & Feedback',
      t1NavMenu: 'Menü',
      reviewsNav: 'Bewertungen',
      contactNav: 'Kontakt',
      t2MenuNav: 'Menü',
      t2ViewServices: 'Leistungen',
      t2ReviewsNav: 'Bewertungen',
      t2ContactNav: 'Kontakt',
      navMenu: 'Menü',
      navServices: 'Leistungen',
      navProducts: 'Produkte',
      ownerNavBookingUnavailable: 'Buchung. Kontoupgrade nötig.',
      ownerNavReviewsUnavailable: 'Bewertungen. Kontoupgrade nötig.',
      ownerNavLocationUnavailable: 'Standort. Adresse oder Karte in den Einstellungen hinzufügen.',
      ownerNavAboutUnavailable: 'Unsere Geschichte. Beschreibung in den Einstellungen hinzufügen.',
      ownerNavHoursUnavailable: 'Öffnungszeiten. In den Einstellungen hinzufügen.',
      t3FooterTaglineRestaurants: 'Unvergessliche kulinarische Erlebnisse. Exzellenz auf jedem Teller.',
      t3FooterTaglineEcommerce: 'Qualitätsprodukte, sorgfältig ausgewählt. Exzellenz bei jeder Bestellung.',
      t3FooterTaglineServices: 'Außergewöhnlicher Service. Exzellenz in jedem Detail.',
      t2CommunicationsLink: 'Kommunikationslink',
      noLogoSetOwnerMessage: 'Kein Logo festgelegt. In den Einstellungen hinzufügen.',
      ownerEditLogoAria: 'Logo und Banner bearbeiten',
      heroEstYear: 'Gegr. {year}',
      noTaglineOrDescriptionSetOwnerMessage: 'Kein Slogan oder Beschreibung. In den Einstellungen hinzufügen.',
      noDescriptionSetOwnerMessage: 'Keine Beschreibung. In den Einstellungen hinzufügen.',
      noMapSetOwnerMessage: 'Keine Karte. Adresse oder Kartenlink in den Einstellungen hinzufügen.',
      noBookingAvailableOwnerMessage: 'Buchungen sind nicht verfügbar. In den Einstellungen aktivieren.',
      noMailingListAvailableOwnerMessage: 'Mailingliste ist nicht verfügbar. In den Einstellungen aktivieren.',
      noFeedbackAvailableOwnerMessage: 'Bewertungen und Feedback sind nicht verfügbar. In den Einstellungen aktivieren.',
      t2NewsletterTitle: 'NEWSLETTER-ABO',
      t2FeedbackPerformanceLogging: 'Leistungsprotokoll',
      t2FeedbackOperationalLog: 'BETRIEBSLOG',
      t2FeedbackTransmitCopy: 'Senden Sie Ihren Erfahrungsbericht zur Qualitätssicherung und Optimierung.',
      t2FeedbackSessionMeta: 'Sitzungsmetadaten:',
      t2FeedbackTerminalId: 'Terminal-ID: TGT-LOG-992',
      t2FeedbackProtocol: 'Protokoll: feedback_v2.0.sh',
      t2FeedbackRatingLabel: 'LEISTUNGSBEWERTUNG',
      t2FeedbackRatingHint: 'Bewertungsmetriken wählen (1-5)',
      t2FeedbackSitrepLabel: 'LAGEBERICHT / DETAILLIERTES FEEDBACK',
      t2FeedbackPlaceholder: 'Logdaten hier eingeben...',
      t2FeedbackSubmitReport: 'BERICHT SENDEN',
      t2FeedbackDisclaimer: 'Mit dem Absenden stimmen Sie den Datenarchivierungsprotokollen zu.',
      ourMenu: 'Unsere Karte',
      ourProducts: 'Produkte',
      ourServices: 'Unsere Leistungen',
      ourCatalog: 'Unser Katalog',
      viewMenu: 'Menü anzeigen',
      viewCatalog: 'Katalog anzeigen',
      viewServices: 'Leistungen anzeigen',
      leaveFeedback: 'Hinterlassen Sie Ihr Feedback',
      ratingLabel: 'Bewertung (1–5)',
      yourName: 'Ihr Name',
      yourMessage: 'Ihre Nachricht',
      sendFeedback: 'Feedback senden',
      formErrorGeneric: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
      mailingListError: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
      noReviewsYet: 'Noch keine Bewertungen. Seien Sie der Erste, der unten Feedback hinterlässt.',
      openingHours: 'Öffnungszeiten',
      noContactListed: 'Keine Telefonnummern oder Links angegeben.',
      whatsapp: 'WhatsApp',
      mobile: 'Mobil',
      email: 'E-Mail',
      phone: 'Telefon',
      fax: 'Fax',
      other: 'Kontakt',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      website: 'Website',
      viewingAsOwner: 'Sie sehen diese Seite als Inhaber.',
      viewAsCustomer: 'Als Kunde ansehen',
      viewAsOwner: 'Als Inhaber anzeigen',
      restaurantNotFoundTitle: 'Restaurant nicht gefunden',
      backToHome: 'Zurück zur Startseite',
      ownerNeedsDataIntro: 'Diese Seite benötigt mehr Inhalte. Fügen Sie Beschreibung, Menü oder anderes im Admin-Bereich hinzu.',
      openAdminPage: 'Admin-Bereich öffnen',
      addBannerInAdmin: 'Titelbild in den Einstellungen hinzufügen',
      bookTable: 'Tisch reservieren',
      bookAppointment: 'Termin buchen',
      mapPlaceholder: 'Karte',
      allRightsReserved: 'Alle Rechte vorbehalten.',
      surpriseMe: 'Überraschen Sie mich',
      surpriseMeAria: 'Überraschen Sie mich – zufälligen Menüpunkt wählen',
      noMenuItems: 'Keine Menüpunkte',
      noProducts: 'Keine Produkte',
      noServices: 'Keine Leistungen',
      noCatalogItems: 'Keine Einträge im Katalog',
      notAvailable: 'Nicht verfügbar',
      notAvailableWithNextTime: 'Jetzt nicht verfügbar. Wieder verfügbar: {time}.',
      sizeAndPriceOptions: 'Größen- und Preisoptionen',
      option: 'Option',
      comboContents: 'Kombo-Inhalt',
      ownerEditMenuItemInCatalogAria: 'Artikel im Katalog bearbeiten (öffnet Verwaltung)',
      closeMenu: 'Menü schließen',
      closeCatalogModal: 'Katalog schließen',
      closeServicesModal: 'Leistungen schließen',
      ariaViewMenu: 'Menü anzeigen',
      ariaViewCatalog: 'Katalog anzeigen',
      ariaViewServices: 'Leistungen anzeigen',
      modalTitleMenu: 'Menü',
      modalTitleCatalog: 'Katalog',
      modalTitleProducts: 'Produkte',
      modalTitleServices: 'Leistungen',
      ariaLocation: 'Standort',
      ariaMap: 'Karte',
      category: 'Kategorie',
      mapAriaLabel: 'Karte mit Restaurantstandort',
      openInMaps: 'In Karten öffnen',
      mapIframeTitle: 'Google-Karte für diesen Ort',
      mapOpenExternallyHint:
        "Tippe unten, um diesen Ort in deiner Karten-App zu öffnen. Für eine eingebettete Karte hier bitte Breiten- und Längengrad oder einen Google-Maps-Link mit Stecknadel ({'@'}lat,lng) in den Unternehmenseinstellungen hinterlegen.",
      openNow: 'Jetzt geöffnet',
      closedNow: 'Geschlossen',
      businessHoursNotListed: 'Öffnungszeiten nicht angegeben',
      open24_7: 'Rund um die Uhr geöffnet',
      daySunday: 'Sonntag',
      dayMonday: 'Montag',
      dayTuesday: 'Dienstag',
      dayWednesday: 'Mittwoch',
      dayThursday: 'Donnerstag',
      dayFriday: 'Freitag',
      daySaturday: 'Samstag',
      t5TableIcon: 'Symbol',
      t5TableImage: 'Bild',
      t5TableProduct: 'Produkt',
      t5TablePrice: 'Preis',
      t5TableStatus: 'Status',
      t5TableAction: 'Aktion',
      t5ViewDetails: 'Details anzeigen',
      t5InStock: 'Auf Lager',
      selectVariantToAdd: 'Wählen Sie eine Variante zum Hinzufügen zum Warenkorb',
      selectRequiredModifierChoice: 'Wählen Sie in jeder Pflichtgruppe eine Option, bevor Sie in den Warenkorb legen.',
      modifiersHeading: 'Zusatzoptionen (optional)',
      modifiersAria: 'Optionale Zusatzoptionen',
      itemDetailDialogAria: 'Menüpunkt-Details',
      itemUntitled: 'Ohne Titel',
      itemNotAvailable: 'Nicht verfügbar',
      variantOptionNotAvailable: 'Nicht verfügbar',
      variantOptionsAria: 'Optionen und Preise',
      variantImageAlt: 'Variante',
      priceFrom: 'Ab {price}',
      comboContentsAria: 'Combo-Inhalt',
      modifierUnnamed: 'Zusatzoption',
      modifierNoExtraCost: 'Kein Aufpreis',
      quickOrder: {
        addToCart: 'In den Warenkorb',
        updateCart: 'Warenkorb aktualisieren',
        yourOrder: 'Ihre Bestellung',
        clearAll: 'Alles löschen',
        name: 'Ihr Name',
        namePlaceholder: 'z.B. Max',
        notes: 'Anmerkungen',
        notesPlaceholder: 'z.B. Extra Soße, ohne Zwiebeln...',
        sendViaEmail: 'Per E-Mail senden',
        sendViaWhatsApp: 'Per WhatsApp senden',
        sendViaViber: 'Per Viber senden',
        sendViaSMS: 'Per SMS senden',
        orderSent: 'Bestellung gesendet!',
        orderSentDesc: 'Ihre Bestellung wurde an das Unternehmen gesendet.',
        nameRequired: 'Name ist erforderlich.',
        nameMaxLength: 'Der Name darf höchstens 100 Zeichen haben.',
        notesMaxLength: 'Anmerkungen dürfen höchstens 500 Zeichen haben.',
        notesRequired: 'Anmerkungen sind erforderlich.',
        cartEmpty: 'Ihr Warenkorb ist leer.',
        quantity: 'Menge',
        remove: 'Entfernen',
        itemAdded: 'Zum Warenkorb hinzugefügt!',
        itemUpdated: 'Warenkorb aktualisiert!',
        ariaClose: 'Schließen',
        ariaDecrease: 'Menge verringern',
        ariaIncrease: 'Menge erhöhen',
        ariaRemoveItem: 'Artikel entfernen',
        fulfillment: 'Abholung oder vor Ort',
        fulfillmentPickup: 'Abholung',
        fulfillmentDineIn: 'Vor Ort',
        dineInReferenceLabel: 'Tisch / Referenz (optional)',
        dineInReferencePlaceholder: 'z. B. Tisch 5',
        checkout: 'Zur Kasse',
        orderSummary: 'Bestellübersicht',
        confirmCheckout: 'Bestellen',
        checkoutSubmitting: 'Wird gesendet…',
        checkoutFailed: 'Bestellung konnte nicht gesendet werden. Bitte erneut versuchen.',
        subtotal: 'Zwischensumme',
        ariaCloseSummary: 'Bestellübersicht schließen',
        mobile: 'Handynummer',
        mobilePlaceholder: '+491701234567',
        mobileHint: 'Mit + und Ländervorwahl beginnen (z. B. +49…). Wir senden einen Code per SMS.',
        mobileRequired: 'Handynummer ist für Abholung erforderlich.',
        mobileInvalid: 'Bitte gültige Nummer mit Ländervorwahl eingeben.',
        verificationCode: 'Bestätigungscode',
        verificationCodePlaceholder: '6-stelliger Code',
        verificationCodeInvalid: 'Den 6-stelligen Code aus der SMS eingeben.',
        sendVerificationCode: 'Code senden',
        pickupServiceFee: 'Abholgebühr ({percent} %)',
        total: 'Gesamt',
      },
      cookieConsent: {
        barTitle: 'Cookies und ähnliche Technologien',
        barBody:
          'Wir verwenden wesentliche Funktionen für den Betrieb der Website. Mit Ihrer Einwilligung nutzen wir zusätzlich First-Party-Analysen und Tools von Drittanbietern (z. B. Sicherheitsprüfungen oder Anmeldung). Details in der ',
        acceptAll: 'Alle akzeptieren',
        rejectNonEssential: 'Nicht notwendige ablehnen',
        customize: 'Anpassen',
        modalTitle: 'Cookie-Einstellungen',
        necessaryTitle: 'Unbedingt erforderlich',
        necessaryDesc: 'Erforderlich für Sicherheit, Anmeldung und Kernfunktionen. Immer aktiv.',
        analyticsTitle: 'Analyse (First-Party)',
        analyticsDesc: 'Hilft uns, öffentliche Seiten zu verbessern. Daten werden auf unseren Servern verarbeitet.',
        thirdPartyTitle: 'Funktionen von Drittanbietern',
        thirdPartyDesc: 'Lädt z. B. Google reCAPTCHA bei bestimmten Formularen oder bei der Anmeldung.',
        save: 'Einstellungen speichern',
        privacyLink: 'Datenschutzerklärung',
      },
      // Quick-add item wizard (owner public site)
      quickAddItem: 'Artikel hinzufügen',
      quickAddItemAria: 'Menüpunkt hinzufügen',
      quickAddStepOf: 'Schritt {step} von {total}',
      quickAddStep0Title: 'Artikel<em>typ</em>',
      quickAddStep1Title: '<em>Kategorie</em> wählen',
      quickAddStep2Title: 'Artikel<em>details</em>',
      quickAddStep3Title: 'Prüfen &amp; <em>speichern</em>',
      quickAddWhatKind: 'Welche Art von Artikel?',
      quickAddTypeSimpleSub: 'Ein Preis',
      quickAddTypeVariantsSub: 'Größen, Optionen…',
      quickAddTypeCombSub: 'Paket',
      quickAddTypeSimpleHint: 'Ein Produkt oder Gericht mit einem festen Preis.',
      quickAddTypeVariantsHint: 'Ein Artikel mit mehreren Optionen — jeweils mit eigenem Preis (z. B. Klein / Groß).',
      quickAddTypeComboHint: 'Mehrere Artikel zusammen verkauft (z. B. Menü-Deal).',
      quickAddNoCats: 'Noch keine Kategorien',
      quickAddNoCatsSub: 'Erstellen Sie unten Ihre erste Kategorie, um Artikel zu gruppieren.',
      quickAddOrCreate: 'oder neu anlegen',
      quickAddFirstCatName: 'Name Ihrer ersten Kategorie',
      quickAddNewCat: 'Neue Kategorie',
      quickAddCatPlaceholder: 'z. B. Hauptgerichte, Getränke, Services…',
      quickAddCreate: 'Anlegen',
      quickAddCatWillCreate: 'Wird angelegt: {name}',
      quickAddItem1: 'Artikel',
      quickAddItemN: 'Artikel',
      quickAddNameLabel: 'Artikelname',
      quickAddNamePlaceholder: 'z. B. Pizza Margherita',
      quickAddPriceOptional: 'Preis (optional)',
      quickAddComboPriceOptional: 'Paketpreis (optional)',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: 'Beschreibung (optional)',
      quickAddDescPlaceholder: 'Wird auf der Artikelkarte angezeigt…',
      quickAddVariantsLabel: 'Varianten',
      quickAddVariantName: 'Optionsname',
      quickAddVariantNamePlaceholder: 'z. B. Groß',
      quickAddVariantPrice: 'Preis',
      quickAddRemoveVariant: 'Variante entfernen',
      quickAddAddVariant: 'Variante hinzufügen',
      quickAddComboItems: 'Artikel im Paket',
      quickAddPickItem: 'Artikel wählen…',
      quickAddAdd: 'Hinzufügen',
      quickAddRemoveComboItem: 'Entfernen',
      quickAddNoItemsForCombo: 'Noch keine Artikel — fügen Sie zuerst einfache oder Varianten-Artikel hinzu.',
      quickAddReviewItem: 'Artikel',
      quickAddReviewName: 'Name',
      quickAddReviewType: 'Typ',
      quickAddReviewPrice: 'Preis',
      quickAddReviewIncludes: 'Enthält',
      quickAddReviewCategory: 'Kategorie',
      quickAddReviewCategoryLabel: 'Kategorie',
      quickAddNoPrice: 'Kein Preis',
      quickAddNoCategory: 'Keine',
      quickAddNew: 'neu',
      quickAddPublishNow: 'Sofort veröffentlichen',
      quickAddPublishNowSub: 'Wird sofort auf Ihrer Website sichtbar',
      quickAddAddAnother: 'Nach dem Speichern weiteren hinzufügen',
      quickAddAddAnotherSub: 'Diesen Ablauf offen lassen',
      quickAddSaveItem: 'Artikel speichern',
      quickAddReview: 'Prüfen',
      quickAddContinue: 'Weiter',
      quickAddBack: 'Zurück',
      quickAddSaved: 'Artikel gespeichert.',
      quickAddSavedSub: '{name} wurde zu {category} hinzugefügt.',
      quickAddAnotherItem: '+ Weiteren Artikel hinzufügen',
      quickAddDone: 'Fertig',
    },
    tour: {
      welcomeTitle: 'Willkommen!',
      welcomeDescription: 'Wir führen Sie kurz durch Ihr Dashboard. Sie sehen, wo Sie Restaurants, Menüs und mehr verwalten.',
      step1Title: 'Hauptbereich',
      step1Description: 'Ihr Inhalt erscheint hier. Nutzen Sie die Seitenleiste für Dashboard, Restaurants und weitere Bereiche.',
      step2Title: 'Navigation',
      step2Description: 'Nutzen Sie diese Seitenleiste für Dashboard, Restaurants, Menüpunkte, Rückmeldungen und Profil.',
      step2DescriptionSuperadmin: 'Nutzen Sie diese Seitenleiste für Dashboard, Benutzer, Inhaber-Feedback, Restaurants und mehr.',
      step3Title: 'Dashboard',
      step3Description: 'Ihre Übersicht. Restaurants, Menüpunkte und Rückmeldungen auf einen Blick.',
      step4Title: 'Restaurants',
      step4Description: 'Verwalten Sie Ihre Restaurant(s). Standorte, Details und Menüs pro Restaurant.',
      step5Title: 'Menüpunkte',
      step5Description: 'Gerichte, Getränke und Kombos anlegen. In Kategorien pro Restaurant-Menü einfügen.',
      step6Title: 'Rückmeldungen',
      step6Description: 'Kundenbewertungen von Ihren öffentlichen Seiten ansehen und moderieren. Genehmigen oder ablehnen.',
      step7Title: 'Feature-Anfrage',
      step7Description: 'Feedback oder Feature-Wünsche an das Team senden. Hier sehen Sie auch Ihre früheren Einreichungen.',
      step8Title: 'Profil & Einstellungen',
      step8Description: 'Name, E-Mail, Passwort und App-Sprache aktualisieren.',
      next: 'Weiter',
      prev: 'Zurück',
      done: 'Fertig',
      close: 'Schließen',
      skip: 'Tour überspringen',
    },
    restaurantsTour: {
      step1Title: 'Restaurantliste',
      step1Description: 'Das ist Ihre Restaurantliste. Fügen Sie Standorte hinzu, bearbeiten Sie Details und verwalten Sie Menüs von hier aus.',
      step2Title: 'Restaurant hinzufügen',
      step2Description: 'Klicken Sie hier, um ein neues Restaurant zu erstellen und Name, Adresse und Subdomain festzulegen.',
      step3Title: 'Ihre Restaurants',
      step3Description: 'Ihre Restaurants erscheinen als Karten. Klicken Sie auf eine Karte, um das Restaurant zu öffnen, oder nutzen Sie den leeren Zustand für Ihr erstes.',
      step4Title: 'Restaurantkarte',
      step4Description: 'Klicken Sie auf ein Restaurant, um es zu bearbeiten, Menüs zu verwalten, Verfügbarkeit festzulegen und den öffentlichen Link oder QR-Code zu erhalten.',
      tooltip: 'Restaurant-Tour',
    },
    welcome: {
      title: 'Create your first restaurant',
      subtitle: 'Get your menu online, share your address and hours, and let customers find you in minutes.',
      cta: 'Create my first restaurant',
    },
    wizard: {
      stepOf: 'Step {current} of {total}',
      step1Title: 'Name your restaurant',
      nameLabel: 'Restaurant name',
      taglineOptional: 'Tagline (optional)',
      slugPreview: 'Your web address',
      templateLabel: 'Public page template',
      templateHint: 'Choose the layout for your public page.',
      next: 'Next',
      back: 'Back',
      skipForNow: 'Skip for now',
      doLater: "I'll do this later",
      step2Title: 'Location & contact (optional)',
      youreAllSet: "You're all set",
      sublineLive: 'Your restaurant page is live. Share the link or QR so customers can find you.',
      copyAddress: 'Copy address',
      copied: 'Copied!',
      saveQr: 'Save QR code',
      scanHint: 'Customers scan this to open your page',
      goToMyRestaurant: 'Go to my restaurant',
      backToDashboard: 'Back to dashboard',
      firstTimeLink: 'First time? Create your first restaurant',
    },
    startTour: 'App-Tour starten',
    startTourDescription: 'Die geführte Tour erneut starten, um die wichtigsten Bereiche der App zu sehen.',
  },
  fr: {
    app: {
      name: 'RMS',
      login: 'Connexion',
      register: 'Créer un compte',
      logout: 'Déconnexion',
      profile: 'Profil et paramètres',
      restaurants: 'Restaurants',
      menu: 'Menu',
      languages: 'Langues et description',
      addLanguage: 'Ajouter une langue',
      defaultLanguage: 'Langue par défaut',
      languagesHint: 'Ajoutez ou supprimez des langues. La langue par défaut est utilisée lorsqu\'aucune langue n\'est sélectionnée sur le site public.',
      description: 'Description',
      save: 'Enregistrer',
      cancel: 'Annuler',
      remove: 'Supprimer',
      translate: 'Traduire depuis la langue par défaut',
      addItem: 'Ajouter un élément',
      editItem: 'Modifier l\'élément du menu',
      createItem: 'Ajouter un élément au menu',
      dashboard: 'Tableau de bord',
      feedbacks: 'Commentaires',
      feedbacksListSubtitle: 'Approuvez les commentaires pour les afficher sur votre page publique. Rejetez ou supprimez pour les masquer.',
      feedbacksListEmpty: 'Aucun commentaire pour l\'instant. Ils apparaîtront ici lorsque les clients enverront des avis.',
      feedbacksLoadError: 'Échec du chargement des commentaires.',
      retry: 'Réessayer',
      ariaMainNav: 'Navigation principale',
      ariaAppLanguage: 'Langue de l\'application',
      ariaOpenMenu: 'Ouvrir le menu',
      ownerFeedbacksSubtitle: 'Demandes de fonctionnalités et retours des propriétaires. Basculez le statut après examen.',
      ownerFeedbacksEmpty: 'Aucun commentaire propriétaire pour l\'instant.',
      markPending: 'Marquer en attente',
      markReviewed: 'Marquer comme examiné',
      feedbacksUpdateForbidden: 'Vous n\'avez pas la permission de mettre à jour les commentaires.',
      feedbackNotFound: 'Commentaire introuvable.',
      feedbackUpdated: 'Commentaire mis à jour.',
      feedbackApproved: 'Commentaire approuvé.',
      feedbackRejected: 'Commentaire rejeté.',
      failedToUpdate: 'Échec de la mise à jour.',
      feedbackDeleted: 'Commentaire supprimé.',
      failedToDelete: 'Échec de la suppression.',
      deleteFeedbackConfirm: 'Supprimer ce commentaire ? Cette action est irréversible.',
      users: 'Utilisateurs',
      superadminUsersSubtitle: 'Gérer les comptes : statut payant et activer/désactiver.',
      noUsersFound: 'Aucun utilisateur trouvé.',
      paid: 'Payant',
      free: 'Gratuit',
      active: 'Actif',
      deactivated: 'Désactivé',
      superadmin: 'Superadmin',
      markPaid: 'Marquer payant',
      removePaid: 'Retirer payant',
      activate: 'Activer',
      deactivate: 'Désactiver',
      ariaDeactivateUser: 'Désactiver l\'utilisateur',
      ariaActivateUser: 'Activer l\'utilisateur',
      you: '(vous)',
      usersForbidden: 'Vous n\'avez pas la permission de voir les utilisateurs.',
      superadminRestaurantsSubtitle: 'Liste en lecture seule de tous les restaurants.',
      noRestaurantsFound: 'Aucun restaurant trouvé.',
      availability: 'Disponibilité',
      availabilitySubtitle: 'Quand votre restaurant est ouvert. Modifiez les horaires ci-dessous et enregistrez.',
      deleteRestaurant: 'Supprimer le restaurant',
      logoAndBanner: 'Logo et bannière',
      logoBannerDescription: 'Modifier ou mettre à jour le logo et la bannière du restaurant.',
      uploadImagesHint: 'Téléchargez des images. JPEG, PNG, GIF ou WebP. Max 2 Mo chacune.',
      logo: 'Logo',
      banner: 'Bannière',
      takePhoto: 'Prendre une photo',
      chooseFromGallery: 'Choisir dans la galerie',
      changeLogo: 'Changer le logo',
      changeBanner: 'Changer la bannière',
      done: 'Terminé',
      deleteRestaurantConfirm: 'Supprimer le restaurant ?',
      cannotBeUndone: 'Cette action est irréversible.',
      typeToConfirm: 'Tapez {slug} pour confirmer',
      ariaUploadingLogo: 'Téléchargement du logo',
      ariaUploadingBanner: 'Téléchargement de la bannière',
      ariaUploadLogo: 'Télécharger le logo',
      ariaUploadBanner: 'Télécharger la bannière',
      ariaUploadLogoFromGallery: 'Télécharger le logo depuis la galerie',
      ariaUploadBannerFromGallery: 'Télécharger la bannière depuis la galerie',
      ariaTakePhotoLogo: 'Prendre une photo pour le logo',
      ariaTakePhotoBanner: 'Prendre une photo pour la bannière',
      settings: 'Paramètres',
      ariaManageSections: 'Sections de gestion du commerce',
      contactAndDetails: 'Contact et détails',
      address: 'Adresse',
      email: 'E-mail',
      social: 'Réseaux sociaux',
      addLanguageEllipsis: 'Ajouter une langue…',
      addLanguageHint: 'Ajoutez au moins une langue ci-dessus pour gérer les descriptions.',
      editDescriptionFor: 'Modifier la description pour :',
      ariaSelectTemplate: 'Sélectionner le modèle',
      menusAndCategories: 'Menus et catégories',
      createFirstMenuHint: 'Créez votre premier menu pour ajouter des catégories.',
      createCategoryFirstHint: 'Créez d\'abord une catégorie pour ajouter des éléments au menu.',
      menuItemsSettingsHint: 'Ajoutez les plats depuis la page de chaque catégorie. Les traductions utilisent les paramètres du restaurant.',
      createCategoryAboveHint: 'Créez une catégorie ci-dessus pour ajouter des éléments.',
      renameMenu: 'Renommer le menu',
      selectMenu: 'Sélectionner le menu',
      editCategory: 'Modifier la catégorie',
      removeCategory: 'Supprimer la catégorie',
      addMenu: 'Ajouter un menu',
      addCategory: 'Ajouter une catégorie',
      categoryAvailability: 'Disponibilité de la catégorie',
      removeCategoryImage: 'Supprimer l\'image de la catégorie',
      addMenuOrCategory: 'Ajouter un menu ou une catégorie',
      editIn: 'Modifier dans',
      changeImage: 'Changer l\'image',
      uploadImage: 'Télécharger une image',
      translating: 'Traduction…',
      removing: 'Suppression…',
      mainMenuDefault: 'Menu principal',
      menuManagement: 'Gestion du menu',
      menuManagementSubtitle: 'Menus, catégories et éléments. Glissez pour réorganiser.',
      noMenuItemsYet: 'Aucun élément pour l\'instant. Utilisez le bouton + ci-dessous.',
      categoryNameRequired: 'Le nom de la catégorie est requis.',
      categoryUpdated: 'Catégorie mise à jour.',
      categoryCreated: 'Catégorie créée.',
      addMenuItemsToCategory: 'Ajouter des éléments à cette catégorie',
      noMenuItemsOrMatch: 'Aucun élément ou aucun résultat.',
      assignTags: 'Attribuer des tags',
      itemAvailability: 'Disponibilité de l\'élément',
      dragToReorder: 'Glisser pour réorganiser',
      setAvailabilityTimes: 'Définir les horaires',
      available: 'Disponible',
      notAvailable: 'Non disponible',
      markNotAvailable: 'Marquer non disponible',
      markAvailableOnMenu: 'Marquer disponible sur le menu public',
      visibleOnMenu: 'Visible sur le menu',
      hiddenFromMenu: 'Masqué du menu',
      addMenuItemToCategory: 'Ajouter un élément à la catégorie',
      menuItemTags: 'Tags des éléments',
      menuItems: 'Éléments du menu',
      createRestaurantFirstForMenus: 'Créez d\'abord un restaurant pour gérer les menus.',
      addRestaurant: 'Ajouter un restaurant',
      ratingOutOf5: 'Note : {n} sur 5',
      anonymous: 'Anonyme',
      approve: 'Approuver',
      reject: 'Rejeter',
      ariaApproveFeedback: 'Approuver le commentaire',
      ariaRejectFeedback: 'Rejeter le commentaire',
      ariaDeleteFeedback: 'Supprimer le commentaire',
      resetPasswordSuccess: 'Votre mot de passe a été réinitialisé. Vous pouvez vous connecter.',
      resetPasswordEnterNew: 'Entrez votre nouveau mot de passe ci-dessous.',
      type: 'Type',
      labelOptional: 'Libellé (optionnel)',
      placeholderUrl: 'https://...',
      placeholderPhone: 'ex. +33 1 23 45 67 89',
      placeholderContactLabel: 'ex. Réservations, Cuisine',
      showOnPublicPage: 'Afficher sur la page publique',
      hideOnPublicPage: 'Masquer de la page publique',
      ariaEditContact: 'Modifier le contact',
      ariaDeleteContact: 'Supprimer le contact',
      noContactsYet: 'Aucun contact ou lien. Ajoutez-en un pour que les clients vous joignent.',
      deleteContactConfirm: 'Supprimer ce contact ?',
      ariaPagination: 'Pagination',
      ariaOpeningTime: 'Heure d\'ouverture',
      ariaClosingTime: 'Heure de fermeture',
      removeTimeSlot: 'Supprimer le créneau',
      menuItemLabel: 'Élément du menu',
      addMenuItem: 'Ajouter un élément au menu',
      ariaRemoveImage: 'Supprimer l\'image',
      ariaRemoveVariantImage: 'Supprimer l\'image de la variante',
      ariaRemoveEntry: 'Supprimer l\'entrée',
      ariaRemoveGroup: 'Supprimer le groupe',
      ariaShowHelp: 'Afficher l\'aide : que signifient les boutons ?',
      help: 'Aide',
      ariaButtonLegend: 'Légende des boutons',
      helpLegendDescription: 'Signification des icônes sur les lignes menu et catégorie.',
      legendDragHandle: 'Poignée de glissement',
      legendDragDesc: 'Glisser pour réorganiser',
      legendSchedule: 'Horaires',
      legendScheduleDesc: 'Définir les horaires',
      legendAvailability: 'Disponibilité',
      legendAvailabilityDesc: 'Quand cet élément est disponible',
      legendVisibility: 'Visibilité',
      legendVisibilityDesc: 'Afficher ou masquer sur le menu public',
      legendTags: 'Tags',
      legendTagsDesc: 'Attribuer des tags',
      paidFeature: 'Fonction payante',
      upgradeToUse: 'Passez à une version supérieure pour utiliser cette fonction.',
      paidFeatureAiDescription: 'Embellir les images du menu avec l\'IA est disponible sur un plan payant.',
      maybeLater: 'Plus tard',
      upgrade: 'Mettre à niveau',
      ariaNotifications: 'Notifications',
      dismiss: 'Fermer',
      reservationsHowItWorksTitle: 'Comment fonctionnent les réservations',
      reservationsHowItWorksIntro: 'Les clients peuvent demander une table depuis la page publique de votre restaurant. Vous acceptez, refusez ou modifiez les réservations ici.',
      reservationsHowItWorksPhoneRequired: 'Un numéro de téléphone valide est requis pour chaque réservation afin d\'envoyer les confirmations et rappels par SMS.',
      reservationsHowItWorksSteps: 'Définissez le numéro de notification SMS pour chaque restaurant pour recevoir les alertes de nouvelles réservations. Acceptez ou refusez les demandes ; les clients reçoivent un SMS. Vous pouvez aussi modifier la date, l\'heure ou le nombre de convives et annuler — les clients sont notifiés par SMS.',
      reservationsHowItWorksTooltip: 'Comment fonctionnent les réservations',
      close: 'Fermer',
      loading: 'Chargement…',
      back: 'Retour',
      delete: 'Supprimer',
      pending: 'En attente',
      featureRequest: 'Demande de fonctionnalité',
      ownerFeedbacks: 'Commentaires des propriétaires',
    },
    errors: {
      forbidden: 'Vous n\'avez pas la permission de faire cela.',
      validation: 'Veuillez vérifier votre saisie.',
      pageNotFound: 'Page introuvable',
      pageNotFoundDescription: 'La page que vous recherchez n’existe pas ou a été déplacée.',
    },
    landing: {
      hero: {
        badge: 'La technologie pour votre entreprise, simplifiée',
        titleBefore: 'Votre entreprise, ',
        titleHighlight: 'en ligne en 10 minutes.',
        subtitle: 'Mettez votre entreprise en ligne dès aujourd’hui — Catalogue produits, Horaires, Réservation et plus. Gratuit pour commencer. Aucune compétence technique requise.',
        trustedBy: 'Gratuit pour toujours. Aucune carte de crédit requise.',
      },
      cta: {
        bookDemo: 'Voir la démo {businessType}',
        bookDemoGeneric: 'Voir la démo',
      },
      footer: {
        cookies: 'Paramètres des cookies',
      },
    },
    public: {
      about: 'À propos',
      t3NavAbout: 'À propos',
      siteCreatedViaQRestoo: 'Site créé par QRestoo',
      createdByQRestoo: 'Créé sur QRestoo',
      sitePoweredByDLR: 'Site powered by DLR Web Solutions',
      contactUs: 'Nous contacter',
      reviewsAndFeedback: 'Avis et commentaires',
      t1NavMenu: 'Menu',
      reviewsNav: 'Avis',
      contactNav: 'Contact',
      t2MenuNav: 'Menu',
      t2ViewServices: 'Services',
      t2ReviewsNav: 'Avis',
      t2ContactNav: 'Contact',
      navMenu: 'Menu',
      navServices: 'Services',
      navProducts: 'Produits',
      ownerNavBookingUnavailable: 'Réservation. Mise à niveau du compte requise.',
      ownerNavReviewsUnavailable: 'Avis. Mise à niveau du compte requise.',
      ownerNavLocationUnavailable: 'Emplacement. Ajoutez l’adresse ou la carte dans les paramètres.',
      ownerNavAboutUnavailable: 'Notre histoire. Ajoutez la description dans les paramètres.',
      ownerNavHoursUnavailable: 'Horaires. Ajoutez les heures d’ouverture dans les paramètres.',
      t3FooterTaglineRestaurants: 'Créer des expériences culinaires inoubliables. L’excellence dans chaque assiette.',
      t3FooterTaglineEcommerce: 'Produits de qualité, soigneusement sélectionnés. L’excellence à chaque commande.',
      t3FooterTaglineServices: 'Un service d’exception. L’excellence dans chaque détail.',
      t2CommunicationsLink: 'Lien de communication',
      noLogoSetOwnerMessage: 'Aucun logo. Ajoutez-en un dans les paramètres de votre établissement.',
      ownerEditLogoAria: 'Modifier le logo et la bannière',
      heroEstYear: 'Fond. {year}',
      noTaglineOrDescriptionSetOwnerMessage: 'Aucun slogan ni description. Ajoutez-les dans les paramètres de votre établissement.',
      noDescriptionSetOwnerMessage: 'Aucune description. Ajoutez-en une dans les paramètres de votre établissement.',
      noMapSetOwnerMessage: 'Aucune carte. Ajoutez l’adresse ou un lien de carte dans les paramètres.',
      noBookingAvailableOwnerMessage: 'La réservation n’est pas disponible. Activez-la dans les paramètres de votre établissement.',
      noMailingListAvailableOwnerMessage: 'La liste de diffusion n’est pas disponible. Activez-la dans les paramètres.',
      noFeedbackAvailableOwnerMessage: 'Les avis et retours ne sont pas disponibles. Activez-les dans les paramètres.',
      t2NewsletterTitle: 'INSCRIPTION À LA NEWSLETTER',
      t2FeedbackPerformanceLogging: 'Journal des performances',
      t2FeedbackOperationalLog: 'JOURNAL OPÉRATIONNEL',
      t2FeedbackTransmitCopy: 'Transmettez votre rapport d\'expérience pour l\'assurance qualité et l\'optimisation.',
      t2FeedbackSessionMeta: 'Métadonnées de session :',
      t2FeedbackTerminalId: 'ID terminal : TGT-LOG-992',
      t2FeedbackProtocol: 'Protocole : feedback_v2.0.sh',
      t2FeedbackRatingLabel: 'ÉVALUATION DES PERFORMANCES',
      t2FeedbackRatingHint: 'Sélectionnez les métriques d\'évaluation (1-5)',
      t2FeedbackSitrepLabel: 'RAPPORT DE SITUATION / AVIS DÉTAILLÉ',
      t2FeedbackPlaceholder: 'Saisissez les données du journal ici...',
      t2FeedbackSubmitReport: 'ENVOYER LE RAPPORT',
      t2FeedbackDisclaimer: 'En soumettant, vous acceptez les protocoles d\'archivage des données.',
      ourMenu: 'Notre menu',
      ourProducts: 'Produits',
      ourServices: 'Nos services',
      ourCatalog: 'Notre catalogue',
      viewMenu: 'Voir le menu',
      viewCatalog: 'Voir le catalogue',
      viewServices: 'Voir les services',
      leaveFeedback: 'Laissez votre avis',
      ratingLabel: 'Note (1–5)',
      yourName: 'Votre nom',
      yourMessage: 'Votre message',
      sendFeedback: 'Envoyer le commentaire',
      formErrorGeneric: 'Une erreur s\'est produite. Veuillez réessayer.',
      mailingListError: 'Une erreur s\'est produite. Veuillez réessayer.',
      noReviewsYet: 'Aucun avis pour le moment. Soyez le premier à laisser un commentaire ci-dessous.',
      openingHours: 'Horaires d\'ouverture',
      noContactListed: 'Aucun numéro ou lien indiqué.',
      whatsapp: 'WhatsApp',
      mobile: 'Mobile',
      email: 'E-mail',
      phone: 'Téléphone',
      fax: 'Fax',
      other: 'Contact',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      website: 'Site web',
      viewingAsOwner: 'Vous consultez cette page en tant que propriétaire.',
      viewAsCustomer: 'Voir en tant que client',
      viewAsOwner: 'Voir en tant que propriétaire',
      restaurantNotFoundTitle: 'Restaurant introuvable',
      backToHome: 'Retour à l\'accueil',
      ownerNeedsDataIntro: 'Ce site a besoin de plus de contenu. Ajoutez une description, un menu ou autre dans l\'admin.',
      openAdminPage: 'Ouvrir l\'admin',
      addBannerInAdmin: 'Ajouter une image de couverture dans Paramètres',
      bookTable: 'Réserver une table',
      bookAppointment: 'Prendre rendez-vous',
      mapPlaceholder: 'Carte',
      allRightsReserved: 'Tous droits réservés.',
      surpriseMe: 'Surprise me',
      surpriseMeAria: 'Surprise me – choisir un élément au hasard',
      noMenuItems: 'Aucun élément au menu',
      noProducts: 'Aucun produit',
      noServices: 'Aucun service',
      noCatalogItems: 'Aucun élément dans le catalogue',
      notAvailable: 'Non disponible',
      notAvailableWithNextTime: 'Indisponible pour le moment. Prochaine disponibilité : {time}.',
      sizeAndPriceOptions: 'Options de taille et de prix',
      option: 'Option',
      comboContents: 'Contenu du combo',
      ownerEditMenuItemInCatalogAria: 'Modifier l’article dans le catalogue (ouvre l’administration)',
      closeMenu: 'Fermer le menu',
      closeCatalogModal: 'Fermer le catalogue',
      closeServicesModal: 'Fermer les services',
      ariaViewMenu: 'Voir le menu',
      ariaViewCatalog: 'Voir le catalogue',
      ariaViewServices: 'Voir les services',
      modalTitleMenu: 'Menu',
      modalTitleCatalog: 'Catalogue',
      modalTitleProducts: 'Produits',
      modalTitleServices: 'Services',
      ariaLocation: 'Emplacement',
      ariaMap: 'Carte',
      category: 'Catégorie',
      mapAriaLabel: 'Carte montrant l\'emplacement du restaurant',
      openInMaps: 'Ouvrir dans la carte',
      mapIframeTitle: 'Google Maps pour cet emplacement',
      mapOpenExternallyHint:
        "Appuyez ci-dessous pour ouvrir ce lieu dans votre application Plans. Pour afficher une carte ici, ajoutez la latitude et la longitude ou un lien Google Maps avec épingle ({'@'}lat,lng) dans les paramètres de l’établissement.",
      openNow: 'Ouvert maintenant',
      closedNow: 'Fermé',
      businessHoursNotListed: 'Horaires non publiés',
      open24_7: 'Ouvert 24h/24 et 7j/7',
      daySunday: 'Dimanche',
      dayMonday: 'Lundi',
      dayTuesday: 'Mardi',
      dayWednesday: 'Mercredi',
      dayThursday: 'Jeudi',
      dayFriday: 'Vendredi',
      daySaturday: 'Samedi',
      t5TableIcon: 'Icône',
      t5TableImage: 'Image',
      t5TableProduct: 'Produit',
      t5TablePrice: 'Prix',
      t5TableStatus: 'État',
      t5TableAction: 'Action',
      t5ViewDetails: 'Voir les détails',
      t5InStock: 'En stock',
      selectVariantToAdd: 'Sélectionnez une variante à ajouter au panier',
      selectRequiredModifierChoice: 'Choisissez une option dans chaque groupe obligatoire avant d’ajouter au panier.',
      modifiersHeading: 'Options supplémentaires (facultatif)',
      modifiersAria: 'Options supplémentaires facultatives',
      itemDetailDialogAria: 'Détails de l’article du menu',
      itemUntitled: 'Sans titre',
      itemNotAvailable: 'Indisponible',
      variantOptionNotAvailable: 'Indisponible',
      variantOptionsAria: 'Options et prix',
      variantImageAlt: 'Variante',
      priceFrom: 'À partir de {price}',
      comboContentsAria: 'Contenu du menu composé',
      modifierUnnamed: 'Option',
      modifierNoExtraCost: 'Sans supplément',
      quickOrder: {
        addToCart: 'Ajouter au panier',
        updateCart: 'Mettre à jour le panier',
        yourOrder: 'Votre commande',
        clearAll: 'Tout effacer',
        name: 'Votre nom',
        namePlaceholder: 'ex. Jean',
        notes: 'Notes',
        notesPlaceholder: 'ex. Sauce supplémentaire, sans oignons...',
        sendViaEmail: 'Envoyer par e-mail',
        sendViaWhatsApp: 'Envoyer par WhatsApp',
        sendViaViber: 'Envoyer par Viber',
        sendViaSMS: 'Envoyer par SMS',
        orderSent: 'Commande envoyée !',
        orderSentDesc: 'Votre commande a été envoyée à l\'établissement.',
        nameRequired: 'Le nom est requis.',
        nameMaxLength: 'Le nom doit comporter au plus 100 caractères.',
        notesMaxLength: 'Les notes doivent comporter au plus 500 caractères.',
        notesRequired: 'Les notes sont requises.',
        cartEmpty: 'Votre panier est vide.',
        quantity: 'Quantité',
        remove: 'Supprimer',
        itemAdded: 'Ajouté au panier !',
        itemUpdated: 'Panier mis à jour !',
        ariaClose: 'Fermer',
        ariaDecrease: 'Diminuer la quantité',
        ariaIncrease: 'Augmenter la quantité',
        ariaRemoveItem: 'Supprimer l\'article',
        fulfillment: 'À emporter ou sur place',
        fulfillmentPickup: 'À emporter',
        fulfillmentDineIn: 'Sur place',
        dineInReferenceLabel: 'Table / référence (facultatif)',
        dineInReferencePlaceholder: 'ex. Table 5',
        checkout: 'Paiement',
        orderSummary: 'Récapitulatif',
        confirmCheckout: 'Valider la commande',
        checkoutSubmitting: 'Envoi en cours…',
        checkoutFailed: 'Impossible d’envoyer la commande. Réessayez.',
        subtotal: 'Sous-total',
        ariaCloseSummary: 'Fermer le récapitulatif',
        mobile: 'Mobile',
        mobilePlaceholder: '+33612345678',
        mobileHint: 'Commencez par + et l’indicatif pays (ex. +33…). Nous enverrons un code par SMS.',
        mobileRequired: 'Le mobile est requis pour le retrait.',
        mobileInvalid: 'Numéro valide avec indicatif requis.',
        verificationCode: 'Code de vérification',
        verificationCodePlaceholder: 'Code à 6 chiffres',
        verificationCodeInvalid: 'Saisissez le code à 6 chiffres reçu par SMS.',
        sendVerificationCode: 'Envoyer le code',
        pickupServiceFee: 'Frais de retrait ({percent} %)',
        total: 'Total',
      },
      cookieConsent: {
        barTitle: 'Cookies et technologies similaires',
        barBody:
          'Nous utilisons des fonctions essentielles pour faire fonctionner le site. Avec votre accord, nous ajoutons des analyses internes et des outils tiers (p. ex. contrôles de sécurité ou connexion). Pour plus de détails, ouvrez la ',
        acceptAll: 'Tout accepter',
        rejectNonEssential: 'Refuser les non essentiels',
        customize: 'Personnaliser',
        modalTitle: 'Préférences de cookies',
        necessaryTitle: 'Strictement nécessaires',
        necessaryDesc: 'Indispensables pour la sécurité, la connexion et les fonctions de base. Toujours actives.',
        analyticsTitle: 'Analytique (première partie)',
        analyticsDesc: 'Nous aide à améliorer les pages publiques. Les données sont traitées sur nos serveurs.',
        thirdPartyTitle: 'Fonctions tierces',
        thirdPartyDesc: 'Charge des outils comme Google reCAPTCHA pour certains formulaires ou la connexion.',
        save: 'Enregistrer les préférences',
        privacyLink: 'politique de confidentialité',
      },
      // Quick-add item wizard (owner public site)
      quickAddItem: 'Ajouter un article',
      quickAddItemAria: 'Ajouter un article au menu',
      quickAddStepOf: 'Étape {step} sur {total}',
      quickAddStep0Title: 'Type d\'<em>article</em>',
      quickAddStep1Title: 'Choisir une <em>catégorie</em>',
      quickAddStep2Title: '<em>Détails</em> de l\'article',
      quickAddStep3Title: 'Vérifier et <em>enregistrer</em>',
      quickAddWhatKind: 'Quel type d\'article ?',
      quickAddTypeSimpleSub: 'Un prix',
      quickAddTypeVariantsSub: 'Tailles, options…',
      quickAddTypeCombSub: 'Offre groupée',
      quickAddTypeSimpleHint: 'Un produit ou plat avec un prix fixe.',
      quickAddTypeVariantsHint: 'Un article proposé en plusieurs options — chacune avec son prix (ex. Petit / Grand).',
      quickAddTypeComboHint: 'Plusieurs articles vendus ensemble (ex. formule).',
      quickAddNoCats: 'Pas encore de catégories',
      quickAddNoCatsSub: 'Créez votre première catégorie ci-dessous pour organiser vos articles.',
      quickAddOrCreate: 'ou en créer une',
      quickAddFirstCatName: 'Nom de votre première catégorie',
      quickAddNewCat: 'Nouvelle catégorie',
      quickAddCatPlaceholder: 'ex. Plats, Boissons, Services…',
      quickAddCreate: 'Créer',
      quickAddCatWillCreate: 'Sera créée : {name}',
      quickAddItem1: 'article',
      quickAddItemN: 'articles',
      quickAddNameLabel: 'Nom de l\'article',
      quickAddNamePlaceholder: 'ex. Pizza Margherita',
      quickAddPriceOptional: 'Prix (facultatif)',
      quickAddComboPriceOptional: 'Prix du combo (facultatif)',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: 'Description (facultatif)',
      quickAddDescPlaceholder: 'Affichée sur la fiche article…',
      quickAddVariantsLabel: 'Variantes',
      quickAddVariantName: 'Nom de l\'option',
      quickAddVariantNamePlaceholder: 'ex. Grand',
      quickAddVariantPrice: 'Prix',
      quickAddRemoveVariant: 'Supprimer la variante',
      quickAddAddVariant: 'Ajouter une variante',
      quickAddComboItems: 'Articles du combo',
      quickAddPickItem: 'Choisir un article…',
      quickAddAdd: 'Ajouter',
      quickAddRemoveComboItem: 'Retirer l\'article',
      quickAddNoItemsForCombo: 'Pas encore d\'articles — ajoutez d\'abord des articles simples ou avec variantes.',
      quickAddReviewItem: 'Article',
      quickAddReviewName: 'Nom',
      quickAddReviewType: 'Type',
      quickAddReviewPrice: 'Prix',
      quickAddReviewIncludes: 'Comprend',
      quickAddReviewCategory: 'Catégorie',
      quickAddReviewCategoryLabel: 'Catégorie',
      quickAddNoPrice: 'Aucun prix',
      quickAddNoCategory: 'Aucune',
      quickAddNew: 'nouvelle',
      quickAddPublishNow: 'Publier immédiatement',
      quickAddPublishNowSub: 'Visible sur votre site tout de suite',
      quickAddAddAnother: 'Ajouter un autre après enregistrement',
      quickAddAddAnotherSub: 'Garder ce flux ouvert',
      quickAddSaveItem: 'Enregistrer l\'article',
      quickAddReview: 'Vérifier',
      quickAddContinue: 'Continuer',
      quickAddBack: 'Retour',
      quickAddSaved: 'Article enregistré.',
      quickAddSavedSub: '{name} a été ajouté à {category}.',
      quickAddAnotherItem: '+ Ajouter un autre article',
      quickAddDone: 'Terminé',
    },
    tour: {
      welcomeTitle: 'Bienvenue !',
      welcomeDescription: 'Faisons un rapide tour de votre tableau de bord. Vous verrez où gérer restaurants, menus et plus.',
      step1Title: 'Zone principale',
      step1Description: 'Votre contenu s\'affiche ici. Utilisez la barre latérale pour accéder au tableau de bord, aux restaurants, etc.',
      step2Title: 'Navigation',
      step2Description: 'Utilisez cette barre pour le tableau de bord, les restaurants, les éléments de menu, les avis et le profil.',
      step2DescriptionSuperadmin: 'Utilisez cette barre pour le tableau de bord, les utilisateurs, les retours propriétaires, les restaurants et plus.',
      step3Title: 'Tableau de bord',
      step3Description: 'Votre vue d\'ensemble. Restaurants, éléments de menu et avis en un coup d\'œil.',
      step4Title: 'Restaurants',
      step4Description: 'Gérez vos restaurant(s). Ajoutez des établissements, modifiez les détails et organisez les menús.',
      step5Title: 'Éléments de menu',
      step5Description: 'Créez et modifiez plats, boissons et combos. Ajoutez-les aux catégories de chaque menu.',
      step6Title: 'Avis',
      step6Description: 'Consultez et modérez les avis clients de vos pages publiques. Approuvez ou rejettez pour les afficher ou les masquer.',
      step7Title: 'Demande de fonction',
      step7Description: 'Envoyez des retours ou des demandes de fonction à l\'équipe. Vous pouvez aussi voir vos envois précédents ici.',
      step8Title: 'Profil et paramètres',
      step8Description: 'Mettez à jour nom, e-mail, mot de passe et langue de l\'app.',
      next: 'Suivant',
      prev: 'Précédent',
      done: 'Terminer',
      close: 'Fermer',
      skip: 'Passer la visite',
    },
    restaurantsTour: {
      step1Title: 'Liste des restaurants',
      step1Description: 'Voici votre liste de restaurants. Ajoutez des établissements, modifiez les détails et gérez les menus d\'ici.',
      step2Title: 'Ajouter un restaurant',
      step2Description: 'Cliquez ici pour créer un nouveau restaurant et définir son nom, adresse et sous-domaine.',
      step3Title: 'Vos restaurants',
      step3Description: 'Vos restaurants s\'affichent en cartes. Cliquez sur une carte pour l\'ouvrir ou utilisez l\'état vide pour créer le premier.',
      step4Title: 'Carte restaurant',
      step4Description: 'Cliquez sur un restaurant pour le modifier, gérer les menus, définir les disponibilités et obtenir le lien public ou le QR code.',
      tooltip: 'Tour des restaurants',
    },
    welcome: {
      title: 'Create your first restaurant',
      subtitle: 'Get your menu online, share your address and hours, and let customers find you in minutes.',
      cta: 'Create my first restaurant',
    },
    wizard: {
      stepOf: 'Step {current} of {total}',
      step1Title: 'Name your restaurant',
      nameLabel: 'Restaurant name',
      taglineOptional: 'Tagline (optional)',
      slugPreview: 'Your web address',
      templateLabel: 'Public page template',
      templateHint: 'Choose the layout for your public page.',
      next: 'Next',
      back: 'Back',
      skipForNow: 'Skip for now',
      doLater: "I'll do this later",
      step2Title: 'Location & contact (optional)',
      youreAllSet: "You're all set",
      sublineLive: 'Your restaurant page is live. Share the link or QR so customers can find you.',
      copyAddress: 'Copy address',
      copied: 'Copied!',
      saveQr: 'Save QR code',
      scanHint: 'Customers scan this to open your page',
      goToMyRestaurant: 'Go to my restaurant',
      backToDashboard: 'Back to dashboard',
      firstTimeLink: 'First time? Create your first restaurant',
    },
    startTour: 'Relancer la visite guidée',
    startTourDescription: 'Relancez la visite guidée pour revoir les zones principales de l\'app.',
  },
  uk: {
    app: {
      name: 'RMS',
      login: 'Увійти',
      register: 'Створити обліковий запис',
      logout: 'Вийти',
      profile: 'Профіль та налаштування',
      restaurants: 'Ресторани',
      menu: 'Меню',
      languages: 'Мови та опис',
      addLanguage: 'Додати мову',
      defaultLanguage: 'Мова за замовчуванням',
      languagesHint: 'Додавайте або видаляйте мови. Мова за замовчуванням використовується, коли на публічному сайті не обрано жодної мови.',
      description: 'Опис',
      save: 'Зберегти',
      cancel: 'Скасувати',
      remove: 'Видалити',
      translate: 'Перекласти з мови за замовчуванням',
      addItem: 'Додати пункт',
      editItem: 'Редагувати пункт меню',
      createItem: 'Додати пункт до меню',
      dashboard: 'Панель керування',
      feedbacks: 'Відгуки',
      feedbacksListSubtitle: 'Схвалюйте відгуки, щоб показувати їх на публічній сторінці. Відхиляйте або видаляйте, щоб приховати.',
      feedbacksListEmpty: 'Відгуків ще немає. Вони з\'являться тут, коли клієнти залишать відгуки.',
      feedbacksLoadError: 'Не вдалося завантажити відгуки.',
      retry: 'Повторити',
      ariaMainNav: 'Головна навігація',
      ariaAppLanguage: 'Мова додатку',
      ariaOpenMenu: 'Відкрити меню',
      ownerFeedbacksSubtitle: 'Запити функцій і відгуки від власників. Змініть статус після перегляду.',
      ownerFeedbacksEmpty: 'Відгуків від власників ще немає.',
      markPending: 'Позначити як очікує',
      markReviewed: 'Позначити як переглянуто',
      feedbacksUpdateForbidden: 'У вас немає дозволу оновлювати відгуки.',
      feedbackNotFound: 'Відгук не знайдено.',
      feedbackUpdated: 'Відгук оновлено.',
      feedbackApproved: 'Відгук схвалено.',
      feedbackRejected: 'Відгук відхилено.',
      failedToUpdate: 'Не вдалося оновити.',
      feedbackDeleted: 'Відгук видалено.',
      failedToDelete: 'Не вдалося видалити.',
      deleteFeedbackConfirm: 'Видалити цей відгук? Це незворотньо.',
      users: 'Користувачі',
      superadminUsersSubtitle: 'Керування обліковими записами: платний статус та активність.',
      noUsersFound: 'Користувачів не знайдено.',
      paid: 'Платний',
      free: 'Безкоштовний',
      active: 'Активний',
      deactivated: 'Деактивовано',
      superadmin: 'Суперадмін',
      markPaid: 'Позначити платним',
      removePaid: 'Зняти платний',
      activate: 'Активувати',
      deactivate: 'Деактивувати',
      ariaDeactivateUser: 'Деактивувати користувача',
      ariaActivateUser: 'Активувати користувача',
      you: '(ви)',
      usersForbidden: 'У вас немає дозволу переглядати користувачів.',
      superadminRestaurantsSubtitle: 'Список усіх ресторанів лише для перегляду.',
      noRestaurantsFound: 'Ресторанів не знайдено.',
      availability: 'Доступність',
      availabilitySubtitle: 'Коли ваш ресторан відкритий. Змініть час нижче та натисніть Зберегти.',
      deleteRestaurant: 'Видалити ресторан',
      logoAndBanner: 'Логотип і банер',
      logoBannerDescription: 'Змінити або оновити логотип і банер ресторану.',
      uploadImagesHint: 'Завантажте зображення. JPEG, PNG, GIF або WebP. Макс. 2 МБ кожне.',
      logo: 'Логотип',
      banner: 'Банер',
      takePhoto: 'Зробити фото',
      chooseFromGallery: 'Обрати з галереї',
      changeLogo: 'Змінити логотип',
      changeBanner: 'Змінити банер',
      done: 'Готово',
      deleteRestaurantConfirm: 'Видалити ресторан?',
      cannotBeUndone: 'Це незворотньо.',
      typeToConfirm: 'Введіть {slug} для підтвердження',
      ariaUploadingLogo: 'Завантаження логотипу',
      ariaUploadingBanner: 'Завантаження банера',
      ariaUploadLogo: 'Завантажити логотип',
      ariaUploadBanner: 'Завантажити банер',
      ariaUploadLogoFromGallery: 'Завантажити логотип з галереї',
      ariaUploadBannerFromGallery: 'Завантажити банер з галереї',
      ariaTakePhotoLogo: 'Зробити фото для логотипу',
      ariaTakePhotoBanner: 'Зробити фото для банера',
      settings: 'Налаштування',
      ariaManageSections: 'Розділи керування бізнесом',
      contactAndDetails: 'Контакт та деталі',
      address: 'Адреса',
      email: 'Email',
      social: 'Соцмережі',
      addLanguageEllipsis: 'Додати мову…',
      addLanguageHint: 'Додайте щонайменше одну мову вище для керування описом.',
      editDescriptionFor: 'Редагувати опис для:',
      ariaSelectTemplate: 'Обрати шаблон',
      menusAndCategories: 'Меню та категорії',
      createFirstMenuHint: 'Створіть перше меню, щоб додати категорії.',
      createCategoryFirstHint: 'Спочатку створіть категорію, щоб додати пункти меню.',
      menuItemsSettingsHint: 'Додавайте страви з сторінки кожної категорії. Переклади використовують налаштування ресторану.',
      createCategoryAboveHint: 'Створіть категорію вище, щоб додати пункти.',
      renameMenu: 'Перейменувати меню',
      selectMenu: 'Обрати меню',
      editCategory: 'Редагувати категорію',
      removeCategory: 'Видалити категорію',
      addMenu: 'Додати меню',
      addCategory: 'Додати категорію',
      categoryAvailability: 'Доступність категорії',
      removeCategoryImage: 'Видалити зображення категорії',
      addMenuOrCategory: 'Додати меню або категорію',
      editIn: 'Редагувати в',
      changeImage: 'Змінити зображення',
      uploadImage: 'Завантажити зображення',
      translating: 'Переклад…',
      removing: 'Видалення…',
      mainMenuDefault: 'Головне меню',
      menuManagement: 'Керування меню',
      menuManagementSubtitle: 'Меню, категорії та пункти. Перетягуйте для зміни порядку.',
      noMenuItemsYet: 'Пунктів меню ще немає. Використовуйте кнопку + нижче.',
      categoryNameRequired: 'Назва категорії обов\'язкова.',
      categoryUpdated: 'Категорію оновлено.',
      categoryCreated: 'Категорію створено.',
      addMenuItemsToCategory: 'Додати пункти до цієї категорії',
      noMenuItemsOrMatch: 'Немає пунктів або жоден не відповідає пошуку.',
      assignTags: 'Призначити теги',
      itemAvailability: 'Доступність пункту',
      dragToReorder: 'Перетягуйте для зміни порядку',
      setAvailabilityTimes: 'Встановити час доступності',
      available: 'Доступний',
      notAvailable: 'Недоступний',
      markNotAvailable: 'Позначити недоступним',
      markAvailableOnMenu: 'Позначити доступним у публічному меню',
      visibleOnMenu: 'Видимий у меню',
      hiddenFromMenu: 'Прихований у меню',
      addMenuItemToCategory: 'Додати пункт до категорії',
      menuItemTags: 'Теги пунктів меню',
      menuItems: 'Пункти меню',
      createRestaurantFirstForMenus: 'Спочатку створіть ресторан для керування меню.',
      addRestaurant: 'Додати ресторан',
      ratingOutOf5: 'Оцінка: {n} з 5',
      anonymous: 'Анонімно',
      approve: 'Схвалити',
      reject: 'Відхилити',
      ariaApproveFeedback: 'Схвалити відгук',
      ariaRejectFeedback: 'Відхилити відгук',
      ariaDeleteFeedback: 'Видалити відгук',
      resetPasswordSuccess: 'Ваш пароль скинуто. Можете увійти.',
      resetPasswordEnterNew: 'Введіть новий пароль нижче.',
      type: 'Тип',
      labelOptional: 'Мітка (необов\'язково)',
      placeholderUrl: 'https://...',
      placeholderPhone: 'напр. +380 12 345 6789',
      placeholderContactLabel: 'напр. Бронь, Кухня',
      showOnPublicPage: 'Показувати на публічній сторінці',
      hideOnPublicPage: 'Приховати на публічній сторінці',
      ariaEditContact: 'Редагувати контакт',
      ariaDeleteContact: 'Видалити контакт',
      noContactsYet: 'Контактів або посилань ще немає. Додайте один.',
      deleteContactConfirm: 'Видалити цей контакт?',
      ariaPagination: 'Пагінація',
      ariaOpeningTime: 'Час відкриття',
      ariaClosingTime: 'Час закриття',
      removeTimeSlot: 'Видалити часовий слот',
      menuItemLabel: 'Пункт меню',
      addMenuItem: 'Додати пункт меню',
      ariaRemoveImage: 'Видалити зображення',
      ariaRemoveVariantImage: 'Видалити зображення варіанту',
      ariaRemoveEntry: 'Видалити запис',
      ariaRemoveGroup: 'Видалити групу',
      ariaShowHelp: 'Показати допомогу: що означають кнопки?',
      help: 'Допомога',
      ariaButtonLegend: 'Легенда кнопок',
      helpLegendDescription: 'Значення іконок у рядках меню та категорій.',
      legendDragHandle: 'Маніпулятор перетягування',
      legendDragDesc: 'Перетягуйте для зміни порядку',
      legendSchedule: 'Розклад',
      legendScheduleDesc: 'Встановити години роботи',
      legendAvailability: 'Доступність',
      legendAvailabilityDesc: 'Коли цей пункт доступний',
      legendVisibility: 'Видимість',
      legendVisibilityDesc: 'Показати або приховати в публічному меню',
      legendTags: 'Теги',
      legendTagsDesc: 'Призначити теги',
      paidFeature: 'Платна функція',
      upgradeToUse: 'Оновіться, щоб використовувати цю функцію.',
      paidFeatureAiDescription: 'Прикрасити зображення меню за допомогою ШІ доступно в платному тарифі.',
      maybeLater: 'Можливо пізніше',
      upgrade: 'Оновити',
      ariaNotifications: 'Сповіщення',
      dismiss: 'Закрити',
      reservationsHowItWorksTitle: 'Як працюють резервації столів',
      reservationsHowItWorksIntro: 'Гості можуть замовити стіл на публічній сторінці вашого ресторану. Ви підтверджуєте, відхиляєте або редагуєте броні з цієї сторінки.',
      reservationsHowItWorksPhoneRequired: 'Для кожної резервації потрібен дійсний номер телефону, щоб надсилати підтвердження та нагадування по SMS.',
      reservationsHowItWorksSteps: 'Вкажіть номер для SMS-сповіщень по кожному ресторану, щоб отримувати сповіщення про нові резервації. Підтверджуйте або відхиляйте запити; гості отримають SMS. Ви також можете змінити дату, час або кількість гостей і скасувати — гості отримають SMS.',
      reservationsHowItWorksTooltip: 'Як працюють резервації',
      close: 'Закрити',
      loading: 'Завантаження…',
      back: 'Назад',
      delete: 'Видалити',
      pending: 'Очікує',
      featureRequest: 'Запит функції',
      ownerFeedbacks: 'Відгуки власників',
    },
    errors: {
      forbidden: 'У вас немає дозволу на це.',
      validation: 'Перевірте введені дані.',
      pageNotFound: 'Сторінку не знайдено',
      pageNotFoundDescription: 'Сторінка, яку ви шукаєте, не існує або була переміщена.',
    },
    landing: {
      hero: {
        badge: 'Бізнес-технології, просто',
        titleBefore: 'Ваш бізнес, ',
        titleHighlight: 'онлайн за 10 хвилин.',
        subtitle: 'Виведіть свій бізнес онлайн сьогодні — Каталог товарів, Графік роботи, Бронювання та більше. Безкоштовний старт. Технічні навички не потрібні.',
        trustedBy: 'Безкоштовно назавжди. Кредитна картка не потрібна.',
      },
      cta: {
        bookDemo: 'Переглянути демо: {businessType}',
        bookDemoGeneric: 'Переглянути демо',
      },
      footer: {
        cookies: 'Налаштування cookie',
      },
    },
    public: {
      about: 'Про нас',
      t3NavAbout: 'Про нас',
      siteCreatedViaQRestoo: 'Сайт створено QRestoo',
      createdByQRestoo: 'Створено на QRestoo',
      sitePoweredByDLR: 'Site powered by DLR Web Solutions',
      contactUs: 'Зв\'язатися з нами',
      reviewsAndFeedback: 'Відгуки та коментарі',
      t1NavMenu: 'Меню',
      reviewsNav: 'Відгуки',
      contactNav: 'Контакт',
      t2MenuNav: 'Меню',
      t2ViewServices: 'Послуги',
      t2ReviewsNav: 'Відгуки',
      t2ContactNav: 'Контакт',
      navMenu: 'Меню',
      navServices: 'Послуги',
      navProducts: 'Товари',
      ownerNavBookingUnavailable: 'Бронювання. Потрібне оновлення облікового запису.',
      ownerNavReviewsUnavailable: 'Відгуки. Потрібне оновлення облікового запису.',
      ownerNavLocationUnavailable: 'Розташування. Додайте адресу або карту в налаштуваннях.',
      ownerNavAboutUnavailable: 'Наша історія. Додайте опис в налаштуваннях.',
      ownerNavHoursUnavailable: 'Години. Додайте години роботи в налаштуваннях.',
      t3FooterTaglineRestaurants: 'Створюємо незабутні гастрономічні враження. Досконалість у кожній страві.',
      t3FooterTaglineEcommerce: 'Якісні товари, ретельно відібрані. Досконалість у кожному замовленні.',
      t3FooterTaglineServices: 'Надзвичайний сервіс. Досконалість у кожній деталі.',
      noLogoSetOwnerMessage: 'Логотип не встановлено. Додайте його в налаштуваннях бізнесу.',
      ownerEditLogoAria: 'Редагувати логотип і банер',
      heroEstYear: 'Засн. {year}',
      noTaglineOrDescriptionSetOwnerMessage: 'Немає слогану чи опису. Додайте їх у налаштуваннях бізнесу.',
      noDescriptionSetOwnerMessage: 'Немає опису. Додайте його в налаштуваннях бізнесу.',
      noMapSetOwnerMessage: 'Немає карти. Додайте адресу або посилання на карту в налаштуваннях.',
      noBookingAvailableOwnerMessage: 'Бронювання недоступне. Увімкніть у налаштуваннях бізнесу.',
      noMailingListAvailableOwnerMessage: 'Список розсилки недоступний. Увімкніть у налаштуваннях бізнесу.',
      noFeedbackAvailableOwnerMessage: 'Відгуки та коментарі недоступні. Увімкніть у налаштуваннях бізнесу.',
      t2CommunicationsLink: 'Зв’язок',
      t2NewsletterTitle: 'ПІДПИСКА НА РОЗСИЛКУ',
      t2FeedbackPerformanceLogging: 'Журнал продуктивності',
      t2FeedbackOperationalLog: 'ОПЕРАТИВНИЙ ЖУРНАЛ',
      t2FeedbackTransmitCopy: 'Надішліть звіт про досвід для забезпечення якості та оптимізації.',
      t2FeedbackSessionMeta: 'Метадані сесії:',
      t2FeedbackTerminalId: 'ID терміналу: TGT-LOG-992',
      t2FeedbackProtocol: 'Протокол: feedback_v2.0.sh',
      t2FeedbackRatingLabel: 'ОЦІНКА РОБОТИ',
      t2FeedbackRatingHint: 'Оберіть метрики оцінки (1-5)',
      t2FeedbackSitrepLabel: 'ЗВІТ / ДЕТАЛЬНИЙ ВІДГУК',
      t2FeedbackPlaceholder: 'Введіть дані журналу тут...',
      t2FeedbackSubmitReport: 'НАДІСЛАТИ ЗВІТ',
      t2FeedbackDisclaimer: 'Надсилаючи, ви погоджуєтесь з протоколами архівування даних.',
      ourMenu: 'Наше меню',
      ourProducts: 'Продукти',
      ourServices: 'Наші послуги',
      ourCatalog: 'Наш каталог',
      viewMenu: 'Переглянути меню',
      viewCatalog: 'Переглянути каталог',
      viewServices: 'Переглянути послуги',
      leaveFeedback: 'Залиште свій відгук',
      ratingLabel: 'Оцінка (1–5)',
      yourName: 'Ваше ім\'я',
      yourMessage: 'Ваше повідомлення',
      sendFeedback: 'Надіслати відгук',
      formErrorGeneric: 'Щось пішло не так. Будь ласка, спробуйте ще раз.',
      mailingListError: 'Щось пішло не так. Будь ласка, спробуйте ще раз.',
      noReviewsYet: 'Ще немає відгуків. Будьте першим, хто залишить відгук нижче.',
      openingHours: 'Години роботи',
      noContactListed: 'Немає вказаних телефонів або посилань.',
      whatsapp: 'WhatsApp',
      mobile: 'Мобільний',
      email: 'Електронна пошта',
      phone: 'Телефон',
      fax: 'Факс',
      other: 'Контакт',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      website: 'Веб-сайт',
      viewingAsOwner: 'Ви переглядаєте це як власник.',
      viewAsCustomer: 'Переглянути як клієнт',
      viewAsOwner: 'Переглянути як власник',
      restaurantNotFoundTitle: 'Ресторан не знайдено',
      backToHome: 'На головну',
      ownerNeedsDataIntro: 'Цей сайт потребує більше даних. Додайте опис, меню або інший контент в адмін-панелі.',
      openAdminPage: 'Відкрити адмін-панель',
      addBannerInAdmin: 'Додати зображення обкладинки в Налаштуваннях',
      bookTable: 'Забронювати стіл',
      bookAppointment: 'Записатися на прийом',
      mapPlaceholder: 'Карта',
      allRightsReserved: 'Усі права захищено.',
      surpriseMe: 'Здивуйте мене',
      surpriseMeAria: 'Здивуйте мене – обрати випадковий пункт меню',
      noMenuItems: 'Немає пунктів меню',
      noProducts: 'Немає продуктів',
      noServices: 'Немає послуг',
      noCatalogItems: 'Немає пунктів у каталозі',
      notAvailable: 'Недоступний',
      notAvailableWithNextTime: 'Зараз недоступно. Наступна доступність: {time}.',
      sizeAndPriceOptions: 'Варіанти розміру та ціни',
      option: 'Варіант',
      comboContents: 'Вміст комбо',
      ownerEditMenuItemInCatalogAria: 'Редагувати позицію в каталозі (відкриє адмін-панель)',
      closeMenu: 'Закрити меню',
      closeCatalogModal: 'Закрити каталог',
      closeServicesModal: 'Закрити послуги',
      ariaViewMenu: 'Переглянути меню',
      ariaViewCatalog: 'Переглянути каталог',
      ariaViewServices: 'Переглянути послуги',
      modalTitleMenu: 'Меню',
      modalTitleCatalog: 'Каталог',
      modalTitleProducts: 'Продукти',
      modalTitleServices: 'Послуги',
      ariaLocation: 'Розташування',
      ariaMap: 'Карта',
      category: 'Категорія',
      mapAriaLabel: 'Карта з розташуванням ресторану',
      openInMaps: 'Відкрити на карті',
      mapIframeTitle: 'Google Карта для цього місця',
      mapOpenExternallyHint:
        "Натисніть нижче, щоб відкрити це місце в додатку карт. Щоб показати карту тут, додайте широту та довготу або посилання Google Maps із шпилькою ({'@'}lat,lng) у налаштуваннях бізнесу.",
      openNow: 'Зараз відкрито',
      closedNow: 'Зачинено',
      businessHoursNotListed: 'Графік не вказано',
      open24_7: 'Цілодобово',
      daySunday: 'Неділя',
      dayMonday: 'Понеділок',
      dayTuesday: 'Вівторок',
      dayWednesday: 'Середа',
      dayThursday: 'Четвер',
      dayFriday: 'П\'ятниця',
      daySaturday: 'Субота',
      t5TableIcon: 'Іконка',
      t5TableImage: 'Зображення',
      t5TableProduct: 'Продукт',
      t5TablePrice: 'Ціна',
      t5TableStatus: 'Статус',
      t5TableAction: 'Дія',
      t5ViewDetails: 'Деталі',
      t5InStock: 'В наявності',
      selectVariantToAdd: 'Виберіть варіант, щоб додати до кошика',
      selectRequiredModifierChoice: 'Оберіть один варіант у кожній обов’язковій групі перед додаванням до кошика.',
      modifiersHeading: 'Додатки (необов’язково)',
      modifiersAria: 'Необов’язкові додатки',
      itemDetailDialogAria: 'Деталі пункту меню',
      itemUntitled: 'Без назви',
      itemNotAvailable: 'Недоступно',
      variantOptionNotAvailable: 'Недоступно',
      variantOptionsAria: 'Варіанти та ціни',
      variantImageAlt: 'Варіант',
      priceFrom: 'Від {price}',
      comboContentsAria: 'Склад комбо',
      modifierUnnamed: 'Додаток',
      modifierNoExtraCost: 'Без додаткової плати',
      quickOrder: {
        addToCart: 'Додати до кошика',
        updateCart: 'Оновити кошик',
        yourOrder: 'Ваше замовлення',
        clearAll: 'Очистити все',
        name: 'Ваше ім\'я',
        namePlaceholder: 'напр. Олександр',
        notes: 'Примітки',
        notesPlaceholder: 'напр. Додати соус, без цибулі...',
        sendViaEmail: 'Надіслати email',
        sendViaWhatsApp: 'Надіслати WhatsApp',
        sendViaViber: 'Надіслати Viber',
        sendViaSMS: 'Надіслати SMS',
        orderSent: 'Замовлення надіслано!',
        orderSentDesc: 'Ваше замовлення надіслано до закладу.',
        nameRequired: 'Ім\'я обов\'язкове.',
        nameMaxLength: 'Ім’я має містити не більше 100 символів.',
        notesMaxLength: 'Примітки мають містити не більше 500 символів.',
        notesRequired: 'Примітки обов\'язкові.',
        cartEmpty: 'Ваш кошик порожній.',
        quantity: 'Кількість',
        remove: 'Видалити',
        itemAdded: 'Додано до кошика!',
        itemUpdated: 'Кошик оновлено!',
        ariaClose: 'Закрити',
        ariaDecrease: 'Зменшити кількість',
        ariaIncrease: 'Збільшити кількість',
        ariaRemoveItem: 'Видалити товар',
        fulfillment: 'На виніс або в закладі',
        fulfillmentPickup: 'На виніс',
        fulfillmentDineIn: 'У закладі',
        dineInReferenceLabel: 'Стіл / позначка (необов’язково)',
        dineInReferencePlaceholder: 'напр. Стіл 5',
        checkout: 'Оформити',
        orderSummary: 'Підсумок замовлення',
        confirmCheckout: 'Підтвердити замовлення',
        checkoutSubmitting: 'Надсилання…',
        checkoutFailed: 'Не вдалося оформити замовлення. Спробуйте ще раз.',
        subtotal: 'Підсумок',
        ariaCloseSummary: 'Закрити підсумок замовлення',
        mobile: 'Мобільний',
        mobilePlaceholder: '+380501234567',
        mobileHint: 'Почніть з + і коду країни (напр. +380…). Надішлемо SMS з кодом.',
        mobileRequired: 'Для самовивозу потрібен номер телефону.',
        mobileInvalid: 'Введіть коректний номер із кодом країни.',
        verificationCode: 'Код підтвердження',
        verificationCodePlaceholder: '6-значний код',
        verificationCodeInvalid: 'Введіть 6-значний код із SMS.',
        sendVerificationCode: 'Надіслати код',
        pickupServiceFee: 'Збір за самовивіз ({percent}%)',
        total: 'Разом',
      },
      cookieConsent: {
        barTitle: 'Файли cookie та подібні технології',
        barBody:
          'Ми використовуємо необхідні функції для роботи сайту. З вашої згоди вмикаємо аналітику першої сторони та сторонні інструменти (наприклад, перевірки безпеки або вхід). Детальніше — у ',
        acceptAll: 'Прийняти все',
        rejectNonEssential: 'Відхилити не обов’язкові',
        customize: 'Налаштувати',
        modalTitle: 'Налаштування cookie',
        necessaryTitle: 'Суворо необхідні',
        necessaryDesc: 'Потрібні для безпеки, входу та основних функцій. Завжди увімкнено.',
        analyticsTitle: 'Аналітика (перша сторона)',
        analyticsDesc: 'Допомагає покращувати публічні сторінки. Дані обробляються на наших серверах.',
        thirdPartyTitle: 'Функції третіх сторін',
        thirdPartyDesc: 'Завантажує інструменти на кшталт Google reCAPTCHA у певних формах або під час входу.',
        save: 'Зберегти налаштування',
        privacyLink: 'політиці конфіденційності',
      },
      // Quick-add item wizard (owner public site)
      quickAddItem: 'Додати позицію',
      quickAddItemAria: 'Додати пункт меню',
      quickAddStepOf: 'Крок {step} з {total}',
      quickAddStep0Title: '<em>Тип</em> позиції',
      quickAddStep1Title: 'Оберіть <em>категорію</em>',
      quickAddStep2Title: '<em>Деталі</em> позиції',
      quickAddStep3Title: 'Перевірка та <em>збереження</em>',
      quickAddWhatKind: 'Який тип позиції?',
      quickAddTypeSimpleSub: 'Одна ціна',
      quickAddTypeVariantsSub: 'Розміри, опції…',
      quickAddTypeCombSub: 'Набір',
      quickAddTypeSimpleHint: 'Один продукт або страва з фіксованою ціною.',
      quickAddTypeVariantsHint: 'Одна позиція з кількома варіантами — кожен зі своєю ціною (напр. Малий / Великий).',
      quickAddTypeComboHint: 'Кілька позицій, що продаються разом (напр. комбо).',
      quickAddNoCats: 'Ще немає категорій',
      quickAddNoCatsSub: 'Створіть першу категорію нижче, щоб згрупувати позиції.',
      quickAddOrCreate: 'або створити нову',
      quickAddFirstCatName: 'Назва першої категорії',
      quickAddNewCat: 'Нова категорія',
      quickAddCatPlaceholder: 'напр. Основні, Напої, Послуги…',
      quickAddCreate: 'Створити',
      quickAddCatWillCreate: 'Буде створено: {name}',
      quickAddItem1: 'позиція',
      quickAddItemN: 'позицій',
      quickAddNameLabel: 'Назва позиції',
      quickAddNamePlaceholder: 'напр. Піца Маргарита',
      quickAddPriceOptional: 'Ціна (необов\'язково)',
      quickAddComboPriceOptional: 'Ціна набору (необов\'язково)',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: 'Опис (необов\'язково)',
      quickAddDescPlaceholder: 'Показується на картці позиції…',
      quickAddVariantsLabel: 'Варіанти',
      quickAddVariantName: 'Назва опції',
      quickAddVariantNamePlaceholder: 'напр. Великий',
      quickAddVariantPrice: 'Ціна',
      quickAddRemoveVariant: 'Видалити варіант',
      quickAddAddVariant: 'Додати варіант',
      quickAddComboItems: 'Позиції в наборі',
      quickAddPickItem: 'Оберіть позицію…',
      quickAddAdd: 'Додати',
      quickAddRemoveComboItem: 'Прибрати позицію',
      quickAddNoItemsForCombo: 'Ще немає позицій — спочатку додайте прості або з варіантами.',
      quickAddReviewItem: 'Позиція',
      quickAddReviewName: 'Назва',
      quickAddReviewType: 'Тип',
      quickAddReviewPrice: 'Ціна',
      quickAddReviewIncludes: 'Містить',
      quickAddReviewCategory: 'Категорія',
      quickAddReviewCategoryLabel: 'Категорія',
      quickAddNoPrice: 'Без ціни',
      quickAddNoCategory: 'Немає',
      quickAddNew: 'нова',
      quickAddPublishNow: 'Опублікувати одразу',
      quickAddPublishNowSub: 'Одразу з’явиться на сайті',
      quickAddAddAnother: 'Додати ще після збереження',
      quickAddAddAnotherSub: 'Залишити цей процес відкритим',
      quickAddSaveItem: 'Зберегти позицію',
      quickAddReview: 'Перевірка',
      quickAddContinue: 'Далі',
      quickAddBack: 'Назад',
      quickAddSaved: 'Позицію збережено.',
      quickAddSavedSub: '{name} додано до {category}.',
      quickAddAnotherItem: '+ Додати ще позицію',
      quickAddDone: 'Готово',
    },
    tour: {
      welcomeTitle: 'Ласкаво просимо!',
      welcomeDescription: 'Давайте швидко ознайомимося з вашою панеллю. Ви побачите, де керувати ресторанами, меню тощо.',
      step1Title: 'Головна область',
      step1Description: 'Тут відображається ваш контент. Використовуйте бічну панель для переходу між панеллю, ресторанами та іншим.',
      step2Title: 'Навігація',
      step2Description: 'Ця бічна панель: панель, ресторани, пункти меню, відгуки та профіль.',
      step2DescriptionSuperadmin: 'Ця бічна панель: панель, користувачі, відгуки власників, ресторани та інше.',
      step3Title: 'Панель керування',
      step3Description: 'Огляд ресторанів, пунктів меню та відгуків.',
      step4Title: 'Ресторани',
      step4Description: 'Керуйте своїми ресторанами. Додавайте заклади, редагуйте деталі та меню.',
      step5Title: 'Пункти меню',
      step5Description: 'Створюйте та редагуйте страви, напої та комбо. Додавайте їх у категорії кожного меню.',
      step6Title: 'Відгуки',
      step6Description: 'Переглядайте та модеруйте відгуки клієнтів з публічних сторінок. Схвалюйте або відхиляйте.',
      step7Title: 'Запит функції',
      step7Description: 'Надсилайте відгуки або пропозиції команді. Тут також видно ваші попередні надсилання.',
      step8Title: 'Профіль і налаштування',
      step8Description: 'Оновіть ім\'я, електронну пошту, пароль та мову застосунку.',
      next: 'Далі',
      prev: 'Назад',
      done: 'Готово',
      close: 'Закрити',
      skip: 'Пропустити тур',
    },
    restaurantsTour: {
      step1Title: 'Список ресторанів',
      step1Description: 'Це ваш список ресторанів. Додавайте заклади, редагуйте деталі та керуйте меню звідси.',
      step2Title: 'Додати ресторан',
      step2Description: 'Натисніть тут, щоб створити новий ресторан та вказати назву, адресу та субдомен.',
      step3Title: 'Ваші ресторани',
      step3Description: 'Ресторани відображаються картками. Натисніть на картку, щоб відкрити ресторан, або створіть перший у порожньому стані.',
      step4Title: 'Картка ресторану',
      step4Description: 'Натисніть на ресторан, щоб редагувати його, керувати меню, встановлювати доступність та отримати публічне посилання або QR-код.',
      tooltip: 'Тур по ресторанах',
    },
    welcome: {
      title: 'Create your first restaurant',
      subtitle: 'Get your menu online, share your address and hours, and let customers find you in minutes.',
      cta: 'Create my first restaurant',
    },
    wizard: {
      stepOf: 'Step {current} of {total}',
      step1Title: 'Name your restaurant',
      nameLabel: 'Restaurant name',
      taglineOptional: 'Tagline (optional)',
      slugPreview: 'Your web address',
      templateLabel: 'Public page template',
      templateHint: 'Choose the layout for your public page.',
      next: 'Next',
      back: 'Back',
      skipForNow: 'Skip for now',
      doLater: "I'll do this later",
      step2Title: 'Location & contact (optional)',
      youreAllSet: "You're all set",
      sublineLive: 'Your restaurant page is live. Share the link or QR so customers can find you.',
      copyAddress: 'Copy address',
      copied: 'Copied!',
      saveQr: 'Save QR code',
      scanHint: 'Customers scan this to open your page',
      goToMyRestaurant: 'Go to my restaurant',
      backToDashboard: 'Back to dashboard',
      firstTimeLink: 'First time? Create your first restaurant',
    },
    startTour: 'Запустити тур по застосунку',
    startTourDescription: 'Запустіть тур ще раз, щоб побачити основні розділи застосунку.',
  },
  ru: {
    app: {
      name: 'RMS',
      login: 'Войти',
      register: 'Создать аккаунт',
      logout: 'Выйти',
      profile: 'Профиль и настройки',
      restaurants: 'Рестораны',
      menu: 'Меню',
      languages: 'Языки и описание',
      addLanguage: 'Добавить язык',
      defaultLanguage: 'Язык по умолчанию',
      languagesHint: 'Добавляйте или удаляйте языки. Язык по умолчанию используется, когда на публичном сайте не выбрана ни одна языковая версия.',
      description: 'Описание',
      save: 'Сохранить',
      cancel: 'Отмена',
      remove: 'Удалить',
      translate: 'Перевести с языка по умолчанию',
      addItem: 'Добавить пункт',
      editItem: 'Редактировать пункт меню',
      createItem: 'Добавить пункт в меню',
      dashboard: 'Панель управления',
      feedbacks: 'Отзывы',
      feedbacksListSubtitle: 'Одобряйте отзывы, чтобы показывать их на публичной странице. Отклоняйте или удаляйте, чтобы скрыть.',
      feedbacksListEmpty: 'Отзывов пока нет. Они появятся здесь, когда клиенты оставят отзывы.',
      feedbacksLoadError: 'Не удалось загрузить отзывы.',
      retry: 'Повторить',
      ariaMainNav: 'Основная навигация',
      ariaAppLanguage: 'Язык приложения',
      ariaOpenMenu: 'Открыть меню',
      ownerFeedbacksSubtitle: 'Запросы функций и отзывы от владельцев. Измените статус после проверки.',
      ownerFeedbacksEmpty: 'Отзывов от владельцев пока нет.',
      markPending: 'Пометить ожидающим',
      markReviewed: 'Пометить проверенным',
      feedbacksUpdateForbidden: 'У вас нет прав обновлять отзывы.',
      feedbackNotFound: 'Отзыв не найден.',
      feedbackUpdated: 'Отзыв обновлён.',
      feedbackApproved: 'Отзыв одобрен.',
      feedbackRejected: 'Отзыв отклонён.',
      failedToUpdate: 'Не удалось обновить.',
      feedbackDeleted: 'Отзыв удалён.',
      failedToDelete: 'Не удалось удалить.',
      deleteFeedbackConfirm: 'Удалить этот отзыв? Это необратимо.',
      users: 'Пользователи',
      superadminUsersSubtitle: 'Управление учётными записями: платный статус и активация.',
      noUsersFound: 'Пользователи не найдены.',
      paid: 'Платный',
      free: 'Бесплатный',
      active: 'Активный',
      deactivated: 'Деактивирован',
      superadmin: 'Суперадмин',
      markPaid: 'Пометить платным',
      removePaid: 'Снять платный',
      activate: 'Активировать',
      deactivate: 'Деактивировать',
      ariaDeactivateUser: 'Деактивировать пользователя',
      ariaActivateUser: 'Активировать пользователя',
      you: '(вы)',
      usersForbidden: 'У вас нет прав просматривать пользователей.',
      superadminRestaurantsSubtitle: 'Список всех ресторанов только для просмотра.',
      noRestaurantsFound: 'Рестораны не найдены.',
      availability: 'Доступность',
      availabilitySubtitle: 'Когда ваш ресторан открыт. Измените время ниже и нажмите Сохранить.',
      deleteRestaurant: 'Удалить ресторан',
      logoAndBanner: 'Логотип и баннер',
      logoBannerDescription: 'Изменить или обновить логотип и баннер ресторана.',
      uploadImagesHint: 'Загрузите изображения. JPEG, PNG, GIF или WebP. Макс. 2 МБ каждое.',
      logo: 'Логотип',
      banner: 'Баннер',
      takePhoto: 'Сделать фото',
      chooseFromGallery: 'Выбрать из галереи',
      changeLogo: 'Изменить логотип',
      changeBanner: 'Изменить баннер',
      done: 'Готово',
      deleteRestaurantConfirm: 'Удалить ресторан?',
      cannotBeUndone: 'Это необратимо.',
      typeToConfirm: 'Введите {slug} для подтверждения',
      ariaUploadingLogo: 'Загрузка логотипа',
      ariaUploadingBanner: 'Загрузка баннера',
      ariaUploadLogo: 'Загрузить логотип',
      ariaUploadBanner: 'Загрузить баннер',
      ariaUploadLogoFromGallery: 'Загрузить логотип из галереи',
      ariaUploadBannerFromGallery: 'Загрузить баннер из галереи',
      ariaTakePhotoLogo: 'Сделать фото для логотипа',
      ariaTakePhotoBanner: 'Сделать фото для баннера',
      settings: 'Настройки',
      ariaManageSections: 'Разделы управления бизнесом',
      contactAndDetails: 'Контакт и детали',
      address: 'Адрес',
      email: 'Email',
      social: 'Соцсети',
      addLanguageEllipsis: 'Добавить язык…',
      addLanguageHint: 'Добавьте хотя бы один язык выше для управления описанием.',
      editDescriptionFor: 'Редактировать описание для:',
      ariaSelectTemplate: 'Выбрать шаблон',
      menusAndCategories: 'Меню и категории',
      createFirstMenuHint: 'Создайте первое меню, чтобы добавить категории.',
      createCategoryFirstHint: 'Сначала создайте категорию, чтобы добавить пункты меню.',
      menuItemsSettingsHint: 'Добавляйте блюда со страницы каждой категории. Переводы используют настройки ресторана.',
      createCategoryAboveHint: 'Создайте категорию выше, чтобы добавить пункты.',
      renameMenu: 'Переименовать меню',
      selectMenu: 'Выбрать меню',
      editCategory: 'Редактировать категорию',
      removeCategory: 'Удалить категорию',
      addMenu: 'Добавить меню',
      addCategory: 'Добавить категорию',
      categoryAvailability: 'Доступность категории',
      removeCategoryImage: 'Удалить изображение категории',
      addMenuOrCategory: 'Добавить меню или категорию',
      editIn: 'Редактировать в',
      changeImage: 'Изменить изображение',
      uploadImage: 'Загрузить изображение',
      translating: 'Перевод…',
      removing: 'Удаление…',
      mainMenuDefault: 'Главное меню',
      menuManagement: 'Управление меню',
      menuManagementSubtitle: 'Меню, категории и пункты. Перетаскивайте для изменения порядка.',
      noMenuItemsYet: 'Пунктов меню пока нет. Используйте кнопку + ниже.',
      categoryNameRequired: 'Название категории обязательно.',
      categoryUpdated: 'Категория обновлена.',
      categoryCreated: 'Категория создана.',
      addMenuItemsToCategory: 'Добавить пункты в эту категорию',
      noMenuItemsOrMatch: 'Нет пунктов или ни один не подходит под поиск.',
      assignTags: 'Назначить теги',
      itemAvailability: 'Доступность пункта',
      dragToReorder: 'Перетащите для изменения порядка',
      setAvailabilityTimes: 'Установить время доступности',
      available: 'Доступен',
      notAvailable: 'Недоступен',
      markNotAvailable: 'Пометить недоступным',
      markAvailableOnMenu: 'Пометить доступным в публичном меню',
      visibleOnMenu: 'Виден в меню',
      hiddenFromMenu: 'Скрыт в меню',
      addMenuItemToCategory: 'Добавить пункт в категорию',
      menuItemTags: 'Теги пунктов меню',
      menuItems: 'Пункты меню',
      createRestaurantFirstForMenus: 'Сначала создайте ресторан для управления меню.',
      addRestaurant: 'Добавить ресторан',
      ratingOutOf5: 'Оценка: {n} из 5',
      anonymous: 'Анонимно',
      approve: 'Одобрить',
      reject: 'Отклонить',
      ariaApproveFeedback: 'Одобрить отзыв',
      ariaRejectFeedback: 'Отклонить отзыв',
      ariaDeleteFeedback: 'Удалить отзыв',
      resetPasswordSuccess: 'Ваш пароль сброшен. Можете войти.',
      resetPasswordEnterNew: 'Введите новый пароль ниже.',
      type: 'Тип',
      labelOptional: 'Метка (необязательно)',
      placeholderUrl: 'https://...',
      placeholderPhone: 'напр. +7 123 456 78 90',
      placeholderContactLabel: 'напр. Бронирование, Кухня',
      showOnPublicPage: 'Показывать на публичной странице',
      hideOnPublicPage: 'Скрыть на публичной странице',
      ariaEditContact: 'Редактировать контакт',
      ariaDeleteContact: 'Удалить контакт',
      noContactsYet: 'Контактов или ссылок пока нет. Добавьте один.',
      deleteContactConfirm: 'Удалить этот контакт?',
      ariaPagination: 'Пагинация',
      ariaOpeningTime: 'Время открытия',
      ariaClosingTime: 'Время закрытия',
      removeTimeSlot: 'Удалить временной слот',
      menuItemLabel: 'Пункт меню',
      addMenuItem: 'Добавить пункт меню',
      ariaRemoveImage: 'Удалить изображение',
      ariaRemoveVariantImage: 'Удалить изображение варианта',
      ariaRemoveEntry: 'Удалить запись',
      ariaRemoveGroup: 'Удалить группу',
      ariaShowHelp: 'Показать справку: что означают кнопки?',
      help: 'Справка',
      ariaButtonLegend: 'Легенда кнопок',
      helpLegendDescription: 'Значение иконок в строках меню и категорий.',
      legendDragHandle: 'Маркер перетаскивания',
      legendDragDesc: 'Перетащите для изменения порядка',
      legendSchedule: 'Расписание',
      legendScheduleDesc: 'Установить часы работы',
      legendAvailability: 'Доступность',
      legendAvailabilityDesc: 'Когда этот пункт доступен',
      legendVisibility: 'Видимость',
      legendVisibilityDesc: 'Показать или скрыть в публичном меню',
      legendTags: 'Теги',
      legendTagsDesc: 'Назначить теги',
      paidFeature: 'Платная функция',
      upgradeToUse: 'Обновитесь, чтобы использовать эту функцию.',
      paidFeatureAiDescription: 'Улучшение изображений меню с ИИ доступно в платном тарифе.',
      maybeLater: 'Возможно позже',
      upgrade: 'Обновить',
      ariaNotifications: 'Уведомления',
      dismiss: 'Закрыть',
      reservationsHowItWorksTitle: 'Как работают бронирование столиков',
      reservationsHowItWorksIntro: 'Гости могут заказать столик на публичной странице вашего ресторана. Вы подтверждаете, отклоняете или редактируете брони здесь.',
      reservationsHowItWorksPhoneRequired: 'Для каждого бронирования нужен действительный номер телефона, чтобы отправлять подтверждения и напоминания по SMS.',
      reservationsHowItWorksSteps: 'Укажите номер для SMS-уведомлений по каждому ресторану, чтобы получать оповещения о новых бронированиях. Подтверждайте или отклоняйте заявки; гости получат SMS. Вы также можете изменить дату, время или число гостей и отменить — гости получат SMS.',
      reservationsHowItWorksTooltip: 'Как работают бронирования',
      close: 'Закрыть',
      loading: 'Загрузка…',
      back: 'Назад',
      delete: 'Удалить',
      pending: 'Ожидает',
      featureRequest: 'Запрос функции',
      ownerFeedbacks: 'Отзывы владельцев',
    },
    errors: {
      forbidden: 'У вас нет разрешения на это.',
      validation: 'Проверьте введённые данные.',
      pageNotFound: 'Страница не найдена',
      pageNotFoundDescription: 'Запрашиваемая страница не существует или была перемещена.',
    },
    landing: {
      hero: {
        badge: 'Бизнес-технологии, просто',
        titleBefore: 'Ваш бизнес, ',
        titleHighlight: 'онлайн за 10 минут.',
        subtitle: 'Выведите свой бизнес в онлайн сегодня — Каталог товаров, Часы работы, Бронирование и многое другое. Начните бесплатно. Технические навыки не нужны.',
        trustedBy: 'Бесплатно навсегда. Кредитная карта не нужна.',
      },
      cta: {
        bookDemo: 'Посмотреть демо: {businessType}',
        bookDemoGeneric: 'Посмотреть демо',
      },
      footer: {
        cookies: 'Настройки cookie',
      },
    },
    public: {
      about: 'О нас',
      t3NavAbout: 'О нас',
      siteCreatedViaQRestoo: 'Сайт создан QRestoo',
      createdByQRestoo: 'Создано на QRestoo',
      sitePoweredByDLR: 'Site powered by DLR Web Solutions',
      contactUs: 'Связаться с нами',
      reviewsAndFeedback: 'Отзывы и комментарии',
      t1NavMenu: 'Меню',
      reviewsNav: 'Отзывы',
      contactNav: 'Контакты',
      t2MenuNav: 'Меню',
      t2ViewServices: 'Услуги',
      t2ReviewsNav: 'Отзывы',
      t2ContactNav: 'Контакты',
      navMenu: 'Меню',
      navServices: 'Услуги',
      navProducts: 'Товары',
      ownerNavBookingUnavailable: 'Бронирование. Требуется обновление аккаунта.',
      ownerNavReviewsUnavailable: 'Отзывы. Требуется обновление аккаунта.',
      ownerNavLocationUnavailable: 'Местоположение. Добавьте адрес или карту в настройках.',
      ownerNavAboutUnavailable: 'Наша история. Добавьте описание в настройках.',
      ownerNavHoursUnavailable: 'Часы работы. Добавьте в настройках.',
      t3FooterTaglineRestaurants: 'Создаём незабываемые гастрономические впечатления. Совершенство в каждой тарелке.',
      t3FooterTaglineEcommerce: 'Качественные товары, тщательно отобранные. Совершенство в каждом заказе.',
      t3FooterTaglineServices: 'Исключительный сервис. Совершенство в каждой детали.',
      t2CommunicationsLink: 'Связь',
      noLogoSetOwnerMessage: 'Логотип не задан. Добавьте в настройках бизнеса.',
      ownerEditLogoAria: 'Изменить логотип и баннер',
      heroEstYear: 'Осн. {year}',
      noTaglineOrDescriptionSetOwnerMessage: 'Нет слогана или описания. Добавьте в настройках бизнеса.',
      noDescriptionSetOwnerMessage: 'Нет описания. Добавьте в настройках бизнеса.',
      noMapSetOwnerMessage: 'Нет карты. Добавьте адрес или ссылку на карту в настройках.',
      noBookingAvailableOwnerMessage: 'Бронирование недоступно. Включите в настройках бизнеса.',
      noMailingListAvailableOwnerMessage: 'Список рассылки недоступен. Включите в настройках бизнеса.',
      noFeedbackAvailableOwnerMessage: 'Отзывы и комментарии недоступны. Включите в настройках бизнеса.',
      t2NewsletterTitle: 'ПОДПИСКА НА РАССЫЛКУ',
      t2FeedbackPerformanceLogging: 'Журнал производительности',
      t2FeedbackOperationalLog: 'ОПЕРАЦИОННЫЙ ЖУРНАЛ',
      t2FeedbackTransmitCopy: 'Отправьте отчёт об опыте для контроля качества и оптимизации.',
      t2FeedbackSessionMeta: 'Метаданные сессии:',
      t2FeedbackTerminalId: 'ID терминала: TGT-LOG-992',
      t2FeedbackProtocol: 'Протокол: feedback_v2.0.sh',
      t2FeedbackRatingLabel: 'ОЦЕНКА РАБОТЫ',
      t2FeedbackRatingHint: 'Выберите метрики оценки (1-5)',
      t2FeedbackSitrepLabel: 'ОТЧЁТ / ПОДРОБНЫЙ ОТЗЫВ',
      t2FeedbackPlaceholder: 'Введите данные журнала здесь...',
      t2FeedbackSubmitReport: 'ОТПРАВИТЬ ОТЧЁТ',
      t2FeedbackDisclaimer: 'Отправляя, вы соглашаетесь с протоколами архивирования данных.',
      ourMenu: 'Наше меню',
      ourProducts: 'Продукция',
      ourServices: 'Наши услуги',
      ourCatalog: 'Наш каталог',
      viewMenu: 'Смотреть меню',
      viewCatalog: 'Смотреть каталог',
      viewServices: 'Смотреть услуги',
      leaveFeedback: 'Оставьте отзыв',
      ratingLabel: 'Оценка (1–5)',
      yourName: 'Ваше имя',
      yourMessage: 'Ваше сообщение',
      sendFeedback: 'Отправить отзыв',
      formErrorGeneric: 'Что-то пошло не так. Пожалуйста, попробуйте снова.',
      mailingListError: 'Что-то пошло не так. Пожалуйста, попробуйте снова.',
      noReviewsYet: 'Пока нет отзывов. Оставьте первый отзыв ниже.',
      openingHours: 'Часы работы',
      noContactListed: 'Нет указанных телефонов или ссылок.',
      whatsapp: 'WhatsApp',
      mobile: 'Мобильный',
      email: 'Email',
      phone: 'Телефон',
      fax: 'Факс',
      other: 'Контакт',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      website: 'Веб-сайт',
      viewingAsOwner: 'Вы просматриваете это как владелец.',
      viewAsCustomer: 'Просмотреть как клиент',
      viewAsOwner: 'Просмотреть как владелец',
      restaurantNotFoundTitle: 'Ресторан не найден',
      backToHome: 'На главную',
      ownerNeedsDataIntro: 'Этому сайту нужно больше контента. Добавьте описание, меню или другое в админ-панели.',
      openAdminPage: 'Открыть админ-панель',
      addBannerInAdmin: 'Добавить обложку в Настройках',
      bookTable: 'Забронировать стол',
      bookAppointment: 'Записаться на прием',
      mapPlaceholder: 'Карта',
      allRightsReserved: 'Все права защищены.',
      surpriseMe: 'Удивите меня',
      surpriseMeAria: 'Удивите меня – выбрать случайный пункт меню',
      noMenuItems: 'Нет пунктов меню',
      noProducts: 'Нет продукции',
      noServices: 'Нет услуг',
      noCatalogItems: 'Нет пунктов в каталоге',
      notAvailable: 'Недоступен',
      notAvailableWithNextTime: 'Сейчас недоступно. Следующее время: {time}.',
      sizeAndPriceOptions: 'Варианты размера и цены',
      option: 'Вариант',
      comboContents: 'Содержимое комбо',
      ownerEditMenuItemInCatalogAria: 'Редактировать позицию в каталоге (откроется админка)',
      closeMenu: 'Закрыть меню',
      closeCatalogModal: 'Закрыть каталог',
      closeServicesModal: 'Закрыть услуги',
      ariaViewMenu: 'Смотреть меню',
      ariaViewCatalog: 'Смотреть каталог',
      ariaViewServices: 'Смотреть услуги',
      modalTitleMenu: 'Меню',
      modalTitleCatalog: 'Каталог',
      modalTitleProducts: 'Продукция',
      modalTitleServices: 'Услуги',
      ariaLocation: 'Расположение',
      ariaMap: 'Карта',
      category: 'Категория',
      mapAriaLabel: 'Карта с расположением ресторана',
      openInMaps: 'Открыть на карте',
      mapIframeTitle: 'Карта Google для этого места',
      mapOpenExternallyHint:
        "Нажмите ниже, чтобы открыть это место в приложении карт. Чтобы показать карту здесь, укажите широту и долготу или ссылку Google Maps с меткой ({'@'}lat,lng) в настройках бизнеса.",
      openNow: 'Сейчас открыто',
      closedNow: 'Закрыто',
      businessHoursNotListed: 'Часы работы не указаны',
      open24_7: 'Круглосуточно',
      daySunday: 'Воскресенье',
      dayMonday: 'Понедельник',
      dayTuesday: 'Вторник',
      dayWednesday: 'Среда',
      dayThursday: 'Четвер',
      dayFriday: 'Пятница',
      daySaturday: 'Суббота',
      t5TableIcon: 'Иконка',
      t5TableImage: 'Изображение',
      t5TableProduct: 'Товар',
      t5TablePrice: 'Цена',
      t5TableStatus: 'Статус',
      t5TableAction: 'Действие',
      t5ViewDetails: 'Подробнее',
      t5InStock: 'В наличии',
      selectVariantToAdd: 'Выберите вариант для добавления в корзину',
      selectRequiredModifierChoice: 'Выберите по одному варианту в каждой обязательной группе перед добавлением в корзину.',
      modifiersHeading: 'Дополнения (по желанию)',
      modifiersAria: 'Дополнительные опции',
      itemDetailDialogAria: 'Сведения о пункте меню',
      itemUntitled: 'Без названия',
      itemNotAvailable: 'Недоступно',
      variantOptionNotAvailable: 'Недоступно',
      variantOptionsAria: 'Варианты и цены',
      variantImageAlt: 'Вариант',
      priceFrom: 'От {price}',
      comboContentsAria: 'Состав комбо',
      modifierUnnamed: 'Дополнение',
      modifierNoExtraCost: 'Без доплаты',
      quickOrder: {
        addToCart: 'В корзину',
        updateCart: 'Обновить корзину',
        yourOrder: 'Ваш заказ',
        clearAll: 'Очистить всё',
        name: 'Ваше имя',
        namePlaceholder: 'напр. Иван',
        notes: 'Примечания',
        notesPlaceholder: 'напр. Дополнительный соус, без лука...',
        sendViaEmail: 'Отправить по email',
        sendViaWhatsApp: 'Отправить в WhatsApp',
        sendViaViber: 'Отправить в Viber',
        sendViaSMS: 'Отправить SMS',
        orderSent: 'Заказ отправлен!',
        orderSentDesc: 'Ваш заказ отправлен в заведение.',
        nameRequired: 'Имя обязательно.',
        nameMaxLength: 'Имя не длиннее 100 символов.',
        notesMaxLength: 'Примечания не длиннее 500 символов.',
        notesRequired: 'Примечания обязательны.',
        cartEmpty: 'Ваша корзина пуста.',
        quantity: 'Количество',
        remove: 'Удалить',
        itemAdded: 'Добавлено в корзину!',
        itemUpdated: 'Корзина обновлена!',
        ariaClose: 'Закрыть',
        ariaDecrease: 'Уменьшить количество',
        ariaIncrease: 'Увеличить количество',
        ariaRemoveItem: 'Удалить товар',
        fulfillment: 'С собой или в зале',
        fulfillmentPickup: 'С собой',
        fulfillmentDineIn: 'В зале',
        dineInReferenceLabel: 'Стол / метка (необязательно)',
        dineInReferencePlaceholder: 'напр. Стол 5',
        checkout: 'Оформить',
        orderSummary: 'Сводка заказа',
        confirmCheckout: 'Подтвердить заказ',
        checkoutSubmitting: 'Отправка…',
        checkoutFailed: 'Не удалось оформить заказ. Попробуйте снова.',
        subtotal: 'Промежуточный итог',
        ariaCloseSummary: 'Закрыть сводку заказа',
        mobile: 'Телефон',
        mobilePlaceholder: '+79001234567',
        mobileHint: 'Начните с + и кода страны (напр. +7…). Отправим код в SMS.',
        mobileRequired: 'Для самовывоза нужен номер телефона.',
        mobileInvalid: 'Введите корректный номер с кодом страны.',
        verificationCode: 'Код подтверждения',
        verificationCodePlaceholder: '6 цифр',
        verificationCodeInvalid: 'Введите 6-значный код из SMS.',
        sendVerificationCode: 'Отправить код',
        pickupServiceFee: 'Сбор за самовывоз ({percent}%)',
        total: 'Итого',
      },
      cookieConsent: {
        barTitle: 'Файлы cookie и аналогичные технологии',
        barBody:
          'Мы используем необходимые функции для работы сайта. С вашего согласия подключаем аналитику первой стороны и сторонние инструменты (например, проверки безопасности или вход). Подробности в ',
        acceptAll: 'Принять все',
        rejectNonEssential: 'Отклонить необязательные',
        customize: 'Настроить',
        modalTitle: 'Настройки cookie',
        necessaryTitle: 'Строго необходимые',
        necessaryDesc: 'Нужны для безопасности, входа и основных функций. Всегда включены.',
        analyticsTitle: 'Аналитика (первая сторона)',
        analyticsDesc: 'Помогает улучшать публичные страницы. Данные обрабатываются на наших серверах.',
        thirdPartyTitle: 'Функции третьих сторон',
        thirdPartyDesc: 'Загружает инструменты вроде Google reCAPTCHA в некоторых формах или при входе.',
        save: 'Сохранить настройки',
        privacyLink: 'политике конфиденциальности',
      },
      // Quick-add item wizard (owner public site)
      quickAddItem: 'Добавить позицию',
      quickAddItemAria: 'Добавить пункт меню',
      quickAddStepOf: 'Шаг {step} из {total}',
      quickAddStep0Title: '<em>Тип</em> позиции',
      quickAddStep1Title: 'Выберите <em>категорию</em>',
      quickAddStep2Title: '<em>Детали</em> позиции',
      quickAddStep3Title: 'Проверка и <em>сохранение</em>',
      quickAddWhatKind: 'Какой тип позиции?',
      quickAddTypeSimpleSub: 'Одна цена',
      quickAddTypeVariantsSub: 'Размеры, опции…',
      quickAddTypeCombSub: 'Набор',
      quickAddTypeSimpleHint: 'Один продукт или блюдо с фиксированной ценой.',
      quickAddTypeVariantsHint: 'Одна позиция с несколькими вариантами — у каждого своя цена (напр. Маленький / Большой).',
      quickAddTypeComboHint: 'Несколько позиций, продающихся вместе (напр. комбо).',
      quickAddNoCats: 'Пока нет категорий',
      quickAddNoCatsSub: 'Создайте первую категорию ниже, чтобы сгруппировать позиции.',
      quickAddOrCreate: 'или создать новую',
      quickAddFirstCatName: 'Название первой категории',
      quickAddNewCat: 'Новая категория',
      quickAddCatPlaceholder: 'напр. Основные, Напитки, Услуги…',
      quickAddCreate: 'Создать',
      quickAddCatWillCreate: 'Будет создано: {name}',
      quickAddItem1: 'позиция',
      quickAddItemN: 'позиций',
      quickAddNameLabel: 'Название позиции',
      quickAddNamePlaceholder: 'напр. Пицца Маргарита',
      quickAddPriceOptional: 'Цена (необязательно)',
      quickAddComboPriceOptional: 'Цена набора (необязательно)',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: 'Описание (необязательно)',
      quickAddDescPlaceholder: 'Показывается на карточке позиции…',
      quickAddVariantsLabel: 'Варианты',
      quickAddVariantName: 'Название опции',
      quickAddVariantNamePlaceholder: 'напр. Большой',
      quickAddVariantPrice: 'Цена',
      quickAddRemoveVariant: 'Удалить вариант',
      quickAddAddVariant: 'Добавить вариант',
      quickAddComboItems: 'Позиции в наборе',
      quickAddPickItem: 'Выберите позицию…',
      quickAddAdd: 'Добавить',
      quickAddRemoveComboItem: 'Убрать позицию',
      quickAddNoItemsForCombo: 'Пока нет позиций — сначала добавьте простые или с вариантами.',
      quickAddReviewItem: 'Позиция',
      quickAddReviewName: 'Название',
      quickAddReviewType: 'Тип',
      quickAddReviewPrice: 'Цена',
      quickAddReviewIncludes: 'Состав',
      quickAddReviewCategory: 'Категория',
      quickAddReviewCategoryLabel: 'Категория',
      quickAddNoPrice: 'Без цены',
      quickAddNoCategory: 'Нет',
      quickAddNew: 'новая',
      quickAddPublishNow: 'Опубликовать сразу',
      quickAddPublishNowSub: 'Сразу появится на сайте',
      quickAddAddAnother: 'Добавить ещё после сохранения',
      quickAddAddAnotherSub: 'Оставить этот процесс открытым',
      quickAddSaveItem: 'Сохранить позицию',
      quickAddReview: 'Проверка',
      quickAddContinue: 'Далее',
      quickAddBack: 'Назад',
      quickAddSaved: 'Позиция сохранена.',
      quickAddSavedSub: '{name} добавлено в {category}.',
      quickAddAnotherItem: '+ Добавить ещё позицию',
      quickAddDone: 'Готово',
    },
    tour: {
      welcomeTitle: 'Добро пожаловать!',
      welcomeDescription: 'Краткий обзор панели: где управлять ресторанами, меню и другим.',
      step1Title: 'Основная область',
      step1Description: 'Здесь отображается контент. Боковая панель: панель, рестораны и другие разделы.',
      step2Title: 'Навигация',
      step2Description: 'Боковая панель: панель, рестораны, пункты меню, отзывы и профиль.',
      step2DescriptionSuperadmin: 'Боковая панель: панель, пользователи, отзывы владельцев, рестораны и другое.',
      step3Title: 'Панель управления',
      step3Description: 'Обзор ресторанов, пунктов меню и отзывов.',
      step4Title: 'Рестораны',
      step4Description: 'Управляйте ресторанами. Добавляйте заведения, редактируйте детали и меню.',
      step5Title: 'Пункты меню',
      step5Description: 'Создавайте и редактируйте блюда, напитки и комбо. Добавляйте в категории каждого меню.',
      step6Title: 'Отзывы',
      step6Description: 'Просматривайте и модерируйте отзывы клиентов с публичных страниц. Одобряйте или отклоняйте.',
      step7Title: 'Запрос функции',
      step7Description: 'Отправляйте отзывы или запросы функций команде. Здесь же видны ваши прошлые отправки.',
      step8Title: 'Профиль и настройки',
      step8Description: 'Обновите имя, email, пароль и язык приложения.',
      next: 'Далее',
      prev: 'Назад',
      done: 'Готово',
      close: 'Закрыть',
      skip: 'Пропустить тур',
    },
    restaurantsTour: {
      step1Title: 'Список ресторанов',
      step1Description: 'Это ваш список ресторанов. Добавляйте заведения, редактируйте детали и управляйте меню отсюда.',
      step2Title: 'Добавить ресторан',
      step2Description: 'Нажмите здесь, чтобы создать новый ресторан и указать название, адрес и поддомен.',
      step3Title: 'Ваши рестораны',
      step3Description: 'Рестораны отображаются карточками. Нажмите на карточку, чтобы открыть ресторан, или создайте первый в пустом состоянии.',
      step4Title: 'Карточка ресторана',
      step4Description: 'Нажмите на ресторан, чтобы редактировать его, управлять меню, задавать доступность и получить публичную ссылку или QR-код.',
      tooltip: 'Тур по ресторанам',
    },
    welcome: {
      title: 'Create your first restaurant',
      subtitle: 'Get your menu online, share your address and hours, and let customers find you in minutes.',
      cta: 'Create my first restaurant',
    },
    wizard: {
      stepOf: 'Step {current} of {total}',
      step1Title: 'Name your restaurant',
      nameLabel: 'Restaurant name',
      taglineOptional: 'Tagline (optional)',
      slugPreview: 'Your web address',
      templateLabel: 'Public page template',
      templateHint: 'Choose the layout for your public page.',
      next: 'Next',
      back: 'Back',
      skipForNow: 'Skip for now',
      doLater: "I'll do this later",
      step2Title: 'Location & contact (optional)',
      youreAllSet: "You're all set",
      sublineLive: 'Your restaurant page is live. Share the link or QR so customers can find you.',
      copyAddress: 'Copy address',
      copied: 'Copied!',
      saveQr: 'Save QR code',
      scanHint: 'Customers scan this to open your page',
      goToMyRestaurant: 'Go to my restaurant',
      backToDashboard: 'Back to dashboard',
      firstTimeLink: 'First time? Create your first restaurant',
    },
    startTour: 'Запустить тур по приложению',
    startTourDescription: 'Запустите тур снова, чтобы увидеть основные разделы приложения.',
  },
  ja: {
    app: {
      name: 'RMS',
      login: 'ログイン',
      register: 'アカウント作成',
      logout: 'ログアウト',
      profile: 'プロフィールと設定',
      restaurants: 'レストラン',
      menu: 'メニュー',
      languages: '言語と説明',
      addLanguage: '言語を追加',
      defaultLanguage: 'デフォルト言語',
      languagesHint: '言語の追加・削除。公開サイトで言語が選択されていない場合はデフォルト言語が使われます。',
      description: '説明',
      save: '保存',
      cancel: 'キャンセル',
      remove: '削除',
      translate: 'デフォルトから翻訳',
      addItem: '項目を追加',
      editItem: 'メニュー項目を編集',
      createItem: 'メニュー項目を追加',
      dashboard: 'ダッシュボード',
      feedbacks: 'フィードバック',
      feedbacksListSubtitle: '公開ページに表示するにはフィードバックを承認してください。非表示にするには拒否または削除。',
      feedbacksListEmpty: 'まだフィードバックはありません。お客様がレビューを送信するとここに表示されます。',
      feedbacksLoadError: 'フィードバックの読み込みに失敗しました。',
      retry: '再試行',
      ariaMainNav: 'メインナビゲーション',
      ariaAppLanguage: 'アプリの言語',
      ariaOpenMenu: 'メニューを開く',
      ownerFeedbacksSubtitle: 'オーナーからの機能リクエストとフィードバック。確認後にステータスを切り替え。',
      ownerFeedbacksEmpty: 'オーナーフィードバックはまだありません。',
      markPending: '保留にする',
      markReviewed: '確認済みにする',
      feedbacksUpdateForbidden: 'フィードバックを更新する権限がありません。',
      feedbackNotFound: 'フィードバックが見つかりません。',
      feedbackUpdated: 'フィードバックを更新しました。',
      feedbackApproved: 'フィードバックを承認しました。',
      feedbackRejected: 'フィードバックを拒否しました。',
      failedToUpdate: '更新に失敗しました。',
      feedbackDeleted: 'フィードバックを削除しました。',
      failedToDelete: '削除に失敗しました。',
      deleteFeedbackConfirm: 'このフィードバックを削除しますか？元に戻せません。',
      users: 'ユーザー',
      superadminUsersSubtitle: 'ユーザーアカウントの管理：有料ステータスと有効/無効。',
      noUsersFound: 'ユーザーが見つかりません。',
      paid: '有料',
      free: '無料',
      active: '有効',
      deactivated: '無効',
      superadmin: 'スーパー管理者',
      markPaid: '有料にする',
      removePaid: '有料を解除',
      activate: '有効にする',
      deactivate: '無効にする',
      ariaDeactivateUser: 'ユーザーを無効にする',
      ariaActivateUser: 'ユーザーを有効にする',
      you: '（あなた）',
      usersForbidden: 'ユーザーを表示する権限がありません。',
      superadminRestaurantsSubtitle: 'システム内の全レストランの閲覧専用リスト。',
      noRestaurantsFound: 'レストランが見つかりません。',
      availability: '利用可能時間',
      availabilitySubtitle: 'レストランの営業時間。下の時間を変更して保存をタップ。',
      deleteRestaurant: 'レストランを削除',
      logoAndBanner: 'ロゴとバナー',
      logoBannerDescription: 'レストランのロゴとバナー画像を変更または更新。',
      uploadImagesHint: '画像をアップロード。JPEG、PNG、GIF、WebP。各2MBまで。',
      logo: 'ロゴ',
      banner: 'バナー',
      takePhoto: '写真を撮る',
      chooseFromGallery: 'ギャラリーから選択',
      changeLogo: 'ロゴを変更',
      changeBanner: 'バナーを変更',
      done: '完了',
      deleteRestaurantConfirm: 'レストランを削除しますか？',
      cannotBeUndone: '元に戻せません。',
      typeToConfirm: '確認するには {slug} と入力',
      ariaUploadingLogo: 'ロゴをアップロード中',
      ariaUploadingBanner: 'バナーをアップロード中',
      ariaUploadLogo: 'ロゴをアップロード',
      ariaUploadBanner: 'バナーをアップロード',
      ariaUploadLogoFromGallery: 'ギャラリーからロゴをアップロード',
      ariaUploadBannerFromGallery: 'ギャラリーからバナーをアップロード',
      ariaTakePhotoLogo: 'ロゴ用に写真を撮る',
      ariaTakePhotoBanner: 'バナー用に写真を撮る',
      settings: '設定',
      ariaManageSections: 'ビジネス管理セクション',
      contactAndDetails: '連絡先と詳細',
      address: '住所',
      email: 'メール',
      social: 'ソーシャル',
      addLanguageEllipsis: '言語を追加…',
      addLanguageHint: '説明を管理するには上で少なくとも1つの言語を追加してください。',
      editDescriptionFor: '説明を編集：',
      ariaSelectTemplate: 'テンプレートを選択',
      menusAndCategories: 'メニューとカテゴリ',
      createFirstMenuHint: '最初のメニューを作成してカテゴリを追加。',
      createCategoryFirstHint: 'まずカテゴリを作成してメニュー項目を追加。',
      menuItemsSettingsHint: '各カテゴリのページから料理を追加。翻訳はレストランの設定を使用。',
      createCategoryAboveHint: '上のカテゴリを作成してメニュー項目を追加。',
      renameMenu: 'メニュー名を変更',
      selectMenu: 'メニューを選択',
      editCategory: 'カテゴリを編集',
      removeCategory: 'カテゴリを削除',
      addMenu: 'メニューを追加',
      addCategory: 'カテゴリを追加',
      categoryAvailability: 'カテゴリの利用可能時間',
      removeCategoryImage: 'カテゴリ画像を削除',
      addMenuOrCategory: 'メニューまたはカテゴリを追加',
      editIn: '編集先',
      changeImage: '画像を変更',
      uploadImage: '画像をアップロード',
      translating: '翻訳中…',
      removing: '削除中…',
      mainMenuDefault: 'メインメニュー',
      menuManagement: 'メニュー管理',
      menuManagementSubtitle: 'メニュー、カテゴリ、項目。ドラッグで並べ替え。',
      noMenuItemsYet: 'メニュー項目はまだありません。下の+ボタンを使用。',
      categoryNameRequired: 'カテゴリ名は必須です。',
      categoryUpdated: 'カテゴリを更新しました。',
      categoryCreated: 'カテゴリを作成しました。',
      addMenuItemsToCategory: 'このカテゴリにメニュー項目を追加',
      noMenuItemsOrMatch: 'メニュー項目がないか、検索に一致しません。',
      assignTags: 'タグを割り当て',
      itemAvailability: '項目の利用可能時間',
      dragToReorder: 'ドラッグして並べ替え',
      setAvailabilityTimes: '利用可能時間を設定',
      available: '利用可能',
      notAvailable: '利用不可',
      markNotAvailable: '利用不可にする',
      markAvailableOnMenu: '公開メニューで利用可能にする',
      visibleOnMenu: 'メニューに表示',
      hiddenFromMenu: 'メニューで非表示',
      addMenuItemToCategory: 'カテゴリにメニュー項目を追加',
      menuItemTags: 'メニュー項目タグ',
      menuItems: 'メニュー項目',
      createRestaurantFirstForMenus: 'まずレストランを作成してメニューを管理。',
      addRestaurant: 'レストランを追加',
      ratingOutOf5: '評価：5点中{n}点',
      anonymous: '匿名',
      approve: '承認',
      reject: '拒否',
      ariaApproveFeedback: 'フィードバックを承認',
      ariaRejectFeedback: 'フィードバックを拒否',
      ariaDeleteFeedback: 'フィードバックを削除',
      resetPasswordSuccess: 'パスワードをリセットしました。サインインできます。',
      resetPasswordEnterNew: '新しいパスワードを下に入力してください。',
      type: 'タイプ',
      labelOptional: 'ラベル（任意）',
      placeholderUrl: 'https://...',
      placeholderPhone: '例：+81 3-1234-5678',
      placeholderContactLabel: '例：予約、キッチン',
      showOnPublicPage: '公開ページに表示',
      hideOnPublicPage: '公開ページで非表示',
      ariaEditContact: '連絡先を編集',
      ariaDeleteContact: '連絡先を削除',
      noContactsYet: '連絡先やリンクはまだありません。1つ追加してください。',
      deleteContactConfirm: 'この連絡先を削除しますか？',
      ariaPagination: 'ページネーション',
      ariaOpeningTime: '開店時間',
      ariaClosingTime: '閉店時間',
      removeTimeSlot: '時間枠を削除',
      menuItemLabel: 'メニュー項目',
      addMenuItem: 'メニュー項目を追加',
      ariaRemoveImage: '画像を削除',
      ariaRemoveVariantImage: 'バリアント画像を削除',
      ariaRemoveEntry: 'エントリを削除',
      ariaRemoveGroup: 'グループを削除',
      ariaShowHelp: 'ヘルプを表示：ボタンの意味',
      help: 'ヘルプ',
      ariaButtonLegend: 'ボタン凡例',
      helpLegendDescription: 'メニュー・カテゴリ行のアイコンの意味。',
      legendDragHandle: 'ドラッグハンドル',
      legendDragDesc: 'ドラッグして並べ替え',
      legendSchedule: 'スケジュール',
      legendScheduleDesc: '営業時間を設定',
      legendAvailability: '利用可能時間',
      legendAvailabilityDesc: 'この項目が利用可能な時間',
      legendVisibility: '表示設定',
      legendVisibilityDesc: '公開メニューで表示/非表示',
      legendTags: 'タグ',
      legendTagsDesc: 'タグを割り当て',
      paidFeature: '有料機能',
      upgradeToUse: 'この機能を使うにはアップグレードしてください。',
      paidFeatureAiDescription: 'AIでメニュー画像を美しくする機能は有料プランで利用可能です。',
      maybeLater: '後で',
      upgrade: 'アップグレード',
      ariaNotifications: '通知',
      dismiss: '閉じる',
      reservationsHowItWorksTitle: 'テーブル予約の仕組み',
      reservationsHowItWorksIntro: 'お客様はレストランの公開ページからテーブルをリクエストできます。ここで予約の承認・拒否・編集ができます。',
      reservationsHowItWorksPhoneRequired: '確認とリマインドをSMSで送るため、各予約には有効な電話番号が必要です。',
      reservationsHowItWorksSteps: '各レストランのSMS通知番号を設定すると、新規予約の通知を受け取れます。リクエストを承認または拒否すると、お客様にSMSが送信されます。日時や人数の変更・キャンセルも可能で、お客様にSMSで通知されます。',
      reservationsHowItWorksTooltip: '予約の仕組み',
      close: '閉じる',
      loading: '読み込み中…',
      back: '戻る',
      delete: '削除',
      pending: '保留',
      featureRequest: '機能リクエスト',
      ownerFeedbacks: 'オーナーフィードバック',
    },
    errors: {
      forbidden: 'この操作を行う権限がありません。',
      validation: '入力内容を確認してください。',
      pageNotFound: 'ページが見つかりません',
      pageNotFoundDescription: 'お探しのページは存在しないか、移動した可能性があります。',
    },
    landing: {
      hero: {
        badge: 'ビジネステクノロジー、シンプルに',
        titleBefore: 'あなたのビジネスを、',
        titleHighlight: '10分でオンラインに。',
        subtitle: '今すぐビジネスをオンラインに — 商品カタログ、営業時間、予約など。無料で始められます。技術的な知識は不要です。',
        trustedBy: 'ずっと無料。クレジットカード不要。',
      },
      cta: {
        bookDemo: '{businessType}のデモを見る',
        bookDemoGeneric: 'デモを見る',
      },
      footer: {
        cookies: 'Cookie 設定',
      },
    },
    public: {
      about: '私たちについて',
      t3NavAbout: '私たちについて',
      siteCreatedViaQRestoo: 'このサイトは QRestoo で作成されました',
      createdByQRestoo: 'QRestoo で作成されました',
      sitePoweredByDLR: 'Site powered by DLR Web Solutions',
      contactUs: 'お問い合わせ',
      reviewsAndFeedback: 'レビューとフィードバック',
      t1NavMenu: 'メニュー',
      reviewsNav: 'レビュー',
      contactNav: 'お問い合わせ',
      t2MenuNav: 'メニュー',
      t2ViewServices: 'サービス',
      t2ReviewsNav: 'レビュー',
      t2ContactNav: 'お問い合わせ',
      navMenu: 'メニュー',
      navServices: 'サービス',
      navProducts: '商品',
      ownerNavBookingUnavailable: '予約。アカウントのアップグレードが必要です。',
      ownerNavReviewsUnavailable: 'レビュー。アカウントのアップグレードが必要です。',
      ownerNavLocationUnavailable: '場所。設定で住所または地図を追加してください。',
      ownerNavAboutUnavailable: '私たちのストーリー。設定で説明を追加してください。',
      ownerNavHoursUnavailable: '営業時間。設定で追加してください。',
      t3FooterTaglineRestaurants: '忘れられない食事の体験を。一皿一皿にこだわりを。',
      t3FooterTaglineEcommerce: '厳選された品質の商品。すべての注文にこだわりを。',
      t3FooterTaglineServices: '最高のサービスを。細部までこだわりを。',
      t2CommunicationsLink: 'コミュニケーションリンク',
      noLogoSetOwnerMessage: 'ロゴが設定されていません。ビジネス設定で追加してください。',
      ownerEditLogoAria: 'ロゴとバナーを編集',
      heroEstYear: '{year}年創業',
      noTaglineOrDescriptionSetOwnerMessage: 'キャッチコピーまたは説明がありません。ビジネス設定で追加してください。',
      noDescriptionSetOwnerMessage: '説明がありません。ビジネス設定で追加してください。',
      noMapSetOwnerMessage: '地図がありません。ビジネス設定で住所または地図リンクを追加してください。',
      noBookingAvailableOwnerMessage: '予約は利用できません。ビジネス設定で有効にしてください。',
      noMailingListAvailableOwnerMessage: 'メーリングリストは利用できません。ビジネス設定で有効にしてください。',
      noFeedbackAvailableOwnerMessage: 'レビューとフィードバックは利用できません。ビジネス設定で有効にしてください。',
      t2NewsletterTitle: 'ニュースレター登録',
      t2FeedbackPerformanceLogging: 'パフォーマンス記録',
      t2FeedbackOperationalLog: '運用ログ',
      t2FeedbackTransmitCopy: '品質保証と最適化のため、体験レポートを送信してください。',
      t2FeedbackSessionMeta: 'セッションメタデータ：',
      t2FeedbackTerminalId: '端末ID: TGT-LOG-992',
      t2FeedbackProtocol: 'プロトコル: feedback_v2.0.sh',
      t2FeedbackRatingLabel: 'ユニットパフォーマンス評価',
      t2FeedbackRatingHint: '評価指標を選択 (1-5)',
      t2FeedbackSitrepLabel: '状況報告 / 詳細フィードバック',
      t2FeedbackPlaceholder: 'ログデータをここに入力...',
      t2FeedbackSubmitReport: 'レポートを送信',
      t2FeedbackDisclaimer: '送信すると、データアーカイブプロトコルに同意したことになります。',
      ourMenu: 'メニュー',
      ourProducts: '商品',
      ourServices: '私たちのサービス',
      ourCatalog: 'カタログ',
      viewMenu: 'メニューを見る',
      viewCatalog: 'カタログを見る',
      viewServices: 'サービスを見る',
      leaveFeedback: 'フィードバックを残す',
      ratingLabel: '評価（1–5）',
      yourName: 'お名前',
      yourMessage: 'メッセージ',
      sendFeedback: '送信',
      formErrorGeneric: '問題が発生しました。もう一度お試しください。',
      mailingListError: '問題が発生しました。もう一度お試しください。',
      noReviewsYet: 'まだレビューはありません。以下から最初のフィードバックを送ってください。',
      openingHours: '営業時間',
      noContactListed: '電話番号やリンクの記載はありません。',
      whatsapp: 'WhatsApp',
      mobile: '携帯',
      email: 'メール',
      phone: '電話',
      fax: 'FAX',
      other: '連絡先',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      website: 'ウェブサイト',
      viewingAsOwner: 'オーナーとして閲覧しています。',
      viewAsCustomer: '顧客として表示',
      viewAsOwner: 'オーナーとして表示',
      restaurantNotFoundTitle: 'レストランが見つかりません',
      backToHome: 'ホームに戻る',
      ownerNeedsDataIntro: 'このサイトにはコンテンツを追加してください。管理画面で説明、メニューなどを追加できます。',
      openAdminPage: '管理画面を開く',
      addBannerInAdmin: '設定でカバー画像を追加',
      bookTable: 'テーブルを予約',
      bookAppointment: '予約する',
      mapPlaceholder: '地図',
      allRightsReserved: '著作権所有。',
      surpriseMe: 'おまかせ',
      surpriseMeAria: 'おまかせ – ランダムにメニュー項目を選ぶ',
      noMenuItems: 'メニュー項目なし',
      noProducts: '商品がありません',
      noServices: 'サービスがありません',
      noCatalogItems: 'カタログにアイテムがありません',
      notAvailable: '利用不可',
      notAvailableWithNextTime: '現在ご利用いただけません。次回のご利用可能時間：{time}',
      sizeAndPriceOptions: 'サイズと価格オプション',
      option: 'オプション',
      comboContents: 'コンボの内容',
      ownerEditMenuItemInCatalogAria: 'カタログで項目を編集（管理画面を開く）',
      closeMenu: 'メニューを閉じる',
      closeCatalogModal: 'カタログを閉じる',
      closeServicesModal: 'サービスを閉じる',
      ariaViewMenu: 'メニューを見る',
      ariaViewCatalog: 'カタログを見る',
      ariaViewServices: 'サービスを見る',
      modalTitleMenu: 'メニュー',
      modalTitleCatalog: 'カタログ',
      modalTitleProducts: '商品',
      modalTitleServices: 'サービス',
      ariaLocation: '場所',
      ariaMap: '地図',
      category: 'カテゴリ',
      mapAriaLabel: 'レストランの場所を示す地図',
      openInMaps: 'マップで開く',
      mapIframeTitle: 'この場所の Google マップ',
      mapOpenExternallyHint:
        "下のボタンでマップアプリで場所を開きます。このページに地図を表示するには、事業設定で緯度・経度、またはピン付きの Google マップリンク（{'@'}lat,lng）を追加してください。",
      openNow: '営業中',
      closedNow: '閉店中',
      businessHoursNotListed: '営業時間は未掲載です',
      open24_7: '24時間営業',
      daySunday: '日曜日',
      dayMonday: '月曜日',
      dayTuesday: '火曜日',
      dayWednesday: '水曜日',
      dayThursday: '木曜日',
      dayFriday: '金曜日',
      daySaturday: '土曜日',
      t5TableIcon: 'アイコン',
      t5TableImage: '画像',
      t5TableProduct: '商品',
      t5TablePrice: '価格',
      t5TableStatus: '状態',
      t5TableAction: '操作',
      t5ViewDetails: '詳細を見る',
      t5InStock: '在庫あり',
      selectVariantToAdd: 'カートに追加するバリアントを選択してください',
      selectRequiredModifierChoice: 'カートに入れる前に、必須の各グループから1つずつ選んでください。',
      modifiersHeading: 'オプション追加（任意）',
      modifiersAria: '任意のオプション',
      itemDetailDialogAria: 'メニュー項目の詳細',
      itemUntitled: '無題',
      itemNotAvailable: '提供なし',
      variantOptionNotAvailable: '提供なし',
      variantOptionsAria: 'オプションと価格',
      variantImageAlt: 'バリアント',
      priceFrom: '{price}～',
      comboContentsAria: 'セット内容',
      modifierUnnamed: 'オプション',
      modifierNoExtraCost: '追加料金なし',
      quickOrder: {
        addToCart: 'カートに追加',
        updateCart: 'カートを更新',
        yourOrder: 'ご注文',
        clearAll: 'すべてクリア',
        name: 'お名前',
        namePlaceholder: '例: 太郎',
        notes: 'メモ',
        notesPlaceholder: '例: ソース多め、玉ねぎ抜き...',
        sendViaEmail: 'メールで送信',
        sendViaWhatsApp: 'WhatsApp で送信',
        sendViaViber: 'Viber で送信',
        sendViaSMS: 'SMS で送信',
        orderSent: '注文が送信されました！',
        orderSentDesc: 'ご注文がお店に送信されました。',
        nameRequired: '名前は必須です。',
        nameMaxLength: '名前は100文字以内にしてください。',
        notesMaxLength: 'メモは500文字以内にしてください。',
        notesRequired: 'メモは必須です。',
        cartEmpty: 'カートは空です。',
        quantity: '数量',
        remove: '削除',
        itemAdded: 'カートに追加しました！',
        itemUpdated: 'カートを更新しました！',
        ariaClose: '閉じる',
        ariaDecrease: '数量を減らす',
        ariaIncrease: '数量を増やす',
        ariaRemoveItem: '商品を削除',
        fulfillment: 'お受け取り方法',
        fulfillmentPickup: 'テイクアウト',
        fulfillmentDineIn: '店内',
        dineInReferenceLabel: 'テーブル / 卓番（任意）',
        dineInReferencePlaceholder: '例：5番',
        checkout: 'お会計',
        orderSummary: '注文内容',
        confirmCheckout: '注文する',
        checkoutSubmitting: '送信中…',
        checkoutFailed: '注文を送信できませんでした。もう一度お試しください。',
        subtotal: '小計',
        ariaCloseSummary: '注文内容を閉じる',
        mobile: '携帯番号',
        mobilePlaceholder: '+819012345678',
        mobileHint: '先頭に+と国番号（例: +81…）。SMSでワンタイムコードを送ります。',
        mobileRequired: 'テイクアウトには携帯番号が必要です。',
        mobileInvalid: '国番号付きの有効な番号を入力してください。',
        verificationCode: '認証コード',
        verificationCodePlaceholder: '6桁のコード',
        verificationCodeInvalid: 'SMSの6桁コードを入力してください。',
        sendVerificationCode: 'コードを送信',
        pickupServiceFee: 'テイクアウト手数料（{percent}%）',
        total: '合計',
      },
      cookieConsent: {
        barTitle: 'Cookie および類似の技術',
        barBody:
          'サイトの運営に必要な機能を使います。ご同意いただける場合は、ファーストパーティの分析や第三者のツール（例：セキュリティ確認やログイン）も使います。詳しくは',
        acceptAll: 'すべて許可',
        rejectNonEssential: '必須以外を拒否',
        customize: '設定を変更',
        modalTitle: 'Cookie の設定',
        necessaryTitle: '必須',
        necessaryDesc: 'セキュリティ、ログイン、基本機能に必要です。常に有効です。',
        analyticsTitle: '分析（ファーストパーティ）',
        analyticsDesc: '公開ページの改善に役立ちます。データは当社サーバーで処理されます。',
        thirdPartyTitle: '第三者の機能',
        thirdPartyDesc: '特定のフォームやログインで Google reCAPTCHA などを読み込みます。',
        save: '設定を保存',
        privacyLink: 'プライバシーポリシー',
      },
      // Quick-add item wizard (owner public site)
      quickAddItem: '商品を追加',
      quickAddItemAria: 'メニュー項目を追加',
      quickAddStepOf: 'ステップ {step} / {total}',
      quickAddStep0Title: '商品の<em>種類</em>',
      quickAddStep1Title: '<em>カテゴリ</em>を選択',
      quickAddStep2Title: '商品の<em>詳細</em>',
      quickAddStep3Title: '確認して<em>保存</em>',
      quickAddWhatKind: 'どの種類の商品ですか？',
      quickAddTypeSimpleSub: '単一価格',
      quickAddTypeVariantsSub: 'サイズ、オプション…',
      quickAddTypeCombSub: 'セット',
      quickAddTypeSimpleHint: '固定価格の単一の商品または料理。',
      quickAddTypeVariantsHint: '複数のオプションがあり、それぞれ価格が異なる商品（例：S / L）。',
      quickAddTypeComboHint: 'まとめて販売する商品の組み合わせ（例：セットメニュー）。',
      quickAddNoCats: 'カテゴリがありません',
      quickAddNoCatsSub: '下で最初のカテゴリを作成して商品を整理しましょう。',
      quickAddOrCreate: 'または新規作成',
      quickAddFirstCatName: '最初のカテゴリ名',
      quickAddNewCat: '新しいカテゴリ',
      quickAddCatPlaceholder: '例：メイン、ドリンク、サービス…',
      quickAddCreate: '作成',
      quickAddCatWillCreate: '作成します：{name}',
      quickAddItem1: '件',
      quickAddItemN: '件',
      quickAddNameLabel: '商品名',
      quickAddNamePlaceholder: '例：マルゲリータピザ',
      quickAddPriceOptional: '価格（任意）',
      quickAddComboPriceOptional: 'セット価格（任意）',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: '説明（任意）',
      quickAddDescPlaceholder: '商品カードに表示されます…',
      quickAddVariantsLabel: 'バリエーション',
      quickAddVariantName: 'オプション名',
      quickAddVariantNamePlaceholder: '例：大',
      quickAddVariantPrice: '価格',
      quickAddRemoveVariant: 'バリエーションを削除',
      quickAddAddVariant: 'バリエーションを追加',
      quickAddComboItems: 'セットに含める商品',
      quickAddPickItem: '商品を選択…',
      quickAddAdd: '追加',
      quickAddRemoveComboItem: '削除',
      quickAddNoItemsForCombo: '商品がありません — 先にシンプルまたはバリエーション付きを追加してください。',
      quickAddReviewItem: '商品',
      quickAddReviewName: '名前',
      quickAddReviewType: '種類',
      quickAddReviewPrice: '価格',
      quickAddReviewIncludes: '含まれるもの',
      quickAddReviewCategory: 'カテゴリ',
      quickAddReviewCategoryLabel: 'カテゴリ',
      quickAddNoPrice: '価格なし',
      quickAddNoCategory: 'なし',
      quickAddNew: '新規',
      quickAddPublishNow: 'すぐに公開',
      quickAddPublishNowSub: 'サイトにすぐ反映されます',
      quickAddAddAnother: '保存後にさらに追加',
      quickAddAddAnotherSub: 'この流れを開いたままにする',
      quickAddSaveItem: '商品を保存',
      quickAddReview: '確認',
      quickAddContinue: '続ける',
      quickAddBack: '戻る',
      quickAddSaved: '保存しました。',
      quickAddSavedSub: '{name} を {category} に追加しました。',
      quickAddAnotherItem: '+ さらに商品を追加',
      quickAddDone: '完了',
    },
    tour: {
      welcomeTitle: 'ようこそ！',
      welcomeDescription: 'ダッシュボードの簡単なツアーです。レストランやメニューなどの管理場所をご案内します。',
      step1Title: 'メインエリア',
      step1Description: 'コンテンツはここに表示されます。サイドバーでダッシュボード、レストランなどに切り替えられます。',
      step2Title: 'ナビゲーション',
      step2Description: 'このサイドバーでダッシュボード、レストラン、メニュー項目、フィードバック、プロファイルへ移動します。',
      step2DescriptionSuperadmin: 'このサイドバーでダッシュボード、ユーザー、オーナーフィードバック、レストランなどへ移動します。',
      step3Title: 'ダッシュボード',
      step3Description: 'レストラン、メニュー項目、フィードバックの概要を一覧で確認できます。',
      step4Title: 'レストラン',
      step4Description: 'レストランを管理します。店舗の追加、詳細・メニューの編集ができます。',
      step5Title: 'メニュー項目',
      step5Description: '料理、ドリンク、コンボを作成・編集し、各レストランのメニューカテゴリに追加します。',
      step6Title: 'フィードバック',
      step6Description: '公開ページの顧客レビューを表示・モデレート。承認または拒否で表示・非表示を切り替え。',
      step7Title: '機能リクエスト',
      step7Description: 'チームにフィードバックや機能要望を送信。過去の送信履歴もここで確認できます。',
      step8Title: 'プロフィールと設定',
      step8Description: '名前、メール、パスワード、アプリの言語を更新します。',
      next: '次へ',
      prev: '前へ',
      done: '完了',
      close: '閉じる',
      skip: 'ツアーをスキップ',
    },
    restaurantsTour: {
      step1Title: 'レストラン一覧',
      step1Description: 'ここがレストラン一覧です。拠点の追加、詳細の編集、メニューの管理ができます。',
      step2Title: 'レストランを追加',
      step2Description: 'ここをクリックして新しいレストランを作成し、名前・住所・サブドメインを設定します。',
      step3Title: 'あなたのレストラン',
      step3Description: 'レストランはカードで表示されます。カードをクリックして開くか、空の状態から最初の1件を作成します。',
      step4Title: 'レストランカード',
      step4Description: 'レストランをクリックして編集、メニュー管理、稼働設定、公開リンクやQRコードの取得ができます。',
      tooltip: 'レストランツアー',
    },
    welcome: {
      title: 'Create your first restaurant',
      subtitle: 'Get your menu online, share your address and hours, and let customers find you in minutes.',
      cta: 'Create my first restaurant',
    },
    wizard: {
      stepOf: 'Step {current} of {total}',
      step1Title: 'Name your restaurant',
      nameLabel: 'Restaurant name',
      taglineOptional: 'Tagline (optional)',
      slugPreview: 'Your web address',
      templateLabel: 'Public page template',
      templateHint: 'Choose the layout for your public page.',
      next: 'Next',
      back: 'Back',
      skipForNow: 'Skip for now',
      doLater: "I'll do this later",
      step2Title: 'Location & contact (optional)',
      youreAllSet: "You're all set",
      sublineLive: 'Your restaurant page is live. Share the link or QR so customers can find you.',
      copyAddress: 'Copy address',
      copied: 'Copied!',
      saveQr: 'Save QR code',
      scanHint: 'Customers scan this to open your page',
      goToMyRestaurant: 'Go to my restaurant',
      backToDashboard: 'Back to dashboard',
      firstTimeLink: 'First time? Create your first restaurant',
    },
    startTour: 'アプリのツアーを開始',
    startTourDescription: 'ガイドツアーを再度実行して、アプリの主な機能を確認できます。',
  },
  nl: {
    app: {
      name: 'RMS',
      login: 'Inloggen',
      register: 'Account aanmaken',
      logout: 'Uitloggen',
      profile: 'Profiel en instellingen',
      restaurants: 'Restaurants',
      menu: 'Menu',
      languages: 'Talen en beschrijving',
      addLanguage: 'Taal toevoegen',
      defaultLanguage: 'Standaardtaal',
      languagesHint: 'Voeg talen toe of verwijder ze. De standaardtaal wordt gebruikt wanneer er op de openbare site geen taal is geselecteerd.',
      description: 'Beschrijving',
      save: 'Opslaan',
      cancel: 'Annuleren',
      remove: 'Verwijderen',
      translate: 'Vertalen van standaard',
      addItem: 'Item toevoegen',
      editItem: 'Menuitem bewerken',
      createItem: 'Menuitem toevoegen',
      dashboard: 'Dashboard',
      feedbacks: 'Feedback',
      feedbacksListSubtitle: 'Keur feedback goed om het op uw openbare pagina te tonen. Afwijzen of verwijderen om te verbergen.',
      feedbacksListEmpty: 'Nog geen feedback. Ze verschijnen hier wanneer klanten beoordelingen insturen.',
      feedbacksLoadError: 'Feedback laden mislukt.',
      retry: 'Opnieuw proberen',
      ariaMainNav: 'Hoofdnavigatie',
      ariaAppLanguage: 'Taal van de app',
      ariaOpenMenu: 'Menu openen',
      ownerFeedbacksSubtitle: 'Functieverzoeken en feedback van restaurant eigenaren. Wijzig status na beoordeling.',
      ownerFeedbacksEmpty: 'Nog geen feedback van eigenaren.',
      markPending: 'Markeer als in behandeling',
      markReviewed: 'Markeer als beoordeeld',
      feedbacksUpdateForbidden: 'U heeft geen toestemming om feedback bij te werken.',
      feedbackNotFound: 'Feedback niet gevonden.',
      feedbackUpdated: 'Feedback bijgewerkt.',
      feedbackApproved: 'Feedback goedgekeurd.',
      feedbackRejected: 'Feedback afgewezen.',
      failedToUpdate: 'Bijwerken mislukt.',
      feedbackDeleted: 'Feedback verwijderd.',
      failedToDelete: 'Verwijderen mislukt.',
      deleteFeedbackConfirm: 'Deze feedback verwijderen? Dit kan niet ongedaan worden gemaakt.',
      users: 'Gebruikers',
      superadminUsersSubtitle: 'Beheer gebruikersaccounts: betaalstatus en activeren/deactiveren.',
      noUsersFound: 'Geen gebruikers gevonden.',
      paid: 'Betaald',
      free: 'Gratis',
      active: 'Actief',
      deactivated: 'Gedeactiveerd',
      superadmin: 'Superadmin',
      markPaid: 'Markeer als betaald',
      removePaid: 'Betaald verwijderen',
      activate: 'Activeren',
      deactivate: 'Deactiveren',
      ariaDeactivateUser: 'Gebruiker deactiveren',
      ariaActivateUser: 'Gebruiker activeren',
      you: '(u)',
      usersForbidden: 'U heeft geen toestemming om gebruikers te bekijken.',
      superadminRestaurantsSubtitle: 'Alleen-lezen lijst van alle restaurants in het systeem.',
      noRestaurantsFound: 'Geen restaurants gevonden.',
      availability: 'Beschikbaarheid',
      availabilitySubtitle: 'Wanneer uw restaurant open is. Wijzig tijden hieronder en tik op Opslaan.',
      deleteRestaurant: 'Restaurant verwijderen',
      logoAndBanner: 'Logo en banner',
      logoBannerDescription: 'Wijzig of werk het logo en de banner van het restaurant bij.',
      uploadImagesHint: 'Upload afbeeldingen. JPEG, PNG, GIF of WebP. Max 2 MB elk.',
      logo: 'Logo',
      banner: 'Banner',
      takePhoto: 'Foto maken',
      chooseFromGallery: 'Kiezen uit galerij',
      changeLogo: 'Logo wijzigen',
      changeBanner: 'Banner wijzigen',
      done: 'Klaar',
      deleteRestaurantConfirm: 'Restaurant verwijderen?',
      cannotBeUndone: 'Dit kan niet ongedaan worden gemaakt.',
      typeToConfirm: 'Typ {slug} om te bevestigen',
      ariaUploadingLogo: 'Logo uploaden',
      ariaUploadingBanner: 'Banner uploaden',
      ariaUploadLogo: 'Logo uploaden',
      ariaUploadBanner: 'Banner uploaden',
      ariaUploadLogoFromGallery: 'Logo uit galerij uploaden',
      ariaUploadBannerFromGallery: 'Banner uit galerij uploaden',
      ariaTakePhotoLogo: 'Foto maken voor logo',
      ariaTakePhotoBanner: 'Foto maken voor banner',
      settings: 'Instellingen',
      ariaManageSections: 'Bedrijfsbeheer-secties',
      contactAndDetails: 'Contact en gegevens',
      address: 'Adres',
      email: 'E-mail',
      social: 'Social media',
      addLanguageEllipsis: 'Taal toevoegen…',
      addLanguageHint: 'Voeg minimaal één taal hierboven toe om beschrijvingen te beheren.',
      editDescriptionFor: 'Beschrijving bewerken voor:',
      ariaSelectTemplate: 'Sjabloon selecteren',
      menusAndCategories: 'Menu\'s en categorieën',
      createFirstMenuHint: 'Maak uw eerste menu om categorieën toe te voegen.',
      createCategoryFirstHint: 'Maak eerst een categorie om menu-items toe te voegen.',
      menuItemsSettingsHint: 'Voeg gerechten toe vanaf de pagina van elke categorie. Vertalingen gebruiken restaurantinstellingen.',
      createCategoryAboveHint: 'Maak hierboven een categorie om menu-items toe te voegen.',
      renameMenu: 'Menu hernoemen',
      selectMenu: 'Menu selecteren',
      editCategory: 'Categorie bewerken',
      removeCategory: 'Categorie verwijderen',
      addMenu: 'Menu toevoegen',
      addCategory: 'Categorie toevoegen',
      categoryAvailability: 'Beschikbaarheid categorie',
      removeCategoryImage: 'Categorie-afbeelding verwijderen',
      addMenuOrCategory: 'Menu of categorie toevoegen',
      editIn: 'Bewerken in',
      changeImage: 'Afbeelding wijzigen',
      uploadImage: 'Afbeelding uploaden',
      translating: 'Vertalen…',
      removing: 'Verwijderen…',
      mainMenuDefault: 'Hoofdmenu',
      menuManagement: 'Menubeheer',
      menuManagementSubtitle: 'Menu\'s, categorieën en items. Sleep om te sorteren.',
      noMenuItemsYet: 'Nog geen menu-items. Gebruik de + knop hieronder.',
      categoryNameRequired: 'Categorienaam is verplicht.',
      categoryUpdated: 'Categorie bijgewerkt.',
      categoryCreated: 'Categorie aangemaakt.',
      addMenuItemsToCategory: 'Menu-items aan deze categorie toevoegen',
      noMenuItemsOrMatch: 'Geen menu-items of geen resultaten.',
      assignTags: 'Tags toewijzen',
      itemAvailability: 'Beschikbaarheid item',
      dragToReorder: 'Sleep om te sorteren',
      setAvailabilityTimes: 'Beschikbaarheidstijden instellen',
      available: 'Beschikbaar',
      notAvailable: 'Niet beschikbaar',
      markNotAvailable: 'Markeer als niet beschikbaar',
      markAvailableOnMenu: 'Markeer als beschikbaar op openbaar menu',
      visibleOnMenu: 'Zichtbaar op menu',
      hiddenFromMenu: 'Verborgen op menu',
      addMenuItemToCategory: 'Menu-item aan categorie toevoegen',
      menuItemTags: 'Menu-item tags',
      menuItems: 'Menu-items',
      createRestaurantFirstForMenus: 'Maak eerst een restaurant om menu\'s te beheren.',
      addRestaurant: 'Restaurant toevoegen',
      ratingOutOf5: 'Beoordeling: {n} van 5',
      anonymous: 'Anoniem',
      approve: 'Goedkeuren',
      reject: 'Afwijzen',
      ariaApproveFeedback: 'Feedback goedkeuren',
      ariaRejectFeedback: 'Feedback afwijzen',
      ariaDeleteFeedback: 'Feedback verwijderen',
      resetPasswordSuccess: 'Uw wachtwoord is gereset. U kunt inloggen.',
      resetPasswordEnterNew: 'Voer hieronder uw nieuwe wachtwoord in.',
      type: 'Type',
      labelOptional: 'Label (optioneel)',
      placeholderUrl: 'https://...',
      placeholderPhone: 'bijv. +31 20 123 4567',
      placeholderContactLabel: 'bijv. Reserveringen, Keuken',
      showOnPublicPage: 'Tonen op openbare pagina',
      hideOnPublicPage: 'Verbergen op openbare pagina',
      ariaEditContact: 'Contact bewerken',
      ariaDeleteContact: 'Contact verwijderen',
      noContactsYet: 'Nog geen contacten of links. Voeg er een toe.',
      deleteContactConfirm: 'Dit contact verwijderen?',
      ariaPagination: 'Paginering',
      ariaOpeningTime: 'Openingstijd',
      ariaClosingTime: 'Sluitingstijd',
      removeTimeSlot: 'Tijdslot verwijderen',
      menuItemLabel: 'Menu-item',
      addMenuItem: 'Menu-item toevoegen',
      ariaRemoveImage: 'Afbeelding verwijderen',
      ariaRemoveVariantImage: 'Variantafbeelding verwijderen',
      ariaRemoveEntry: 'Vermelding verwijderen',
      ariaRemoveGroup: 'Groep verwijderen',
      ariaShowHelp: 'Help tonen: wat betekenen de knoppen?',
      help: 'Help',
      ariaButtonLegend: 'Knoplegenda',
      helpLegendDescription: 'Betekenis van de pictogrammen op menu- en categorieregels.',
      legendDragHandle: 'Sleephendel',
      legendDragDesc: 'Sleep om te sorteren',
      legendSchedule: 'Schema',
      legendScheduleDesc: 'Openingstijden instellen',
      legendAvailability: 'Beschikbaarheid',
      legendAvailabilityDesc: 'Wanneer dit item beschikbaar is',
      legendVisibility: 'Zichtbaarheid',
      legendVisibilityDesc: 'Tonen of verbergen op openbaar menu',
      legendTags: 'Tags',
      legendTagsDesc: 'Tags toewijzen',
      paidFeature: 'Betaalde functie',
      upgradeToUse: 'Upgrade om deze functie te gebruiken.',
      paidFeatureAiDescription: 'Menufoto\'s verbeteren met AI is beschikbaar in een betaald abonnement.',
      maybeLater: 'Misschien later',
      upgrade: 'Upgraden',
      ariaNotifications: 'Meldingen',
      dismiss: 'Sluiten',
      reservationsHowItWorksTitle: 'Hoe tafelreserveringen werken',
      reservationsHowItWorksIntro: 'Gasten kunnen een tafel aanvragen via de openbare pagina van uw restaurant. U keurt reserveringen hier goed, wijst ze af of bewerkt ze.',
      reservationsHowItWorksPhoneRequired: 'Voor elke reservering is een geldig telefoonnummer nodig om bevestigingen en herinneringen per sms te sturen.',
      reservationsHowItWorksSteps: 'Stel het sms-notificatienummer per restaurant in om meldingen over nieuwe reserveringen te ontvangen. Keur verzoeken goed of af; gasten ontvangen een sms. U kunt ook datum, tijd of aantal gasten wijzigen en annuleren — gasten worden per sms op de hoogte gebracht.',
      reservationsHowItWorksTooltip: 'Hoe reserveringen werken',
      close: 'Sluiten',
      loading: 'Laden…',
      back: 'Terug',
      delete: 'Verwijderen',
      pending: 'In behandeling',
      featureRequest: 'Functieverzoek',
      ownerFeedbacks: 'Eigenaar feedback',
    },
    errors: {
      forbidden: 'U heeft geen toestemming voor deze actie.',
      validation: 'Controleer uw invoer.',
      pageNotFound: 'Pagina niet gevonden',
      pageNotFoundDescription: 'De pagina die u zoekt bestaat niet of is verplaatst.',
    },
    landing: {
      hero: {
        badge: 'Bedrijfstechnologie, eenvoudig gemaakt',
        titleBefore: 'Uw bedrijf, ',
        titleHighlight: 'online in 10 minuten.',
        subtitle: 'Breng uw bedrijf vandaag online — Productcatalogus, Openingstijden, Boekingen en meer. Gratis starten. Geen technische vaardigheden nodig.',
        trustedBy: 'Voor altijd gratis. Geen creditcard nodig.',
      },
      cta: {
        bookDemo: 'Bekijk {businessType}-demo',
        bookDemoGeneric: 'Bekijk demo',
      },
      footer: {
        cookies: 'Cookie-instellingen',
      },
    },
    public: {
      about: 'Over ons',
      t3NavAbout: 'Over ons',
      siteCreatedViaQRestoo: 'Site gemaakt door QRestoo',
      createdByQRestoo: 'Gemaakt met QRestoo',
      sitePoweredByDLR: 'Site powered by DLR Web Solutions',
      contactUs: 'Neem contact op',
      reviewsAndFeedback: 'Recensies en feedback',
      t1NavMenu: 'Menu',
      reviewsNav: 'Recensies',
      contactNav: 'Contact',
      t2MenuNav: 'Menu',
      t2ViewServices: 'Diensten',
      t2ReviewsNav: 'Recensies',
      t2ContactNav: 'Contact',
      navMenu: 'Menu',
      navServices: 'Diensten',
      navProducts: 'Producten',
      ownerNavBookingUnavailable: 'Reserveren. Accountupgrade nodig.',
      ownerNavReviewsUnavailable: 'Recensies. Accountupgrade nodig.',
      ownerNavLocationUnavailable: 'Locatie. Voeg adres of kaart toe in instellingen.',
      ownerNavAboutUnavailable: 'Ons verhaal. Voeg beschrijving toe in instellingen.',
      ownerNavHoursUnavailable: 'Openingstijden. Voeg toe in instellingen.',
      t3FooterTaglineRestaurants: 'Onvergetelijke dinerervaringen. Uitmuntendheid op elk bord.',
      t3FooterTaglineEcommerce: 'Kwaliteitsproducten, zorgvuldig geselecteerd. Uitmuntendheid bij elke bestelling.',
      t3FooterTaglineServices: 'Uitzonderlijke service. Uitmuntendheid in elk detail.',
      t2CommunicationsLink: 'Communicatielink',
      noLogoSetOwnerMessage: 'Geen logo ingesteld. Voeg er een toe in je bedrijfsinstellingen.',
      ownerEditLogoAria: 'Logo en banner bewerken',
      heroEstYear: 'Opgericht {year}',
      noTaglineOrDescriptionSetOwnerMessage: 'Geen tagline of beschrijving. Voeg toe in je bedrijfsinstellingen.',
      noDescriptionSetOwnerMessage: 'Geen beschrijving. Voeg toe in je bedrijfsinstellingen.',
      noMapSetOwnerMessage: 'Geen kaart. Voeg adres of kaartlink toe in je bedrijfsinstellingen.',
      noBookingAvailableOwnerMessage: 'Reserveren is niet beschikbaar. Schakel in via je bedrijfsinstellingen.',
      noMailingListAvailableOwnerMessage: 'Mailinglijst is niet beschikbaar. Schakel in via je bedrijfsinstellingen.',
      noFeedbackAvailableOwnerMessage: 'Beoordelingen en feedback zijn niet beschikbaar. Schakel in via je bedrijfsinstellingen.',
      t2NewsletterTitle: 'NIEUWSBRIEF INSCHRIJVEN',
      t2FeedbackPerformanceLogging: 'Prestatielogboek',
      t2FeedbackOperationalLog: 'OPERATIONEEL LOG',
      t2FeedbackTransmitCopy: 'Stuur uw ervaringsrapport voor kwaliteitsborging en optimalisatie.',
      t2FeedbackSessionMeta: 'Sessiemetadata:',
      t2FeedbackTerminalId: 'Terminal-ID: TGT-LOG-992',
      t2FeedbackProtocol: 'Protocol: feedback_v2.0.sh',
      t2FeedbackRatingLabel: 'PRESTATIEBEOORDELING',
      t2FeedbackRatingHint: 'Selecteer beoordelingscriteria (1-5)',
      t2FeedbackSitrepLabel: 'SITREP / GEDETAILLEERDE FEEDBACK',
      t2FeedbackPlaceholder: 'Voer loggegevens hier in...',
      t2FeedbackSubmitReport: 'RAPPORT VERZENDEN',
      t2FeedbackDisclaimer: 'Door te verzenden gaat u akkoord met de gegevensarchiveringsprotocollen.',
      ourMenu: 'Ons menu',
      ourProducts: 'Producten',
      ourServices: 'Onze diensten',
      ourCatalog: 'Onze catalogus',
      viewMenu: 'Menu bekijken',
      viewCatalog: 'Catalogus bekijken',
      viewServices: 'Diensten bekijken',
      leaveFeedback: 'Laat uw feedback achter',
      ratingLabel: 'Beoordeling (1–5)',
      yourName: 'Uw naam',
      yourMessage: 'Uw bericht',
      sendFeedback: 'Feedback versturen',
      formErrorGeneric: 'Er is iets misgegaan. Probeer het opnieuw.',
      mailingListError: 'Er is iets misgegaan. Probeer het opnieuw.',
      noReviewsYet: 'Nog geen recensies. Wees de eerste die hieronder feedback achterlaat.',
      openingHours: 'Openingstijden',
      noContactListed: 'Geen telefoonnummers of links vermeld.',
      whatsapp: 'WhatsApp',
      mobile: 'Mobiel',
      email: 'E-mail',
      phone: 'Telefoon',
      fax: 'Fax',
      other: 'Contact',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      website: 'Website',
      viewingAsOwner: 'U bekijkt dit als eigenaar.',
      viewAsCustomer: 'Bekijken als klant',
      viewAsOwner: 'Bekijken als eigenaar',
      restaurantNotFoundTitle: 'Restaurant niet gevonden',
      backToHome: 'Terug naar start',
      ownerNeedsDataIntro: 'Deze site heeft meer inhoud nodig. Voeg een beschrijving, menu of anders toe in het adminpaneel.',
      openAdminPage: 'Admin openen',
      addBannerInAdmin: 'Voeg een omslagfoto toe in Instellingen',
      bookTable: 'Tafel reserveren',
      bookAppointment: 'Afspraak boeken',
      mapPlaceholder: 'Kaart',
      allRightsReserved: 'Alle rechten voorbehouden.',
      surpriseMe: 'Verras me',
      surpriseMeAria: 'Verras me – kies een willekeurig menu-item',
      noMenuItems: 'Geen menu-items',
      noProducts: 'Geen producten',
      noServices: 'Geen diensten',
      noCatalogItems: 'Geen items in catalogus',
      notAvailable: 'Niet beschikbaar',
      notAvailableWithNextTime: 'Nu niet beschikbaar. Volgende keer beschikbaar: {time}.',
      sizeAndPriceOptions: 'Opties voor maat en prijs',
      option: 'Optie',
      comboContents: 'Combo-inhoud',
      ownerEditMenuItemInCatalogAria: 'Item in catalogus bewerken (opent beheer)',
      closeMenu: 'Menu sluiten',
      closeCatalogModal: 'Catalogus sluiten',
      closeServicesModal: 'Diensten sluiten',
      ariaViewMenu: 'Menu bekijken',
      ariaViewCatalog: 'Catalogus bekijken',
      ariaViewServices: 'Diensten bekijken',
      modalTitleMenu: 'Menu',
      modalTitleCatalog: 'Catalogus',
      modalTitleProducts: 'Producten',
      modalTitleServices: 'Diensten',
      ariaLocation: 'Locatie',
      ariaMap: 'Kaart',
      category: 'Categorie',
      mapAriaLabel: 'Kaart met restaurantlocatie',
      openInMaps: 'Openen in kaarten',
      mapIframeTitle: 'Google Maps voor deze locatie',
      mapOpenExternallyHint:
        "Tik hieronder om deze locatie in je kaarten-app te openen. Voor een ingesloten kaart: voeg breedte- en lengtegraad toe of een Google Maps-link met pin ({'@'}lat,lng) in de bedrijfsinstellingen.",
      openNow: 'Nu open',
      closedNow: 'Gesloten',
      businessHoursNotListed: 'Geen openingstijden vermeld',
      open24_7: '24 uur per dag geopend',
      daySunday: 'Zondag',
      dayMonday: 'Maandag',
      dayTuesday: 'Dinsdag',
      dayWednesday: 'Woensdag',
      dayThursday: 'Donderdag',
      dayFriday: 'Vrijdag',
      daySaturday: 'Zaterdag',
      t5TableIcon: 'Pictogram',
      t5TableImage: 'Afbeelding',
      t5TableProduct: 'Product',
      t5TablePrice: 'Prijs',
      t5TableStatus: 'Status',
      t5TableAction: 'Actie',
      t5ViewDetails: 'Details bekijken',
      t5InStock: 'Op voorraad',
      selectVariantToAdd: 'Selecteer een variant om toe te voegen aan het winkelwagentje',
      selectRequiredModifierChoice: 'Kies in elke verplichte groep één optie voordat u toevoegt aan de winkelwagen.',
      modifiersHeading: 'Extra’s (optioneel)',
      modifiersAria: 'Optionele extra’s',
      itemDetailDialogAria: 'Menu-itemdetails',
      itemUntitled: 'Zonder titel',
      itemNotAvailable: 'Niet beschikbaar',
      variantOptionNotAvailable: 'Niet beschikbaar',
      variantOptionsAria: 'Opties en prijzen',
      variantImageAlt: 'Variant',
      priceFrom: 'Vanaf {price}',
      comboContentsAria: 'Comboinhoud',
      modifierUnnamed: 'Extra',
      modifierNoExtraCost: 'Geen meerprijs',
      quickOrder: {
        addToCart: 'Aan winkelwagen toevoegen',
        updateCart: 'Winkelwagen bijwerken',
        yourOrder: 'Uw bestelling',
        clearAll: 'Alles wissen',
        name: 'Uw naam',
        namePlaceholder: 'bijv. Jan',
        notes: 'Opmerkingen',
        notesPlaceholder: 'bijv. Extra saus, zonder uien...',
        sendViaEmail: 'Verzenden via e-mail',
        sendViaWhatsApp: 'Verzenden via WhatsApp',
        sendViaViber: 'Verzenden via Viber',
        sendViaSMS: 'Verzenden via SMS',
        orderSent: 'Bestelling verzonden!',
        orderSentDesc: 'Uw bestelling is verzonden naar het bedrijf.',
        nameRequired: 'Naam is verplicht.',
        nameMaxLength: 'Naam mag maximaal 100 tekens zijn.',
        notesMaxLength: 'Opmerkingen mogen maximaal 500 tekens zijn.',
        notesRequired: 'Opmerkingen zijn verplicht.',
        cartEmpty: 'Uw winkelwagen is leeg.',
        quantity: 'Aantal',
        remove: 'Verwijderen',
        itemAdded: 'Toegevoegd aan winkelwagen!',
        itemUpdated: 'Winkelwagen bijgewerkt!',
        ariaClose: 'Sluiten',
        ariaDecrease: 'Aantal verlagen',
        ariaIncrease: 'Aantal verhogen',
        ariaRemoveItem: 'Artikel verwijderen',
        fulfillment: 'Afhalen of ter plaatse',
        fulfillmentPickup: 'Afhalen',
        fulfillmentDineIn: 'Ter plaatse',
        dineInReferenceLabel: 'Tafel / referentie (optioneel)',
        dineInReferencePlaceholder: 'bijv. Tafel 5',
        checkout: 'Afrekenen',
        orderSummary: 'Besteloverzicht',
        confirmCheckout: 'Bestelling plaatsen',
        checkoutSubmitting: 'Bezig met verzenden…',
        checkoutFailed: 'Bestelling kon niet worden geplaatst. Probeer opnieuw.',
        subtotal: 'Subtotaal',
        ariaCloseSummary: 'Besteloverzicht sluiten',
        mobile: 'Mobiel nummer',
        mobilePlaceholder: '+31612345678',
        mobileHint: 'Begin met + en je landnummer (bijv. +31…). We sturen een eenmalige code per SMS.',
        mobileRequired: 'Mobiel is verplicht voor afhalen.',
        mobileInvalid: 'Voer een geldig nummer met landcode in.',
        verificationCode: 'Verificatiecode',
        verificationCodePlaceholder: '6-cijferige code',
        verificationCodeInvalid: 'Voer de 6-cijferige code uit de SMS in.',
        sendVerificationCode: 'Code versturen',
        pickupServiceFee: 'Afhaalkosten ({percent}%)',
        total: 'Totaal',
      },
      cookieConsent: {
        barTitle: 'Cookies en vergelijkbare technologieën',
        barBody:
          'We gebruiken essentiële functies om de site te laten werken. Met jouw toestemming voegen we first-party analytics en tools van derden toe (bijv. beveiligingscontroles of inloggen). Zie het ',
        acceptAll: 'Alles accepteren',
        rejectNonEssential: 'Niet-noodzakelijke weigeren',
        customize: 'Aanpassen',
        modalTitle: 'Cookievoorkeuren',
        necessaryTitle: 'Strikt noodzakelijk',
        necessaryDesc: 'Nodig voor beveiliging, inloggen en kernfuncties. Altijd aan.',
        analyticsTitle: 'Analytics (first-party)',
        analyticsDesc: 'Helpt ons openbare pagina’s te verbeteren. Gegevens worden op onze servers verwerkt.',
        thirdPartyTitle: 'Functies van derden',
        thirdPartyDesc: 'Laadt tools zoals Google reCAPTCHA bij bepaalde formulieren of bij inloggen.',
        save: 'Voorkeuren opslaan',
        privacyLink: 'privacybeleid',
      },
      // Quick-add item wizard (owner public site)
      quickAddItem: 'Item toevoegen',
      quickAddItemAria: 'Menu-item toevoegen',
      quickAddStepOf: 'Stap {step} van {total}',
      quickAddStep0Title: 'Item<em>type</em>',
      quickAddStep1Title: 'Kies een <em>categorie</em>',
      quickAddStep2Title: 'Item<em>details</em>',
      quickAddStep3Title: 'Controleren &amp; <em>opslaan</em>',
      quickAddWhatKind: 'Wat voor soort item?',
      quickAddTypeSimpleSub: 'Eén prijs',
      quickAddTypeVariantsSub: 'Maten, opties…',
      quickAddTypeCombSub: 'Bundel',
      quickAddTypeSimpleHint: 'Eén product of gerecht met een vaste prijs.',
      quickAddTypeVariantsHint: 'Eén item met meerdere opties — elk met eigen prijs (bijv. Klein / Groot).',
      quickAddTypeComboHint: 'Meerdere items samen verkocht (bijv. maaltijddeal).',
      quickAddNoCats: 'Nog geen categorieën',
      quickAddNoCatsSub: 'Maak hieronder uw eerste categorie om items te groeperen.',
      quickAddOrCreate: 'of nieuw aanmaken',
      quickAddFirstCatName: 'Naam van uw eerste categorie',
      quickAddNewCat: 'Nieuwe categorie',
      quickAddCatPlaceholder: 'bijv. Hoofdgerechten, Dranken, Diensten…',
      quickAddCreate: 'Aanmaken',
      quickAddCatWillCreate: 'Wordt aangemaakt: {name}',
      quickAddItem1: 'item',
      quickAddItemN: 'items',
      quickAddNameLabel: 'Itemnaam',
      quickAddNamePlaceholder: 'bijv. Pizza Margherita',
      quickAddPriceOptional: 'Prijs (optioneel)',
      quickAddComboPriceOptional: 'Comboprijs (optioneel)',
      quickAddPricePlaceholder: '0.00',
      quickAddDescOptional: 'Beschrijving (optioneel)',
      quickAddDescPlaceholder: 'Wordt op de itemkaart getoond…',
      quickAddVariantsLabel: 'Varianten',
      quickAddVariantName: 'Optienaam',
      quickAddVariantNamePlaceholder: 'bijv. Groot',
      quickAddVariantPrice: 'Prijs',
      quickAddRemoveVariant: 'Variant verwijderen',
      quickAddAddVariant: 'Variant toevoegen',
      quickAddComboItems: 'Items in combo',
      quickAddPickItem: 'Kies een item…',
      quickAddAdd: 'Toevoegen',
      quickAddRemoveComboItem: 'Item verwijderen',
      quickAddNoItemsForCombo: 'Nog geen items — voeg eerst eenvoudige of variant-items toe.',
      quickAddReviewItem: 'Item',
      quickAddReviewName: 'Naam',
      quickAddReviewType: 'Type',
      quickAddReviewPrice: 'Prijs',
      quickAddReviewIncludes: 'Bevat',
      quickAddReviewCategory: 'Categorie',
      quickAddReviewCategoryLabel: 'Categorie',
      quickAddNoPrice: 'Geen prijs',
      quickAddNoCategory: 'Geen',
      quickAddNew: 'nieuw',
      quickAddPublishNow: 'Direct publiceren',
      quickAddPublishNowSub: 'Meteen zichtbaar op uw site',
      quickAddAddAnother: 'Nog een toevoegen na opslaan',
      quickAddAddAnotherSub: 'Deze flow open houden',
      quickAddSaveItem: 'Item opslaan',
      quickAddReview: 'Controleren',
      quickAddContinue: 'Doorgaan',
      quickAddBack: 'Terug',
      quickAddSaved: 'Item opgeslagen.',
      quickAddSavedSub: '{name} toegevoegd aan {category}.',
      quickAddAnotherItem: '+ Nog een item toevoegen',
      quickAddDone: 'Klaar',
    },
    tour: {
      welcomeTitle: 'Welkom!',
      welcomeDescription: 'Laten we een korte rondleiding door uw dashboard geven. U ziet waar u restaurants, menu\'s en meer beheert.',
      step1Title: 'Hoofdgebied',
      step1Description: 'Uw inhoud verschijnt hier. Gebruik de zijbalk om te wisselen tussen Dashboard, Restaurants en andere secties.',
      step2Title: 'Navigatie',
      step2Description: 'Gebruik deze zijbalk voor Dashboard, Restaurants, Menu-items, feedback en Profiel.',
      step2DescriptionSuperadmin: 'Gebruik deze zijbalk voor Dashboard, Gebruikers, eigenaar-feedback, Restaurants en meer.',
      step3Title: 'Dashboard',
      step3Description: 'Uw overzicht. Restaurants, menu-items en feedback in één oogopslag.',
      step4Title: 'Restaurants',
      step4Description: 'Beheer uw restaurant(s). Voeg locaties toe, bewerk gegevens en organiseer menu\'s per restaurant.',
      step5Title: 'Menu-items',
      step5Description: 'Maak en bewerk gerechten, drankjes en combo\'s. Voeg ze toe aan categorieën in het menu van elk restaurant.',
      step6Title: 'Feedback',
      step6Description: 'Bekijk en beheer klantbeoordelingen van uw openbare pagina\'s. Keur goed of af om ze te tonen of verbergen.',
      step7Title: 'Functieverzoek',
      step7Description: 'Stuur feedback of functieverzoeken naar het team. U ziet hier ook uw eerdere inzendingen.',
      step8Title: 'Profiel en instellingen',
      step8Description: 'Werk uw naam, e-mail, wachtwoord en apptaal bij.',
      next: 'Volgende',
      prev: 'Vorige',
      done: 'Klaar',
      close: 'Sluiten',
      skip: 'Tour overslaan',
    },
    restaurantsTour: {
      step1Title: 'Restaurantlijst',
      step1Description: 'Dit is uw restaurantlijst. Voeg locaties toe, bewerk gegevens en beheer menu\'s vanaf hier.',
      step2Title: 'Restaurant toevoegen',
      step2Description: 'Klik hier om een nieuw restaurant aan te maken en naam, adres en subdomein in te stellen.',
      step3Title: 'Uw restaurants',
      step3Description: 'Uw restaurants verschijnen als kaarten. Klik op een kaart om dat restaurant te openen, of gebruik de lege staat om uw eerste aan te maken.',
      step4Title: 'Restaurantkaart',
      step4Description: 'Klik op een restaurant om het te bewerken, menu\'s te beheren, beschikbaarheid in te stellen en de openbare link of QR-code te krijgen.',
      tooltip: 'Restauranttour',
    },
    welcome: {
      title: 'Create your first restaurant',
      subtitle: 'Get your menu online, share your address and hours, and let customers find you in minutes.',
      cta: 'Create my first restaurant',
    },
    wizard: {
      stepOf: 'Step {current} of {total}',
      step1Title: 'Name your restaurant',
      nameLabel: 'Restaurant name',
      taglineOptional: 'Tagline (optional)',
      slugPreview: 'Your web address',
      templateLabel: 'Public page template',
      templateHint: 'Choose the layout for your public page.',
      next: 'Next',
      back: 'Back',
      skipForNow: 'Skip for now',
      doLater: "I'll do this later",
      step2Title: 'Location & contact (optional)',
      youreAllSet: "You're all set",
      sublineLive: 'Your restaurant page is live. Share the link or QR so customers can find you.',
      copyAddress: 'Copy address',
      copied: 'Copied!',
      saveQr: 'Save QR code',
      scanHint: 'Customers scan this to open your page',
      goToMyRestaurant: 'Go to my restaurant',
      backToDashboard: 'Back to dashboard',
      firstTimeLink: 'First time? Create your first restaurant',
    },
    startTour: 'App-tour starten',
    startTourDescription: 'Start de rondleiding opnieuw om de belangrijkste onderdelen van de app te zien.',
  },
}

export const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  messages,
})

export default i18n
